import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/useToast';
import { Header, Icon, Container, Message, Button, Grid, Form, Dropdown, Segment, Label, Modal, Table, Pagination, Loader, Dimmer, Accordion, Progress, Popup, Checkbox, Input, Tab, TextArea } from 'semantic-ui-react';
import GlassPageWrapper from '../../components/Layout/GlassPageWrapper';
import GlassTable from '../../components/Common/GlassTable';
import { ContractFormModal, CONTRACT_FORM_TABS } from '../../components/contracts/ContractFormModal';
import { ContractColetaPainel } from '../../components/contracts/ContractColetaPainel';
import { ColetaAprovacaoAlerta } from '../../components/contracts/ColetaAprovacaoAlerta';
import { devePerguntarColeta } from '../../components/contracts/coletaFluxoTexto';
import { consultarFluxo } from '../../services/coletaApi';
import { ColetaFluxo } from '../../types/coleta';
import { getDefaultButtonStyle, getButtonHoverEffects } from '../../styles/designSystem';
import { CreateContractRequestDto, ContractResponseDto, ContractPagedResponseDto, ContractListPagedResponseDto, ContractListDto, ContractFiltersDto, EContractStatus, PendingSellerContractDto, ContractLineDto, SendContractToDocusignRequest, LatestDocusignSendSummaryDto, DocusignSendProgressUpdateDto, ClientObservationEntryDto, PatchContractResultDto, UpdateContractRequestDto, ContractCorrectionCycleStateDto, ContractCorrectionActionRequest } from '../../services/contracts/ContractService';
import { TrackingInfoDto } from '../../types/tracking';
import { clientService } from '../../services/clients/ClientService';
import { mapMeuTimHealthCheckResponse, mapMeuTimVerifyOnDemandError } from '../../utils/meuTimVerifyOnDemand';
import { planService } from '../../services/plans/PlanService';
import { contractService, fetchSuggestedSaleToken } from '../../services/contracts/ContractService';
import { icCidService } from '../../services/icCids/ICcIdService';
import { FASTCHIP_ICCID_POOL_PAGE_SIZE } from '../../hooks/useICcIds';
import {
  cqvRequestService,
  CqvRequestResponseDto,
  ECqvRequestStatus as ECqvRequestSlaStatus,
  ECqvRequestMode,
  CQV_REQUEST_MODE_LABELS,
} from '../../services/contracts/CqvRequestService';
import { cqvRequestReachedGroup } from '../../services/contracts/cqvRequestDelivery';
import { userService } from '../../services/users/UserService';
import contractLineService from '../../services/contracts/ContractLineService';
import { useSessionUser } from '../../hooks/useSessionUser';
import { ClientResponse, ClientListResponse, ClientPartnerResponse, PlanResponse, ICcIdResponse } from '../../types/dtos';
import { normalizeCep, formatCep, formatCnpj, cleanCpf, formatCpfProgressive, isValidCpfLength, cleanPhone, extractDddFromPhone, formatPhoneProgressive, isValidPhoneFormat, isValidDdd, cleanCcid, isValidCcidLength, normalizeRepresentativePhoneInput, mergeDddAndLocalPhoneDigits, renegociacaoLocalDigitsFromFormField, toBrazilNationalPhoneDigits, parseRenegociacaoNumeroFieldInput } from '../../services/utils/stringUtils';
import { extractApiErrorMessage, isAbortError, isCanceledError } from '../../services/utils/apiErrorUtils';
import {
  type AdminInlineFieldErrors,
  type DeliveryAddressInlineFieldErrors,
  type RepInlineFieldErrors,
  type ReceiverInlineFieldErrors,
  hasInlineFieldErrors,
  validateAdminInlineEdit,
  validateDeliveryAddressInlineEdit,
  validateRepInlineEdit,
  validateReceiverInlineEdit,
} from '../../utils/linesModalInlineEditValidation';
import {
  coerceDeliveryAddressStreetNumber,
  DELIVERY_ADDRESS_STREET_NUMBER_EMPTY_ERROR,
  isFilledDeliveryAddressStreetNumber,
  isValidDeliveryAddressStreetNumber,
  normalizeDeliveryAddressStreetNumberForApi,
} from '../../utils/deliveryAddressStreetNumber';
import {
  DELIVERY_ADDRESS_STREET_NAME_EMPTY_ERROR,
  isValidDeliveryAddressStreetName,
  normalizeDeliveryAddressStreetNameForApi,
  normalizeDeliveryAddressStreetNameForInput,
} from '../../utils/deliveryAddressStreetName';
import {
  getLineModalRepresentativeEditKey,
  mergeLineModalRepresentatives,
} from '../../utils/lineModalRepresentativesUtils';
import { runInlineRepresentativeSave } from '../../utils/linesModalRepresentativeInlineSave';
import {
  EMPTY_WHATSAPP_CQV_PARTS,
  LINES_MODAL_CQV_BLOCKED_PLACEHOLDER,
  pickGroupModalAnchorContract,
  resolveExpectedClientIdentity,
  shouldGenerateCqvScripts,
  validateGroupModalClientConsistency,
} from '../../utils/linesModalCqvClientGuard';
import LinesList from '../../components/contracts/LinesList';
import ContractLinesEditor, { type ContractLineEditorItem } from '../../components/contracts/ContractLinesEditor';
import QuantitySelector from '../../components/contracts/QuantitySelector';
import { DuplicateContractModal } from '../../components/contracts/DuplicateContractModal';
import { SeparateContractModal } from '../../components/contracts/SeparateContractModal';
import { FastchipAddressModal } from '../../components/contracts/FastchipAddressModal';
import { FastchipSwapModal } from '../../components/contracts/FastchipSwapModal';
import TrackingModal from '../../components/contracts/TrackingModal';
import EditContractWarningModal from '../../components/contracts/EditContractWarningModal';
import ContractFilters from '../../components/contracts/ContractFilters';
import ContractsFastchipStockSummary from './components/ContractsFastchipStockSummary';
import ClientObservationsPanel from '../../components/clients/ClientObservationsPanel';
import ClientObservationsSummary from '../../components/clients/ClientObservationsSummary';
import { useFastchipStockBalance } from './hooks/useFastchipStockBalance';
import { ClientDraftsModal } from '../../components/contracts/ClientDraftsModal';
import type { DraftSummaryDto } from '../../services/contracts/ContractService';
import { useFastchipAllocationFlag } from './hooks/useFastchipAllocationFlag';
import { ContractTable } from '../../components/contracts/ContractTable';
import ContractRollbackPreviewModal from '../../components/contracts/ContractRollbackPreviewModal';
import { SendContractEmailModal } from '../../components/contracts/SendContractEmailModal';
import { RejectContractModal, type ContractRejectionModalKind } from '../../components/contracts/RejectContractModal';
import StatusHistoryPopover from '../../components/contracts/StatusHistoryPopover';
import DocusignSendSummaryBlock from '../../components/contracts/DocusignSendSummaryBlock';
import { DocusignPhaseTimeline } from '../../components/contracts/DocusignPhaseTimeline';
import {
  buildTimelineFromPollResponse,
  isDocusignPriorityWaitPhase,
  type DocusignTimelineProgress,
} from '../../utils/docusignProgressTimelineUtils';
import EditHistoryModal from '../../components/contracts/EditHistoryModal';
import ContractLineAuditHistoryModal from '../../components/contracts/ContractLineAuditHistoryModal';
import { CancelSuspensionModal } from '../../components/Suspensions/cancel-suspension-modal';
import { ContractFormTestemunhasTab } from '../../components/contracts/ContractFormTestemunhasTab';
import { ContractFormAdministradorTab } from '../../components/contracts/ContractFormAdministradorTab';
import { ContractFormEnderecoTab } from '../../components/contracts/ContractFormEnderecoTab';
import { ContractFormRepresentativesTab } from '../../components/contracts/ContractFormRepresentativesTab';
import { ContractFormRecebedoresTab } from '../../components/contracts/ContractFormRecebedoresTab';
import { ContractFormContratoDadosTab } from '../../components/contracts/ContractFormContratoDadosTab';
import { ContractFormLinhasDesativarTab } from '../../components/contracts/ContractFormLinhasDesativarTab';
import { ContractClientCancellationLinesPanel } from '../../components/contracts/ContractClientCancellationLinesPanel';
import { SuspensionBlockBanner } from '../../components/clientCancellationLines/SuspensionBlockBanner';
import { useSuspensionBlockStatusQuery } from '../../hooks/clientCancellationLines/useClientCancellationLines';
import {
  deveExibirBannerPoliticaSuspensao,
  formularioBloqueiaFluxoSuspensaoLinhas,
  mensagemSuspensaoBloqueada1056,
  politicaBloqueiaSuspensaoEmNovoContrato,
} from '../../utils/clientCancellationLines/clientCancellationSuspensionPolicy';
import { ContractFormLinhasTab } from '../../components/contracts/ContractFormLinhasTab';
import { ContractFormClienteTab } from '../../components/contracts/ContractFormClienteTab';
import { useClientCnpjSearch } from '../../hooks/contracts/useClientCnpjSearch';
import { ContractFormDadosTimTab } from '../../components/contracts/ContractFormDadosTimTab';
import { fetchTimClientData, type TimClientData } from '../../services/timData/timDataService';
import { ResponsibleConsultantSelect } from '../../components/contracts/ResponsibleConsultantSelect';
import {
  carteiraAutomacaoPollIntervalMs,
  carteiraAutomacaoPollUseFresh,
  CARTEIRA_AUTOMACAO_POLL_MAX_ATTEMPTS_SAFETY,
  CARTEIRA_AUTOMACAO_POLL_TIMEOUT_MS,
  getContractStatusText as getStatusText,
  getContractStatusSemanticColor,
  getQualityCcvFlag,
  aggregateGroupContractStatus,
  normalizeContractStatus,
  canMarkFastchipEnviadoForStatus,
  isContractStatusSwapAllowedForContractsPage,
  isTrocaDeCarteiraFlowStatus,
  isQualityCcvPipelineStatus,
  shouldShowLinesModalCqvButtons,
  shouldShowSolicitarCqvButton,
  shouldShowLinesModalVtmeCriadoButton,
  shouldShowLinesModalQualidadeButtons,
  shouldShowLinesModalCcvButtons,
  isCarteiraListaStatusChangeBlocked,
  canEditLinesModalContractFields,
  canEditLinesModalClusterToken,
  canEditResponsibleConsultantAtStatus,
  canSellerTeamLeaderEditLinesModalPartialFields,
  contractStatusHistoryIndicatesTrocaSolicitadaAfterAprovadoCqv,
  isAprovadoCqvOrBeyondPipelineStatus,
} from '../../utils/contractStatusUtils';
import { isQualityClassifier } from '../../utils/qualityCcvAuth';
import {
  buildResponsibleConsultantDropdownOptions,
  resolveConsultantDisplayLabel,
  type ResponsibleConsultantDropdownOption,
} from '../../utils/responsibleConsultantOptions';
import {
  applyDocusignSendResultToSummary,
  buildOptimisticDocusignProcessingSummary,
  EDocusignSendHistoryStatus,
  mapHistoryItemToLatestSummary,
  normalizeDocusignSendStatus,
  resolveLatestDocusignSendFromContract,
  analyzeDocusignSendScenario,
  getDocusignSendTargets,
  getDocusignSendTargetsForResend,
  getPerContractDocusignSendState,
  getPerContractDocusignSendStateLabel,
} from '../../utils/docusignSendHistoryUtils';
import { getContractTypeLabel, sortContractsForVtmeGroupOrder } from '../../utils/contractGroupVtmeOrder';
import { isDocusignUiEnabled } from '../../config/docusignUi';
import {
  checkRestoreWindowForContracts,
  RESTORE_CANCELLED_WINDOW_DAYS,
  type RestoreWindowCheckItem,
} from '../../utils/contractRestoreUtils';
import {
  buildCarteiraVerificacaoTimeoutEmAndamentoToast,
  buildChangeReasonPosErroVerificacao,
  CARTEIRA_ATC_ERRO_CONEXAO_MSG,
  CARTEIRA_AUTOMACAO_UPDATED_BY,
  carteiraVerificacaoFalhouOuExpirou,
  CHANGE_REASON_VTME_AINDA_PROCESSANDO,
  isCarteiraVerificacaoTimeoutComJobEmAndamento,
  resolveCarteiraVerificacaoErroMensagem,
  resolveStatusAlvoPosErroVerificacao,
} from '../../utils/trocaCarteiraVerificacao';
import type { ContractLineFormItem } from '../../types/contractForm';
import { collectLinhasParaDesativarIds } from '../../types/contractForm';
import {
  allowsDhDeliverySuspensionWithoutFastchip,
  allowsSuspension,
} from '../../utils/contractSuspensionEligibility';
import {
  canAdminMutateContractLinesOnEdit,
  shouldSkipFastchipConversionModal,
  shouldSkipSuspensionSaveBlock,
} from '../../utils/adminCorrectionSuspensionGuards';
import {
  assignSourceContractIdsToLines,
  buildContractUpdatePayload,
  buildContractUpdatePayloadForGroup,
  buildLinesOnlyPatchPayload,
  buildLinesOnlyPatchPayloadForGroup,
  contractMatchesNegociacao,
  findActiveGroupContractForNegociacao,
  getContractsThatWillBeEmpty,
  getFirstContractLineFieldValidationError,
  clearContractLinePlanSelection,
  getRequiredNegociacoesFromLines,
  groupNeedsEnsureForLines,
  sortGroupContractsForLinePatch,
  type ContractEmptyOnSaveItem,
  type ContractUpdateFormSnapshot,
} from '../../services/contracts/buildContractUpdatePayload';
import { useSimulationToContract } from '../../context/SimulationToContractContext';
import { useContractFilters } from '../../hooks/contracts/useContractFilters';
import { contractsQueryKeys, CONTRACT_MODAL_CACHE_STALE_TIME_MS } from '../../hooks/contracts/contractsQueryKeys';
import { useContractForm } from '../../hooks/contracts/useContractForm';
import { useContractActions } from '../../hooks/contracts/useContractActions';
import teamService, { TeamContractRules, defaultContractRules } from '../../services/teams/teamService';
import { useTeamsQuery } from '../../hooks/teams/useTeams';
import { TEAMS_STALE_TIME_MS, teamsQueryKeys } from '../../hooks/teams/teamsQueryKeys';
import { podeAcionarColetaManual } from '../../components/contracts/coletaBotoesVisibilidade';
import { loadTeamContractRules, usuarioEhDoTimeCqv } from '../../utils/teamContractRules';
import { canPersistContractDraft, createSerializedDraftPersister } from '../../utils/contractDraftPersistence';
import {
  buildFastchipIccidErrorToast,
  hasActiveFastchipAditivoLines,
  parseFastchipStockApiError,
} from '../../utils/fastchipIccidSaveValidation';
import { ConfirmEmptyContractCancellationModal } from '../../components/contracts/ConfirmEmptyContractCancellationModal';
import { IeBypassConfirmModal } from '../../components/contracts/IeBypassConfirmModal';
import { ClusterConfirmModal } from '../../components/contracts/ClusterConfirmModal';
import { ConvertToEntregaModal } from '../../components/contracts/ConvertToEntregaModal';
import {
  ContractClusterTokensEditModal,
  type ContractTokenTarget,
} from '../../components/contracts/ContractClusterTokensEditModal';
import { evaluateIePolicy, isMeiOuIndividual } from '../../utils/iePolicy';
import {
  FASTCHIP_CLUSTER_CP,
  FASTCHIP_CLUSTER_HB,
  formatRjMgEsFastchipClusterUiLabel,
  iccidHasCluster,
  iccidHasDhCluster,
  isHbCpMarketEstado,
  isPrMhClusterCity,
  resolveRjMgEsFastchipIccidSearch,
} from '../../utils/fastchipClusterRules';
import {
  buildEsimUnavailableMessage,
  isEsimStockSegmentAvailable,
  resolveEsimStockSegment,
} from '../../utils/fastchipEsimStock';
import {
  collectUsedIccids,
  inferDddForSuspensionAutoAditivo,
  pickRandomAvailableIccid,
} from '../../utils/suspensionAutoAditivoLines';
import PortabilidadeDadosForm from '../../components/contracts/PortabilidadeDadosForm';
import PortabilidadeConflictModal from '../../components/contracts/PortabilidadeConflictModal';
import {
  applyCedenteUnification,
  applyPortabilidadeTipoConversion,
  applyPortanteUnification,
  copyPortabilidadeFieldsFromLine,
  detectPortabilidadeConflict,
  getPortabilidadeValidationError,
  isPortabilidadeAditivoLine,
  isPortabilidadeIncompleta,
  validateAllPortabilidadeLines,
  type PortabilidadeConflict,
  type PortabilidadeRepresentanteFields,
} from '../../utils/portabilidadeContractRules';
import {
  normalizeContractSaleTokenForForm,
  hydrateSaleTokenFromContract,
  mapNonFastchipOperationalToSeniorCanonical,
  inferPrSeniorClusterTokenFromAditivoDdd,
  resolveFastchipClusterTokenForAutoFill,
  resolveNonFastchipClusterTokenForAutoFill,
  resolveFastchipClusterSuggestionSource,
  resolveNonFastchipClusterSuggestionSource,
  resolveSuggestedClusterBanner,
  formatContractTokenDisplayLabel,
  resolveHbCpClusterFromContractToken,
} from '../../utils/contractClusterTokens';
import {
  buildTabProgressSnapshotFromContract,
  resolveFirstIncompleteContractFormTab,
} from '../../utils/contractFormTabProgress';
import {
  contractHasEsimLines,
  resolveFastchipDeliveryBadge,
  resolveFastchipDeliveryDetailLabel,
} from '../../utils/fastchipDeliveryLabel';
import {
  buildCqvScriptContext,
  formatCallScript as formatCqvCallScript,
  formatWhatsappCqvPart1,
  isActiveCqvLine,
} from '../../utils/cqvRoteiroScript';
import { isWttxLine, WTTX_APARELHO_VALUE, lineHasAnyHandset, lineHasCatalogHandset } from '../../utils/handsetLine';
import { formatLineAparelhoLabel } from '../../utils/handsetSummary';
import {
  filterAditivoEligiblePlans,
  filterPlansForContractLine,
  formatContractPlanOptionLabel,
  isWttxPlan,
  resolvePlanTotalMonthlyPrice,
} from '../../utils/contractPlanCompatibility';
import { ContractLineHandsetFields } from '../../components/contracts/ContractLineHandsetFields';
import {
  handsetDeviceService,
  type HandsetDeviceDto,
} from '../../services/handsetDevices/handsetDeviceService';
import {
  getClientClassificationMeta,
  readClientClassification,
  readClientInternalNotes,
  readContractInternalNotes,
} from '../../utils/clientClassification';

/** Modal manual Unity pós troca — desabilitado (WhatsApp via automação/Central). */
const UNITY_TROCA_CARTEIRA_MESSAGE_MODAL_ENABLED = false;

// Constante para plano RP 6 GB usado em linhas manuais de suspensão
const RP_6GB_PLAN_ID = '550e8400-e29b-41d4-a716-446655440017';
const RP_6GB_PLAN_NAME = 'RP 6 GB';

/** Mensagens 2 e 3 do roteiro CQV WhatsApp (texto fixo). */
const WHATSAPP_CQV_MSG2_ASSINATURA =
  'A ASSINATURA DO CONTRATO FOI FÍSICA OU O ACEITE DIGITAL NO WHATSAPP?';
const WHATSAPP_CQV_MSG3_ENCERRAMENTO =
  'TEM MAIS ALGUMA DÚVIDA?!\nOBRIGADA E UM ÓTIMO DIA!';

const mapContractResponseToListDto = (c: ContractResponseDto): ContractListDto => {
  if (c.listItem) {
    return c.listItem;
  }

  const linhasValidas = c.linhas?.filter((l) => !l.excluirDoContrato) ?? [];
  const totalLinhas =
    linhasValidas.length > 0
      ? linhasValidas.reduce((sum, l) => sum + (l.quantidade || 1), 0)
      : 0;

  return {
    id: c.id,
    clientRazaoSocial: c.client?.razaoSocial ?? '',
    clientCnpj: c.clientCnpj || c.client?.cnpj || '',
    clientNomeFantasia: c.client?.nomeFantasia ?? '',
    clientClassification: readClientClassification(c.client),
    clientInternalNotes: readClientInternalNotes(c.client) || undefined,
    clientEstado: c.client?.estado,
    tipoContratacao: c.tipoContratacao,
    status: c.status ?? EContractStatus.Criado,
    dataAssinatura: c.dataAssinatura ? String(c.dataAssinatura) : '',
    dataVenda: c.dataVenda ? String(c.dataVenda) : '',
    valorTotalMensal: c.valorTotalMensal ?? 0,
    administratorNome: c.administrator?.nomeCompleto ?? '',
    administratorCpf: c.administrator?.cpf ?? '',
    administratorEmail: c.administrator?.email ?? '',
    totalLinhas,
    createdAt: c.createdAt ? String(c.createdAt) : '',
    updatedAt: c.updatedAt ? String(c.updatedAt) : '',
    porteCliente: '',
    groupId: c.groupId,
    contractType: c.contractType,
    fastchip: c.fastchip,
    token: (c.token ?? '').trim() || undefined,
    internalNotes: readContractInternalNotes(c) || undefined,
  };
};

// Função para calcular aditivos necessários baseado em linhas suspensas
// Regra: 1 aditivo base já cobre as primeiras 2 suspensões
// A partir da 3ª suspensão, cada 2 suspensões = 1 aditivo adicional
const calcularAditivosNecessarios = (linhasSuspensas: number): number => {
  return Math.max(1, Math.ceil(linhasSuspensas / 2));
};

/** Une listas de contratos pelo id (API + linha da tabela/modal) para não perder membro do grupo na CQV/e-mail. */
const mergeContractArraysById = (a: any[], b: any[]): any[] => {
  const m = new Map<string, any>();
  for (const c of [...a, ...b]) {
    const id = c?.id;
    if (!id) continue;
    m.set(id, m.has(id) ? { ...m.get(id), ...c } : c);
  }
  return Array.from(m.values());
};

/** Rótulo para o modal "Ver dados CQV" em grupos (Aditivo / Renegociação). */
const labelContractForCqvChoice = (
  c: Pick<ContractResponseDto, 'tipoContratacao' | 'contractType'>,
  fallbackIndex: number
): string => {
  const raw = `${c.tipoContratacao ?? ''} ${c.contractType ?? ''}`.trim();
  const low = raw.toLowerCase();
  if (low.includes('aditivo')) return 'Aditivo';
  if (low.includes('reneg')) return 'Renegociação';
  return raw !== '' ? raw : `Contrato ${fallbackIndex + 1}`;
};

/** Tipo de contrato a partir de uma linha do modal (contractType injectado no grupo ou negociacao). */
const contractPickForCqvFromLine = (line: any): Pick<ContractResponseDto, 'tipoContratacao' | 'contractType'> => {
  const ct = line?.contractType ?? line?.tipoContratacao;
  if (ct != null && String(ct).trim() !== '') {
    const s = String(ct);
    return { tipoContratacao: s, contractType: line?.contractType ?? s };
  }
  const neg = String(line?.negociacao ?? '').toLowerCase();
  if (neg.includes('reneg')) return { tipoContratacao: 'Renegociação', contractType: 'Renegociação' };
  if (neg.includes('adit')) return { tipoContratacao: 'Aditivo', contractType: 'Aditivo' };
  return { tipoContratacao: '', contractType: null };
};

// ─── Boas-Vindas: helpers ───────────────────────────────────────────────────

function buildWelcomeScript(
  leaderName: string,
  sellerName: string,
  monthYear: string,
  clientRazaoSocial: string,
  clientCnpj: string,
  contractAction: string,
  actionNoun: string
): string {
  return `Olá!

Queremos agradecer pela confiança em ${contractAction} o seu plano TIM Empresarial conosco. É um prazer continuar atendendo você através da Gettel Telecom!

Meu nome é ${leaderName}, sou supervisor(a) do(a) ${sellerName} que fez sua ${actionNoun} em ${monthYear}, da empresa:
Cliente: ${clientRazaoSocial}
CNPJ: ${clientCnpj}
E estou à sua disposição para qualquer dúvida, suporte ou necessidade que surgir ao longo do seu contrato.

Você pode falar diretamente comigo por esse número.
Conte conosco sempre! 🤝

Atenciosamente,
${leaderName}.`;
}

function formatMonthYear(dateStr: string): string {
  return new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' })
    .format(new Date(dateStr))
    .replace(/^./, (c) => c.toUpperCase());
}

function buildWhatsAppUrl(phone: string, message: string): string {
  const clean = phone.replace(/\D/g, '');
  return `https://wa.me/55${clean}?text=${encodeURIComponent(message)}`;
}

/** HH:mm em America/Sao_Paulo — usado no texto "Em correção por ... desde HH:mm" (T08). */
function formatCorrectionCycleStartedAt(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

// ────────────────────────────────────────────────────────────────────────────

const ContractsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { getUserId, isAuthenticated, getUserInfo } = useSessionUser();
  const { showSuccess, showError, showWarning, showInfo } = useToast();
  const { hasRole, hasAnyRole, user } = useAuth();
  const isAdminColeta = hasRole('Admin');
  const { data: timesColeta } = useTeamsQuery({ enabled: !isAdminColeta && isAuthenticated });
  const podeAcionarColeta = podeAcionarColetaManual(
    isAdminColeta,
    usuarioEhDoTimeCqv(timesColeta, user?.id),
  );
  /** Módulo DocuSign desativado (2026-08-02): CTAs/envio ocultos salvo REACT_APP_DOCUSIGN_ENABLED=true */
  const docusignUiEnabled = isDocusignUiEnabled();
  const { simulationData, clearSimulationData } = useSimulationToContract();
  const contractFilters = useContractFilters();
  const {
    contracts,
    contractsLoading,
    contractsError,
    retryLoadContracts,
    currentPage,
    totalPages,
    pageSize,
    filters,
    setFilters,
    clientSearchFilter,
    setClientSearchFilter,
    tipoContratacaoFilter,
    setTipoContratacaoFilter,
    valorMinimoFilter,
    setValorMinimoFilter,
    valorMaximoFilter,
    setValorMaximoFilter,
    dataInicioFilter,
    setDataInicioFilter,
    dataFimFilter,
    setDataFimFilter,
    contractIdFilter,
    setContractIdFilter,
    iccidFilter,
    setIccidFilter,
    statusFilter,
    setStatusFilter,
    temTrocaLinhaTriFilter,
    setTemTrocaLinhaTriFilter,
    temPendenciaSuspensaoTriFilter,
    setTemPendenciaSuspensaoTriFilter,
    temAparelhoCatalogoTriFilter,
    setTemAparelhoCatalogoTriFilter,
    effectiveClustersFilter,
    setEffectiveClustersFilter,
    userNameFilter,
    setUserNameFilter,
    enderecoCidadeFilter,
    setEnderecoCidadeFilter,
    enderecoEstadoFilter,
    setEnderecoEstadoFilter,
    administratorNomeFilter,
    setAdministratorNomeFilter,
    searchTermFilter,
    setSearchTermFilter,
    showAdvancedFilters,
    setShowAdvancedFilters,
    loadContracts,
    patchContractsStatus,
    patchContractLatestDocusignSend,
    patchContractStatuses,
    patchContractListItem,
    applyFilters,
    clearFilters,
    handlePageChange,
  } = contractFilters;
  const invalidateListAndModalQueries = useCallback(
    (contractId?: string, groupId?: string | null) => {
      void queryClient.invalidateQueries({ queryKey: contractsQueryKeys.lists() });
      if (contractId) {
        void queryClient.invalidateQueries({
          queryKey: contractsQueryKeys.lineDetailsByContract(contractId),
        });
      }
      if (groupId) {
        void queryClient.invalidateQueries({
          queryKey: contractsQueryKeys.lineDetailsByGroup(groupId),
        });
      }
    },
    [queryClient]
  );
  const {
    balance: fastchipStockBalance,
    loading: fastchipStockLoading,
    reload: reloadFastchipStockBalance,
  } = useFastchipStockBalance(isAuthenticated);

  /** Flag do backend: alocação atômica de ICCID habilitada (SKIP LOCKED). */
  const backendAllocationEnabled = useFastchipAllocationFlag(isAuthenticated);

  /** Cancela poll/toasts de verificação de carteira ao sair da página ou iniciar outro fluxo. */
  const carteiraPollRunIdRef = useRef(0);
  const carteiraPollAbortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    return () => {
      carteiraPollRunIdRef.current += 1;
      carteiraPollAbortRef.current?.abort();
    };
  }, []);
  const beginCarteiraPollRun = useCallback(() => {
    carteiraPollAbortRef.current?.abort();
    carteiraPollRunIdRef.current += 1;
    carteiraPollAbortRef.current = new AbortController();
    return {
      runId: carteiraPollRunIdRef.current,
      signal: carteiraPollAbortRef.current.signal,
    };
  }, []);
  const isCarteiraPollRunActive = useCallback(
    (runId: number) => carteiraPollRunIdRef.current === runId,
    []
  );

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('cliente');
  // Dados TIM (consulta por CNPJ) — tab extra "Dados TIM" só aparece quando found=true.
  const [timClientData, setTimClientData] = useState<TimClientData | null>(null);
  const timDataTabVisible = !!timClientData;
  const [selectedContractsForRestore, setSelectedContractsForRestore] = useState<Set<string>>(new Set());
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreModalContracts, setRestoreModalContracts] = useState<ContractListDto[]>([]);
  const [showRestoreWindowAlert, setShowRestoreWindowAlert] = useState(false);
  const [restoreWindowExpiredItems, setRestoreWindowExpiredItems] = useState<RestoreWindowCheckItem[]>([]);
  const [restoreWindowEligibleContracts, setRestoreWindowEligibleContracts] = useState<ContractListDto[]>([]);
  const [restoreWindowChecking, setRestoreWindowChecking] = useState(false);

  /** Conteúdo rolável do modal de contrato é `.scrolling.content` (Semantic UI); ao trocar de aba, voltar ao topo. */
  useLayoutEffect(() => {
    if (!isCreateModalOpen) return;
    const anchor = document.getElementById('gettel-contract-form-modal-body');
    const scrollEl = anchor?.closest('.scrolling.content');
    if (scrollEl instanceof HTMLElement) {
      scrollEl.scrollTop = 0;
    }
  }, [activeTab, isCreateModalOpen]);
  const [contractData, setContractData] = useState<Partial<CreateContractRequestDto>>({});
  
  // Estados para duplicidade
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [emptyContractConfirmOpen, setEmptyContractConfirmOpen] = useState(false);
  const [emptyContractsOnSave, setEmptyContractsOnSave] = useState<ContractEmptyOnSaveItem[]>([]);
  const pendingContractSaveRef = useRef<(() => Promise<void>) | null>(null);
  const skipPortabilidadeConflictCheckRef = useRef(false);
  const addLinhaButtonRef = useRef<Button>(null);
  const [duplicateContract, setDuplicateContract] = useState<ContractResponseDto | null>(null);
  const [showExistingContractModal, setShowExistingContractModal] = useState(false);
  const [existingContractCnpj, setExistingContractCnpj] = useState<string | null>(null);
  
  // Estados para separação
  const [showSeparateModal, setShowSeparateModal] = useState(false);
  const [shouldSeparateContracts, setShouldSeparateContracts] = useState<boolean | undefined>(undefined);
  
  // Estados para modal de confirmação Fastchip
  const [showFastchipAddressModal, setShowFastchipAddressModal] = useState(false);
  const [hasSeenFastchipAddressModal, setHasSeenFastchipAddressModal] = useState(false);
  
  // Estados para visualização agrupada
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [groupedContracts, setGroupedContracts] = useState<Map<string, any[]>>(new Map());
  
  // Estados para modal de linhas
  const [showLinesModal, setShowLinesModal] = useState(false);
  const [linesModalLoading, setLinesModalLoading] = useState(false);
  const [linesDetailCanEditLines, setLinesDetailCanEditLines] = useState(false);
  const [openingLinesOnlyEdit, setOpeningLinesOnlyEdit] = useState(false);
  // Quando o carregamento do modal de detalhes demora (cache miss no Redis → monta na base),
  // mostramos a mensagem "buscando informação na base de dados".
  const [linesModalSlowLoad, setLinesModalSlowLoad] = useState(false);
  const [linesModalError, setLinesModalError] = useState<string | null>(null);
  const [selectedContractLines, setSelectedContractLines] = useState<any[]>([]);
  const [selectedContractInfo, setSelectedContractInfo] = useState<any>(null);
  /** Espelha `selectedContractInfo?.id` para leitura "ao vivo" dentro de callbacks assíncronos
   * (evita ler o valor congelado na closure de uma função criada em um render anterior). */
  const selectedContractInfoIdRef = useRef<string | null>(null);
  useEffect(() => {
    selectedContractInfoIdRef.current = selectedContractInfo?.id ?? null;
  }, [selectedContractInfo]);
  const [linesModalGroupContracts, setLinesModalGroupContracts] = useState<any[]>([]);
  const [linesModalTrocaSolicitadaAfterAprovadoCqv, setLinesModalTrocaSolicitadaAfterAprovadoCqv] =
    useState(false);
  /** Estado do ciclo de correção manual (SLA 2) — botões "Comecei a corrigir"/"Terminei a correção". */
  const [correctionCycle, setCorrectionCycle] = useState<ContractCorrectionCycleStateDto | null>(null);
  const [correctionCycleLoading, setCorrectionCycleLoading] = useState(false);
  /** Descarta respostas fora de ordem (contrato trocado antes da resposta anterior chegar). */
  const correctionCycleRequestSeqRef = useRef(0);
  /** Formulário inline de motivo opcional (T08) — null quando fechado. */
  const [correctionActionKind, setCorrectionActionKind] = useState<'start' | 'end' | null>(null);
  const [correctionActionReason, setCorrectionActionReason] = useState('');
  const [correctionActionSubmitting, setCorrectionActionSubmitting] = useState(false);
  const [showClusterTokensEditModal, setShowClusterTokensEditModal] = useState(false);
  const [clusterTokensSaving, setClusterTokensSaving] = useState(false);
  const [cancelSuspensionModalOpen, setCancelSuspensionModalOpen] = useState(false);
  const [cancelSuspensionLines, setCancelSuspensionLines] = useState<any[]>([]);
  const [cancelSuspensionContractInfo, setCancelSuspensionContractInfo] = useState<any>(null);
  const [cancelSuspensionLoading, setCancelSuspensionLoading] = useState(false);
  const [highlightedContractId, setHighlightedContractId] = useState<string | null>(null);
  /** IDs do modal "Ver dados CQV" (grupo); declarado cedo para não ficar abaixo de handlers que o usam. */
  const cqvOpenChoiceIdsRef = useRef<string[]>([]);
  const [lineDetailsClientObservations, setLineDetailsClientObservations] = useState<ClientObservationEntryDto[]>([]);
  const [lineDetailsObservationsLoading, setLineDetailsObservationsLoading] = useState(false);
  const [selectedClientObservations, setSelectedClientObservations] = useState<ClientObservationEntryDto[]>([]);
  const [selectedClientObservationsLoading, setSelectedClientObservationsLoading] = useState(false);

  // Estados para modal Boas-Vindas
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [welcomeModalTabIndex, setWelcomeModalTabIndex] = useState(0);
  const [welcomeSelectedRepIdx, setWelcomeSelectedRepIdx] = useState(0);

  const lineModalClientId =
    selectedContractInfo?.clientId ?? selectedContractInfo?.client?.id ?? null;

  const resolveMutationGroupId = useCallback(
    (contractId: string, explicitGroupId?: string | null) => {
      if (explicitGroupId) return explicitGroupId;
      const listMatch = contracts?.items?.find((item: ContractListDto) => item.id === contractId);
      if (listMatch?.groupId) return listMatch.groupId;
      if (selectedContractInfo?.id === contractId) return selectedContractInfo?.groupId ?? null;
      if (selectedContractInfo?.groupContracts?.some((item: any) => item?.id === contractId)) {
        return selectedContractInfo?.groupId ?? null;
      }
      return null;
    },
    [contracts?.items, selectedContractInfo]
  );

  const invalidateAfterContractMutation = useCallback(
    (contractId: string, explicitGroupId?: string | null) => {
      invalidateListAndModalQueries(contractId, resolveMutationGroupId(contractId, explicitGroupId));
    },
    [invalidateListAndModalQueries, resolveMutationGroupId]
  );

  const patchContractWithCacheInvalidation = useCallback(
    async (contractId: string, data: any, explicitGroupId?: string | null) => {
      await contractService.patchContract(contractId, data);
      invalidateAfterContractMutation(contractId, explicitGroupId);
    },
    [invalidateAfterContractMutation]
  );

  const updateContractStatusWithCacheInvalidation = useCallback(
    async (
      contractId: string,
      status: EContractStatus,
      options?: {
        changeReason?: string;
        updatedBy?: string;
        suppressTrocaCarteiraAutomation?: boolean;
      },
      explicitGroupId?: string | null
    ) => {
      const result = await contractService.updateContractStatus(contractId, status, options);
      invalidateAfterContractMutation(contractId, explicitGroupId);
      return result;
    },
    [invalidateAfterContractMutation]
  );

  const updateLineIccidWithCacheInvalidation = useCallback(
    async (contractId: string, lineId: string, newIccid: string | null) => {
      await contractService.updateLineIccid(contractId, lineId, newIccid);
      invalidateAfterContractMutation(contractId);
    },
    [invalidateAfterContractMutation]
  );

  const updateLineIsAptoWithCacheInvalidation = useCallback(
    async (contractId: string, lineId: string, isApto: boolean) => {
      await contractService.updateLineIsApto(contractId, lineId, isApto);
      invalidateAfterContractMutation(contractId);
    },
    [invalidateAfterContractMutation]
  );

  const confirmarVtmeWithCacheInvalidation = useCallback(
    async (
      contractId: string,
      payload: {
        idPedidoContrato1?: string | null;
        idPedidoContrato2?: string | null;
        linhasAptas: { linhaId: string; apto: boolean }[];
      },
      explicitGroupId?: string | null
    ) => {
      await contractService.confirmarVtme(contractId, payload);
      invalidateAfterContractMutation(contractId, explicitGroupId);
    },
    [invalidateAfterContractMutation]
  );

  const updatePedidoIdsWithCacheInvalidation = useCallback(
    async (
      contractId: string,
      payload: { idPedidoContrato1?: string | null; idPedidoContrato2?: string | null },
      explicitGroupId?: string | null
    ) => {
      await contractService.updatePedidoIds(contractId, payload);
      invalidateAfterContractMutation(contractId, explicitGroupId);
    },
    [invalidateAfterContractMutation]
  );

  const loadLineDetailsClientObservationsForClient = useCallback(async (clientId: string | null | undefined) => {
    if (!clientId) {
      setLineDetailsClientObservations([]);
      return;
    }
    setLineDetailsObservationsLoading(true);
    try {
      const items = await clientService.getClientObservations(clientId);
      setLineDetailsClientObservations(items);
    } catch {
      setLineDetailsClientObservations([]);
    } finally {
      setLineDetailsObservationsLoading(false);
    }
  }, []);

  const reloadLineDetailsClientObservations = useCallback(async () => {
    await loadLineDetailsClientObservationsForClient(lineModalClientId);
  }, [lineModalClientId, loadLineDetailsClientObservationsForClient]);

  useEffect(() => {
    if (!showLinesModal) {
      setLineDetailsClientObservations([]);
      setLineDetailsObservationsLoading(false);
    }
  }, [showLinesModal]);

  // Detalhes das linhas: o backend serve do cache Redis (rápido) ou monta a partir da base (lento).
  // Se o carregamento ultrapassar ~500ms, sinalizamos que está buscando direto na base de dados.
  useEffect(() => {
    if (!linesModalLoading) {
      setLinesModalSlowLoad(false);
      return;
    }
    setLinesModalSlowLoad(false);
    const timer = window.setTimeout(() => setLinesModalSlowLoad(true), 500);
    return () => window.clearTimeout(timer);
  }, [linesModalLoading]);

  // Estados para edição de ICCID
  const [editingIccid, setEditingIccid] = useState<{ lineId: string; value: string; contractId: string } | null>(null);
  const [updatingIccid, setUpdatingIccid] = useState<string | null>(null);

  // Estados para troca de FastChip em lote
  const [selectedLinesForSwap, setSelectedLinesForSwap] = useState<Set<string>>(new Set());
  const [showFastchipSwapModal, setShowFastchipSwapModal] = useState(false);

  // Reenvio de QR Code e-SIM
  const [resendingEsimQrCode, setResendingEsimQrCode] = useState<string | null>(null);

  // S6: confirmacao de conversao automatica para Fastchip ao tentar suspender com fastchip=false.
  // pendingSuspensionLinhaIds = ids que serao marcados pra desativar/suspender depois do "Confirmar".
  const [showConvertToFastchipModal, setShowConvertToFastchipModal] = useState(false);
  const [pendingSuspensionLinhaIds, setPendingSuspensionLinhaIds] = useState<string[]>([]);
  const [pendingManualSuspensionLine, setPendingManualSuspensionLine] = useState<{ ddd: string; numero: string } | null>(
    null
  );
  const [pendingSuspensionCanConvert, setPendingSuspensionCanConvert] = useState(true);
  const [showConvertToEntregaModal, setShowConvertToEntregaModal] = useState(false);
  const [pendingEntregaConversionLines, setPendingEntregaConversionLines] = useState<string[]>([]);
  const [pendingDraftIccidValidation, setPendingDraftIccidValidation] = useState(false);
  const fastchipAutoAdjustSignatureRef = useRef<string>('');

  // Estados para script de ligação CQV + WhatsApp (modal detalhes das linhas)
  const [callScript, setCallScript] = useState('');
  const [cqvScriptTabIndex, setCqvScriptTabIndex] = useState(0);
  /** Três blocos CQV WhatsApp (enviar em sequência). */
  const [whatsappCqvParts, setWhatsappCqvParts] = useState<{
    part1: string;
    part2: string;
    part3: string;
  }>({ ...EMPTY_WHATSAPP_CQV_PARTS });
  /** Quando setado, bloqueia geração do script CQV (inconsistência de cliente/CNPJ). */
  const [linesModalCqvBlockedReason, setLinesModalCqvBlockedReason] = useState<string | null>(
    null
  );

  // Estados para edição inline de admin/representantes no modal de linhas
  const [editingAdminInModal, setEditingAdminInModal] = useState(false);
  const [editingAdminData, setEditingAdminData] = useState({ nomeCompleto: '', cpf: '', telefone1: '' });
  const [editingConsultantInModal, setEditingConsultantInModal] = useState(false);
  const [responsibleConsultantEditInModal, setResponsibleConsultantEditInModal] = useState('');
  const [editingRepInModal, setEditingRepInModal] = useState<string | null>(null);
  const [editingRepData, setEditingRepData] = useState({ nome: '', cpf: '', telefone: '', email: '' });
  const [editingReceiverInModal, setEditingReceiverInModal] = useState<string | null>(null);
  const [editingReceiverData, setEditingReceiverData] = useState({ nome: '', cpf: '', qualificacao: '', rg: '' });
  const [savingInlineEdit, setSavingInlineEdit] = useState(false);
  const [editingDeliveryAddress, setEditingDeliveryAddress] = useState(false);
  const [editingDeliveryAddressData, setEditingDeliveryAddressData] = useState({
    cep: '',
    estado: '',
    cidade: '',
    endereco: '',
    numero: '',
    bairro: '',
    complemento: '',
    referencia: '',
  });
  // Loading do auto-preenchimento via ViaCEP no form inline de edicao do endereco.
  // Separado do cepLoading do form de criacao para nao interferir nos dois fluxos.
  const [editingDeliveryAddressCepLoading, setEditingDeliveryAddressCepLoading] = useState(false);
  const [editingAdminFieldErrors, setEditingAdminFieldErrors] = useState<AdminInlineFieldErrors>({});
  const [editingRepFieldErrors, setEditingRepFieldErrors] = useState<RepInlineFieldErrors>({});
  const [editingReceiverFieldErrors, setEditingReceiverFieldErrors] = useState<ReceiverInlineFieldErrors>({});
  const [editingDeliveryAddressFieldErrors, setEditingDeliveryAddressFieldErrors] =
    useState<DeliveryAddressInlineFieldErrors>({});

  // Estados para loading dos botões CQV
  const [confirmingCQV, setConfirmingCQV] = useState(false);
  const [rejectingCQV, setRejectingCQV] = useState(false);
  /** Solicitação automática de CQV com SLA (specs/solicitacao-cqv-automatica-sla). */
  const [requestingCqv, setRequestingCqv] = useState(false);
  /** Pedido mais recente de CQV do contrato aberto — usado pra oferecer "Reenviar CQV" (T12). */
  const [latestCqvRequest, setLatestCqvRequest] = useState<CqvRequestResponseDto | null>(null);
  /** Modo da CQV (Ligação/WhatsApp) — só supervisor escolhe; vendedor é sempre Ligação. */
  const [cqvRequestMode, setCqvRequestMode] = useState<ECqvRequestMode>(ECqvRequestMode.Ligacao);
  const [showConfirmCQVModal, setShowConfirmCQVModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [alertaColeta, setAlertaColeta] = useState<{ fluxo: ColetaFluxo; resolver: (enviar: boolean) => void } | null>(null);
  const [pendingRejectionContractId, setPendingRejectionContractId] = useState<string | null>(null);
  /** Fluxo da modal de recusa: CQV, Qualidade, CCV, CVP ou troca de carteira (mesma modal). */
  const [pendingRejectionKind, setPendingRejectionKind] = useState<ContractRejectionModalKind>('cqv');

  // Estados para confirmação VTME
  const [confirmingVtme, setConfirmingVtme] = useState(false);
  const [idPedido1, setIdPedido1] = useState<string>('');
  const [idPedido2, setIdPedido2] = useState<string>('');
  const [savingPedidoIds, setSavingPedidoIds] = useState(false);
  const [linhasAptas, setLinhasAptas] = useState<Map<string, boolean>>(new Map());
  const [savingLinhaAptoId, setSavingLinhaAptoId] = useState<string | null>(null);
  const [showVtmeWarningModal, setShowVtmeWarningModal] = useState(false);
  const [vtmeMissingFields, setVtmeMissingFields] = useState<string[]>([]);
  /** Modal de linhas: PUT Troca de Carteira a partir de Aprovado CQV (evita duplo clique). */
  const [trocaDeCarteiraFromModalLoading, setTrocaDeCarteiraFromModalLoading] = useState(false);
  const [reconsultarCarteiraVtmeLoading, setReconsultarCarteiraVtmeLoading] = useState(false);
  /** Confirmação em React (evita `window.confirm` invisível com o modal de linhas aberto). */
  const [showTrocaCarteiraConfirmModal, setShowTrocaCarteiraConfirmModal] = useState(false);
  /** Mensagem WhatsApp (Unity) após Troca de Carteira bem-sucedida no modal de linhas. */
  const [showUnityTrocaCarteiraMessageModal, setShowUnityTrocaCarteiraMessageModal] = useState(false);
  const [unityTrocaCarteiraMessageBody, setUnityTrocaCarteiraMessageBody] = useState('');

  /** Status do modal de linhas normalizado (API pode enviar número ou string de enum). */
  const linesModalStatusNorm = useMemo(
    () => normalizeContractStatus(selectedContractInfo?.status),
    [selectedContractInfo?.status]
  );
  /** Mesmos membros que o header do modal (grupo) — RF16. */
  const linesModalGroupMembers = useMemo(() => {
    if (linesModalGroupContracts.length > 0) return linesModalGroupContracts;
    return selectedContractInfo?.groupContracts ?? [];
  }, [linesModalGroupContracts, selectedContractInfo?.groupContracts]);
  /** Status agregado do grupo, igual ao header (RF16). */
  const linesModalHeaderStatus = useMemo(() => {
    const aggregated =
      linesModalGroupMembers.length > 0
        ? aggregateGroupContractStatus(linesModalGroupMembers)
        : null;
    return aggregated ?? selectedContractInfo?.status;
  }, [linesModalGroupMembers, selectedContractInfo?.status]);
  /** Qualquer membro do grupo em Troca Solicitada (62) dispara fluxo CQV especial. */
  const grupoTemMembroEmTrocaSolicitada = useMemo(() => {
    if (linesModalStatusNorm === EContractStatus.TrocaDeCarteiraSolicitada) return true;
    const members = selectedContractInfo?.groupContracts as
      | Array<{ status?: string | number | null }>
      | undefined;
    if (!members?.length) return false;
    return members.some(
      (c) => normalizeContractStatus(c?.status) === EContractStatus.TrocaDeCarteiraSolicitada
    );
  }, [linesModalStatusNorm, selectedContractInfo?.groupContracts]);
  const linesModalCanEditFields = useMemo(
    () => canEditLinesModalContractFields(linesModalStatusNorm),
    [linesModalStatusNorm]
  );
  const linesModalCanInlineEdit = useMemo(() => {
    if (!linesModalCanEditFields) return false;
    if (hasRole('Admin')) return true;
    if (hasRole('Seller') || hasRole('TeamLeader')) {
      return canSellerTeamLeaderEditLinesModalPartialFields(linesModalStatusNorm, {
        trocaSolicitadaAfterAprovadoCqv: linesModalTrocaSolicitadaAfterAprovadoCqv,
      });
    }
    return false;
  }, [
    linesModalCanEditFields,
    linesModalStatusNorm,
    linesModalTrocaSolicitadaAfterAprovadoCqv,
    user?.roles,
  ]);
  const linesModalCanEditClusterToken = useMemo(
    () =>
      canEditLinesModalClusterToken(linesModalStatusNorm) &&
      (hasRole('Admin') || hasRole('TeamLeader')),
    [linesModalStatusNorm, user?.roles]
  );
  const linesModalVtmeDone = !!selectedContractInfo?.vtmeConfirmado;
  /** Mesmo fluxo de IDs de pedido / VTME Criado do pós-CQV (ex.: Troca de Carteira). */
  const linesModalPostCqvVtmeFlow =
    linesModalStatusNorm === EContractStatus.AprovadoCQV ||
    isTrocaDeCarteiraFlowStatus(linesModalStatusNorm) ||
    isQualityCcvPipelineStatus(linesModalStatusNorm);
  const linesModalShowPedidoIds =
    !!selectedContractInfo &&
    (linesModalPostCqvVtmeFlow ||
      linesModalStatusNorm === EContractStatus.VtmeCriado ||
      linesModalVtmeDone);
  const linesModalCqvButtonsCtx = {
    hasContract: !!selectedContractInfo,
    vtmeConfirmado: linesModalVtmeDone,
  };
  const linesModalShowCqvButtons = shouldShowLinesModalCqvButtons(
    linesModalStatusNorm,
    linesModalCqvButtonsCtx
  );
  const linesModalShowVtmeCriadoButton = shouldShowLinesModalVtmeCriadoButton(
    linesModalStatusNorm,
    linesModalCqvButtonsCtx
  );
  const linesModalShowQualidadeButtons =
    shouldShowLinesModalQualidadeButtons(linesModalHeaderStatus) &&
    isQualityClassifier(user?.email);
  const linesModalShowCcvButtons =
    shouldShowLinesModalCcvButtons(linesModalHeaderStatus) &&
    hasRole('Admin');
  /** T08: botões de correção manual (SLA 2) — Admin + Aprovado CQV(35) ou além na esteira, exceto 37/38. */
  const linesModalShowCorrectionButtons =
    isAprovadoCqvOrBeyondPipelineStatus(linesModalStatusNorm) && hasRole('Admin');
  /** Pós-VTME: somente Admin edita; demais perfis permanecem read-only. */
  const linesModalPedidoIdsReadOnly =
    !!selectedContractInfo?.vtmeConfirmado && !hasRole('Admin');
  const linesModalPedidoIdsDirty = useMemo(() => {
    if (!selectedContractInfo?.vtmeConfirmado) return false;
    const saved1 = (selectedContractInfo.idPedidoContrato1 ?? '').trim();
    const saved2 = (selectedContractInfo.idPedidoContrato2 ?? '').trim();
    return idPedido1.trim() !== saved1 || idPedido2.trim() !== saved2;
  }, [selectedContractInfo, idPedido1, idPedido2]);

  /** Exibe botões de recusa nos status VTME Criado, Qualidade e CVP. */
  const linesModalShowRejectQualidadeCvpButtons =
    !!selectedContractInfo &&
    (linesModalStatusNorm === EContractStatus.VtmeCriado ||
      linesModalStatusNorm === EContractStatus.Qualidade ||
      linesModalStatusNorm === EContractStatus.CVP);

  /**
   * Uma guia por contrato. Com 2+ URLs, o Chrome costuma bloquear o 2.º `window.open` / 2.º `a.click()` no mesmo handler.
   * `form.submit()` com `target="_blank"` dispara navegações GET distintas e costuma abrir todas as guias.
   */
  const openCqvViewTabs = (ids: string[]) => {
    const unique = Array.from(new Set(ids.filter((id) => typeof id === 'string' && id.length > 0)));
    if (unique.length === 0) return;
    if (unique.length === 1) {
      const cid = unique[0];
      window.open(`/contracts/${cid}/cqv-view`, '_blank', 'noopener,noreferrer');
      return;
    }
    const origin = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
    unique.forEach((cid) => {
      const path = `/contracts/${cid}/cqv-view`;
      const action = origin ? new URL(path, origin).toString() : path;
      const form = document.createElement('form');
      form.method = 'GET';
      form.action = action;
      form.target = `cqv_contract_${cid.replace(/-/g, '_')}`;
      form.style.cssText = 'position:fixed;left:-9999px;top:0;width:1px;height:1px;opacity:0';
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    });
  };

  const handleOpenCqvViewPages = async () => {
    const info = selectedContractInfo;
    if (!info?.id) return;

    if (!info.groupId) {
      const fromLines = Array.from(
        new Set(
          (selectedContractLines || [])
            .map((l: any) => l.contractId as string | undefined)
            .filter((cid): cid is string => typeof cid === 'string' && cid.length > 0)
        )
      );
      const ids = fromLines.length > 0 ? fromLines : [info.id];
      openCqvViewTabs(ids);
      return;
    }

    setCqvOpenChoiceLoading(true);
    setCqvOpenChoiceContracts([]);
    cqvOpenChoiceIdsRef.current = [];
    setShowCqvOpenChoiceModal(true);
    try {
      const group = await contractService.getGroupLineDetailsModal(info.groupId);
      const seen = new Set<string>();
      const items: { id: string; label: string }[] = [];
      let idx = 0;
      for (const c of group) {
        if (!c?.id || seen.has(c.id)) continue;
        seen.add(c.id);
        items.push({ id: c.id, label: labelContractForCqvChoice(c, idx) });
        idx += 1;
      }

      const lineIds = Array.from(
        new Set(
          (selectedContractLines || [])
            .map((l: any) => l.contractId as string | undefined)
            .filter((lid): lid is string => typeof lid === 'string' && lid.length > 0)
        )
      );
      for (const lid of lineIds) {
        if (seen.has(lid)) continue;
        const sample = (selectedContractLines || []).find((l: any) => l.contractId === lid);
        seen.add(lid);
        items.push({ id: lid, label: labelContractForCqvChoice(contractPickForCqvFromLine(sample), idx) });
        idx += 1;
      }

      if (items.length < 2) {
        try {
          const fallback = await contractService.getContractsByGroupId(info.groupId);
          for (const c of fallback) {
            if (!c?.id || seen.has(c.id)) continue;
            seen.add(c.id);
            items.push({ id: c.id, label: labelContractForCqvChoice(c, idx) });
            idx += 1;
          }
        } catch {
          /* ignora — já temos linha(s) ou modal do grupo */
        }
      }
      if (items.length === 0) {
        setShowCqvOpenChoiceModal(false);
        cqvOpenChoiceIdsRef.current = [];
        openCqvViewTabs([info.id]);
        return;
      }
      if (items.length === 1) {
        setShowCqvOpenChoiceModal(false);
        cqvOpenChoiceIdsRef.current = [];
        openCqvViewTabs([items[0].id]);
        return;
      }
      cqvOpenChoiceIdsRef.current = items.map((x) => x.id);
      setCqvOpenChoiceContracts(items);
    } catch (e: unknown) {
      console.error(e);
      const msg = e instanceof Error ? e.message : 'Não foi possível carregar os contratos do grupo.';
      showError(msg);
      setShowCqvOpenChoiceModal(false);
      cqvOpenChoiceIdsRef.current = [];
      openCqvViewTabs([info.id]);
    } finally {
      setCqvOpenChoiceLoading(false);
    }
  };

  const handleLineAptoToggle = async (line: { id?: string; contractId?: string }, checked: boolean) => {
    if (hasRole('Admin') ? false : selectedContractInfo?.vtmeConfirmado) return;

    const lineId = line.id;
    if (!lineId) {
      showError('Linha inválida');
      return;
    }

    // Em grupo, cada linha pertence ao contrato Aditivo/Renegociação correto (não ao líder da modal).
    const contractId =
      line.contractId || highlightedContractId || selectedContractInfo?.id;
    if (!contractId) {
      showError('Contrato não selecionado');
      return;
    }

    const previousMapValue = linhasAptas.get(lineId);
    const optimisticMap = new Map(linhasAptas);
    optimisticMap.set(lineId, checked);
    setLinhasAptas(optimisticMap);
    setSavingLinhaAptoId(lineId);

    try {
      await updateLineIsAptoWithCacheInvalidation(contractId, lineId, checked);
      setSelectedContractLines((prev) =>
        prev.map((l) => (l.id === lineId ? { ...l, isApto: checked } : l))
      );
    } catch (error: any) {
      const revertMap = new Map(linhasAptas);
      if (previousMapValue === undefined) revertMap.delete(lineId);
      else revertMap.set(lineId, previousMapValue);
      setLinhasAptas(revertMap);
      showError(error?.message || 'Erro ao salvar aptidão da linha');
    } finally {
      setSavingLinhaAptoId(null);
    }
  };

  // Função helper para converter valor isApto para boolean
  const convertIsAptoToBoolean = (value: any): boolean => {
    if (value === undefined || value === null) return false;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true' || value === '1';
    }
    return false;
  };

  // Estados para verificação de contratos pendentes
  const [pendingContracts, setPendingContracts] = useState<PendingSellerContractDto[]>([]);
  const [checkingPendingContracts, setCheckingPendingContracts] = useState(false);

  // Estados para edição de credenciais Meu TIM por contrato (somente Admin)
  const [showTimCredentialsModal, setShowTimCredentialsModal] = useState(false);
  const [editingTimCredentialsContractId, setEditingTimCredentialsContractId] = useState<string | null>(null);
  const [timLogin, setTimLogin] = useState('');
  const [timSenha, setTimSenha] = useState('');

  // Estados para modal de rastreamento
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingModalContractId, setTrackingModalContractId] = useState<string | null>(null);
  const [trackingModalPersistedCode, setTrackingModalPersistedCode] = useState<string>('');
  const [trackingInfo, setTrackingInfo] = useState<TrackingInfoDto | null>(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingFetchError, setTrackingFetchError] = useState<string | null>(null);
  const [trackingSaveLoading, setTrackingSaveLoading] = useState(false);
  const [trackingSaveError, setTrackingSaveError] = useState<string | null>(null);

  // Estados para edição de contrato com aviso de 24h
  const [showEditWarningModal, setShowEditWarningModal] = useState(false);
  const [contractToEdit, setContractToEdit] = useState<ContractResponseDto | null>(null);
  const loadContractForEditingRef = useRef<((contract: ContractResponseDto) => Promise<void>) | null>(null);
  const [contractUpdatedAt, setContractUpdatedAt] = useState<string | null>(null);

  // Edição de grupo (Aditivo + Renegociação): editar uma vez e replicar para os dois (exceto linhas)
  const [editingGroupContracts, setEditingGroupContracts] = useState<ContractResponseDto[] | null>(null);
  const [editingGroupIndex, setEditingGroupIndex] = useState(0);
  const [editingGroupSecondContractLines, setEditingGroupSecondContractLines] = useState<ContractLineDto[] | null>(null);

  // Modal de seleção de edição de grupo
  const [showEditGroupSelectionModal, setShowEditGroupSelectionModal] = useState(false);
  const [selectedGroupForEdit, setSelectedGroupForEdit] = useState<ContractResponseDto[] | null>(null);

  const [showCqvOpenChoiceModal, setShowCqvOpenChoiceModal] = useState(false);
  const [cqvOpenChoiceLoading, setCqvOpenChoiceLoading] = useState(false);
  const [cqvOpenChoiceContracts, setCqvOpenChoiceContracts] = useState<{ id: string; label: string }[]>([]);

  // Estados para histórico de edições
  const [showEditHistoryModal, setShowEditHistoryModal] = useState(false);
  const [showLineAuditHistoryModal, setShowLineAuditHistoryModal] = useState(false);
  const [lineAuditHistoryMode, setLineAuditHistoryMode] = useState<'solo' | 'group'>('solo');
  const [lineAuditHistoryContractId, setLineAuditHistoryContractId] = useState<string | null>(null);
  const [lineAuditHistoryGroupId, setLineAuditHistoryGroupId] = useState<string | null>(null);
  const [editHistoryContractId, setEditHistoryContractId] = useState<string | null>(null);

  // Modal de bypass de IE inapta
  const [showIeBypassModal, setShowIeBypassModal] = useState(false);
  /**
   * Callback a executar APÓS confirmação do bypass (ex.: avançar para próxima tab ou salvar).
   * Armazenado em ref para não disparar re-renders e não precisar de useCallback extra.
   */
  const pendingIeBypassActionRef = React.useRef<(() => void) | null>(null);
  /** Espelho síncrono de ieBypassConfirmed — evita reabrir o modal na ação pendente antes do re-render. */
  const ieBypassConfirmedRef = React.useRef(false);

  const [showClusterConfirmModal, setShowClusterConfirmModal] = useState(false);
  const pendingClusterConfirmActionRef = React.useRef<(() => void) | null>(null);

  // Estados para envio de email
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [sendingEmailContractIds, setSendingEmailContractIds] = useState<string[] | null>(null);
  const [sendingEmail, setSendingEmail] = useState(false);

  // Regras de contrato do time do usuário
  const [teamContractRules, setTeamContractRules] = useState<TeamContractRules>(defaultContractRules);
  const teamRulesReloadPromiseRef = useRef<Promise<void> | null>(null);
  const reloadTeamContractRulesRef = useRef<
    (options?: { showErrorToast?: boolean; errorMessage?: string }) => Promise<void>
  >(() => Promise.resolve());
  const teamRulesInitialLoadDoneRef = useRef(false);

  const reloadTeamContractRules = useCallback(
    async ({
      showErrorToast = false,
      errorMessage = 'Nao foi possivel atualizar as regras do time. As regras padrao continuam em uso.',
    }: { showErrorToast?: boolean; errorMessage?: string } = {}): Promise<void> => {
      if (teamRulesReloadPromiseRef.current) {
        return teamRulesReloadPromiseRef.current;
      }

      const reloadPromise = (async (): Promise<void> => {
        const currentUserId = getUserId();
        if (!currentUserId || !isAuthenticated) return;

        try {
          const rules = await loadTeamContractRules();
          setTeamContractRules(rules);
        } catch {
          if (showErrorToast) {
            showError(errorMessage);
          }
        }
      })();

      teamRulesReloadPromiseRef.current = reloadPromise;
      await reloadPromise;
      teamRulesReloadPromiseRef.current = null;
    },
    [getUserId, isAuthenticated, showError]
  );

  const openContractModalWithRules = useCallback(
    async (initialTab: string = 'cliente') => {
      await reloadTeamContractRules({
        showErrorToast: true,
        errorMessage: 'Nao foi possivel atualizar as regras do time. As regras padrao continuam em uso.',
      });
      setActiveTab(initialTab);
      setIsCreateModalOpen(true);
    },
    [reloadTeamContractRules]
  );

  const refreshTeamContractRulesQuietly = useCallback(
    () =>
      reloadTeamContractRules({
        showErrorToast: false,
      }),
    [reloadTeamContractRules]
  );

  const contractActions = useContractActions({
    loadContracts,
    showSuccess,
    showError,
    currentPage,
    openContractForEditInCreateModal: async (contract) => {
      await loadContractForEditingRef.current?.(contract);
    },
    setContractToEdit,
    setShowEditWarningModal,
    setSendingEmailContractIds,
    setShowSendEmailModal,
  });
  const { handleDeleteContract, handleSendContractEmail, handleEditContract } = contractActions;

  // Estados para duplicação de contrato
  const [duplicatingContractId, setDuplicatingContractId] = useState<string | null>(null);
  const [duplicatedFromContractId, setDuplicatedFromContractId] = useState<string | null>(null);
  
  // Estados para duplicação de grupos
  const [showDuplicateGroupModal, setShowDuplicateGroupModal] = useState(false);
  const [duplicatingGroupId, setDuplicatingGroupId] = useState<string | null>(null);
  const [selectedGroupForDuplicate, setSelectedGroupForDuplicate] = useState<any | null>(null);

  const [responsibleConsultantUserId, setResponsibleConsultantUserId] = useState('');
  const [consultantDropdownOptions, setConsultantDropdownOptions] = useState<
    ResponsibleConsultantDropdownOption[]
  >([]);
  const [loadingConsultantOptions, setLoadingConsultantOptions] = useState(false);
  const [consultantLoadFailed, setConsultantLoadFailed] = useState(false);
  const [editingResponsibleConsultant, setEditingResponsibleConsultant] = useState(false);
  const [responsibleConsultantEditValue, setResponsibleConsultantEditValue] = useState('');
  const [savingResponsibleConsultant, setSavingResponsibleConsultant] = useState(false);
  const consultantRequestInFlightRef = useRef(false);
  const responsibleConsultantUserIdRef = useRef('');
  const consultantInitialLoadDoneRef = useRef(false);
  const getUserIdRef = useRef(getUserId);

  useEffect(() => {
    reloadTeamContractRulesRef.current = reloadTeamContractRules;
  }, [reloadTeamContractRules]);

  // Carregar regras ao abrir a página de contratos (uma vez por sessão autenticada)
  useEffect(() => {
    if (!isAuthenticated) {
      teamRulesInitialLoadDoneRef.current = false;
      return;
    }
    if (teamRulesInitialLoadDoneRef.current) return;
    teamRulesInitialLoadDoneRef.current = true;
    void reloadTeamContractRulesRef.current();
  }, [isAuthenticated]);

  // Ler parâmetros da URL ao carregar a página (para filtro de CNPJ e highlight).
  // A busca em si só dispara depois da autenticação, com o CNPJ passado por override.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const clientCnpj = urlParams.get('clientCnpj')?.trim();
    const highlightId = urlParams.get('highlight');

    if (clientCnpj) {
      setClientSearchFilter(clientCnpj);
    }
    if (highlightId) {
      setHighlightedContractId(highlightId);
    }
  }, []);

  // Listener para abrir modal quando cliente não encontrado no simulador (ou vindo do botão
  // flutuante de Dados TIM, com CNPJ pré-preenchido via detail.cnpj — ver FloatingTimDataButton).
  useEffect(() => {
    const handleOpenModal = (event: Event) => {
      const cnpj = (event as CustomEvent<{ cnpj?: string }>).detail?.cnpj;
      if (cnpj) {
        setClientSearchQuery(cnpj);
      }
      void openContractModalWithRules('cliente');
    };

    window.addEventListener('openContractModal', handleOpenModal);
    return () => window.removeEventListener('openContractModal', handleOpenModal);
  }, [openContractModalWithRules]);

  // Funções de validação de duplicatas
  const hasDuplicateCcid = (linhas: any[]): boolean => {
    const ccids = linhas
      .filter(linha => linha.negociacao === 'Aditivo' && linha.chipCcid)
      .map(linha => linha.chipCcid);
    
    return new Set(ccids).size !== ccids.length;
  };

  const hasDuplicatePhone = (linhas: any[]): boolean => {
    const phones = linhas
      .filter(linha => linha.negociacao === 'Renegociação' && linha.numero)
      .map(linha => {
        const n = cleanPhone(linha.numero);
        const d = linha.ddd || '';
        return d && n ? mergeDddAndLocalPhoneDigits(d, n) : n;
      });
    
    return new Set(phones).size !== phones.length;
  };

  const getDuplicateCcidLines = (linhas: any[]): string[] => {
    const ccidCount: { [key: string]: string[] } = {};
    
    linhas
      .filter(linha => linha.negociacao === 'Aditivo' && linha.chipCcid)
      .forEach(linha => {
        if (!ccidCount[linha.chipCcid]) {
          ccidCount[linha.chipCcid] = [];
        }
        ccidCount[linha.chipCcid].push(linha.id);
      });
    
    return Object.values(ccidCount)
      .filter(ids => ids.length > 1)
      .flat();
  };

  const getDuplicatePhoneLines = (linhas: any[]): string[] => {
    const phoneCount: { [key: string]: string[] } = {};
    
    linhas
      .filter(linha => linha.negociacao === 'Renegociação' && linha.numero)
      .forEach(linha => {
        const n = cleanPhone(linha.numero);
        const d = linha.ddd || '';
        const numeroCompleto = d && n ? mergeDddAndLocalPhoneDigits(d, n) : n;

        if (!phoneCount[numeroCompleto]) {
          phoneCount[numeroCompleto] = [];
        }
        phoneCount[numeroCompleto].push(linha.id);
      });
    
    return Object.values(phoneCount)
      .filter(ids => ids.length > 1)
      .flat();
  };

  // Verificar se usuário pode ver filtros avançados (qualquer role exceto Seller)
  const canViewAdvancedFilters = user?.roles && !user.roles.includes('Seller');
  
  // Verificar se usuário tem role de Admin
  const isAdmin = user?.roles && user.roles.includes('Admin');
  const isTeamLeader = hasRole('TeamLeader');
  const isTeamManagerRole = hasRole('Team Manager') || hasRole('TeamManager') || hasRole('Manager');
  const canAssignResponsibleConsultant = isAdmin || isTeamLeader || isTeamManagerRole;
  const canEditResponsibleConsultant = canAssignResponsibleConsultant;
  const responsibleConsultantEditOptions = useMemo(
    () => ({
      isAdmin: !!isAdmin,
      isTeamLeader,
      isManager: isTeamManagerRole,
    }),
    [isAdmin, isTeamLeader, isTeamManagerRole]
  );
  const canEditConsultantInLinesModal = useMemo(
    () =>
      canEditResponsibleConsultant &&
      canEditResponsibleConsultantAtStatus(linesModalStatusNorm, responsibleConsultantEditOptions),
    [canEditResponsibleConsultant, linesModalStatusNorm, responsibleConsultantEditOptions]
  );

  useEffect(() => {
    responsibleConsultantUserIdRef.current = responsibleConsultantUserId;
  }, [responsibleConsultantUserId]);

  useEffect(() => {
    getUserIdRef.current = getUserId;
  }, [getUserId]);

  const buildLoggedInConsultantOption = useCallback((): {
    key: string;
    value: string;
    text: string;
  } | null => {
    const currentUserId = getUserIdRef.current();
    if (!currentUserId) return null;
    const displayName =
      user?.nome?.trim() ||
      user?.name?.trim() ||
      user?.username?.trim() ||
      getUserInfo().name ||
      'Usuário atual';
    return { key: currentUserId, value: currentUserId, text: displayName };
  }, [getUserInfo, user]);

  /** Garante valor e opção no select quando a API falha ou ainda está carregando. */
  const ensureLoggedInConsultantFallback = useCallback(() => {
    const option = buildLoggedInConsultantOption();
    if (!option) return;

    if (!responsibleConsultantUserIdRef.current) {
      setResponsibleConsultantUserId(option.value);
    }

    setConsultantDropdownOptions((prev) => {
      const norm = option.value.toLowerCase();
      if (prev.some((o) => o.value.toLowerCase() === norm)) return prev;
      return [option, ...prev];
    });
  }, [buildLoggedInConsultantOption]);

  const loadConsultantOptions = useCallback(
    async (options?: { trigger: 'initial' | 'field-interaction'; retryAttempts?: number }) => {
      if (!isAuthenticated || !canAssignResponsibleConsultant) {
        setConsultantDropdownOptions([]);
        setConsultantLoadFailed(false);
        return;
      }
      if (consultantRequestInFlightRef.current) return;

      const currentUserId = getUserIdRef.current();
      if (!currentUserId) return;

      ensureLoggedInConsultantFallback();

      consultantRequestInFlightRef.current = true;
      let loadingTimer: ReturnType<typeof setTimeout> | undefined;
      const loadingActivatedRef = { current: false };
      const activateLoading = () => {
        if (!loadingActivatedRef.current) {
          loadingActivatedRef.current = true;
          setLoadingConsultantOptions(true);
        }
      };
      loadingTimer = setTimeout(activateLoading, 0);

      try {
        const maxAttempts = Math.max(1, options?.retryAttempts ?? 1);
        let loadedOptions: ResponsibleConsultantDropdownOption[] = [];
        let loadSucceeded = false;

        for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
          try {
            const teams = await queryClient.fetchQuery({
              queryKey: teamsQueryKeys.list(),
              queryFn: () => teamService.getTeams(),
              staleTime: TEAMS_STALE_TIME_MS,
            });
            loadedOptions = buildResponsibleConsultantDropdownOptions(teams, {
              isAdmin: !!isAdmin,
              currentUserId,
            });
            loadSucceeded = true;
            if (loadedOptions.length > 0 || attempt === maxAttempts) break;
          } catch {
            if (attempt === maxAttempts) break;
          }

          if (attempt < maxAttempts) {
            await new Promise((resolve) => setTimeout(resolve, 500));
          }
        }

        const shouldApplyFallback = !loadSucceeded || loadedOptions.length === 0;
        setConsultantLoadFailed(shouldApplyFallback);

        if (loadedOptions.length > 0) {
          setConsultantDropdownOptions(loadedOptions);
        } else if (shouldApplyFallback) {
          ensureLoggedInConsultantFallback();
        }

        if (shouldApplyFallback) {
          setResponsibleConsultantEditValue((prev) => prev || currentUserId);
          setResponsibleConsultantEditInModal((prev) => prev || currentUserId);
          console.info('[contracts] consultant fallback applied', {
            trigger: options?.trigger ?? 'initial',
            reason: loadSucceeded ? 'empty-result' : 'request-failure',
          });
        }

        if (options?.trigger === 'field-interaction' && maxAttempts > 1) {
          console.info('[contracts] consultant retry completed', {
            success: loadSucceeded && loadedOptions.length > 0,
            optionsCount: loadedOptions.length,
          });
        }
      } finally {
        if (loadingTimer !== undefined) {
          clearTimeout(loadingTimer);
        }
        setLoadingConsultantOptions(false);
        consultantRequestInFlightRef.current = false;
      }
    },
    [canAssignResponsibleConsultant, ensureLoggedInConsultantFallback, isAdmin, isAuthenticated]
  );

  const handleConsultantFieldInteraction = useCallback(() => {
    if (!canAssignResponsibleConsultant || loadingConsultantOptions) return;
    if (consultantDropdownOptions.length > 0 && !consultantLoadFailed) return;
    void loadConsultantOptions({ trigger: 'field-interaction', retryAttempts: 2 });
  }, [
    canAssignResponsibleConsultant,
    consultantDropdownOptions.length,
    consultantLoadFailed,
    loadConsultantOptions,
    loadingConsultantOptions,
  ]);

  useEffect(() => {
    if (!isAuthenticated || !canAssignResponsibleConsultant) {
      consultantInitialLoadDoneRef.current = false;
      setConsultantDropdownOptions([]);
      setConsultantLoadFailed(false);
    }
  }, [canAssignResponsibleConsultant, isAuthenticated]);

  useEffect(() => {
    if (!isCreateModalOpen) {
      consultantInitialLoadDoneRef.current = false;
    }
  }, [isCreateModalOpen]);

  const handleConsultantFieldVisible = useCallback(() => {
    if (!isAuthenticated || !canAssignResponsibleConsultant) return;
    if (consultantInitialLoadDoneRef.current) return;
    consultantInitialLoadDoneRef.current = true;
    void loadConsultantOptions({ trigger: 'initial', retryAttempts: 1 });
  }, [canAssignResponsibleConsultant, isAuthenticated, loadConsultantOptions]);

  // Estados para ações da tabela
  const [downloadingPdf, setDownloadingPdf] = useState<string | null>(null);
  const [viewingContract, setViewingContract] = useState<ContractResponseDto | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [loadingContractDetails, setLoadingContractDetails] = useState(false);
  const viewingStatusNorm = useMemo(
    () => (viewingContract ? normalizeContractStatus(viewingContract.status) : null),
    [viewingContract]
  );
  const canEditConsultantInViewModal = useMemo(
    () =>
      canEditResponsibleConsultant &&
      canEditResponsibleConsultantAtStatus(viewingStatusNorm, responsibleConsultantEditOptions),
    [canEditResponsibleConsultant, viewingStatusNorm, responsibleConsultantEditOptions]
  );

  // Estados para confirmação de fechamento
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [pendingCloseAction, setPendingCloseAction] = useState<(() => void) | null>(null);
  const [draftContractId, setDraftContractId] = useState<string | null>(null);
  const draftContractIdRef = useRef<string | null>(null);
  /** Suspende persistência de rascunho durante o Salvar Contrato (evita corrida com finalize). */
  const draftPersistSuspendedRef = useRef(false);
  const [showClientDraftsModal, setShowClientDraftsModal] = useState(false);
  const [clientDraftsList, setClientDraftsList] = useState<DraftSummaryDto[]>([]);
  const [clientDraftsLoading, setClientDraftsLoading] = useState(false);
  const draftSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftPersisterRef = useRef(
    createSerializedDraftPersister<'created' | 'updated' | undefined>()
  );
  
  // Estado para loading do salvamento
  const [isSavingContract, setIsSavingContract] = useState(false);
  const [contractInternalNotes, setContractInternalNotes] = useState('');
  const [contractInternalNotesOccurrence, setContractInternalNotesOccurrence] = useState('');
  const [meuTimPasswordVisibility, setMeuTimPasswordVisibility] = useState<Record<string, boolean>>({});
  // Fatia 0 (GCM) — soft-fail: 'soft_failed' é uma verificação real que ATÉ falhou
  // (credencial inválida / captcha / timeout / erro portal), mas nunca bloqueia o
  // avanço/salvamento do contrato. 'error' fica reservado para pendências client-side
  // (sem cliente selecionado / credenciais incompletas) que impedem até tentar verificar.
  const [meuTimVerificationStatus, setMeuTimVerificationStatus] = useState<
    'idle' | 'loading' | 'success' | 'soft_failed' | 'error'
  >('idle');
  const [meuTimVerificationMessage, setMeuTimVerificationMessage] = useState('');
  const [meuTimVerifiedSignature, setMeuTimVerifiedSignature] = useState<string | null>(null);
  
  // Flag para controlar se deve processar o salvamento após decisão do modal
  const [shouldProcessSave, setShouldProcessSave] = useState(false);

  // Estados para tela de sucesso e download de contratos
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const showSuccessModalRef = useRef(false);
  const linesModalRequestSequenceRef = useRef(0);
  const linesModalAbortRef = useRef<AbortController | null>(null);
  /** Contrato atualmente aberto no modal — usado para ignorar softRefresh atrasado. */
  const linesModalOpenContractIdRef = useRef<string | null>(null);
  const showLinesModalRef = useRef(false);
  useEffect(() => {
    showLinesModalRef.current = showLinesModal;
  }, [showLinesModal]);

  const clearCqvScriptState = useCallback(() => {
    setCallScript('');
    setWhatsappCqvParts({ ...EMPTY_WHATSAPP_CQV_PARTS });
    setLinesModalCqvBlockedReason(null);
  }, []);

  const applyLinesModalCqvClientValidation = useCallback(
    (
      apiContracts: Array<{
        id?: string;
        clientId?: string | null;
        clientCnpj?: string | null;
        client?: { id?: string; cnpj?: string } | null;
      }>,
      clickedContractInfo: any
    ): boolean => {
      const expected = resolveExpectedClientIdentity(clickedContractInfo);
      const validation = validateGroupModalClientConsistency(apiContracts, expected);
      if (!validation.ok) {
        setLinesModalCqvBlockedReason(validation.message);
        setCallScript(LINES_MODAL_CQV_BLOCKED_PLACEHOLDER);
        setWhatsappCqvParts({ ...EMPTY_WHATSAPP_CQV_PARTS });
        showWarning(validation.message, { autoClose: 16000 });
        return false;
      }
      setLinesModalCqvBlockedReason(null);
      return true;
    },
    [showWarning]
  );
  const [createdContractId, setCreatedContractId] = useState<string | null>(null);
  const [createdContractGroupId, setCreatedContractGroupId] = useState<string | null>(null);
  const [postCreateDocusignQueue, setPostCreateDocusignQueue] = useState<ContractListDto[]>([]);
  const postCreateDocusignQueueRef = useRef<ContractListDto[]>([]);
  const postCreateDocusignSendActiveRef = useRef(false);
  const [postCreateAssinadoLoading, setPostCreateAssinadoLoading] = useState(false);
  const [postCreateAssinadoConcluido, setPostCreateAssinadoConcluido] = useState(false);
  const [postCreateAssinadoFeedback, setPostCreateAssinadoFeedback] = useState<
    'success' | 'error' | null
  >(null);
  const [isDownloadingContracts, setIsDownloadingContracts] = useState(false);

  // RPA progress (WebSocket)
  type DocusignCompletionResult = 'success' | 'error' | 'detached';
  type RpaProgressUpdate = {
    contractId: string;
    stage: string;
    progress: number;
    message: string;
    status: 'processing' | 'success' | 'error';
    timestamp: string;
    preserveProgress?: boolean;
    resetProgress?: boolean;
    isQueued?: boolean;
    queuePosition?: number;
    queueTotal?: number;
  };

  const DOCUSIGN_PROGRESS_POLL_INTERVAL_MS = 2_000;
  const DOCUSIGN_PROGRESS_POLL_INTERVAL_QUEUED_MS = 5_000;
  const DOCUSIGN_PROGRESS_POLL_MAX_FAILURES = 3;
  const DOCUSIGN_PROGRESS_POLL_EMPTY_BEFORE_HISTORY = 2;
  const [rpaProgress, setRpaProgress] = useState<RpaProgressUpdate | null>(null);
  const [rpaWs, setRpaWs] = useState<WebSocket | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [rpaStarted, setRpaStarted] = useState(false);
  const [isSendingToDocusign, setIsSendingToDocusign] = useState(false);
  const rpaLastProgressSignatureRef = useRef('');
  const rpaWsRef = useRef<WebSocket | null>(null);
  const rpaFinalizedRef = useRef(false);
  const docusignPollTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const docusignPollSessionsRef = useRef<Map<string, number>>(new Map());
  const docusignPollFailureCountsRef = useRef<Map<string, number>>(new Map());
  const docusignPollEmptyCountsRef = useRef<Map<string, number>>(new Map());
  const docusignCompletionResolversRef = useRef<
    Map<string, (result: DocusignCompletionResult) => void>
  >(new Map());
  const docusignFinalizedContractsRef = useRef<Set<string>>(new Set());
  const docusignProgressSignatureByContractRef = useRef<Map<string, string>>(new Map());
  const docusignProgressPollStartedAtRef = useRef<Map<string, number>>(new Map());
  const docusignSendStartedAtByContractRef = useRef<Map<string, number>>(new Map());

  const normalizeDocusignContractId = (contractId: string) => contractId.trim().toLowerCase();

  const isSameDocusignContractId = (left: string, right: string) =>
    normalizeDocusignContractId(left) === normalizeDocusignContractId(right);
  const [showRetryButton, setShowRetryButton] = useState(false);

  // Estados para planos (declarados aqui para uso em generateCallScript)
  const [plans, setPlans] = useState<PlanResponse[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansFiltrados, setPlansFiltrados] = useState<PlanResponse[]>([]);
  const [handsetCatalog, setHandsetCatalog] = useState<HandsetDeviceDto[]>([]);

  const buildDocusignProgressSignature = (data: RpaProgressUpdate) =>
    `${data.stage}|${data.progress ?? 0}|${data.status}|${data.message ?? ''}|${data.isQueued ? 1 : 0}|${data.queuePosition ?? 0}|${data.queueTotal ?? 0}`;

  const isDocusignModalProgressVisible = () =>
    showDocusignSendModalRef.current || showSuccessModalRef.current;

  const getDocusignProgressPollIntervalMs = (data: RpaProgressUpdate) =>
    data.isQueued ? DOCUSIGN_PROGRESS_POLL_INTERVAL_QUEUED_MS : DOCUSIGN_PROGRESS_POLL_INTERVAL_MS;

  const formatDocusignProgressStatusText = (progress: RpaProgressUpdate): string => {
    if (
      progress.message &&
      /na fila.*posição\s+\d+/i.test(progress.message)
    ) {
      return progress.message;
    }
    if (progress.isQueued && progress.queuePosition && progress.queueTotal) {
      return `Na fila (posição ${progress.queuePosition} de ${progress.queueTotal})`;
    }
    if (progress.isQueued) {
      return 'Na fila — aguardando início do processamento';
    }
    return progress.message || 'Processando envio DocuSign...';
  };

  const stopDocusignModalProgressTracking = (options?: { resolveWaitersAs?: 'detached' }) => {
    stopDocusignProgressPolling();
    closeDocusignWsConnection();
    closeRpaWs();
    clearDocusignProgressTimeouts();

    if (options?.resolveWaitersAs === 'detached') {
      docusignCompletionResolversRef.current.forEach((resolve) => {
        resolve('detached');
      });
      docusignCompletionResolversRef.current.clear();
      const legacyCompletion = docusignWsCompletionRef.current;
      docusignWsCompletionRef.current = null;
      legacyCompletion?.('detached');
    }
  };

  const shouldUseDocusignProgressPolling = () => {
    if (!docusignUiEnabled) return false;
    if (process.env.REACT_APP_DOCUSIGN_PROGRESS_POLLING === 'true') return true;
    if (process.env.REACT_APP_DOCUSIGN_PROGRESS_POLLING === 'false') return false;
    const wsUrl = (process.env.REACT_APP_RPA_WS_URL || '').trim();
    // Sem WS explícito: polling via GettelApi (HTTP local, Docker e HTTPS prod).
    if (wsUrl.length === 0) return true;
    return typeof window !== 'undefined' && window.location.protocol === 'https:';
  };

  const stopDocusignProgressPolling = (contractId?: string) => {
    const stopOne = (id: string) => {
      const timeout = docusignPollTimeoutsRef.current.get(id);
      if (timeout) {
        clearTimeout(timeout);
        docusignPollTimeoutsRef.current.delete(id);
      }
      docusignPollSessionsRef.current.delete(id);
      docusignPollFailureCountsRef.current.delete(id);
      docusignPollEmptyCountsRef.current.delete(id);
      docusignProgressPollStartedAtRef.current.delete(id);
      docusignSendStartedAtByContractRef.current.delete(id);
    };

    if (contractId) {
      stopOne(contractId);
      return;
    }

    Array.from(docusignPollTimeoutsRef.current.keys()).forEach(stopOne);
  };

  const isDocusignContractFinalized = (contractId: string) =>
    docusignFinalizedContractsRef.current.has(contractId);

  const markDocusignSendStarted = (contractId: string) => {
    const startedAt = Date.now();
    docusignSendStartedAtByContractRef.current.set(contractId, startedAt);
    docusignProgressPollStartedAtRef.current.set(contractId, startedAt);
    return startedAt;
  };

  const markDocusignContractFinalized = (contractId: string) => {
    docusignFinalizedContractsRef.current.add(contractId);
    docusignFinalizedRef.current = true;
  };

  const resetDocusignContractFinalized = (contractId: string) => {
    docusignFinalizedContractsRef.current.delete(contractId);
    if (docusignFinalizedContractsRef.current.size === 0) {
      docusignFinalizedRef.current = false;
    }
  };

  const resolveDocusignContractCompletion = (
    contractId: string,
    result: DocusignCompletionResult
  ) => {
    const resolver = docusignCompletionResolversRef.current.get(contractId);
    if (resolver) {
      docusignCompletionResolversRef.current.delete(contractId);
      resolver(result);
    }
  };

  const clearDocusignContractCompletionWaiters = (contractIds?: string[]) => {
    if (contractIds?.length) {
      contractIds.forEach((id) => docusignCompletionResolversRef.current.delete(id));
      return;
    }
    docusignCompletionResolversRef.current.clear();
    docusignWsCompletionRef.current = null;
  };

  const shouldForceDocusignProgressPolling = (queueLength: number) =>
    queueLength > 1 || shouldUseDocusignProgressPolling();

  const clearRpaTimeout = () => {};

  const startRpaProgressWatchers = (_contractId: string) => {
    rpaLastProgressSignatureRef.current = '';
  };

  const finalizePostCreateRpaUiState = () => {
    setRpaStarted(false);
    setRpaProgress(null);
    setShowRetryButton(false);
    setIsRetrying(false);
    setIsSendingToDocusign(false);
    closeRpaWs();
    rpaFinalizedRef.current = false;
    rpaLastProgressSignatureRef.current = '';
    postCreateDocusignQueueRef.current = [];
    setPostCreateDocusignQueue([]);
    postCreateDocusignSendActiveRef.current = false;
    setDocusignProgressByContractId({});
    setDocusignShowRetryButton(false);
    docusignCancelRetryInFlightRef.current = false;
    docusignRetryActionConsumedRef.current = false;
  };

  const isPostCreateDocusignStillProcessing = () => {
    if (!rpaStarted) return false;

    const queue =
      postCreateDocusignQueueRef.current.length > 0
        ? postCreateDocusignQueueRef.current
        : docusignSendQueueRef.current;

    if (queue.length > 0) {
      return queue.some((contract) => {
        const progress = docusignProgressByContractId[contract.id];
        return !progress || progress.status === 'processing';
      });
    }

    return Boolean(
      createdContractId && (!rpaProgress || rpaProgress.status === 'processing')
    );
  };

  const closeRpaWs = () => {
    const ws = rpaWsRef.current ?? rpaWs;
    if (ws && ws.readyState !== WebSocket.CLOSED) {
      ws.close();
    }
    rpaWsRef.current = null;
    setRpaWs(null);
  };

  const closeDocusignWsConnection = () => {
    const ws = docusignWsRef.current ?? docusignWs;
    if (ws && ws.readyState !== WebSocket.CLOSED) {
      ws.close();
    }
    docusignWsRef.current = null;
    setDocusignWs(null);
  };

  const closeDocusignWs = (options?: { stopAllPolling?: boolean }) => {
    const shouldStopAllPolling =
      options?.stopAllPolling ?? !isMultiContractDocusignProgress();
    if (shouldStopAllPolling) {
      stopDocusignProgressPolling();
    }
    closeDocusignWsConnection();
  };

  const applyRpaProgressUpdate = (contractId: string, data: RpaProgressUpdate) => {
    if (!data || data.contractId !== contractId || rpaFinalizedRef.current) {
      return;
    }

    if (!showSuccessModalRef.current) {
      stopDocusignProgressPolling(contractId);
      closeRpaWs();
      return;
    }

    setRpaProgress(data);

    if (data.status === 'success' || data.status === 'error') {
      rpaFinalizedRef.current = true;
      closeRpaWs();

      if (!showSuccessModalRef.current) {
        if (data.status === 'success') {
          showSuccess('Envio DocuSign concluído com sucesso.');
        } else {
          showWarning(
            'Envio DocuSign finalizado com erro. Verifique o status na listagem de contratos.'
          );
        }
        setCreatedContractId(null);
        setCreatedContractGroupId(null);
        void loadContracts();
        finalizePostCreateRpaUiState();
      } else if (data.status === 'success') {
        setShowRetryButton(false);
      } else {
        setShowRetryButton(true);
      }
      return;
    }

    const signature = buildDocusignProgressSignature(data);
    if (signature !== rpaLastProgressSignatureRef.current) {
      rpaLastProgressSignatureRef.current = signature;
    }
  };

  const handleRpaWsProgressMessage = (contractId: string, event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data) as RpaProgressUpdate;
      applyRpaProgressUpdate(contractId, data);
    } catch {
      // Ignorar mensagens inválidas do socket.
    }
  };

  const connectRpaProgressSocket = (contractId: string) => {
    closeRpaWs();
    rpaFinalizedRef.current = false;
    const wsUrl = `${getRpaWsBaseUrl()}/ws?contractId=${encodeURIComponent(contractId)}&fresh=1`;
    const ws = new WebSocket(wsUrl);
    rpaWsRef.current = ws;
    setRpaWs(ws);
    ws.onmessage = (event) => handleRpaWsProgressMessage(contractId, event);
    ws.onerror = () => {
      // Mantém último progresso; polling ou histórico tratam o estado terminal.
    };
  };

  type DocusignContactMode = 'existing' | 'custom';
  type DocusignContactForm = {
    recipientName: string;
    recipientPhone: string;
    recipientEmail: string;
  };
  type DocusignContactOption = DocusignContactForm & {
    id: string;
    label: string;
    source: string;
  };

  const emptyDocusignContact: DocusignContactForm = {
    recipientName: '',
    recipientPhone: '',
    recipientEmail: ''
  };

  const [showDocusignSendModal, setShowDocusignSendModal] = useState(false);
  const [showDocusignResendConfirm, setShowDocusignResendConfirm] = useState(false);
  const [showDocusignGroupMenuConfirm, setShowDocusignGroupMenuConfirm] = useState(false);
  const [docusignGroupMenuConfirmMode, setDocusignGroupMenuConfirmMode] = useState<
    'all_sent' | 'partial'
  >('all_sent');
  const [docusignGroupMenuMembers, setDocusignGroupMenuMembers] = useState<ContractListDto[]>([]);
  const [docusignGroupMenuSentMembers, setDocusignGroupMenuSentMembers] = useState<ContractListDto[]>(
    []
  );
  const [docusignGroupMenuPendingMembers, setDocusignGroupMenuPendingMembers] = useState<
    ContractListDto[]
  >([]);
  const [docusignGroupMenuParentContract, setDocusignGroupMenuParentContract] =
    useState<ContractListDto | null>(null);
  const [docusignResendSummary, setDocusignResendSummary] = useState<LatestDocusignSendSummaryDto | null>(null);
  const [docusignPendingContract, setDocusignPendingContract] = useState<ContractListDto | null>(null);
  const [docusignModalPriorSend, setDocusignModalPriorSend] = useState<LatestDocusignSendSummaryDto | null>(null);
  const [docusignSelectedContract, setDocusignSelectedContract] = useState<ContractListDto | null>(null);
  const [docusignContactOptions, setDocusignContactOptions] = useState<DocusignContactOption[]>([]);
  const [docusignSelectedContactId, setDocusignSelectedContactId] = useState<string>('');
  const [docusignContactMode, setDocusignContactMode] = useState<DocusignContactMode>('existing');
  const [docusignContactForm, setDocusignContactForm] = useState<DocusignContactForm>(emptyDocusignContact);
  const [docusignFieldsReadonly, setDocusignFieldsReadonly] = useState(false);
  const [docusignLoadingContact, setDocusignLoadingContact] = useState(false);
  const [docusignSending, setDocusignSending] = useState(false);
  const [docusignProgress, setDocusignProgress] = useState<RpaProgressUpdate | null>(null);
  const [docusignPhaseTimeline, setDocusignPhaseTimeline] = useState<DocusignTimelineProgress[]>([]);
  const [docusignWs, setDocusignWs] = useState<WebSocket | null>(null);
  const [docusignShowRetryButton, setDocusignShowRetryButton] = useState(false);
  const [docusignShowCancelRetryButton, setDocusignShowCancelRetryButton] = useState(false);
  const docusignCancelRetryInFlightRef = useRef(false);
  const docusignRetryActionConsumedRef = useRef(false);
  const docusignLastProgressSignatureRef = useRef('');
  const docusignFinalizedRef = useRef(false);
  const docusignWsSessionRef = useRef(0);
  const docusignWsRef = useRef<WebSocket | null>(null);
  const docusignInProgressRef = useRef(false);
  const docusignTrackingContractIdRef = useRef<string | null>(null);
  const docusignLastProcessingSummaryRef = useRef<LatestDocusignSendSummaryDto | null>(null);
  const showDocusignSendModalRef = useRef(false);
  const [docusignSendQueue, setDocusignSendQueue] = useState<ContractListDto[]>([]);
  const [docusignProgressByContractId, setDocusignProgressByContractId] = useState<
    Record<string, RpaProgressUpdate | null>
  >({});
  const docusignSendQueueRef = useRef<ContractListDto[]>([]);
  const docusignWsCompletionRef = useRef<((result: DocusignCompletionResult) => void) | null>(null);
  const docusignPreserveContactOnRetryRef = useRef(false);
  const docusignGroupFailureIndexRef = useRef<number | null>(null);
  const docusignSequentialSendActiveRef = useRef(false);
  const docusignAbortSequentialRef = useRef(false);
  /** Fila congelada no início do envio — não é limpa ao fechar a modal. */
  const docusignActiveSendQueueRef = useRef<ContractListDto[]>([]);
  const docusignSendPayloadRef = useRef<SendContractToDocusignRequest | null>(null);
  /** Modal fechada com envio sequencial ainda em andamento. */
  const docusignBackgroundSendRef = useRef(false);
  const docusignProcessingSummaryByContractRef = useRef<Record<string, LatestDocusignSendSummaryDto>>({});
  /** Envio DocuSign unificado de grupo (1 envelope, 1 barra de progresso). */
  const docusignUnifiedGroupSendRef = useRef(false);
  /** Acompanha envio já em andamento após HTTP 409 (sem novo POST de envio). */
  const docusignWatchingInProgressRef = useRef(false);

  const isMultiContractDocusignProgress = () => {
    if (docusignUnifiedGroupSendRef.current) {
      return false;
    }
    const queueLength = Math.max(
      docusignSendQueueRef.current.length,
      docusignActiveSendQueueRef.current.length,
      postCreateDocusignQueueRef.current.length
    );
    return queueLength > 1;
  };

  const getDocusignSelectedExistingContact = () =>
    docusignContactOptions.find(contact => contact.id === docusignSelectedContactId) ?? null;

  const hideDocusignCancelRetryButton = () => {
    setDocusignShowCancelRetryButton(false);
  };

  const hideDocusignRetryButton = () => {
    setDocusignShowRetryButton(false);
    hideDocusignCancelRetryButton();
  };

  /** Exibe cancelar + retry quando o envio do grupo já está em andamento (HTTP 409). */
  const revealDocusignCancelRetryButton = () => {
    if (docusignCancelRetryInFlightRef.current) {
      return;
    }
    const isPostCreateActive =
      postCreateDocusignSendActiveRef.current || (showSuccessModalRef.current && rpaStarted);
    if (
      !showDocusignSendModalRef.current &&
      !isPostCreateActive &&
      !docusignBackgroundSendRef.current
    ) {
      showWarning(
        'Envio DocuSign do grupo em andamento. Abra o envio DocuSign para acompanhar o progresso.'
      );
      return;
    }
    setDocusignShowRetryButton(false);
    docusignRetryActionConsumedRef.current = false;
    setDocusignShowCancelRetryButton(true);
    if (isPostCreateActive) {
      setShowRetryButton(true);
    }
  };

  const isDocusignConflictError = (error: unknown): boolean => {
    const response = (error as { response?: { status?: number; data?: { message?: string } } })
      .response;
    if (response?.status === 409) {
      return true;
    }
    const message = (response?.data?.message ?? '').toLowerCase();
    return (
      message.includes('já está em processamento') ||
      message.includes('ja esta em processamento') ||
      message.includes('já em processamento') ||
      message.includes('ja em processamento')
    );
  };

  /** Exibe o botão de retry na modal; libera um novo clique. */
  const revealDocusignRetryButton = () => {
    if (docusignCancelRetryInFlightRef.current) {
      return;
    }
    const isPostCreateActive =
      postCreateDocusignSendActiveRef.current || (showSuccessModalRef.current && rpaStarted);
    if (
      !showDocusignSendModalRef.current &&
      !isPostCreateActive &&
      !docusignBackgroundSendRef.current
    ) {
      showWarning(
        'Envio DocuSign finalizado com erro. Verifique o status na listagem de contratos.'
      );
      return;
    }
    hideDocusignCancelRetryButton();
    docusignRetryActionConsumedRef.current = false;
    setDocusignShowRetryButton(true);
    if (isPostCreateActive) {
      setShowRetryButton(true);
    }
  };

  const hydrateDocusignProgressFromApi = async (contractId: string): Promise<boolean> => {
    try {
      const response = await contractService.getDocusignSendProgress(contractId);
      if (response.currentProgress) {
        const data = mapDocusignSendProgressToUpdate(contractId, response.currentProgress);
        if (data) {
          setDocusignProgressForContract(contractId, data);
          refreshDocusignPhaseTimeline(contractId, response.progress, response.currentProgress);
          return true;
        }
      }
      return await tryResolveDocusignProgressFromHistory(contractId, (id, hist) => {
        setDocusignProgressForContract(id, hist);
      });
    } catch (error) {
      console.warn('Falha ao carregar progresso DocuSign em andamento:', error);
      return false;
    }
  };

  const attachToInProgressDocusignSend = async (
    contractId: string,
    members?: ContractListDto[]
  ) => {
    if (!docusignUiEnabled) return;
    resetDocusignContractFinalized(contractId);
    docusignWsSessionRef.current = Date.now();
    docusignTrackingContractIdRef.current = contractId;
    docusignLastProcessingSummaryRef.current = null;
    resetAllDocusignTimeoutState(contractId);
    docusignInProgressRef.current = true;
    docusignSequentialSendActiveRef.current = true;
    setDocusignFieldsReadonly(true);
    setDocusignSending(false);

    if (members?.length) {
      members.forEach((member) => applyOptimisticDocusignProcessingInList(member));
      await syncUnifiedGroupDocusignMembersInList(members);
    } else {
      await syncContractDocusignSendInList(contractId);
    }

    const hydrated = await hydrateDocusignProgressFromApi(contractId);
    if (!hydrated) {
      const placeholder = members?.find((m) => m.id === contractId) ?? ({ id: contractId } as ContractListDto);
      setDocusignProgressForContract(
        contractId,
        buildInitialDocusignProgressForContract(placeholder, 'Envio DocuSign em andamento...')
      );
    }

    const queueLength = members?.length ?? getDocusignResolvedSendQueue().length;
    connectDocusignProgressTracking(contractId, {
      queueLength: Math.max(1, queueLength),
    });
    startDocusignProgressWatchers(contractId);
    docusignWatchingInProgressRef.current = true;
    revealDocusignCancelRetryButton();
  };

  const getDocusignActiveContact = () =>
    docusignContactMode === 'existing'
      ? getDocusignSelectedExistingContact() ?? emptyDocusignContact
      : docusignContactForm;

  const getDocusignResolvedSendQueue = (): ContractListDto[] =>
    docusignActiveSendQueueRef.current.length > 0
      ? docusignActiveSendQueueRef.current
      : docusignSendQueueRef.current;

  const isDocusignSequentialSendPreserved = () =>
    docusignSequentialSendActiveRef.current || docusignBackgroundSendRef.current;

  const buildDocusignSendPayload = (): SendContractToDocusignRequest => {
    const activeContact = getDocusignActiveContact();
    let recipientContactType = 'outro';
    if (docusignContactMode === 'existing') {
      const selected = getDocusignSelectedExistingContact();
      if (selected?.source) recipientContactType = selected.source;
    }
    return {
      recipientName: activeContact.recipientName.trim(),
      recipientPhone: activeContact.recipientPhone.trim(),
      recipientEmail: activeContact.recipientEmail.trim() || undefined,
      recipientContactType,
    };
  };

  const fetchLatestDocusignSend = async (contract: ContractListDto) => {
    const fromList = resolveLatestDocusignSendFromContract(contract);
    if (fromList) return fromList;
    try {
      const history = await contractService.getDocusignSendHistory(contract.id);
      if (history.length > 0) {
        return mapHistoryItemToLatestSummary(history[0]);
      }
    } catch (error) {
      console.error('Erro ao buscar histórico DocuSign do contrato:', error);
    }
    return undefined;
  };

  const syncContractDocusignSendInList = async (contractId: string) => {
    try {
      const history = await contractService.getDocusignSendHistory(contractId);
      if (history.length > 0) {
        const summary = mapHistoryItemToLatestSummary(history[0]);
        docusignLastProcessingSummaryRef.current = summary;
        patchContractLatestDocusignSend(contractId, summary);
        return;
      }
    } catch (error) {
      console.error('Erro ao sincronizar histórico DocuSign na listagem:', error);
    }
    const contract = contracts?.items?.find((item: ContractListDto) => item.id === contractId);
    if (!contract) return;
    const latest = await fetchLatestDocusignSend(contract);
    patchContractLatestDocusignSend(contractId, latest ?? null);
  };

  const patchDocusignResultOptimistic = (
    contractId: string,
    result: 'success' | 'error'
  ) => {
    const fromList = contracts?.items?.find((item: ContractListDto) => item.id === contractId);
    const latest =
      docusignProcessingSummaryByContractRef.current[contractId] ||
      (fromList ? resolveLatestDocusignSendFromContract(fromList) : undefined);
    if (!latest) return;
    const updated = applyDocusignSendResultToSummary(latest, result);
    docusignProcessingSummaryByContractRef.current[contractId] = updated;
    if (
      !docusignLastProcessingSummaryRef.current ||
      docusignProcessingSummaryByContractRef.current[contractId]
    ) {
      docusignLastProcessingSummaryRef.current = updated;
    }
    patchContractLatestDocusignSend(contractId, updated);
  };

  const applyOptimisticDocusignProcessingInList = (contract: ContractListDto) => {
    const payload = buildDocusignSendPayload();
    const userInfo = getUserInfo();
    const recipientContactTypeText =
      payload.recipientContactType === 'representante'
        ? 'Representante'
        : payload.recipientContactType === 'recebedor'
          ? 'Recebedor'
          : 'Contato personalizado';
    const summary = buildOptimisticDocusignProcessingSummary({
      recipientName: payload.recipientName ?? '',
      recipientPhone: payload.recipientPhone ?? '',
      recipientEmail: payload.recipientEmail ?? null,
      recipientContactType: 0,
      recipientContactTypeText,
      requestedByUserName: userInfo?.name ?? userInfo?.email ?? null,
    });
    docusignProcessingSummaryByContractRef.current[contract.id] = summary;
    docusignLastProcessingSummaryRef.current = summary;
    patchContractLatestDocusignSend(contract.id, summary);
  };

  const waitForDocusignContractCompletion = (contractId: string) =>
    new Promise<DocusignCompletionResult>((resolve) => {
      docusignCompletionResolversRef.current.set(contractId, resolve);
    });

  const waitForDocusignWsCompletion = () => {
    const contractId = docusignTrackingContractIdRef.current;
    if (!contractId) {
      return Promise.resolve('error' as const);
    }
    return waitForDocusignContractCompletion(contractId);
  };

  const mergeDocusignProgressUpdate = (
    contractId: string,
    data: RpaProgressUpdate,
    previous: RpaProgressUpdate | null | undefined
  ): RpaProgressUpdate => {
    if (data.preserveProgress && previous) {
      return {
        ...previous,
        contractId,
        stage: data.stage,
        message: data.message,
        status: 'processing',
        timestamp: data.timestamp,
        isQueued: data.isQueued ?? previous.isQueued,
        queuePosition: data.queuePosition ?? previous.queuePosition,
        queueTotal: data.queueTotal ?? previous.queueTotal,
      };
    }
    if (data.resetProgress) {
      return {
        ...data,
        contractId,
        progress: data.progress ?? 0,
        status:
          data.status === 'success' || data.status === 'error' ? data.status : 'processing',
      };
    }
    return { ...data, contractId };
  };

  const setDocusignProgressForContract = (contractId: string, data: RpaProgressUpdate) => {
    setDocusignProgressByContractId((prev) => {
      const previous = prev[contractId] ?? null;
      const merged = mergeDocusignProgressUpdate(contractId, data, previous);
      const nextEntry = { ...merged };
      if (!isMultiContractDocusignProgress()) {
        setDocusignProgress(nextEntry);
      }
      return { ...prev, [contractId]: nextEntry };
    });
  };

  const getDocusignQueueContractIds = (): string[] => {
    const fromQueue = getDocusignResolvedSendQueue().map((contract) => contract.id);
    if (fromQueue.length > 0) {
      return Array.from(new Set(fromQueue));
    }
    const trackingId = docusignTrackingContractIdRef.current;
    return trackingId ? [trackingId] : [];
  };

  const cancelRpaDocusignProcess = async (primaryContractId: string): Promise<boolean> => {
    try {
      // Use the real tracked leader ID (the one the RPA has as its task key) when available.
      const realPrimaryId = docusignTrackingContractIdRef.current ?? primaryContractId;
      const queueIds = getDocusignQueueContractIds();
      const result = await contractService.cancelDocusignProcessing(
        realPrimaryId,
        queueIds.filter((id) => id !== realPrimaryId)
      );
      console.info(
        '[DocuSign] Cancelamento solicitado via API: primary=%s queue=%o msg=%s',
        realPrimaryId,
        queueIds,
        result.message
      );
      return true;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Erro ao solicitar cancelamento da automação DocuSign';
      console.warn('Falha ao cancelar automação DocuSign:', error);
      showError(message);
      return false;
    }
  };

  const clearDocusignQueueState = () => {
    docusignSendQueueRef.current = [];
    setDocusignSendQueue([]);
    setDocusignProgressByContractId({});
    setDocusignPhaseTimeline([]);
    docusignGroupFailureIndexRef.current = null;
    docusignProcessingSummaryByContractRef.current = {};
  };

  const refreshDocusignPhaseTimeline = (
    contractId: string,
    progress?: DocusignSendProgressUpdateDto[],
    currentProgress?: DocusignSendProgressUpdateDto | null
  ) => {
    if (docusignTrackingContractIdRef.current !== contractId) {
      return;
    }
    setDocusignPhaseTimeline(
      buildTimelineFromPollResponse(contractId, progress, currentProgress ?? null)
    );
  };

  const cleanupDocusignSendTrackingAfterFinish = (options?: { clearQueue?: boolean }) => {
    const preserveForActiveSequential = isDocusignSequentialSendPreserved();

    if (!preserveForActiveSequential) {
      docusignInProgressRef.current = false;
      docusignTrackingContractIdRef.current = null;
      docusignLastProcessingSummaryRef.current = null;
      docusignSequentialSendActiveRef.current = false;
    }

    docusignWsCompletionRef.current = null;
    clearDocusignContractCompletionWaiters();
    clearDocusignTimeout();
    closeDocusignWs();

    if (options?.clearQueue && !preserveForActiveSequential) {
      clearDocusignQueueState();
      docusignActiveSendQueueRef.current = [];
      docusignSendPayloadRef.current = null;
      docusignBackgroundSendRef.current = false;
      docusignUnifiedGroupSendRef.current = false;
    }

    if (
      !showDocusignSendModalRef.current &&
      !postCreateDocusignSendActiveRef.current &&
      !preserveForActiveSequential
    ) {
      setDocusignProgress(null);
      setDocusignPhaseTimeline([]);
      setDocusignSending(false);
      setDocusignFieldsReadonly(false);
      hideDocusignRetryButton();
      docusignCancelRetryInFlightRef.current = false;
      docusignRetryActionConsumedRef.current = false;
      setDocusignSelectedContract(null);
      setDocusignContactOptions([]);
      setDocusignSelectedContactId('');
      setDocusignContactForm(emptyDocusignContact);
      setDocusignContactMode('existing');
      clearDocusignQueueState();
    }
  };

  const clearDocusignProgressTimeouts = (contractId?: string) => {
    if (contractId) {
      docusignProgressSignatureByContractRef.current.delete(contractId);
      return;
    }
    docusignProgressSignatureByContractRef.current.clear();
  };

  const clearDocusignTimeout = (contractId?: string) => {
    clearDocusignProgressTimeouts(contractId);
  };

  const resetAllDocusignTimeoutState = (contractId?: string) => {
    clearDocusignProgressTimeouts(contractId);
    if (!contractId) {
      docusignLastProgressSignatureRef.current = '';
    }
  };

  const buildInitialDocusignProgressForContract = (
    contract: ContractListDto,
    message?: string,
    queueMeta?: { isQueued: boolean; queuePosition: number; queueTotal: number }
  ): RpaProgressUpdate => {
    const resolvedMessage = message ?? 'Enviando documentos para DocuSign...';
    const isQueued =
      queueMeta?.isQueued ?? resolvedMessage.toLowerCase().includes('aguardando');
    return {
      contractId: contract.id,
      stage: isQueued ? 'queued' : 'iniciando',
      progress: isQueued ? 5 : 0,
      message: resolvedMessage,
      status: 'processing',
      timestamp: new Date().toISOString(),
      isQueued: queueMeta?.isQueued ?? isQueued,
      queuePosition: queueMeta?.queuePosition,
      queueTotal: queueMeta?.queueTotal,
    };
  };

  const resetDocusignQueueProgressFromIndex = (
    startIndex: number,
    options?: { pendingMessage?: string; queuedMessage?: string }
  ) => {
    const queue =
      docusignSendQueueRef.current.length > 0
        ? docusignSendQueueRef.current
        : docusignSelectedContract
          ? [docusignSelectedContract]
          : [];

    const pendingMessage =
      options?.pendingMessage ?? 'Reiniciando envio para DocuSign...';
    const queuedMessage = options?.queuedMessage ?? 'Aguardando na fila...';

    setDocusignProgressByContractId((prev) => {
      const next = { ...prev };
      queue.forEach((contract, index) => {
        if (index < startIndex) {
          return;
        }
        next[contract.id] = buildInitialDocusignProgressForContract(
          contract,
          index === startIndex ? pendingMessage : queuedMessage
        );
      });
      return next;
    });

    const activeContract = queue[startIndex];
    if (activeContract && !isMultiContractDocusignProgress()) {
      setDocusignProgress(
        buildInitialDocusignProgressForContract(activeContract, pendingMessage)
      );
    } else if (!queue.length) {
      setDocusignProgress(null);
    }
  };

  const startDocusignProgressWatchers = (contractId: string) => {
    docusignProgressSignatureByContractRef.current.delete(contractId);
  };

  const getRpaWsBaseUrl = () => {
    const envUrl = (process.env.REACT_APP_RPA_WS_URL || '').trim();
    if (envUrl) return envUrl.replace(/\/$/, '');
    const isHttps = window.location.protocol === 'https:';
    const host = window.location.hostname;
    return isHttps ? 'wss://srv1051503.hstgr.cloud:8080' : `ws://${host}:8080`;
  };

  useEffect(() => {
    // Fila pós-criação também alimenta "Contrato Assinado?" — mantém mesmo com DocuSign UI off.
    if (!showSuccessModal || !createdContractId) {
      postCreateDocusignQueueRef.current = [];
      setPostCreateDocusignQueue([]);
      return;
    }

    let cancelled = false;

    const loadPostCreateDocusignQueue = async () => {
      try {
        let queue: ContractListDto[] = [];
        if (createdContractGroupId) {
          const groupContracts = await contractService.getContractsByGroupId(createdContractGroupId);
          queue = sortContractsForVtmeGroupOrder(groupContracts.map(mapContractResponseToListDto));
        } else {
          const contract = await contractService.getContractById(createdContractId);
          queue = [mapContractResponseToListDto(contract)];
        }

        if (!cancelled) {
          postCreateDocusignQueueRef.current = queue;
          setPostCreateDocusignQueue(queue);
        }
      } catch (error) {
        console.error('Erro ao carregar contratos pós-criação:', error);
        if (!cancelled && createdContractId) {
          const fallback: ContractListDto = {
            id: createdContractId,
            clientRazaoSocial: '',
            clientCnpj: '',
            clientNomeFantasia: '',
            tipoContratacao: '',
            status: EContractStatus.Criado,
            dataAssinatura: '',
            dataVenda: '',
            valorTotalMensal: 0,
            administratorNome: '',
            administratorCpf: '',
            administratorEmail: '',
            totalLinhas: 0,
            createdAt: '',
            updatedAt: '',
            porteCliente: '',
            groupId: createdContractGroupId,
          };
          postCreateDocusignQueueRef.current = [fallback];
          setPostCreateDocusignQueue([fallback]);
        }
      }
    };

    void loadPostCreateDocusignQueue();

    return () => {
      cancelled = true;
    };
  }, [showSuccessModal, createdContractId, createdContractGroupId]);

  useEffect(() => {
    if (!showSuccessModal || !createdContractId || rpaStarted || !isDocusignUiEnabled()) return;

    const contactContractId = postCreateDocusignQueue[0]?.id ?? createdContractId;

    let cancelled = false;
    setDocusignLoadingContact(true);
    loadDocusignContactOptions(contactContractId)
      .catch(error => {
        if (cancelled) return;
        console.error('Erro ao carregar contatos para DocuSign:', error);
        showError('Erro ao carregar contatos para envio via DocuSign.');
      })
      .finally(() => {
        if (!cancelled) setDocusignLoadingContact(false);
      });

    return () => {
      cancelled = true;
    };
  }, [showSuccessModal, createdContractId, postCreateDocusignQueue, rpaStarted, user?.id]);

  const buildDocusignContactOptions = (contract: ContractResponseDto, qualityContact?: DocusignContactForm): DocusignContactOption[] => {
    const options: DocusignContactOption[] = [];
    const addContact = (source: string, label: string, contact: DocusignContactForm) => {
      if (!contact.recipientName || !contact.recipientPhone) return;
      options.push({
        id: `${source}-${options.length}`,
        source,
        label,
        ...contact
      });
    };

    if (contract.administrator) {
      addContact('administrador', 'Administrador', {
        recipientName: contract.administrator.nomeCompleto || '',
        recipientPhone: contract.administrator.telefone1 || contract.administrator.telefone2 || '',
        recipientEmail: contract.administrator.email || ''
      });
    }

    if (qualityContact) {
      addContact('qualidade', 'Qualidade', qualityContact);
    }

    contract.representatives?.forEach((representative, index) => {
      addContact('representante', `Representante ${index + 1}`, {
        recipientName: representative.nome || '',
        recipientPhone: representative.telefone || '',
        recipientEmail: representative.email || ''
      });
    });

    contract.authorizedReceivers?.forEach((receiver, index) => {
      addContact('recebedor', `Recebedor autorizado ${index + 1}`, {
        recipientName: receiver.nome || '',
        recipientPhone: receiver.celular || receiver.telefone || '',
        recipientEmail: receiver.email || ''
      });
    });

    return options;
  };

  useEffect(() => {
    showDocusignSendModalRef.current = showDocusignSendModal;
  }, [showDocusignSendModal]);

  useEffect(() => {
    showSuccessModalRef.current = showSuccessModal;
  }, [showSuccessModal]);

  useEffect(() => {
    if (!showSuccessModal) {
      setPostCreateAssinadoLoading(false);
      setPostCreateAssinadoConcluido(false);
      setPostCreateAssinadoFeedback(null);
    }
  }, [showSuccessModal]);

  /** DocuSign desligado: garante que nenhum poll/WS RPA permaneça ativo em background. */
  useEffect(() => {
    if (!docusignUiEnabled) {
      stopDocusignModalProgressTracking();
    }
    return () => {
      stopDocusignModalProgressTracking();
    };
  }, [docusignUiEnabled]);

  const resetDocusignModalState = (keepContract: boolean = false, force: boolean = false) => {
    if (!force && (docusignInProgressRef.current || isDocusignSequentialSendPreserved())) {
      return;
    }
    clearDocusignTimeout();
    closeDocusignWs();
    clearDocusignContractCompletionWaiters();
    docusignFinalizedContractsRef.current.clear();
    docusignSendStartedAtByContractRef.current.clear();
    stopDocusignProgressPolling();
    docusignInProgressRef.current = false;
    docusignTrackingContractIdRef.current = null;
    docusignLastProcessingSummaryRef.current = null;
    docusignFinalizedRef.current = false;
    docusignSequentialSendActiveRef.current = false;
    docusignBackgroundSendRef.current = false;
    docusignUnifiedGroupSendRef.current = false;
    docusignWatchingInProgressRef.current = false;
    docusignActiveSendQueueRef.current = [];
    docusignSendPayloadRef.current = null;
    docusignWsCompletionRef.current = null;
    setDocusignProgress(null);
    hideDocusignRetryButton();
    docusignCancelRetryInFlightRef.current = false;
    docusignRetryActionConsumedRef.current = false;
    setDocusignSending(false);
    setDocusignFieldsReadonly(false);
    if (!keepContract) {
      setDocusignSelectedContract(null);
      setDocusignContactOptions([]);
      setDocusignSelectedContactId('');
      setDocusignContactForm(emptyDocusignContact);
      setDocusignContactMode('existing');
      clearDocusignQueueState();
    }
  };

  const loadDocusignContactOptions = async (contractId: string) => {
    const [fullContract, qualityUser] = await Promise.all([
      contractService.getContractById(contractId),
      user?.id ? userService.getUserById(user.id).catch(() => null) : Promise.resolve(null)
    ]);
    const qualityPhone = qualityUser?.whatsAppCorporativo || qualityUser?.whatsAppPessoal || '';
    const qualityContact = qualityUser && qualityPhone
      ? {
          recipientName: qualityUser.nome || qualityUser.username || user?.name || '',
          recipientPhone: qualityPhone,
          recipientEmail: qualityUser.email || user?.email || ''
        }
      : undefined;
    const contactOptions = buildDocusignContactOptions(fullContract, qualityContact);
    const administradorOption = contactOptions.find((option) => option.source === 'administrador');
    setDocusignContactOptions(contactOptions);
    setDocusignSelectedContactId(administradorOption?.id || contactOptions[0]?.id || '');
    setDocusignContactForm(emptyDocusignContact);
    setDocusignContactMode(contactOptions.length > 0 ? 'existing' : 'custom');
  };

  const openDocusignSendModalInternal = async (
    contract: ContractListDto & { isGroupParent?: boolean; groupContracts?: ContractListDto[] },
    priorSend?: LatestDocusignSendSummaryDto | null,
    sendTargetsOverride?: ContractListDto[]
  ) => {
    if (!isDocusignUiEnabled()) {
      showWarning('Módulo DocuSign descontinuado.');
      return;
    }
    resetDocusignModalState(false, true);
    const targets = sendTargetsOverride ?? getDocusignSendTargets(contract);
    if (!targets.length) {
      showWarning('Todos os contratos deste grupo já foram enviados com sucesso.');
      return;
    }
    docusignSendQueueRef.current = targets;
    setDocusignSendQueue(targets);
    setDocusignProgressByContractId({});
    setDocusignModalPriorSend(priorSend ?? null);
    setDocusignSelectedContract(contract);
    setShowDocusignSendModal(true);

    if (docusignPreserveContactOnRetryRef.current) {
      docusignPreserveContactOnRetryRef.current = false;
      return;
    }

    setDocusignLoadingContact(true);
    try {
      await loadDocusignContactOptions(targets[0].id);
    } catch (error) {
      console.error('Erro ao carregar contato padrão para DocuSign:', error);
      showError('Erro ao carregar contato padrão do contrato.');
    } finally {
      setDocusignLoadingContact(false);
    }
  };

  const resolveDocusignGroupParentContract = async (
    contract: ContractListDto & { isGroupParent?: boolean; groupContracts?: ContractListDto[] }
  ): Promise<ContractListDto & { isGroupParent?: boolean; groupContracts?: ContractListDto[] }> => {
    if (contract.isGroupParent && contract.groupContracts?.length) {
      return contract;
    }

    if (!contract.groupId) {
      return contract;
    }

    try {
      const groupContracts = await contractService.getContractsByGroupId(contract.groupId);
      const members = sortContractsForVtmeGroupOrder(groupContracts.map(mapContractResponseToListDto));
      if (members.length <= 1) {
        return contract;
      }

      return {
        ...members[0],
        isGroupParent: true,
        groupContracts: members,
      };
    } catch (error) {
      console.error('Erro ao resolver contratos do grupo para DocuSign:', error);
      return contract;
    }
  };

  const handleDocusignSendIntent = async (
    contract: ContractListDto & { isGroupParent?: boolean; groupContracts?: ContractListDto[] }
  ) => {
    if (!isDocusignUiEnabled()) {
      showWarning('Módulo DocuSign descontinuado.');
      return;
    }
    const resolvedContract = await resolveDocusignGroupParentContract(contract);
    const scenario = analyzeDocusignSendScenario(resolvedContract);

    if (scenario.kind === 'all_sent') {
      setDocusignGroupMenuConfirmMode('all_sent');
      setDocusignGroupMenuParentContract(contract);
      setDocusignGroupMenuMembers(scenario.members);
      setDocusignGroupMenuSentMembers([]);
      setDocusignGroupMenuPendingMembers([]);
      setShowDocusignGroupMenuConfirm(true);
      return;
    }

    if (scenario.kind === 'partial') {
      setDocusignGroupMenuConfirmMode('partial');
      setDocusignGroupMenuParentContract(contract);
      setDocusignGroupMenuMembers(scenario.members);
      setDocusignGroupMenuSentMembers(scenario.sent);
      setDocusignGroupMenuPendingMembers(scenario.pending);
      setShowDocusignGroupMenuConfirm(true);
      return;
    }

    if (scenario.kind === 'resend_single') {
      setDocusignResendSummary(scenario.summary);
      setDocusignPendingContract(scenario.contract);
      setShowDocusignResendConfirm(true);
      return;
    }

    if (!scenario.targets.length) {
      showWarning(
        'Todos os contratos deste grupo já foram enviados ou estão em processamento pelo DocuSign.'
      );
      return;
    }

    await openDocusignSendModalInternal(resolvedContract, null, scenario.targets);
  };

  const handleOpenDocusignSendModalFromMenu = handleDocusignSendIntent;

  const handleOpenDocusignSendModal = handleDocusignSendIntent;

  const handleCancelDocusignGroupMenuConfirm = () => {
    setShowDocusignGroupMenuConfirm(false);
    setDocusignGroupMenuParentContract(null);
    setDocusignGroupMenuMembers([]);
    setDocusignGroupMenuSentMembers([]);
    setDocusignGroupMenuPendingMembers([]);
  };

  const handleConfirmDocusignGroupMenuSendAll = async () => {
    const parent = docusignGroupMenuParentContract;
    const members = docusignGroupMenuMembers;
    handleCancelDocusignGroupMenuConfirm();
    if (!parent || !members.length) return;

    await openDocusignSendModalInternal(
      parent as ContractListDto & { isGroupParent?: boolean; groupContracts?: ContractListDto[] },
      null,
      members
    );
  };

  const handleConfirmDocusignGroupMenuSendPendingOnly = async () => {
    const parent = docusignGroupMenuParentContract;
    const members = docusignGroupMenuMembers;
    handleCancelDocusignGroupMenuConfirm();
    if (!parent || !members.length) return;

    await openDocusignSendModalInternal(
      parent as ContractListDto & { isGroupParent?: boolean; groupContracts?: ContractListDto[] },
      null,
      members
    );
  };

  const handleConfirmDocusignResend = async () => {
    const contract = docusignPendingContract;
    const prior = docusignResendSummary;
    setShowDocusignResendConfirm(false);
    setDocusignResendSummary(null);
    setDocusignPendingContract(null);
    if (!contract) return;

    const resendTargets = getDocusignSendTargetsForResend(
      contract as ContractListDto & { isGroupParent?: boolean; groupContracts?: ContractListDto[] }
    );
    if (!resendTargets.length) {
      showWarning('Não foi possível identificar o contrato para reenvio pelo DocuSign.');
      return;
    }

    await openDocusignSendModalInternal(
      contract as ContractListDto & { isGroupParent?: boolean; groupContracts?: ContractListDto[] },
      prior,
      resendTargets
    );
  };

  const handleCancelDocusignResend = () => {
    setShowDocusignResendConfirm(false);
    setDocusignResendSummary(null);
    setDocusignPendingContract(null);
  };

  const handleCloseDocusignSendModal = () => {
    const sendRunningInBackground =
      docusignInProgressRef.current ||
      docusignSequentialSendActiveRef.current ||
      docusignSending ||
      docusignFieldsReadonly;
    setShowDocusignSendModal(false);

    if (sendRunningInBackground) {
      stopDocusignModalProgressTracking({ resolveWaitersAs: 'detached' });
      docusignBackgroundSendRef.current = true;
      return;
    }

    stopDocusignModalProgressTracking();
    resetDocusignModalState(false, true);
  };

  const handleClosePostCreateSuccessModal = (options?: { resetForm?: boolean }) => {
    const stillProcessing = isPostCreateDocusignStillProcessing();
    setShowSuccessModal(false);

    if (stillProcessing) {
      stopDocusignModalProgressTracking({ resolveWaitersAs: 'detached' });
      docusignBackgroundSendRef.current = true;
    } else {
      stopDocusignModalProgressTracking();
    }

    setCreatedContractId(null);
    setCreatedContractGroupId(null);
    finalizePostCreateRpaUiState();
    setDocusignContactOptions([]);
    setDocusignSelectedContactId('');
    setDocusignContactForm(emptyDocusignContact);
    setDocusignContactMode('existing');

    if (options?.resetForm !== false) {
      resetForm();
    }
    void loadContracts();
  };

  const handlePostCreateConfirmarAssinado = async () => {
    const members = postCreateDocusignQueueRef.current.length
      ? [...postCreateDocusignQueueRef.current]
      : createdContractId
        ? [{ id: createdContractId, status: EContractStatus.Criado }]
        : [];

    if (
      !members.length ||
      postCreateAssinadoLoading ||
      postCreateAssinadoConcluido ||
      !members.some((c) => contratoPendenteAssinatura(c.status))
    ) {
      return;
    }

    setPostCreateAssinadoLoading(true);
    setPostCreateAssinadoFeedback(null);
    try {
      const sorted = sortContractsForVtmeGroupOrder(members);
      const anchorId = sorted[0]?.id ?? createdContractId;
      if (!anchorId) return;

      await marcarContratosAssinadoComVerificacaoCarteira(members, {
        anchorContractId: anchorId,
        groupId: createdContractGroupId,
      });

      setPostCreateAssinadoConcluido(true);
      setPostCreateAssinadoFeedback('success');
      const updatedQueue = sortContractsForVtmeGroupOrder(
        (members as ContractListDto[]).map((c) => ({
          ...c,
          status: EContractStatus.Assinado,
        }))
      );
      postCreateDocusignQueueRef.current = updatedQueue;
      setPostCreateDocusignQueue(updatedQueue);

      const count = members.length;
      showSuccess(
        count > 1
          ? `Status de ${count} contratos do grupo atualizado(s) para "Assinado".`
          : 'Status do contrato atualizado para "Assinado".'
      );
    } catch (error) {
      console.error('Erro ao atualizar status pós-criação:', error);
      const msg =
        error instanceof Error && error.message
          ? error.message
          : 'Erro ao atualizar status do contrato';
      setPostCreateAssinadoFeedback('error');
      showError(msg);
    } finally {
      setPostCreateAssinadoLoading(false);
    }
  };

  const finalizeDocusignProgressTerminal = (
    contractId: string,
    data: RpaProgressUpdate,
    options?: { closeSocket?: boolean; skipCompletion?: boolean }
  ) => {
    markDocusignContractFinalized(contractId);
    if (options?.closeSocket !== false && !shouldForceDocusignProgressPolling(getDocusignResolvedSendQueue().length)) {
      closeDocusignWsConnection();
    }
    stopDocusignProgressPolling(contractId);
    clearDocusignProgressTimeouts(contractId);

    const result = data.status === 'success' ? 'success' : 'error';
    if (data.status === 'success') {
      hideDocusignRetryButton();
      patchDocusignResultOptimistic(contractId, 'success');
      if (docusignWatchingInProgressRef.current) {
        docusignWatchingInProgressRef.current = false;
        if (docusignUnifiedGroupSendRef.current) {
          finalizeDocusignGroupSendState(true);
          docusignUnifiedGroupSendRef.current = false;
        } else {
          docusignInProgressRef.current = false;
          docusignSequentialSendActiveRef.current = false;
          setDocusignSending(false);
          setDocusignFieldsReadonly(false);
          if (showDocusignSendModalRef.current || showSuccessModalRef.current) {
            showSuccess('Envio DocuSign concluído com sucesso.');
          }
          void loadContracts();
        }
      }
    } else {
      if (docusignWatchingInProgressRef.current) {
        docusignWatchingInProgressRef.current = false;
      }
      revealDocusignRetryButton();
      patchDocusignResultOptimistic(contractId, 'error');
    }

    void syncContractDocusignSendInList(contractId).finally(() => {
      if (docusignUnifiedGroupSendRef.current) {
        const queue = getDocusignResolvedSendQueue();
        void Promise.all(queue.map((member) => syncContractDocusignSendInList(member.id))).finally(() => {
          if (!options?.skipCompletion) {
            resolveDocusignContractCompletion(contractId, result);
            if (docusignTrackingContractIdRef.current === contractId) {
              const legacyCompletion = docusignWsCompletionRef.current;
              docusignWsCompletionRef.current = null;
              legacyCompletion?.(result);
            }
          }
        });
        return;
      }

      if (!options?.skipCompletion) {
        resolveDocusignContractCompletion(contractId, result);
        if (docusignTrackingContractIdRef.current === contractId) {
          const legacyCompletion = docusignWsCompletionRef.current;
          docusignWsCompletionRef.current = null;
          legacyCompletion?.(result);
        }
      }

      const activeQueue = getDocusignResolvedSendQueue();
      if (activeQueue.length > 1) {
        return;
      }

      const currentIndex = docusignGroupFailureIndexRef.current ?? 0;
      const isLastInSequentialQueue =
        activeQueue.length <= 1 || currentIndex >= activeQueue.length - 1;

      if (
        !showDocusignSendModalRef.current &&
        !showSuccessModalRef.current &&
        isLastInSequentialQueue
      ) {
        if (result === 'success') {
          showSuccess('Envio DocuSign concluído com sucesso.');
        } else {
          showWarning(
            'Envio DocuSign finalizado com erro. Verifique o status na listagem de contratos.'
          );
        }
      }
    });
  };

  const shouldIgnoreRegressiveDocusignProgress = (
    contractId: string,
    data: RpaProgressUpdate
  ): boolean => {
    const previous =
      docusignProgressByContractId[contractId] ??
      (docusignTrackingContractIdRef.current === contractId ? docusignProgress : null);
    if (!previous) {
      return false;
    }
    if (
      (previous.status === 'success' || previous.status === 'error') &&
      data.status === 'processing'
    ) {
      return true;
    }
    if (
      data.status === 'processing' &&
      (data.progress ?? 0) < (previous.progress ?? 0) &&
      (previous.status === 'success' || (previous.progress ?? 0) >= 100)
    ) {
      return true;
    }
    return false;
  };

  const applyDocusignProgressUpdate = (
    contractId: string,
    data: RpaProgressUpdate,
    sessionId?: number
  ) => {
    const activeSession = docusignPollSessionsRef.current.get(contractId);
    if (sessionId !== undefined && activeSession !== sessionId) return;
    if (isDocusignContractFinalized(contractId)) return;
    if (data.contractId && !isSameDocusignContractId(data.contractId, contractId)) return;
    if (!isDocusignModalProgressVisible()) {
      stopDocusignProgressPolling(contractId);
      return;
    }
    if (shouldIgnoreRegressiveDocusignProgress(contractId, data)) {
      return;
    }

    if (data.status === 'processing') {
      const sendStartedAt = docusignSendStartedAtByContractRef.current.get(contractId);
      if (sendStartedAt && data.timestamp) {
        const timestampMs = new Date(data.timestamp).getTime();
        if (!Number.isNaN(timestampMs) && timestampMs < sendStartedAt - 2_000) {
          return;
        }
      }
    }

    setDocusignProgressForContract(contractId, data);

    if (docusignTrackingContractIdRef.current === contractId) {
      setDocusignPhaseTimeline((prev) =>
        buildTimelineFromPollResponse(
          contractId,
          prev as unknown as DocusignSendProgressUpdateDto[],
          data as unknown as DocusignSendProgressUpdateDto
        )
      );
    }

    if (data.status === 'processing') {
      const signature = buildDocusignProgressSignature(data);
      const previousSignature = docusignProgressSignatureByContractRef.current.get(contractId) ?? '';
      if (signature !== previousSignature) {
        docusignProgressSignatureByContractRef.current.set(contractId, signature);
        docusignLastProgressSignatureRef.current = signature;
      }
      return;
    }

    if (data.status === 'success' || data.status === 'error') {
      finalizeDocusignProgressTerminal(contractId, data, { closeSocket: false });
    }
  };

  const mapDocusignSendProgressToUpdate = (
    contractId: string,
    current: {
      contractId?: string;
      stage?: string;
      progress?: number;
      message?: string;
      status?: string;
      timestamp?: string;
      preserveProgress?: boolean;
      resetProgress?: boolean;
      isQueued?: boolean;
      queuePosition?: number;
      queueTotal?: number;
    }
  ): RpaProgressUpdate | null => {
    if (current.contractId && !isSameDocusignContractId(current.contractId, contractId)) {
      return null;
    }

    const status =
      current.status === 'success' || current.status === 'error' ? current.status : 'processing';

    return {
      contractId,
      stage: current.stage || 'processando',
      progress: current.progress ?? 0,
      message: current.message || 'Processando envio DocuSign...',
      status,
      timestamp: current.timestamp || new Date().toISOString(),
      preserveProgress: current.preserveProgress,
      resetProgress: current.resetProgress,
      isQueued: current.isQueued,
      queuePosition: current.queuePosition,
      queueTotal: current.queueTotal,
    };
  };

  const tryResolveDocusignProgressFromHistory = async (
    contractId: string,
    onUpdate: (contractId: string, data: RpaProgressUpdate) => void
  ): Promise<boolean> => {
    try {
      const history = await contractService.getDocusignSendHistory(contractId);
      const latest = history[0];
      if (!latest) return false;

      await syncContractDocusignSendInList(contractId);
      if (latest.isProcessing) return false;

      const sendStartedAt =
        docusignSendStartedAtByContractRef.current.get(contractId) ??
        docusignProgressPollStartedAtRef.current.get(contractId);
      if (sendStartedAt && latest.requestedAt) {
        const requestedAtMs = new Date(latest.requestedAt).getTime();
        if (!Number.isNaN(requestedAtMs) && requestedAtMs < sendStartedAt - 2_000) {
          return false;
        }
      }

      if (sendStartedAt && latest.finishedAt) {
        const finishedAtMs = new Date(latest.finishedAt).getTime();
        if (!Number.isNaN(finishedAtMs) && finishedAtMs < sendStartedAt - 2_000) {
          return false;
        }
      }

      const normalizedStatus = normalizeDocusignSendStatus(latest.status, latest.isProcessing);
      const terminalStatus =
        normalizedStatus === EDocusignSendHistoryStatus.Success ? 'success' : 'error';

      onUpdate(contractId, {
        contractId,
        stage: terminalStatus === 'success' ? 'concluido' : 'erro',
        progress: terminalStatus === 'success' ? 100 : 0,
        message:
          terminalStatus === 'success'
            ? 'Envio DocuSign concluído com sucesso.'
            : latest.errorMessage?.trim() ||
              'Envio DocuSign finalizado com erro. Verifique o status na listagem de contratos.',
        status: terminalStatus,
        timestamp: latest.finishedAt || new Date().toISOString(),
      });
      return true;
    } catch (error) {
      console.warn('Falha ao resolver progresso DocuSign via histórico:', error);
      return false;
    }
  };

  const startDocusignProgressPolling = (
    contractId: string,
    onUpdate: (contractId: string, data: RpaProgressUpdate, sessionId: number) => void,
    options?: { allowParallel?: boolean }
  ) => {
    if (!docusignUiEnabled) return;
    if (!options?.allowParallel) {
      stopDocusignProgressPolling();
    } else {
      stopDocusignProgressPolling(contractId);
    }

    const sessionId = Date.now() + docusignPollSessionsRef.current.size;
    docusignPollSessionsRef.current.set(contractId, sessionId);
    docusignProgressPollStartedAtRef.current.set(contractId, Date.now());

    const scheduleNextPoll = (delayMs: number) => {
      const existing = docusignPollTimeoutsRef.current.get(contractId);
      if (existing) {
        clearTimeout(existing);
      }
      const timeoutId = setTimeout(() => {
        void pollOnce();
      }, delayMs);
      docusignPollTimeoutsRef.current.set(contractId, timeoutId);
    };

    const pollOnce = async () => {
      const activeSession = docusignPollSessionsRef.current.get(contractId);
      if (activeSession !== sessionId || isDocusignContractFinalized(contractId)) {
        stopDocusignProgressPolling(contractId);
        return;
      }

      if (!isDocusignModalProgressVisible()) {
        stopDocusignProgressPolling(contractId);
        return;
      }

      try {
        const response = await contractService.getDocusignSendProgress(contractId);
        docusignPollFailureCountsRef.current.set(contractId, 0);

        if (response.currentProgress) {
          if (response.contractId && !isSameDocusignContractId(response.contractId, contractId)) {
            const resolved = await tryResolveDocusignProgressFromHistory(contractId, (id, data) =>
              onUpdate(id, data, sessionId)
            );
            if (resolved) {
              stopDocusignProgressPolling(contractId);
              return;
            }
            scheduleNextPoll(getDocusignProgressPollIntervalMs({
              contractId,
              stage: 'processando',
              progress: 0,
              message: '',
              status: 'processing',
              timestamp: new Date().toISOString(),
              isQueued: true,
            }));
            return;
          }

          docusignPollEmptyCountsRef.current.set(contractId, 0);
          refreshDocusignPhaseTimeline(contractId, response.progress, response.currentProgress);
          const data = mapDocusignSendProgressToUpdate(contractId, response.currentProgress);
          if (!data) {
            const resolved = await tryResolveDocusignProgressFromHistory(contractId, (id, hist) =>
              onUpdate(id, hist, sessionId)
            );
            if (resolved) {
              stopDocusignProgressPolling(contractId);
              return;
            }
            scheduleNextPoll(getDocusignProgressPollIntervalMs({
              contractId,
              stage: 'processando',
              progress: 0,
              message: '',
              status: 'processing',
              timestamp: new Date().toISOString(),
              isQueued: true,
            }));
            return;
          }
          onUpdate(contractId, data, sessionId);

          if (data.status !== 'processing') {
            stopDocusignProgressPolling(contractId);
            return;
          }

          scheduleNextPoll(getDocusignProgressPollIntervalMs(data));
          return;
        }

        const emptyCount = (docusignPollEmptyCountsRef.current.get(contractId) ?? 0) + 1;
        docusignPollEmptyCountsRef.current.set(contractId, emptyCount);
        if (emptyCount >= DOCUSIGN_PROGRESS_POLL_EMPTY_BEFORE_HISTORY) {
          const resolved = await tryResolveDocusignProgressFromHistory(contractId, (id, data) =>
            onUpdate(id, data, sessionId)
          );
          if (resolved) {
            stopDocusignProgressPolling(contractId);
            return;
          }
        }

        scheduleNextPoll(DOCUSIGN_PROGRESS_POLL_INTERVAL_QUEUED_MS);
      } catch (error) {
        const failureCount = (docusignPollFailureCountsRef.current.get(contractId) ?? 0) + 1;
        docusignPollFailureCountsRef.current.set(contractId, failureCount);
        if (failureCount >= DOCUSIGN_PROGRESS_POLL_MAX_FAILURES) {
          const resolved = await tryResolveDocusignProgressFromHistory(contractId, (id, data) =>
            onUpdate(id, data, sessionId)
          );
          stopDocusignProgressPolling(contractId);
          if (!resolved) {
            console.warn(
              `Polling DocuSign interrompido para ${contractId} após falhas consecutivas:`,
              error
            );
          }
          return;
        }

        const backoff =
          DOCUSIGN_PROGRESS_POLL_INTERVAL_MS * 2 ** (failureCount - 1);
        scheduleNextPoll(backoff);
      }
    };

    void pollOnce();
  };

  const connectDocusignProgressSocket = (contractId: string) => {
    if (!docusignUiEnabled) return;
    closeDocusignWs({ stopAllPolling: true });
    const sessionId = Date.now();
    docusignWsSessionRef.current = sessionId;
    docusignPollSessionsRef.current.set(contractId, sessionId);
    resetDocusignContractFinalized(contractId);
    const wsUrl = `${getRpaWsBaseUrl()}/ws?contractId=${encodeURIComponent(contractId)}&fresh=1`;
    const ws = new WebSocket(wsUrl);
    docusignWsRef.current = ws;
    setDocusignWs(ws);

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as RpaProgressUpdate;
        if (data.contractId && !isSameDocusignContractId(data.contractId, contractId)) {
          return;
        }
        applyDocusignProgressUpdate(contractId, data, sessionId);
      } catch {
        // Ignorar mensagens inválidas do socket.
      }
    };

    ws.onclose = () => {
      docusignWsRef.current = null;
      setDocusignWs(null);
    };
  };

  const connectDocusignProgressTracking = (
    contractId: string,
    options?: { allowParallel?: boolean; queueLength?: number }
  ) => {
    if (!docusignUiEnabled) return;
    const queueLength = options?.queueLength ?? getDocusignResolvedSendQueue().length;
    const usePolling = shouldForceDocusignProgressPolling(queueLength);

    if (usePolling) {
      if (!options?.allowParallel) {
        closeDocusignWs({ stopAllPolling: true });
      }
      resetDocusignContractFinalized(contractId);
      startDocusignProgressPolling(
        contractId,
        (id, data, sessionId) => {
          applyDocusignProgressUpdate(id, data, sessionId);
        },
        { allowParallel: options?.allowParallel }
      );
      return;
    }

    connectDocusignProgressSocket(contractId);
  };

  const connectRpaProgressTracking = (contractId: string) => {
    if (!docusignUiEnabled) return;
    if (shouldUseDocusignProgressPolling()) {
      closeRpaWs();
      rpaFinalizedRef.current = false;
      startDocusignProgressPolling(contractId, (id, data, _sessionId) => {
        applyRpaProgressUpdate(id, data);
      });
      return;
    }

    connectRpaProgressSocket(contractId);
  };

  const resolveDocusignSendQueue = (startIndex = 0): ContractListDto[] => {
    const queue =
      docusignActiveSendQueueRef.current.length > 0
        ? docusignActiveSendQueueRef.current
        : docusignSendQueueRef.current.length > 0
          ? docusignSendQueueRef.current
          : docusignSelectedContract
            ? [docusignSelectedContract]
            : [];

    return queue.slice(startIndex);
  };

  const prepareDocusignGroupSendState = (queue: ContractListDto[]) => {
    if (!queue.length) return;

    docusignActiveSendQueueRef.current = queue;
    docusignSendQueueRef.current = queue;
    setDocusignSendQueue(queue);
    docusignSequentialSendActiveRef.current = true;
    docusignAbortSequentialRef.current = false;
    docusignInProgressRef.current = true;
    setDocusignFieldsReadonly(true);
    setDocusignSending(true);
    resetAllDocusignTimeoutState();
    hideDocusignRetryButton();
  };

  const finalizeDocusignGroupSendState = (allSuccess: boolean) => {
    const queue =
      docusignActiveSendQueueRef.current.length > 0
        ? docusignActiveSendQueueRef.current
        : docusignSendQueueRef.current;

    if (
      allSuccess &&
      queue.length > 0 &&
      (showDocusignSendModalRef.current || showSuccessModalRef.current)
    ) {
      showSuccess(
        queue.length > 1
          ? `Os ${queue.length} contratos foram enviados para DocuSign com sucesso.`
          : 'Contrato enviado para DocuSign com sucesso.'
      );
      void loadContracts();
    }

    docusignGroupFailureIndexRef.current = null;
    docusignSequentialSendActiveRef.current = false;
    docusignBackgroundSendRef.current = false;
    setDocusignSending(false);
    setDocusignFieldsReadonly(true);

    const isPostCreateFlow = postCreateDocusignSendActiveRef.current;
    if (isPostCreateFlow) {
      docusignInProgressRef.current = false;
      if (!showSuccessModalRef.current) {
        postCreateDocusignSendActiveRef.current = false;
        cleanupDocusignSendTrackingAfterFinish({ clearQueue: true });
      }
    } else {
      docusignInProgressRef.current = false;
      const keepProgressVisible =
        showDocusignSendModalRef.current || showSuccessModalRef.current;
      cleanupDocusignSendTrackingAfterFinish({
        clearQueue: allSuccess && !keepProgressVisible,
      });
    }
  };

  const initializeDocusignQueueProgress = (fullQueue: ContractListDto[], startIndex: number) => {
    setDocusignProgressByContractId((prev) => {
      const next = { ...prev };
      const pendingTotal = fullQueue.length - startIndex;
      fullQueue.forEach((contract, index) => {
        if (index < startIndex) {
          return;
        }
        const offset = index - startIndex;
        const isQueued = pendingTotal > 1 && offset > 0;
        const message = isQueued
          ? `Na fila (posição ${offset + 1} de ${pendingTotal}) — aguardando worker disponível`
          : undefined;
        next[contract.id] = buildInitialDocusignProgressForContract(
          contract,
          message,
          isQueued
            ? {
                isQueued: true,
                queuePosition: offset + 1,
                queueTotal: pendingTotal,
              }
            : undefined
        );
      });
      return next;
    });
  };

  const shouldUseUnifiedGroupDocusignSend = (
    parent?: (ContractListDto & { isGroupParent?: boolean; groupContracts?: ContractListDto[] }) | null,
    queue?: ContractListDto[]
  ): boolean => {
    const members =
      parent?.isGroupParent && parent.groupContracts?.length
        ? sortContractsForVtmeGroupOrder(parent.groupContracts)
        : queue ?? [];
    return Boolean(parent?.isGroupParent && members.length > 1);
  };

  const resolveUnifiedGroupSendContext = (
    parent?: (ContractListDto & { isGroupParent?: boolean; groupContracts?: ContractListDto[] }) | null,
    queueOverride?: ContractListDto[],
    groupIdOverride?: string | null
  ) => {
    const queue =
      queueOverride ??
      (docusignSendQueueRef.current.length > 0 ? docusignSendQueueRef.current : getDocusignResolvedSendQueue());
    const members =
      parent?.isGroupParent && parent.groupContracts?.length
        ? sortContractsForVtmeGroupOrder(parent.groupContracts)
        : sortContractsForVtmeGroupOrder(queue);
    const leader = members[0] ?? queue[0];
    const groupId =
      groupIdOverride ??
      parent?.groupId ??
      leader?.groupId ??
      null;
    return { members, leader, groupId };
  };

  const extractDocusignApiErrorMessage = (error: unknown, fallback: string): string => {
    if (error && typeof error === 'object' && 'response' in error) {
      const response = (error as { response?: { data?: { message?: string }; status?: number } }).response;
      if (response?.data?.message) {
        return response.data.message;
      }
    }
    if (error instanceof Error) {
      return error.message;
    }
    return fallback;
  };

  const buildUnifiedGroupDocusignProgressMessage = (members: ContractListDto[]): string => {
    const estimatedDocs = members.length * 3;
    return `Enviando ${estimatedDocs} documento(s) para DocuSign...`;
  };

  const syncUnifiedGroupDocusignMembersInList = async (members: ContractListDto[]) => {
    await Promise.all(members.map((member) => syncContractDocusignSendInList(member.id)));
  };

  const runDocusignUnifiedGroupSend = async (options?: {
    parent?: ContractListDto & { isGroupParent?: boolean; groupContracts?: ContractListDto[] };
    queue?: ContractListDto[];
    groupId?: string | null;
  }): Promise<boolean> => {
    const parent =
      options?.parent ??
      (docusignSelectedContract as ContractListDto & {
        isGroupParent?: boolean;
        groupContracts?: ContractListDto[];
      });
    const { members, leader, groupId } = resolveUnifiedGroupSendContext(
      parent,
      options?.queue,
      options?.groupId
    );

    if (!groupId || !leader || members.length <= 1) {
      return runDocusignSequentialSend(0);
    }

    docusignUnifiedGroupSendRef.current = true;
    prepareDocusignGroupSendState(members);
    setDocusignProgressByContractId({});

    const initialProgress: RpaProgressUpdate = {
      contractId: leader.id,
      stage: 'iniciando',
      progress: 0,
      message: buildUnifiedGroupDocusignProgressMessage(members),
      status: 'processing',
      timestamp: new Date().toISOString(),
    };
    setDocusignProgress(initialProgress);
    setDocusignPhaseTimeline([]);

    members.forEach((member) => applyOptimisticDocusignProcessingInList(member));

    resetDocusignContractFinalized(leader.id);
    docusignWsSessionRef.current = Date.now();
    docusignTrackingContractIdRef.current = leader.id;
    docusignLastProcessingSummaryRef.current = null;
    resetAllDocusignTimeoutState(leader.id);
    hideDocusignRetryButton();

    try {
      const payload = docusignSendPayloadRef.current ?? buildDocusignSendPayload();
      await contractService.sendGroupToDocusign(groupId, payload);
      await syncUnifiedGroupDocusignMembersInList(members);
      markDocusignSendStarted(leader.id);
      connectDocusignProgressTracking(leader.id, { queueLength: 1 });
      startDocusignProgressWatchers(leader.id);

      const result = await waitForDocusignContractCompletion(leader.id);
      if (result === 'detached') {
        docusignBackgroundSendRef.current = true;
        finalizeDocusignGroupSendState(false);
        docusignUnifiedGroupSendRef.current = false;
        return true;
      }
      if (result === 'success') {
        await syncUnifiedGroupDocusignMembersInList(members);
      }
      finalizeDocusignGroupSendState(result === 'success');
      docusignUnifiedGroupSendRef.current = false;
      return result === 'success';
    } catch (error) {
      console.error('Erro ao enviar grupo para DocuSign:', error);
      if (isDocusignConflictError(error)) {
        showWarning(
          extractDocusignApiErrorMessage(
            error,
            'Envio DocuSign do grupo já está em processamento.'
          )
        );
        await attachToInProgressDocusignSend(leader.id, members);
        docusignUnifiedGroupSendRef.current = true;
        return false;
      }

      showError(
        extractDocusignApiErrorMessage(error, 'Erro ao enviar contratos do grupo para DocuSign.')
      );
      markDocusignContractFinalized(leader.id);
      patchDocusignResultOptimistic(leader.id, 'error');
      members.slice(1).forEach((member) => patchDocusignResultOptimistic(member.id, 'error'));
      const errorProgress: RpaProgressUpdate = {
        contractId: leader.id,
        stage: 'erro',
        progress: 0,
        message: extractDocusignApiErrorMessage(
          error,
          'Erro ao enviar contratos do grupo para DocuSign.'
        ),
        status: 'error',
        timestamp: new Date().toISOString(),
      };
      setDocusignProgress(errorProgress);
      setDocusignProgressForContract(leader.id, errorProgress);
      revealDocusignRetryButton();
      finalizeDocusignGroupSendState(false);
      docusignUnifiedGroupSendRef.current = false;
      return false;
    }
  };

  const executeDocusignUnifiedGroupRetry = async (): Promise<DocusignCompletionResult> => {
    const parent = docusignSelectedContract as ContractListDto & {
      isGroupParent?: boolean;
      groupContracts?: ContractListDto[];
    };
    const { members, leader, groupId } = resolveUnifiedGroupSendContext(parent);
    if (!groupId || !leader) {
      return 'error';
    }

    const initialProgress: RpaProgressUpdate = {
      contractId: leader.id,
      stage: 'retry',
      progress: 0,
      message: buildUnifiedGroupDocusignProgressMessage(members),
      status: 'processing',
      timestamp: new Date().toISOString(),
    };

    docusignFinalizedRef.current = false;
    resetDocusignContractFinalized(leader.id);
    docusignWsSessionRef.current = Date.now();
    docusignTrackingContractIdRef.current = leader.id;
    resetAllDocusignTimeoutState(leader.id);
    hideDocusignRetryButton();
    setDocusignProgress(initialProgress);
    setDocusignProgressByContractId({});

    try {
      const payload = docusignSendPayloadRef.current ?? buildDocusignSendPayload();
      await contractService.retryGroupDocusignProcessing(groupId, payload);
      markDocusignSendStarted(leader.id);
      connectDocusignProgressTracking(leader.id, { queueLength: 1 });
      startDocusignProgressWatchers(leader.id);
      return await waitForDocusignContractCompletion(leader.id);
    } catch (error) {
      console.error('Erro ao reiniciar grupo no DocuSign:', error);
      markDocusignContractFinalized(leader.id);
      clearDocusignTimeout(leader.id);
      stopDocusignProgressPolling(leader.id);
      closeDocusignWsConnection();
      patchDocusignResultOptimistic(leader.id, 'error');
      const errorProgress: RpaProgressUpdate = {
        contractId: leader.id,
        stage: 'erro',
        progress: 0,
        message: extractDocusignApiErrorMessage(error, 'Erro ao reiniciar envio DocuSign do grupo.'),
        status: 'error',
        timestamp: new Date().toISOString(),
      };
      setDocusignProgress(errorProgress);
      setDocusignProgressForContract(leader.id, errorProgress);
      revealDocusignRetryButton();
      void syncUnifiedGroupDocusignMembersInList(members);
      return 'error';
    }
  };

  const runDocusignParallelMultiSend = async (
    startIndex = 0,
    options?: { skipPrepare?: boolean }
  ): Promise<boolean> => {
    const fullQueue =
      docusignActiveSendQueueRef.current.length > 0
        ? docusignActiveSendQueueRef.current
        : docusignSendQueueRef.current.length > 0
          ? docusignSendQueueRef.current
          : docusignSelectedContract
            ? [docusignSelectedContract]
            : [];

    if (!fullQueue.length || startIndex >= fullQueue.length) return true;

    if (!options?.skipPrepare) {
      prepareDocusignGroupSendState(fullQueue);
      initializeDocusignQueueProgress(fullQueue, startIndex);
    } else {
      docusignSequentialSendActiveRef.current = true;
      docusignInProgressRef.current = true;
      setDocusignSending(true);
      setDocusignFieldsReadonly(true);
    }

    const contractsToSend = fullQueue.slice(startIndex);
    const results = await Promise.all(
      contractsToSend.map((contract) =>
        executeDocusignSendForContract(contract, {
          queueLength: fullQueue.length,
          allowParallel: true,
        })
      )
    );

    if (results.some((result) => result === 'detached')) {
      docusignBackgroundSendRef.current = true;
      finalizeDocusignGroupSendState(false);
      return true;
    }

    if (results.every((result) => result === 'success')) {
      finalizeDocusignGroupSendState(true);
      return true;
    }

    revealDocusignRetryButton();
    finalizeDocusignGroupSendState(false);
    return false;
  };

  const runDocusignSequentialSend = async (
    startIndex = 0,
    options?: { skipPrepare?: boolean }
  ): Promise<boolean> => {
    const fullQueue =
      docusignActiveSendQueueRef.current.length > 0
        ? docusignActiveSendQueueRef.current
        : docusignSendQueueRef.current.length > 0
          ? docusignSendQueueRef.current
          : docusignSelectedContract
            ? [docusignSelectedContract]
            : [];

    if (!fullQueue.length || startIndex >= fullQueue.length) return true;

    if (fullQueue.length - startIndex > 1) {
      return runDocusignParallelMultiSend(startIndex, options);
    }

    if (!options?.skipPrepare) {
      prepareDocusignGroupSendState(fullQueue);
      initializeDocusignQueueProgress(fullQueue, startIndex);
    } else {
      docusignSequentialSendActiveRef.current = true;
      docusignInProgressRef.current = true;
      setDocusignSending(true);
      setDocusignFieldsReadonly(true);
    }

    const contract = fullQueue[startIndex];
    docusignGroupFailureIndexRef.current = startIndex;

    const result = await executeDocusignSendForContract(contract, {
      queueLength: fullQueue.length,
      allowParallel: false,
    });

    if (result === 'detached') {
      docusignBackgroundSendRef.current = true;
      finalizeDocusignGroupSendState(false);
      return true;
    }

    if (result !== 'success') {
      revealDocusignRetryButton();
      finalizeDocusignGroupSendState(false);
      return false;
    }

    finalizeDocusignGroupSendState(true);
    return true;
  };

  const executeDocusignSendForContract = async (
    contract: ContractListDto,
    options?: { queueLength?: number; allowParallel?: boolean }
  ): Promise<DocusignCompletionResult> => {
    const queueLength = options?.queueLength ?? getDocusignResolvedSendQueue().length;
    const initialProgress: RpaProgressUpdate = buildInitialDocusignProgressForContract(
      contract,
      queueLength > 1
        ? `Enviando ${getContractTypeLabel(contract)} para DocuSign...`
        : undefined
    );

    resetDocusignContractFinalized(contract.id);
    docusignWsSessionRef.current = Date.now();
    docusignTrackingContractIdRef.current = contract.id;
    docusignLastProcessingSummaryRef.current = null;
    resetAllDocusignTimeoutState(contract.id);
    hideDocusignRetryButton();
    setDocusignProgressForContract(contract.id, initialProgress);
    setDocusignPhaseTimeline([]);
    applyOptimisticDocusignProcessingInList(contract);

    try {
      const payload = docusignSendPayloadRef.current ?? buildDocusignSendPayload();
      await contractService.sendContractToDocusign(contract.id, payload);
      await syncContractDocusignSendInList(contract.id);
      markDocusignSendStarted(contract.id);
      connectDocusignProgressTracking(contract.id, {
        allowParallel: options?.allowParallel,
        queueLength,
      });
      startDocusignProgressWatchers(contract.id);

      return await waitForDocusignContractCompletion(contract.id);
    } catch (error) {
      console.error('Erro ao enviar contrato para DocuSign:', error);
      markDocusignContractFinalized(contract.id);
      clearDocusignTimeout(contract.id);
      stopDocusignProgressPolling(contract.id);
      closeDocusignWsConnection();
      patchDocusignResultOptimistic(contract.id, 'error');
      const errorProgress: RpaProgressUpdate = {
        contractId: contract.id,
        stage: 'erro',
        progress: 0,
        message: extractDocusignApiErrorMessage(error, 'Erro ao enviar para DocuSign.'),
        status: 'error',
        timestamp: new Date().toISOString(),
      };
      setDocusignProgressForContract(contract.id, errorProgress);
      revealDocusignRetryButton();
      resolveDocusignContractCompletion(contract.id, 'error');
      void syncContractDocusignSendInList(contract.id);
      return 'error';
    }
  };

  const handleSendSelectedContractToDocusign = async () => {
    if (!docusignSelectedContract || docusignSending) return;

    const activeContact = getDocusignActiveContact();
    if (!activeContact.recipientName.trim() || !activeContact.recipientPhone.trim()) {
      showError('Informe nome e telefone do destinatário para envio pelo DocuSign.');
      return;
    }

    const queue =
      docusignSendQueueRef.current.length > 0
        ? docusignSendQueueRef.current
        : getDocusignSendTargets(
            docusignSelectedContract as ContractListDto & {
              isGroupParent?: boolean;
              groupContracts?: ContractListDto[];
            }
          );

    docusignSendQueueRef.current = queue;
    setDocusignSendQueue(queue);
    docusignSendPayloadRef.current = buildDocusignSendPayload();
    docusignInProgressRef.current = true;

    const parent = docusignSelectedContract as ContractListDto & {
      isGroupParent?: boolean;
      groupContracts?: ContractListDto[];
    };
    if (shouldUseUnifiedGroupDocusignSend(parent, queue)) {
      await runDocusignUnifiedGroupSend({ parent, queue });
      return;
    }

    await runDocusignSequentialSend(0);
  };

  const executeDocusignRetryForContract = async (
    contract: ContractListDto
  ): Promise<DocusignCompletionResult> => {
    const contractLabel = getContractTypeLabel(contract);
    const initialProgress: RpaProgressUpdate = {
      contractId: contract.id,
      stage: 'retry',
      progress: 0,
      message:
        getDocusignResolvedSendQueue().length > 1
          ? `Reiniciando envio de ${contractLabel}...`
          : 'Reiniciando processamento...',
      status: 'processing',
      timestamp: new Date().toISOString(),
    };

    docusignFinalizedRef.current = false;
    resetDocusignContractFinalized(contract.id);
    docusignWsSessionRef.current = Date.now();
    docusignTrackingContractIdRef.current = contract.id;
    resetAllDocusignTimeoutState(contract.id);
    hideDocusignRetryButton();
    setDocusignProgressForContract(contract.id, initialProgress);
    setDocusignPhaseTimeline([]);

    try {
      await contractService.retryDocusignProcessing(contract.id);
      markDocusignSendStarted(contract.id);
      connectDocusignProgressTracking(contract.id, {
        queueLength: getDocusignResolvedSendQueue().length,
      });
      startDocusignProgressWatchers(contract.id);
      return await waitForDocusignContractCompletion(contract.id);
    } catch (error) {
      console.error('Erro ao reiniciar contrato no DocuSign:', error);
      markDocusignContractFinalized(contract.id);
      clearDocusignTimeout(contract.id);
      stopDocusignProgressPolling(contract.id);
      closeDocusignWsConnection();
      patchDocusignResultOptimistic(contract.id, 'error');
      const errorProgress: RpaProgressUpdate = {
        contractId: contract.id,
        stage: 'erro',
        progress: 0,
        message: `Erro ao reiniciar DocuSign: ${error instanceof Error ? error.message : 'Erro desconhecido'}`,
        status: 'error',
        timestamp: new Date().toISOString(),
      };
      setDocusignProgressForContract(contract.id, errorProgress);
      revealDocusignRetryButton();
      void syncContractDocusignSendInList(contract.id);
      return 'error';
    }
  };

  const finishDocusignSequentialSendSuccess = () => {
    docusignGroupFailureIndexRef.current = null;
    docusignSequentialSendActiveRef.current = false;
    docusignBackgroundSendRef.current = false;
    docusignInProgressRef.current = false;
    setDocusignSending(false);
    setDocusignFieldsReadonly(true);

    const isPostCreateFlow = postCreateDocusignSendActiveRef.current;
    if (isPostCreateFlow) {
      if (!showSuccessModalRef.current) {
        postCreateDocusignSendActiveRef.current = false;
        cleanupDocusignSendTrackingAfterFinish({ clearQueue: true });
      }
    } else {
      cleanupDocusignSendTrackingAfterFinish({ clearQueue: true });
    }

    const queue = getDocusignResolvedSendQueue();
    showSuccess(
      queue.length > 1
        ? 'Contratos enviados para DocuSign com sucesso.'
        : 'Contrato enviado para DocuSign com sucesso.'
    );
    void loadContracts();
  };

  const continueDocusignSequentialAfterContractSuccess = async (
    completedIndex: number
  ): Promise<boolean> => {
    const queue = getDocusignResolvedSendQueue();
    const nextIndex = completedIndex + 1;
    if (nextIndex >= queue.length) {
      finishDocusignSequentialSendSuccess();
      return true;
    }

    return runDocusignSequentialSend(nextIndex, { skipPrepare: true });
  };

  const handleRetryDocusignOnly = async () => {
    if (
      docusignRetryActionConsumedRef.current ||
      !docusignShowRetryButton ||
      docusignSending
    ) {
      return;
    }

    const startIndex = docusignGroupFailureIndexRef.current ?? 0;
    const queue = getDocusignResolvedSendQueue();
    const contract = queue[startIndex];
    if (!contract) {
      showError('Não foi possível identificar o contrato para tentar novamente.');
      return;
    }

    docusignRetryActionConsumedRef.current = true;
    hideDocusignRetryButton();

    try {
      setDocusignSending(true);
      docusignSequentialSendActiveRef.current = true;
      docusignInProgressRef.current = true;
      setDocusignFieldsReadonly(true);

      if (docusignUnifiedGroupSendRef.current || shouldUseUnifiedGroupDocusignSend(docusignSelectedContract, queue)) {
        docusignUnifiedGroupSendRef.current = true;
        const result = await executeDocusignUnifiedGroupRetry();
        if (result === 'detached') {
          docusignBackgroundSendRef.current = true;
          return;
        }
        if (result === 'success') {
          await syncUnifiedGroupDocusignMembersInList(queue);
          finalizeDocusignGroupSendState(true);
        } else {
          setDocusignSending(false);
          docusignInProgressRef.current = false;
          revealDocusignRetryButton();
        }
        docusignUnifiedGroupSendRef.current = false;
        return;
      }

      const result = await executeDocusignRetryForContract(contract);
      if (result === 'detached') {
        docusignBackgroundSendRef.current = true;
        return;
      }
      if (result === 'success') {
        await continueDocusignSequentialAfterContractSuccess(startIndex);
      } else {
        setDocusignSending(false);
        docusignInProgressRef.current = false;
        revealDocusignRetryButton();
      }
    } catch (error) {
      console.error('Erro ao tentar novamente envio DocuSign:', error);
      showError(
        error instanceof Error ? error.message : 'Erro ao tentar novamente o envio DocuSign.'
      );
      setDocusignSending(false);
      docusignInProgressRef.current = false;
      revealDocusignRetryButton();
    } finally {
      docusignRetryActionConsumedRef.current = false;
    }
  };

  const handleCancelAndRetryDocusign = async () => {
    if (
      docusignRetryActionConsumedRef.current ||
      !docusignShowCancelRetryButton ||
      docusignSending
    ) {
      return;
    }

    const queue = getDocusignResolvedSendQueue();
    const parent = docusignSelectedContract as ContractListDto & {
      isGroupParent?: boolean;
      groupContracts?: ContractListDto[];
    };
    const useUnified =
      docusignUnifiedGroupSendRef.current || shouldUseUnifiedGroupDocusignSend(parent, queue);
    const { leader, members } = useUnified
      ? resolveUnifiedGroupSendContext(parent, queue)
      : { leader: queue[0] ?? null, members: queue };

    if (!leader) {
      showError('Não foi possível identificar o contrato para cancelar e tentar novamente.');
      return;
    }

    docusignRetryActionConsumedRef.current = true;
    docusignCancelRetryInFlightRef.current = true;
    hideDocusignCancelRetryButton();
    setDocusignSending(true);

    try {
      setDocusignProgressForContract(leader.id, {
        contractId: leader.id,
        stage: 'cancelando',
        progress: 0,
        message: 'Cancelando automação e preparando nova tentativa...',
        status: 'processing',
        timestamp: new Date().toISOString(),
      });

      const cancelled = await cancelRpaDocusignProcess(leader.id);
      if (!cancelled) {
        revealDocusignCancelRetryButton();
        setDocusignSending(false);
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 500));

      resetDocusignContractFinalized(leader.id);
      docusignWsSessionRef.current = Date.now();
      docusignTrackingContractIdRef.current = leader.id;
      resetAllDocusignTimeoutState(leader.id);

      if (useUnified) {
        docusignUnifiedGroupSendRef.current = true;
        const result = await executeDocusignUnifiedGroupRetry();
        if (result === 'detached') {
          docusignBackgroundSendRef.current = true;
          return;
        }
        if (result === 'success') {
          await syncUnifiedGroupDocusignMembersInList(members);
          finalizeDocusignGroupSendState(true);
        } else {
          setDocusignSending(false);
          revealDocusignRetryButton();
        }
        docusignUnifiedGroupSendRef.current = false;
        return;
      }

      const result = await executeDocusignRetryForContract(leader);
      if (result === 'detached') {
        docusignBackgroundSendRef.current = true;
        return;
      }
      if (result === 'success') {
        await continueDocusignSequentialAfterContractSuccess(
          docusignGroupFailureIndexRef.current ?? 0
        );
      } else {
        setDocusignSending(false);
        revealDocusignRetryButton();
      }
    } catch (error) {
      console.error('Erro ao cancelar e tentar novamente envio DocuSign:', error);
      showError(
        error instanceof Error
          ? error.message
          : 'Erro ao cancelar e tentar novamente o envio DocuSign.'
      );
      setDocusignSending(false);
      revealDocusignCancelRetryButton();
    } finally {
      docusignCancelRetryInFlightRef.current = false;
      docusignRetryActionConsumedRef.current = false;
    }
  };

  // Funções de formatação para o script CQV
  const formatCnpjForReading = (cnpj: string): string => {
    if (!cnpj) return '';
    // Remove caracteres não numéricos
    const cleanCnpj = cnpj.replace(/\D/g, '');
    // Formata como XX.XXX.XXX/XXXX-XX
    if (cleanCnpj.length === 14) {
      return cleanCnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return cnpj; // Retorna original se não conseguir formatar
  };

  /** Texto padrão para o time Unity (WhatsApp) após confirmar Troca de Carteira. */
  const buildUnityTrocaCarteiraWhatsappMessage = (info: any): string => {
    const rawCnpj = (info?.clientCnpj || info?.client?.cnpj || '').toString();
    const cnpjFmt = formatCnpjForReading(rawCnpj) || (rawCnpj.trim() ? rawCnpj.trim() : 'NÃO INFORMADO');
    const nomeConsultor = (info?.user?.nome || '').trim() || 'NÃO INFORMADO';
    const vtme = (info?.user?.usuarioVtme || '').trim();
    const consultorLinha = vtme ? `${nomeConsultor} (${vtme})` : nomeConsultor;
    return `FAVOR  TROCA DE CARTEIRA\nCNPJ: ${cnpjFmt}\nCONSULTOR: ${consultorLinha}`;
  };

  const formatCpfForReading = (cpf: string): string => {
    if (!cpf) return '';
    // Remove caracteres não numéricos
    const cleanCpf = cpf.replace(/\D/g, '');
    // Formata como XXX.XXX.XXX-XX
    if (cleanCpf.length === 11) {
      return cleanCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return cpf; // Retorna original se não conseguir formatar
  };

  /** Bloco de texto para script CQV (ligação e WhatsApp): modo Fastchip vs entrega TIM + endereço cadastrado */
  const formatDeliveryDetailsForCqvScript = useCallback((info: any): string => {
    const lines: string[] = [];
    const fastchip = !!info?.fastchip;
    lines.push(
      fastchip
        ? 'FASTCHIP: envio rastreado (Gettel) para o endereço de entrega cadastrado no pedido.'
        : 'ENTREGA TIM (SEM FASTCHIP): logística conforme operadora; uso do endereço cadastral do CNPJ (Receita Federal), salvo divergência acordada no contrato.'
    );
    const da = info?.deliveryAddress;
    const hasStreet = da && String(da.endereco || '').trim().length > 0;
    const cepDigits = da?.cep ? String(da.cep).replace(/\D/g, '') : '';
    const hasCep = cepDigits.length === 8;
    if (da && (hasStreet || hasCep)) {
      const cepFmt = hasCep ? `${cepDigits.slice(0, 5)}-${cepDigits.slice(5)}` : da.cep || '—';
      lines.push('Endereço de entrega do pedido:');
      const logr = [da.endereco, da.numero].filter(Boolean).join(', ');
      lines.push(logr || '—');
      if (da.complemento) lines.push(`Complemento: ${da.complemento}`);
      lines.push(`${da.bairro || '—'}, ${da.cidade || '—'}/${da.estado || '—'} — CEP ${cepFmt}`);
      if (da.referencia) lines.push(`Referência: ${da.referencia}`);
    } else {
      lines.push(
        'Endereço de entrega: não consta completo no cadastro — confirmar na ligação o local combinado no contrato e, se for Fastchip, validar o endereço de envio do chip.'
      );
    }
    return lines.join('\n');
  }, []);

  const hasAnyWttxLine = (lines: any[]): boolean => {
    if (!lines?.length) return false;
    return lines.some(linha => {
      const name = linha.planName || linha.plano || '';
      return isWttxPlan(name);
    });
  };

  const generateCallScript = useCallback(() => {
    if (!selectedContractInfo || !selectedContractLines) return;

    const administrador =
      selectedContractInfo?.administrator?.nomeCompleto || '[RESPONSÁVEL PELO CONTRATO]';
    const cnpj = formatCnpjForReading(
      selectedContractInfo?.clientCnpj || selectedContractInfo?.client?.cnpj || ''
    );
    const cpfAdministrador = formatCpfForReading(
      selectedContractInfo?.administrator?.cpf || ''
    );
    const telefoneAdministrador = selectedContractInfo?.administrator?.telefone1
      ? formatPhoneProgressive(selectedContractInfo.administrator.telefone1)
      : '';
    const telefone2Administrador = selectedContractInfo?.administrator?.telefone2
      ? formatPhoneProgressive(selectedContractInfo.administrator.telefone2)
      : '';
    const currentUserName = (
      user?.nome?.trim() ||
      user?.name ||
      user?.username ||
      'OPERADOR CQV'
    ).trim();
    const contractCreatorName =
      selectedContractInfo?.user?.nome ||
      selectedContractInfo?.user?.username ||
      'OPERADOR CRIADOR';
    const teamName = selectedContractInfo?.user?.teamName || 'TIME NÃO IDENTIFICADO';
    const managerName = selectedContractInfo?.user?.managerName || 'GERENTE NÃO IDENTIFICADO';

    const linhasAtivas = selectedContractLines.filter(isActiveCqvLine);
    const linhasSuspensas = selectedContractLines.filter(
      (l) => !isActiveCqvLine(l)
    );
    const linhasNovas = linhasAtivas.filter(
      (l) => l.negociacao === 'Aditivo' || l.contractType === 'Aditivo'
    );
    const isOnlyReneg = linhasNovas.length === 0 && !selectedContractInfo?.groupId;

    let wttxDetalhesLinhas: string | undefined;
    if (hasAnyWttxLine(linhasAtivas)) {
      const wttxPlanosMap: Record<string, number> = {};
      linhasAtivas.forEach((linha: any) => {
        const planObj = plans?.find((p: any) => p.id === linha.planId);
        const label =
          planObj?.description?.trim() ||
          linha.planName ||
          linha.plano ||
          'Plano não identificado';
        wttxPlanosMap[label] = (wttxPlanosMap[label] || 0) + (linha.quantidade || 1);
      });
      wttxDetalhesLinhas = Object.entries(wttxPlanosMap)
        .map(([label, qty]) => `${qty} ${label}`)
        .join('\n');
    }

    const ctx = buildCqvScriptContext({
      linhasAtivas,
      linhasSuspensas,
      administrador,
      cnpj,
      cpfAdministrador,
      telefoneAdministrador,
      telefone2Administrador,
      currentUserName,
      contractCreatorName,
      teamName,
      managerName,
      deliveryBlock: formatDeliveryDetailsForCqvScript(selectedContractInfo),
      deliveryAddress: selectedContractInfo?.deliveryAddress ?? null,
      isOnlyReneg,
      fastchip: !!selectedContractInfo?.fastchip,
      hasWttx: hasAnyWttxLine(linhasAtivas),
      wttxDetalhesLinhas,
    });

    setCallScript(formatCqvCallScript(ctx));
  }, [selectedContractInfo, selectedContractLines, user, plans, formatDeliveryDetailsForCqvScript]);

  /** Gera os três textos CQV WhatsApp (padrão Gettel — uma mensagem por bloco). */
  const generateWhatsappCqvParts = useCallback(() => {
    if (!selectedContractInfo || !selectedContractLines?.length) {
      setWhatsappCqvParts({ part1: '', part2: '', part3: '' });
      return;
    }
    const administradorNome =
      selectedContractInfo?.administrator?.nomeCompleto || '[NOME_RESPONSÁVEL]';
    const primeiroNome =
      administradorNome.trim().split(/\s+/)[0]?.toUpperCase() || '[NOME]';
    const razao =
      selectedContractInfo?.clientRazaoSocial ||
      selectedContractInfo?.client?.razaoSocial ||
      '[RAZÃO_SOCIAL]';
    const cnpjRaw = (
      selectedContractInfo?.clientCnpj ||
      selectedContractInfo?.client?.cnpj ||
      ''
    ).replace(/\D/g, '');
    const cnpjLinha =
      cnpjRaw.length === 14
        ? cnpjRaw
        : formatCnpjForReading(
            selectedContractInfo?.clientCnpj || selectedContractInfo?.client?.cnpj || ''
          );
    const operador = (
      user?.nome?.trim() ||
      user?.name ||
      user?.username ||
      '[NOME_OPERADOR]'
    ).trim();
    const linhasAtivas = selectedContractLines.filter(isActiveCqvLine);
    const linhasSuspensas = selectedContractLines.filter((l) => !isActiveCqvLine(l));
    const linhasNovas = linhasAtivas.filter(
      (l) => l.negociacao === 'Aditivo' || l.contractType === 'Aditivo'
    );
    const isOnlyReneg = linhasNovas.length === 0 && !selectedContractInfo?.groupId;

    const ctx = buildCqvScriptContext({
      linhasAtivas,
      linhasSuspensas,
      administrador: administradorNome,
      cnpj: cnpjLinha,
      cpfAdministrador: '',
      currentUserName: operador,
      contractCreatorName: operador,
      teamName: '',
      managerName: '',
      deliveryBlock: formatDeliveryDetailsForCqvScript(selectedContractInfo),
      isOnlyReneg,
      fastchip: !!selectedContractInfo?.fastchip,
      hasWttx: hasAnyWttxLine(linhasAtivas),
    });

    const h = new Date().getHours();
    const saudacao = h < 12 ? 'Bom dia' : h < 18 ? 'Boa tarde' : 'Boa noite';
    const da = selectedContractInfo?.deliveryAddress;
    const cepDigits = da?.cep ? String(da.cep).replace(/\D/g, '') : '';
    const hasCep = cepDigits.length === 8;
    const hasStreet = da && String(da.endereco || '').trim().length > 0;
    let enderecoBloco = '';
    if (da && (hasStreet || hasCep)) {
      const cepFmt = hasCep ? `${cepDigits.slice(0, 5)}-${cepDigits.slice(5)}` : da.cep || '';
      const logr = [da.endereco, da.numero].filter(Boolean).join(', ');
      const comp = da.complemento ? ` - ${da.complemento}` : '';
      const bairro = da.bairro ? `${da.bairro}, ` : '';
      const cidade = da.cidade || '';
      const estado = da.estado || '';
      enderecoBloco = `📍 Endereço de entrega: ${logr}${comp}, ${bairro}${cidade}/${estado} - CEP ${cepFmt}`;
    } else {
      enderecoBloco = `📍 Endereço de entrega: endereço cadastral do CNPJ (confirmar se necessário)`;
    }

    const part1 = formatWhatsappCqvPart1({
      ctx,
      primeiroNome,
      razao,
      cnpjLinha,
      operador,
      saudacao,
      enderecoBloco,
    });

    setWhatsappCqvParts({
      part1,
      part2: WHATSAPP_CQV_MSG2_ASSINATURA,
      part3: WHATSAPP_CQV_MSG3_ENCERRAMENTO,
    });
  }, [selectedContractInfo, selectedContractLines, user, formatDeliveryDetailsForCqvScript]);

  // Função helper para verificar se contrato está cancelado
  const isContractCancelled = (contractInfo: any): boolean => {
    if (contractInfo?.isGroupParent && contractInfo?.groupContracts) {
      // Para contratos agrupados, verificar se algum está cancelado
      return contractInfo.groupContracts.some((contract: any) => 
        contract.status === EContractStatus.Cancelado || contract.status === 'Cancelado'
      );
    }
    // Para contrato individual
    return contractInfo?.status === EContractStatus.Cancelado || contractInfo?.status === 'Cancelado';
  };

  const handleRetryRpa = async () => {
    if (!createdContractId) return;
    try {
      setIsRetrying(true);
      rpaFinalizedRef.current = false;
      rpaLastProgressSignatureRef.current = '';
      setShowRetryButton(false);

      setRpaProgress({
        contractId: createdContractId,
        stage: 'iniciando',
        progress: 0,
        message: 'Reiniciando processamento...',
        status: 'processing',
        timestamp: new Date().toISOString(),
      });

      connectRpaProgressTracking(createdContractId);
      startRpaProgressWatchers(createdContractId);

      await contractService.retryDocusignProcessing(createdContractId);
    } catch (e: unknown) {
      rpaFinalizedRef.current = true;
      closeRpaWs();
      clearRpaTimeout();
      setShowRetryButton(true);
      setRpaProgress({
        contractId: createdContractId,
        stage: 'erro',
        progress: 0,
        message:
          e instanceof Error
            ? e.message
            : 'Erro ao tentar novamente o processamento.',
        status: 'error',
        timestamp: new Date().toISOString(),
      });
      showError(
        e instanceof Error ? e.message : 'Erro ao tentar novamente o processamento.'
      );
    } finally {
      setIsRetrying(false);
    }
  };

  const handleSendToDocusign = async () => {
    if (!isDocusignUiEnabled()) {
      showWarning('Módulo DocuSign descontinuado.');
      return;
    }
    if (!createdContractId || isSendingToDocusign || docusignSending) return;

    const activeContact = getDocusignActiveContact();
    if (!activeContact.recipientName.trim() || !activeContact.recipientPhone.trim()) {
      showError('Informe nome e telefone do destinatário para envio pelo DocuSign.');
      return;
    }

    const queue =
      postCreateDocusignQueueRef.current.length > 0
        ? postCreateDocusignQueueRef.current
        : [
            {
              id: createdContractId,
              clientRazaoSocial: '',
              clientCnpj: '',
              clientNomeFantasia: '',
              tipoContratacao: '',
              status: EContractStatus.Criado,
              dataAssinatura: '',
              dataVenda: '',
              valorTotalMensal: 0,
              administratorNome: '',
              administratorCpf: '',
              administratorEmail: '',
              totalLinhas: 0,
              createdAt: '',
              updatedAt: '',
              porteCliente: '',
              groupId: createdContractGroupId,
            } as ContractListDto,
          ];

    docusignSendQueueRef.current = queue;
    setDocusignSendQueue(queue);
    docusignSendPayloadRef.current = buildDocusignSendPayload();

    postCreateDocusignSendActiveRef.current = true;
    setIsSendingToDocusign(true);
    setRpaStarted(true);
    hideDocusignRetryButton();
    setShowRetryButton(false);

    try {
      const completed =
        createdContractGroupId && queue.length > 1
          ? await runDocusignUnifiedGroupSend({
              queue,
              groupId: createdContractGroupId,
            })
          : await runDocusignSequentialSend(0);
      if (!completed) {
        revealDocusignRetryButton();
      }
    } catch (error) {
      console.error('Erro ao enviar para DocuSign:', error);
      showError(
        extractDocusignApiErrorMessage(error, 'Erro ao enviar contrato(s) para DocuSign.')
      );
      revealDocusignRetryButton();
    } finally {
      setIsSendingToDocusign(false);
    }
  };

  const handleCancelAndRetry = async () => {
    if (!createdContractId || isRetrying) return;

    try {
      setIsRetrying(true);
      rpaFinalizedRef.current = true;
      rpaLastProgressSignatureRef.current = '';
      closeRpaWs();
      setShowRetryButton(false);

      setRpaProgress({
        contractId: createdContractId,
        stage: 'cancelando',
        progress: 0,
        message: 'Cancelando automação e preparando nova tentativa...',
        status: 'processing',
        timestamp: new Date().toISOString(),
      });

      await contractService.cancelDocusignProcessing(createdContractId);
      await new Promise((resolve) => setTimeout(resolve, 500));

      rpaFinalizedRef.current = false;
      setRpaProgress({
        contractId: createdContractId,
        stage: 'iniciando',
        progress: 0,
        message: 'Reiniciando processamento...',
        status: 'processing',
        timestamp: new Date().toISOString(),
      });

      connectRpaProgressTracking(createdContractId);
      startRpaProgressWatchers(createdContractId);
      await contractService.retryDocusignProcessing(createdContractId);
    } catch (error) {
      rpaFinalizedRef.current = true;
      closeRpaWs();
      setShowRetryButton(true);
      setRpaProgress({
        contractId: createdContractId,
        stage: 'erro',
        progress: 0,
        message:
          error instanceof Error
            ? error.message
            : 'Erro ao cancelar e reiniciar o processamento.',
        status: 'error',
        timestamp: new Date().toISOString(),
      });
      showError(
        error instanceof Error
          ? error.message
          : 'Erro ao cancelar e reiniciar o processamento.'
      );
    } finally {
      setIsRetrying(false);
    }
  };

  // Estados para busca de clientes
  const [clients, setClients] = useState<ClientListResponse[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientResponse | null>(null);

  const reloadSelectedClientObservations = useCallback(async () => {
    if (!selectedClient?.id) {
      setSelectedClientObservations([]);
      return;
    }
    setSelectedClientObservationsLoading(true);
    try {
      const items = await clientService.getClientObservations(selectedClient.id);
      setSelectedClientObservations(items);
    } catch {
      setSelectedClientObservations([]);
    } finally {
      setSelectedClientObservationsLoading(false);
    }
  }, [selectedClient?.id]);

  useEffect(() => {
    if (selectedClient?.id) {
      void reloadSelectedClientObservations();
    } else {
      setSelectedClientObservations([]);
      setSelectedClientObservationsLoading(false);
    }
  }, [selectedClient?.id, reloadSelectedClientObservations]);

  // Estados para partners do cliente
  const [clientPartners, setClientPartners] = useState<ClientPartnerResponse[]>([]);
  const [showQsaModal, setShowQsaModal] = useState(false);

  const [clientSearchQuery, setClientSearchQuery] = useState('');

  // useEffect para gerar script automaticamente quando dados do contrato mudarem
  useEffect(() => {
    const lineCount = selectedContractLines?.length ?? 0;
    if (
      !shouldGenerateCqvScripts({
        blockedReason: linesModalCqvBlockedReason,
        hasContractInfo: !!selectedContractInfo,
        lineCount,
      })
    ) {
      if (!linesModalCqvBlockedReason) {
        setCallScript('');
        setWhatsappCqvParts({ ...EMPTY_WHATSAPP_CQV_PARTS });
      }
      return;
    }
    generateCallScript();
    generateWhatsappCqvParts();
  }, [
    selectedContractInfo,
    selectedContractLines,
    linesModalCqvBlockedReason,
    generateCallScript,
    generateWhatsappCqvParts,
  ]);

  const copyTextToClipboard = async (text: string, okMessage?: string) => {
    try {
      await navigator.clipboard.writeText(text);
      if (okMessage) showSuccess(okMessage);
    } catch {
      showError('Não foi possível copiar. Selecione o texto manualmente.');
    }
  };

  // Hook do formulário (estado completo + DDD/ICCID)
  const contractForm = useContractForm({
    clientEstado: selectedClient?.estado,
    backendAllocationEnabled,
  });
  /** Quando true, não sobrescrever `token` com o resultado de suggested-sale-token. */
  const saleTokenLockedRef = useRef(false);
  const {
    availableICcIds,
    iccidsLoading,
    iccidsError,
    dddOptions,
    setDddOptions,
    dddOptionsEdit,
    setDddOptionsEdit,
    getValidDddsWithICcIdFallback,
    fastchipDisabled,
    setFastchipDisabled,
    fastchipDisabledReason,
    setFastchipDisabledReason,
    iccidsTransientError,
    retryLoadIccids,
    reset: formReset,
    loadFromContract: formLoadFromContract,
    tipoFatura,
    setTipoFatura,
    envioFatura,
    setEnvioFatura,
    origemVenda,
    setOrigemVenda,
    cep,
    setCep,
    cidade,
    setCidade,
    estado,
    setEstado,
    endereco,
    setEndereco,
    numero,
    setNumero,
    bairro,
    setBairro,
    complemento,
    setComplemento,
    referencia,
    setReferencia,
    cepLoading,
    setCepLoading,
    adminNomeCompleto,
    setAdminNomeCompleto,
    adminCpf,
    setAdminCpf,
    adminRg,
    setAdminRg,
    adminEmail,
    setAdminEmail,
    adminTelefone1,
    setAdminTelefone1,
    adminTelefone2,
    setAdminTelefone2,
    adminSource,
    setAdminSource,
    tipoContratacao,
    setTipoContratacao,
    valorTotalMensal,
    setValorTotalMensal,
    diaVencimento,
    setDiaVencimento,
    numeroP2B,
    setNumeroP2B,
    comprometimentoMinimo,
    setComprometimentoMinimo,
    fastchip,
    setFastchip,
    temSuspensao,
    setTemSuspensao,
    representantes,
    setRepresentantes,
    representanteManual,
    setRepresentanteManual,
    selectedRepresentante,
    setSelectedRepresentante,
    recebedores,
    setRecebedores,
    recebedorManual,
    setRecebedorManual,
    testemunhas,
    setTestemunhas,
    testemunhaAtual,
    setTestemunhaAtual,
    linhas,
    setLinhas,
    linhaAtual,
    setLinhaAtual,
    linhasParaDesativar,
    setLinhasParaDesativar,
    editingLinha,
    setEditingLinha,
    showEditLinhaModal,
    setShowEditLinhaModal,
    linhaEditando,
    setLinhaEditando,
    needsIeMonitoring,
    setNeedsIeMonitoring,
    ieBypassConfirmed,
    setIeBypassConfirmed,
    ieBypassReason,
    setIeBypassReason,
    isEditingExistingContract,
    setIsEditingExistingContract,
    tabValidationError,
    setTabValidationError,
    error,
    setError,
    isPrimeiraVenda,
    setIsPrimeiraVenda,
    token,
    setToken,
  } = contractForm;

  const [suggestedClusterDisplay, setSuggestedClusterDisplay] = useState('');
  const [suggestedClusterLoading, setSuggestedClusterLoading] = useState(false);
  const [portabilidadeConflict, setPortabilidadeConflict] = useState<PortabilidadeConflict | null>(null);
  const [portabilidadeConflictAction, setPortabilidadeConflictAction] = useState<'add' | 'edit' | null>(null);
  const fastchipFlowEnabled = fastchip && !fastchipDisabled;
  const backendFastchipAllocationMode = fastchipFlowEnabled && backendAllocationEnabled;

  // TEMPORÁRIO (pedido Eder — bater meta): DH por entrega pode suspender sem Fastchip.
  // Critério único (token DH ou token vazio + UF RJ/MG/ES). ER2 operacional não entra.
  const dhDeliverySuspensionAllowed = useMemo(
    () =>
      allowsDhDeliverySuspensionWithoutFastchip({
        clusterToken: token,
        clientEstado: selectedClient?.estado,
      }),
    [token, selectedClient?.estado]
  );
  // Mesmo critério do toggle/UI — evita save bloquear quando o modal já liberou por mercado.
  const suspensionAllowedForContract = useMemo(
    () => allowsSuspension(fastchip, token, selectedClient?.estado),
    [fastchip, token, selectedClient?.estado]
  );

  const suspensionPolicyQuery = useSuspensionBlockStatusQuery();
  const suspensionPolicy = suspensionPolicyQuery.data;

  const fastchipAutoSaleToken = useMemo(() => {
    if (!fastchip) return '';
    return resolveFastchipClusterTokenForAutoFill({
      linhas,
      linhaAtualDdd: linhaAtual.ddd,
      adminTelefone1,
      adminTelefone2,
      clientTelefone1: selectedClient?.telefone1,
      clientTelefone2: selectedClient?.telefone2,
      clientEstado: selectedClient?.estado,
      apiSuggestion: suggestedClusterDisplay,
      currentFormToken: token,
      teamClusterPreference: (user?.rjMgEsFastchipClusterPreference as 'ER2' | 'DH' | null | undefined) ?? null,
    });
  }, [
    fastchip,
    linhas,
    linhaAtual.ddd,
    adminTelefone1,
    adminTelefone2,
    selectedClient?.telefone1,
    selectedClient?.telefone2,
    selectedClient?.estado,
    suggestedClusterDisplay,
    token,
    user?.rjMgEsFastchipClusterPreference,
  ]);

  const nonFastchipAutoSaleToken = useMemo(() => {
    if (fastchip) return '';
    return resolveNonFastchipClusterTokenForAutoFill({
      clientEstado: selectedClient?.estado,
      clientTelefone1: selectedClient?.telefone1,
      clientTelefone2: selectedClient?.telefone2,
      apiSuggestion: suggestedClusterDisplay,
    });
  }, [
    fastchip,
    selectedClient?.estado,
    selectedClient?.telefone1,
    selectedClient?.telefone2,
    suggestedClusterDisplay,
  ]);

  const clusterSuggestionSource = useMemo(() => {
    if (saleTokenLockedRef.current) return 'Manual';
    if (fastchip) {
      return resolveFastchipClusterSuggestionSource({
        linhas,
        linhaAtualDdd: linhaAtual.ddd,
        adminTelefone1,
        adminTelefone2,
        clientTelefone1: selectedClient?.telefone1,
        clientTelefone2: selectedClient?.telefone2,
        clientEstado: selectedClient?.estado,
        apiSuggestion: suggestedClusterDisplay,
      });
    }
    return resolveNonFastchipClusterSuggestionSource({
      clientEstado: selectedClient?.estado,
      clientTelefone1: selectedClient?.telefone1,
      clientTelefone2: selectedClient?.telefone2,
      apiSuggestion: suggestedClusterDisplay,
    });
  }, [
    fastchip,
    linhas,
    linhaAtual.ddd,
    adminTelefone1,
    adminTelefone2,
    selectedClient?.telefone1,
    selectedClient?.telefone2,
    selectedClient?.estado,
    suggestedClusterDisplay,
    token,
  ]);

  const suggestedClusterBanner = useMemo(
    () =>
      resolveSuggestedClusterBanner({
        apiSuggestion: suggestedClusterDisplay,
        localToken: fastchip ? fastchipAutoSaleToken : nonFastchipAutoSaleToken,
        localSource: clusterSuggestionSource,
      }),
    [
      suggestedClusterDisplay,
      fastchip,
      fastchipAutoSaleToken,
      nonFastchipAutoSaleToken,
      clusterSuggestionSource,
    ]
  );

  const clusterConfirmClientPhoneDisplay = useMemo(() => {
    const parts = [selectedClient?.telefone1, selectedClient?.telefone2]
      .map((p) => (p ?? '').trim())
      .filter(Boolean);
    return parts.length > 0 ? parts.join(' / ') : '—';
  }, [selectedClient?.telefone1, selectedClient?.telefone2]);

  useEffect(() => {
    if (!fastchip) return;
    if (
      saleTokenLockedRef.current &&
      !normalizeContractSaleTokenForForm(token, true) &&
      mapNonFastchipOperationalToSeniorCanonical(token)
    ) {
      saleTokenLockedRef.current = false;
    }
    if (saleTokenLockedRef.current || !fastchipAutoSaleToken) return;
    if (normalizeContractSaleTokenForForm(token, true) === fastchipAutoSaleToken) return;
    setToken(fastchipAutoSaleToken);
  }, [fastchip, fastchipAutoSaleToken, token, setToken]);

  useEffect(() => {
    if (saleTokenLockedRef.current || fastchip || !nonFastchipAutoSaleToken) return;
    if (normalizeContractSaleTokenForForm(token, false) === nonFastchipAutoSaleToken) return;
    setToken(nonFastchipAutoSaleToken);
  }, [fastchip, nonFastchipAutoSaleToken, token, setToken]);

  useEffect(() => {
    const clientId = selectedClient?.id;
    if (!clientId) {
      setSuggestedClusterDisplay('');
      setSuggestedClusterLoading(false);
      return;
    }
    let cancelled = false;
    setSuggestedClusterLoading(true);
    fetchSuggestedSaleToken(clientId, {
        deliveryCidade: cidade?.trim() || undefined,
        deliveryEstado: estado?.trim() || undefined,
      })
      .then((t: string) => {
        const trimmed = t.trim();
        if (!cancelled) {
          setSuggestedClusterDisplay(trimmed);
        }
      })
      .catch(() => {
        if (!cancelled) setSuggestedClusterDisplay('');
      })
      .finally(() => {
        if (!cancelled) setSuggestedClusterLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedClient?.id, cidade, estado, fastchip, temSuspensao, setToken]);

  const prClusterDddMismatchWarning = useMemo(() => {
    if (!fastchip || selectedClient?.estado !== 'PR') return null;
    const firstAditivo = linhas.find(
      (l) => l.negociacao === 'Aditivo' && !l.excluirDoContrato && (l.ddd ?? '').trim()
    );
    const implied = inferPrSeniorClusterTokenFromAditivoDdd(
      firstAditivo?.ddd,
      selectedClient?.cidade
    );
    if (!implied) return null;
    const t = normalizeContractSaleTokenForForm(token, true);
    if (!t || !['MH', 'E.R.GESTAO'].includes(t)) return null;
    if (t === implied) return null;
    return `No PR, a primeira linha Aditivo com DDD ${firstAditivo?.ddd} indica cluster Senior «${implied}», mas o token selecionado é «${t}». Ajuste o select ou o DDD da linha.`;
  }, [fastchip, selectedClient?.estado, selectedClient?.cidade, linhas, token]);

  useEffect(() => {
    if (saleTokenLockedRef.current) return;
    if (!fastchip || selectedClient?.estado !== 'PR') return;
    const first = linhas.find(
      (l) => l.negociacao === 'Aditivo' && !l.excluirDoContrato && (l.ddd ?? '').trim()
    );
    const implied = inferPrSeniorClusterTokenFromAditivoDdd(first?.ddd, selectedClient?.cidade);
    if (!implied) return;
    const cur = normalizeContractSaleTokenForForm(token, true);
    const prPair = new Set(['MH', 'E.R.GESTAO']);
    if (cur && !prPair.has(cur)) return;
    if (implied === 'MH' && cur === 'E.R.GESTAO') {
      setToken('MH');
      return;
    }
    if (implied === 'E.R.GESTAO' && cur === 'MH') {
      setToken('E.R.GESTAO');
    }
  }, [fastchip, selectedClient?.estado, selectedClient?.cidade, linhas, token, setToken]);

  // Cluster PR fixado pelas linhas já adicionadas ao contrato (prioridade: ICCID > DDD da 1ª linha)
  const fixedPrCluster = useMemo((): string | undefined => {
    if (selectedClient?.estado !== 'PR' || !fastchip) return undefined;
    // Prioridade 1: cluster do primeiro ICCID já atribuído
    const firstWithCcid = linhas.find(l => l.chipCcid);
    if (firstWithCcid?.chipCcid) {
      const iccid = availableICcIds.find(ic => ic.ccId === firstWithCcid.chipCcid);
      const prInfo = iccid?.estadosInfo?.find((e: { estado: string; cluster?: string | null }) => e.estado === 'PR');
      if (prInfo?.cluster) return prInfo.cluster;
    }
    // Prioridade 2: DDD da primeira linha adicionada
    const firstAditivoDdd = linhas.find(
      (l) => l.negociacao === 'Aditivo' && !l.excluirDoContrato && (l.ddd ?? '').trim()
    )?.ddd;
    if (firstAditivoDdd === '42') {
      return isPrMhClusterCity(selectedClient?.cidade) ? 'MH' : 'ER';
    }
    if (firstAditivoDdd) return 'ER';
    return undefined;
  }, [selectedClient?.estado, selectedClient?.cidade, fastchip, linhas, availableICcIds]);

  const fixedHbCpCluster = useMemo((): string | undefined => {
    if (!fastchip || !isHbCpMarketEstado(selectedClient?.estado)) return undefined;
    const fromToken = resolveHbCpClusterFromContractToken(token);
    if (fromToken) return fromToken;
    const firstAditivo = linhas.find(
      (l) => l.negociacao === 'Aditivo' && !l.excluirDoContrato && l.chipCcid
    );
    if (firstAditivo?.chipCcid) {
      const iccid = availableICcIds.find((ic) => ic.ccId === firstAditivo.chipCcid);
      if (iccidHasCluster(iccid?.estadosInfo, FASTCHIP_CLUSTER_HB)) return FASTCHIP_CLUSTER_HB;
      if (iccidHasCluster(iccid?.estadosInfo, FASTCHIP_CLUSTER_CP)) return FASTCHIP_CLUSTER_CP;
    }
    return undefined;
  }, [fastchip, selectedClient?.estado, linhas, availableICcIds, token]);

  // Cluster efetivo: fixedPrCluster ou inferido pelo DDD da linha em construção
  const effectivePrCluster = useMemo((): string | undefined => {
    if (fixedPrCluster) return fixedPrCluster;
    if (linhaAtual.ddd === '42') {
      return isPrMhClusterCity(selectedClient?.cidade) ? 'MH' : 'ER';
    }
    if (linhaAtual.ddd) return 'ER';
    return undefined;
  }, [fixedPrCluster, linhaAtual.ddd, selectedClient?.cidade]);

  const effectiveHbCpCluster = useMemo((): string | undefined => {
    if (fixedHbCpCluster) return fixedHbCpCluster;
    const fromToken = resolveHbCpClusterFromContractToken(token);
    if (fromToken) return fromToken;
    return undefined;
  }, [fixedHbCpCluster, token]);

  const esimStockContext = useMemo(
    () => ({
      clientEstado: selectedClient?.estado,
      prCluster: effectivePrCluster,
      hbCpCluster: effectiveHbCpCluster,
    }),
    [selectedClient?.estado, effectivePrCluster, effectiveHbCpCluster]
  );

  const [physicalIccidPool, setPhysicalIccidPool] = useState<ICcIdResponse[]>([]);
  const [esimIccidPool, setEsimIccidPool] = useState<ICcIdResponse[]>([]);
  const [iccidPoolsLoading, setIccidPoolsLoading] = useState(false);
  const [iccidPoolRefreshNonce, setIccidPoolRefreshNonce] = useState(0);
  const [iccidPoolsLoadedNonce, setIccidPoolsLoadedNonce] = useState(0);

  useEffect(() => {
    if (!selectedClient?.estado || !fastchipFlowEnabled || backendFastchipAllocationMode) {
      setPhysicalIccidPool([]);
      setEsimIccidPool([]);
      setIccidPoolsLoading(false);
      setIccidPoolsLoadedNonce(0);
      return;
    }

    const estadoUpper = selectedClient.estado.toUpperCase();
    const rjMgEsSearch = resolveRjMgEsFastchipIccidSearch(estadoUpper);
    const estadoParaBusca = rjMgEsSearch?.estadoFilter ?? estadoUpper;
    let cancelled = false;
    setIccidPoolsLoading(true);

    const buildFilters = (isEsim: boolean) => {
      const filters: {
        estado: string;
        disponivel: boolean;
        isEsim: boolean;
        cluster?: string;
      } = {
        estado: estadoParaBusca,
        disponivel: true,
        isEsim,
      };

      if (estadoUpper === 'PR') {
        const cluster = effectivePrCluster ?? 'ER';
        if (cluster === 'ER' || cluster === 'MH') {
          filters.cluster = cluster;
        }
      } else if (isHbCpMarketEstado(estadoUpper) && effectiveHbCpCluster) {
        filters.cluster = effectiveHbCpCluster;
      } else if (rjMgEsSearch?.cluster) {
        filters.cluster = rjMgEsSearch.cluster;
      }

      return filters;
    };

    const run = async () => {
      try {
        const [physicalResult, esimResult] = await Promise.all([
          icCidService.searchICcIds(
            buildFilters(false),
            1,
            FASTCHIP_ICCID_POOL_PAGE_SIZE,
            undefined,
            false,
            { compact: true }
          ),
          icCidService.searchICcIds(
            buildFilters(true),
            1,
            FASTCHIP_ICCID_POOL_PAGE_SIZE,
            undefined,
            false,
            { compact: true }
          ),
        ]);
        if (!cancelled) {
          setPhysicalIccidPool(physicalResult.items.filter((ic) => ic.disponivel === true));
          setEsimIccidPool(esimResult.items.filter((ic) => ic.disponivel === true));
        }
      } catch {
        if (!cancelled) {
          setPhysicalIccidPool([]);
          setEsimIccidPool([]);
        }
      } finally {
        if (!cancelled) {
          setIccidPoolsLoading(false);
          setIccidPoolsLoadedNonce(iccidPoolRefreshNonce);
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [
    selectedClient?.estado,
    fastchipFlowEnabled,
    backendFastchipAllocationMode,
    effectivePrCluster,
    effectiveHbCpCluster,
  ]);

  const filterIccidPoolForCluster = useCallback(
    (
      source: ICcIdResponse[],
      options?: { prCluster?: string; hbCpCluster?: string; isEsim?: boolean }
    ): ICcIdResponse[] => {
      let filtered = source;
      const prCluster = options?.prCluster;
      const hbCpCluster = options?.hbCpCluster;

      if (selectedClient?.estado === 'PR' && fastchipFlowEnabled && prCluster) {
        filtered = filtered.filter((ic) => {
          const info = ic.estadosInfo?.find(
            (e: { estado: string; cluster?: string | null }) => e.estado === 'PR'
          );
          if (prCluster === 'ER') {
            return !info?.cluster || info.cluster === 'ER';
          }
          return info?.cluster === prCluster;
        });
      }

      if (isHbCpMarketEstado(selectedClient?.estado) && fastchipFlowEnabled && hbCpCluster) {
        filtered = filtered.filter((ic) => iccidHasCluster(ic.estadosInfo, hbCpCluster));
      }

      const rjMgEsSearch = resolveRjMgEsFastchipIccidSearch(selectedClient?.estado);
      if (rjMgEsSearch?.usesDhCluster && fastchipFlowEnabled) {
        filtered = filtered.filter((ic) => iccidHasDhCluster(ic.estadosInfo));
      }

      if (options?.isEsim === true) {
        filtered = filtered.filter((ic) => ic.isEsim === true);
      } else if (options?.isEsim === false) {
        filtered = filtered.filter((ic) => !ic.isEsim);
      }

      return filtered;
    },
    [fastchipFlowEnabled, selectedClient?.estado]
  );

  const resolvePrClusterForLine = useCallback(
    (lineDdd?: string): string | undefined => {
      if (fixedPrCluster) return fixedPrCluster;
      if (lineDdd === '42') {
        return isPrMhClusterCity(selectedClient?.cidade) ? 'MH' : 'ER';
      }
      if (lineDdd) return 'ER';
      return effectivePrCluster;
    },
    [fixedPrCluster, selectedClient?.cidade, effectivePrCluster]
  );

  const getTypedIccidPool = useCallback(
    (isEsim?: boolean): ICcIdResponse[] => {
      if (isEsim === true) {
        return esimIccidPool.length > 0 ? esimIccidPool : availableICcIds;
      }
      // Físico (false) ou default do formulário: pool tipado já vem com cluster=DH na API.
      return physicalIccidPool.length > 0 ? physicalIccidPool : availableICcIds;
    },
    [availableICcIds, esimIccidPool, physicalIccidPool]
  );

  const buildIccidPoolForLineDraft = useCallback(
    (line: Pick<ContractLineFormItem, 'ddd' | 'isEsim'>): ICcIdResponse[] => {
      const prCluster = resolvePrClusterForLine(line.ddd);
      const source = getTypedIccidPool(line.isEsim);
      return filterIccidPoolForCluster(source, {
        prCluster,
        hbCpCluster: effectiveHbCpCluster,
        isEsim: line.isEsim,
      });
    },
    [effectiveHbCpCluster, filterIccidPoolForCluster, getTypedIccidPool, resolvePrClusterForLine]
  );

  const esimStockSegment = useMemo(
    () => resolveEsimStockSegment(fastchipStockBalance, esimStockContext),
    [fastchipStockBalance, esimStockContext]
  );

  const esimToggleAvailable = useMemo(() => {
    if (!selectedClient?.estado || !fastchipFlowEnabled) return false;
    if (esimStockSegment) {
      return isEsimStockSegmentAvailable(esimStockSegment);
    }
    return esimIccidPool.length > 0 || isEsimStockSegmentAvailable(esimStockSegment);
  }, [selectedClient?.estado, fastchipFlowEnabled, esimStockSegment, esimIccidPool]);

  const esimUnavailableMessage = useMemo(
    () => buildEsimUnavailableMessage(esimStockContext),
    [esimStockContext]
  );

  useEffect(() => {
    if (!esimToggleAvailable && linhaAtual.isEsim) {
      setLinhaAtual((prev) => ({ ...prev, isEsim: false, chipCcid: '' }));
    }
  }, [esimToggleAvailable, linhaAtual.isEsim, setLinhaAtual]);

  const linesModalHasEsim = useMemo(
    () => contractHasEsimLines(selectedContractLines),
    [selectedContractLines]
  );

  const linesModalDeliveryBadge = useMemo(
    () =>
      resolveFastchipDeliveryBadge({
        fastchip: !!selectedContractInfo?.fastchip,
        hasEsim: linesModalHasEsim,
      }),
    [selectedContractInfo?.fastchip, linesModalHasEsim]
  );

  // ICCIDs filtrados por e-SIM e cluster PR para a linha atual em edição
  const availableICcIdsForLine = useMemo(
    () =>
      buildIccidPoolForLineDraft({
        ddd: linhaAtual.ddd,
        isEsim: linhaAtual.isEsim,
      }),
    [buildIccidPoolForLineDraft, linhaAtual.ddd, linhaAtual.isEsim]
  );

  // Filtrar planos quando negociação, aparelho ou aba Linhas mudarem
  useEffect(() => {
    if (linhaAtual.negociacao && plans.length > 0) {
      filtrarPlanos(linhaAtual.negociacao, linhaAtual.aparelho);
    }
  }, [linhaAtual.negociacao, linhaAtual.aparelho, plans.length, isCreateModalOpen, activeTab]);

  // Forçar negociação para "Aditivo" quando for 1º Venda
  useEffect(() => {
    if (isPrimeiraVenda) {
      setLinhaAtual(prev => ({ ...prev, negociacao: 'Aditivo' }));
    }
  }, [isPrimeiraVenda]);

  // Definir valor do chip como 0,00 quando fastchip for true e limpar ICCIDs quando fastchip for false
  useEffect(() => {
    if (fastchipFlowEnabled) {
      setLinhaAtual(prev => ({ ...prev, valorChip: '0.00' }));
      if (linhaEditando) {
        setLinhaEditando(prev => ({ ...prev, valorChip: '0.00' }));
      }
    } else {
      // Limpar ICCIDs quando fastchip for desmarcado
      setLinhaAtual(prev => ({
        ...prev,
        chipCcid: '',
        valorChip: prev.negociacao === 'Aditivo' ? '7.00' : prev.valorChip,
        isEsim: false,
      }));
      if (linhaEditando) {
        setLinhaEditando(prev => ({ ...prev, chipCcid: '', valorChip: '7.00', isEsim: false }));
      }
      // Limpar ICCIDs de todas as linhas já adicionadas
      setLinhas(prev => prev.map(linha => ({
        ...linha,
        chipCcid: linha.negociacao === 'Aditivo' ? '' : linha.chipCcid,
        valorChip: linha.negociacao === 'Aditivo' ? 7 : linha.valorChip,
        isEsim: linha.negociacao === 'Aditivo' ? false : linha.isEsim,
      })));
    }
  }, [fastchipFlowEnabled]);

  // Estados para edição de contratos (identificador e dados exibidos fora do formulário)
  const [editingContractId, setEditingContractId] = useState<string | null>(null);
  const [contractCanEditLines, setContractCanEditLines] = useState(false);
  type ContractModalMode = 'create' | 'full' | 'linesOnly';
  const [contractModalMode, setContractModalMode] = useState<ContractModalMode>('create');

  const suspensaoBloqueadaContrato = contractToEdit?.suspensaoPermitida === false;
  const bloqueioSuspensaoNovosContratos = politicaBloqueiaSuspensaoEmNovoContrato(
    editingContractId,
    suspensionPolicy
  );
  const fluxoSuspensaoLinhasBloqueado = formularioBloqueiaFluxoSuspensaoLinhas({
    editingContractId,
    suspensaoPermitidaContrato: contractToEdit?.suspensaoPermitida,
    policy: suspensionPolicy,
  });
  const mensagemOrientacaoSuspensao1056 = mensagemSuspensaoBloqueada1056(suspensionPolicy);

  const canMutateContractLines = React.useMemo(() => {
    if (!editingContractId) return true;
    if (canAdminMutateContractLinesOnEdit(hasRole('Admin'), editingContractId)) return true;
    return contractCanEditLines;
  }, [editingContractId, contractCanEditLines, hasRole, user?.roles]);

  const canEditContractLinesByRole = React.useMemo(
    () => hasRole('Admin') || hasRole('TeamLeader'),
    [user?.roles],
  );

  const showEditLinesShortcut = React.useMemo(() => {
    if (!showLinesModal) return false;
    if (!canEditContractLinesByRole) return false;
    return linesDetailCanEditLines;
  }, [showLinesModal, linesDetailCanEditLines, canEditContractLinesByRole]);

  const isLinesModalGroupView = React.useMemo(() => {
    return !!(
      selectedContractInfo?.groupId &&
      (selectedContractInfo?.isGroupParent || linesModalGroupContracts.length > 1)
    );
  }, [selectedContractInfo, linesModalGroupContracts.length]);

  const clientRequiresExistingDraft = () =>
    !editingContractId &&
    !teamContractRules.allowContractDraft &&
    clientDraftsList.length > 0 &&
    !draftContractId &&
    !!selectedClient;

  const [administratorNome, setAdministratorNome] = useState('');
  const [administratorCpf, setAdministratorCpf] = useState('');
  const [administratorRg, setAdministratorRg] = useState('');
  const [administratorEmail, setAdministratorEmail] = useState('');
  const [administratorTelefone1, setAdministratorTelefone1] = useState('');
  const [administratorTelefone2, setAdministratorTelefone2] = useState('');
  const [administratorDataNascimento, setAdministratorDataNascimento] = useState<Date>(new Date());

  // Carregar lista de clientes (sem search = 30 mais recentes, com search = busca na API)
  const loadClients = async (search?: string) => {
    try {
      setClientsLoading(true);
      const clientsData = await clientService.getClients(search);
      setClients(clientsData);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    } finally {
      setClientsLoading(false);
    }
  };

  // Carregar lista de planos
  const loadPlans = async () => {
    try {
      setPlansLoading(true);
      const plansData = await planService.getAllPlans();
      setPlans(plansData);
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
    } finally {
      setPlansLoading(false);
    }
  };

  const loadHandsetCatalog = async () => {
    try {
      const data = await handsetDeviceService.list({ apenasAtivos: true });
      setHandsetCatalog(data);
    } catch (error) {
      console.error('Erro ao carregar catálogo de aparelhos:', error);
    }
  };

  // Filtrar planos baseado no tipo de negociação e aparelho selecionado
  const filtrarPlanos = (negociacao: string, aparelho?: string) => {
    setPlansFiltrados(
      filterPlansForContractLine(plans, negociacao, aparelho === WTTX_APARELHO_VALUE)
    );
  };

  // Descrição do plano para WTTX/5Mbps no modal Detalhes das Linhas (busca por nome e retorna plan.description)
  const getPlanDescription = (planName: string): string | null => {
    if (!plans?.length || !planName?.trim()) return null;
    const found = plans.find(
      p => p.name === planName || p.name.includes(planName) || planName.includes(p.name)
    );
    if (!found?.description?.trim()) return null;
    return isWttxPlan(found.name) ? found.description : null;
  };

  // Encontrar plano mais barato (prioridade: RP 10GB 2024)
  const findCheapestPlan = (): { id: string; name: string } | null => {
    if (!plans || plans.length === 0) {
      return null;
    }

    // Primeiro: buscar plano "RP 10GB 2024" exato
    const rp10gb2024 = plans.find(plan => 
      plan.name === 'RP 10GB 2024' || 
      plan.name.includes('RP 10GB 2024')
    );

    if (rp10gb2024) {
      return {
        id: rp10gb2024.id,
        name: rp10gb2024.name
      };
    }

    // Fallback: buscar plano RP mais barato
    const rpPlans = plans.filter(plan => plan.name.includes('RP'));
    if (rpPlans.length > 0) {
      const cheapest = rpPlans.reduce((prev, current) => 
        (prev.totalMonthlyPrice < current.totalMonthlyPrice) ? prev : current
      );
      return {
        id: cheapest.id,
        name: cheapest.name
      };
    }

    // Último fallback: plano mais barato de todos
    const cheapestOverall = plans.reduce((prev, current) => 
      (prev.totalMonthlyPrice < current.totalMonthlyPrice) ? prev : current
    );
    return {
      id: cheapestOverall.id,
      name: cheapestOverall.name
    };
  };

  // Funções para gerenciar linhas
  const updateLinha = (
    id: string,
    field: string,
    value: string | number | boolean | null | PortabilidadeRepresentanteFields[]
  ) => {
    setLinhas((prev) =>
      prev.map((linha) => {
        if (linha.id !== id) return linha;

        const updated: ContractLineFormItem = { ...linha, [field]: value } as ContractLineFormItem;

        if (field === 'negociacao') {
          const newNeg = String(value);

          if (linha.negociacao !== newNeg) {
            Object.assign(updated, clearContractLinePlanSelection());
          }

          if (editingContractId) {
            if (newNeg === 'Renegociação') {
              updated.chipCcid = '';
              updated.seraUsadaPeloCliente = null;
              updated.ehPortabilidade = false;
              updated.portabilidadeNomeCompleto = '';
              updated.portabilidadeCpf = '';
              updated.portabilidadeOperadora = '';
              updated.portabilidadeLinha = '';
              updated.portabilidadeCnpjCedente = '';
              updated.portabilidadeRazaoSocialCedente = '';
              updated.portabilidadeRepresentantes = [];
            } else if (newNeg === 'Aditivo') {
              updated.numero = '';
            }

            if (editingGroupContracts && editingGroupContracts.length > 0) {
              const target = findActiveGroupContractForNegociacao(editingGroupContracts, newNeg);
              if (target?.id) {
                updated.sourceContractId = target.id;
              }
            } else if (contractMatchesNegociacao({ tipoContratacao }, newNeg)) {
              updated.sourceContractId = editingContractId;
            } else {
              updated.sourceContractId = undefined;
            }
          }
        }

        return updated;
      })
    );
  };

  const removeLinha = (id: string) => {
    setLinhas(prev => prev.filter(linha => linha.id !== id));
  };

  const removeGroup = (planId: string, negociacao: string) => {
    setLinhas(prev => prev.filter(linha => 
      !(linha.planId === planId && linha.negociacao === negociacao)
    ));
  };

  const resolveSourceContractIdForNewLine = useCallback(
    (negociacao: string): string | undefined => {
      if (editingGroupContracts && editingGroupContracts.length > 0) {
        return findActiveGroupContractForNegociacao(editingGroupContracts, negociacao)?.id;
      }
      if (editingContractId && contractMatchesNegociacao({ tipoContratacao }, negociacao)) {
        return editingContractId;
      }
      return undefined;
    },
    [editingGroupContracts, editingContractId, tipoContratacao]
  );

  const prepareGroupStructureForLineSave = useCallback(async (): Promise<{
    groupContracts: ContractResponseDto[] | null;
    linesToSave: ContractLineFormItem[];
  } | null> => {
    if (!editingContractId) return null;

    let groupContracts = editingGroupContracts;
    let linesToSave = linhas;

    if (groupNeedsEnsureForLines(linhas, groupContracts, tipoContratacao)) {
      const ensureResult = await contractService.ensureGroupForLineEdit(editingContractId, {
        requiredNegotiations: getRequiredNegociacoesFromLines(linhas),
      });
      groupContracts = ensureResult.contracts;
      setEditingGroupContracts(ensureResult.contracts);
      linesToSave = assignSourceContractIdsToLines(linhas, ensureResult.contracts);
      setLinhas(linesToSave);
      if (ensureResult.createdContractIds.length > 0) {
        showInfo(
          ensureResult.createdContractIds.length === 1
            ? 'Foi criado um novo subcontrato no grupo para receber as linhas do tipo correspondente.'
            : `Foram criados ${ensureResult.createdContractIds.length} subcontratos no grupo para as novas linhas.`
        );
      }
    } else if (groupContracts && groupContracts.length > 1) {
      linesToSave = assignSourceContractIdsToLines(linhas, groupContracts);
      setLinhas(linesToSave);
    }

    return { groupContracts, linesToSave };
  }, [
    editingContractId,
    editingGroupContracts,
    linhas,
    tipoContratacao,
    setEditingGroupContracts,
    setLinhas,
    showInfo,
  ]);

  const addLinha = (line: Partial<any>) => {
    const lineToAdd = { ...line } as any;
    const targetContractId = resolveSourceContractIdForNewLine(lineToAdd.negociacao ?? '');
    if (targetContractId) {
      lineToAdd.sourceContractId = targetContractId;
    }
    if (lineToAdd.negociacao === 'Aditivo' && fastchip && !backendFastchipAllocationMode) {
      const ccidsUsados = linhas
        .filter((l: any) => l.negociacao === 'Aditivo' && l.chipCcid)
        .map((l: any) => (l.chipCcid || '').trim());
      const pool = availableICcIdsForLine.filter(
        (ic) => !ccidsUsados.includes((ic.ccId || '').trim())
      );
      const chipCcid =
        pool.length > 0 ? pool[Math.floor(Math.random() * pool.length)].ccId : '';
      if (chipCcid) {
        lineToAdd.chipCcid = chipCcid;
      }
    }
    setLinhas(prev => [...prev, lineToAdd]);
  };

  // Função para duplicar linha
  const duplicarLinha = (linhaOriginal: any) => {
    if (fastchip && linhaOriginal.negociacao === 'Aditivo') {
      let chipCcid = '';
      if (!backendFastchipAllocationMode) {
        const ccidsUsados = linhas
          .filter((l: any) => l.negociacao === 'Aditivo' && l.chipCcid)
          .map((l: any) => (l.chipCcid || '').trim());
        const pool = availableICcIdsForLine.filter(
          (ic) => !ccidsUsados.includes((ic.ccId || '').trim())
        );
        chipCcid =
          pool.length > 0 ? pool[Math.floor(Math.random() * pool.length)].ccId : '';
      }
      const linhaDuplicada = {
        ...linhaOriginal,
        id: `linha-${Date.now()}`,
        chipCcid: chipCcid || '',
        quantidade: 1
      };
      setLinhas(prev => [...prev, linhaDuplicada]);
      if (backendFastchipAllocationMode) {
        showSuccess('Linha duplicada com sucesso! ICCID será alocado no backend ao salvar.');
      } else if (chipCcid) {
        showSuccess('Linha duplicada com sucesso! CCID atribuído automaticamente.');
      } else {
        showWarning('Linha duplicada sem ICCID automático por falta de estoque Fastchip neste cluster.');
      }
    } else {
      // Duplicar normalmente - para Renegociação, não limpar CCID pois não é usado
      const linhaDuplicada = {
        ...linhaOriginal,
        id: `linha-${Date.now()}`,
        chipCcid: linhaOriginal.negociacao === 'Renegociação' ? undefined : '', // Não limpar para Renegociação
        quantidade: 1 // Sempre 1
      };
      
      setLinhas(prev => [...prev, linhaDuplicada]);
      
      // Mostrar toast de sucesso
      showSuccess(`Linha duplicada com sucesso! A linha ${linhaOriginal.ddd} - ${linhaOriginal.negociacao} foi duplicada.`);
    }
  };

  // Função para editar linha
  const editarLinha = (linha: any) => {
    setEditingLinha(linha);
    
    // Para Renegociação, separar DDD e número se o número estiver completo
    let dddToEdit = linha.ddd || '';
    let numeroToEdit = linha.numero || '';
    
    if (linha.negociacao === 'Renegociação' && linha.numero) {
      const { ddd, number } = extractDddFromPhone(linha.numero);
      dddToEdit = ddd || linha.ddd || '';
      numeroToEdit = number || '';
    }
    
    setLinhaEditando({
      negociacao: linha.negociacao || 'Aditivo',
      ddd: dddToEdit,
      numero: numeroToEdit,
      tipoVenda: linha.tipoVenda || 'Sem aparelho',
      planId: linha.planId || '',
      plano: linha.plano || '',
      aparelho: linha.aparelho || '',
      handsetDeviceId: linha.handsetDeviceId,
      aparelhoValorTotal: linha.aparelhoValorTotal,
      aparelhoValorMensal: linha.aparelhoValorMensal,
      valorChip: linha.valorChip?.toString() || '7.00',
      valorMensal: linha.valorMensal?.toString() || '',
      chipCcid: linha.chipCcid || '',
      quantidade: linha.quantidade || 1,
      seraUsadaPeloCliente: linha.seraUsadaPeloCliente !== undefined && linha.seraUsadaPeloCliente !== null ? linha.seraUsadaPeloCliente : null,
      ehPortabilidade: linha.ehPortabilidade || false,
      portabilidadeNomeCompleto: linha.portabilidadeNomeCompleto || '',
      portabilidadeCpf: linha.portabilidadeCpf || '',
      portabilidadeOperadora: linha.portabilidadeOperadora || '',
      portabilidadeLinha: linha.portabilidadeLinha || '',
      portabilidadeCnpjCedente: linha.portabilidadeCnpjCedente || '',
      portabilidadeRazaoSocialCedente: linha.portabilidadeRazaoSocialCedente || '',
      portabilidadeRepresentantes: (linha.portabilidadeRepresentantes || []).map((rep: any) => ({ ...rep })),
      isEsim: linha.isEsim ?? false,
    } as any);
    setShowEditLinhaModal(true);
  };

  // Função para salvar edição da linha
  const salvarEdicaoLinha = () => {
    if (!editingLinha) return;

    // Validações
    if ((linhaEditando.negociacao === 'Aditivo' && !linhaEditando.ddd) || !linhaEditando.valorMensal || !linhaEditando.planId) {
      showError('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    if (linhaEditando.negociacao === 'Renegociação') {
      if (!linhaEditando.ddd) {
        showError('DDD é obrigatório para renegociação.');
        return;
      }
      if (!linhaEditando.numero || linhaEditando.numero.length < 8) {
        showError('Número é obrigatório para renegociação (mínimo 8 dígitos).');
        return;
      }
    }

    let resolvedChipCcid = linhaEditando.chipCcid;
    if (
      fastchipFlowEnabled &&
      linhaEditando.negociacao === 'Aditivo' &&
      !backendFastchipAllocationMode &&
      (!linhaEditando.chipCcid || !isValidCcidLength(linhaEditando.chipCcid))
    ) {
      let chipCcidToSave = (linhaEditando.chipCcid || '').trim();
      if (!chipCcidToSave || !isValidCcidLength(chipCcidToSave)) {
        const ccidsUsados = collectUsedIccids(linhas, editingLinha.id);
        chipCcidToSave = pickRandomAvailableIccid(
          linhaEditando.ddd || '',
          ccidsUsados,
          (line) =>
            buildIccidPoolForLineDraft({
              ddd: line.ddd ?? '',
              isEsim: line.isEsim,
            }),
          linhaEditando.isEsim ?? false
        );
      }
      if (!chipCcidToSave || !isValidCcidLength(chipCcidToSave)) {
        showError('CCID é obrigatório para linhas de Aditivo quando Fastchip está ativado. Deve ter 20 dígitos.');
        return;
      }
      resolvedChipCcid = chipCcidToSave;
    }

    if (linhaEditando.negociacao === 'Aditivo' && linhaEditando.ehPortabilidade) {
      const portErr = getPortabilidadeValidationError(linhaEditando);
      if (portErr) {
        showError(portErr);
        return;
      }

      const conflict = detectPortabilidadeConflict(linhas, linhaEditando, editingLinha?.id);
      if (conflict.conflict) {
        setPortabilidadeConflict(conflict);
        setPortabilidadeConflictAction('edit');
        return;
      }
    }

    aplicarSalvarEdicaoLinha(resolvedChipCcid);
  };

  const aplicarSalvarEdicaoLinha = (resolvedChipCcid?: string) => {
    if (!editingLinha) return;

    setLinhas(prev => prev.map(linha =>
      linha.id === editingLinha.id
        ? {
            ...linha,
            ...linhaEditando,
            chipCcid: resolvedChipCcid ?? linhaEditando.chipCcid,
            numero: linhaEditando.negociacao === 'Renegociação' && linhaEditando.ddd && linhaEditando.numero
              ? renegociacaoLocalDigitsFromFormField(linhaEditando.ddd, linhaEditando.numero)
              : linhaEditando.numero,
            valorChip: parseFloat(linhaEditando.valorChip) || 0,
            valorMensal: parseFloat(linhaEditando.valorMensal),
            quantidade: 1
          }
        : linha
    ));

    // Fechar modal e limpar estados
    setShowEditLinhaModal(false);
    setEditingLinha(null);
    setLinhaEditando({
      negociacao: 'Aditivo',
      ddd: '',
      numero: '',
      tipoVenda: 'Sem aparelho',
      planId: '',
      plano: '',
      aparelho: '',
      valorChip: '7.00',
      valorMensal: '',
      chipCcid: '',
      quantidade: 1,
      seraUsadaPeloCliente: null,
      ehPortabilidade: false,
      portabilidadeNomeCompleto: '',
      portabilidadeCpf: '',
      portabilidadeOperadora: '',
      portabilidadeLinha: '',
      portabilidadeCnpjCedente: '',
      portabilidadeRazaoSocialCedente: '',
      portabilidadeRepresentantes: [],
      isEsim: false,
    });

    showSuccess('Linha editada com sucesso!');
  };

  const handlePortabilidadeConflictConfirm = () => {
    if (!portabilidadeConflict?.conflict) {
      setPortabilidadeConflict(null);
      setPortabilidadeConflictAction(null);
      return;
    }

    if (portabilidadeConflict.kind === 'tipo') {
      const source = portabilidadeConflictAction === 'edit' ? linhaEditando : linhaAtual;
      setLinhas((prev) =>
        applyPortabilidadeTipoConversion(prev, portabilidadeConflict.newTipo, source)
      );
      showSuccess(`${portabilidadeConflict.affectedCount} linha(s) convertida(s).`);
    } else if (portabilidadeConflict.kind === 'cedente') {
      const source = portabilidadeConflictAction === 'edit' ? linhaEditando : linhaAtual;
      setLinhas((prev) => applyCedenteUnification(prev, source));
      showSuccess('Cedente unificado em todas as linhas CNPJ→CNPJ (cedente)');
    }

    setPortabilidadeConflict(null);
    const action = portabilidadeConflictAction;
    setPortabilidadeConflictAction(null);

    if (action === 'edit') {
      aplicarSalvarEdicaoLinha();
    } else if (action === 'add') {
      skipPortabilidadeConflictCheckRef.current = true;
      setTimeout(() => {
        const button = addLinhaButtonRef.current as (Button & {
          handleClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
        }) | null;
        button?.handleClick({ preventDefault: () => undefined } as React.MouseEvent<HTMLButtonElement>);
      }, 0);
    }
  };

  // Função para cancelar edição da linha
  const cancelarEdicaoLinha = () => {
    setShowEditLinhaModal(false);
    setEditingLinha(null);
    setLinhaEditando({
      negociacao: 'Aditivo',
      ddd: '',
      numero: '',
      tipoVenda: 'Sem aparelho',
      planId: '',
      plano: '',
      aparelho: '',
      valorChip: '7.00',
      valorMensal: '',
      chipCcid: '',
      quantidade: 1,
      seraUsadaPeloCliente: null,
      ehPortabilidade: false,
      portabilidadeNomeCompleto: '',
      portabilidadeCpf: '',
      portabilidadeOperadora: '',
      portabilidadeLinha: '',
      portabilidadeCnpjCedente: '',
      portabilidadeRazaoSocialCedente: '',
      portabilidadeRepresentantes: [],
      isEsim: false,
    });
  };

  // Função para adicionar responsável pela auditoria como representante

  // Função para adicionar responsável pela auditoria como recebedor

  /**
   * Consulta a base TIM pelo CNPJ (mini API Python via proxy no GettelApi) e atualiza
   * `timClientData` se houver dados. Nunca bloqueia o fluxo do modal: qualquer erro de
   * rede/timeout ou `found: false` apenas limpa o estado, sem exibir erro ao operador.
   */
  const fetchAndSetTimClientData = async (cnpj: string | null | undefined) => {
    const cleanedCnpj = (cnpj || '').replace(/\D/g, '');
    if (cleanedCnpj.length !== 14) {
      setTimClientData(null);
      return;
    }
    try {
      const data = await fetchTimClientData(cleanedCnpj);
      setTimClientData(data.found ? data : null);
    } catch (err) {
      console.warn('Consulta de dados TIM falhou (não bloqueante):', err);
      setTimClientData(null);
    }
  };

  const handleCnpjClientFound = (client: ClientResponse) => {
    setSelectedClient(client);
    // MEI é isento; os demais clientes sem IE ativa precisam de monitoramento.
    const isMei = client.naturezaJuridica?.includes('2135') ||
                  client.naturezaJuridica?.includes('Empresário (Individual)');
    setNeedsIeMonitoring(!isMei && client.hasActiveInscricaoEstadual === false);
    setFastchipDisabled(false);
    setFastchipDisabledReason('');
    setClientSearchQuery('');
  };

  const { cnpjInputError, searchingCnpj, handleClientSearchBlur, handleSearchCnpj } = useClientCnpjSearch({
    query: clientSearchQuery,
    isModalOpen: isCreateModalOpen,
    loadClients,
    onClientFound: handleCnpjClientFound,
    onTimData: setTimClientData,
    setError,
  });

  // Debounce para busca de clientes via API
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

    if (!clientSearchQuery.trim()) {
      loadClients();
      return;
    }

    searchDebounceRef.current = setTimeout(() => {
      loadClients(clientSearchQuery.trim());
    }, 400);

    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [clientSearchQuery]);

  const openClientDraftsModal = (drafts: DraftSummaryDto[]) => {
    setClientDraftsList(drafts);
    setShowClientDraftsModal(true);
  };

  // Selecionar cliente
  const handleClientSelect = async (client: ClientListResponse) => {
    // Buscar dados completos do cliente para garantir que todos os campos estão presentes
    try {
      const fullClient = await clientService.getClientById(client.id);

      saleTokenLockedRef.current = false;

      setSelectedClient(fullClient);
      setClientSearchQuery('');
      setError('');
      void fetchAndSetTimClientData(fullClient.cnpj);
      setContractData(prev => ({
        ...prev,
        clientId: fullClient.id
      }));

      // Verificar se é MEI (Empresário Individual) - natureza jurídica ID 2135
      const isMei = fullClient.naturezaJuridica?.includes('2135') || 
                    fullClient.naturezaJuridica?.includes('Empresário (Individual)');
      
      // Verificar inscrição estadual
      // MEI é isento - não precisa monitorar IE
      // Se não é MEI E não há inscrição estadual ativa (vazio ou baixada), marcar para monitoramento
      if (!isMei && fullClient.hasActiveInscricaoEstadual === false) {
        // Cliente pode não ter IE ativa - marcar para monitoramento mas não forçar fastchip
        setNeedsIeMonitoring(true);
      } else {
        // Se é MEI ou tem inscrição ativa, não precisa monitorar
        setNeedsIeMonitoring(false);
      }
      
      // Verificar se há ICCIDs disponíveis para o estado
      // Nota: availableICcIds será atualizado pelo hook quando o estado mudar
      // A verificação será feita pelo useEffect que monitora availableICcIds
      setFastchipDisabled(false);
      setFastchipDisabledReason('');

      // ===== VERIFICAÇÕES DE RASCUNHO, DUPLICIDADE E CONTRATO EXISTENTE =====
      const shouldCheckDrafts = !editingContractId && !draftContractId;

      if (shouldCheckDrafts) {
        setClientDraftsLoading(true);
      }

      const [clientDrafts, dupCheck, hasExistingByCnpj] = await Promise.all([
        shouldCheckDrafts
          ? contractService.getContractDraftsByClient(fullClient.id).catch((error) => {
              console.error('Erro ao buscar rascunhos do cliente:', error);
              return [] as DraftSummaryDto[];
            })
          : Promise.resolve([] as DraftSummaryDto[]),
        contractService.checkForDuplicate(fullClient.id, undefined, 24).catch((error) => {
          console.error('Erro ao verificar duplicidade:', error);
          return { hasDuplicate: false, contract: null };
        }),
        fullClient.cnpj
          ? contractService.hasExistingContractByCnpj(fullClient.cnpj).catch((error) => {
              console.error('Erro ao verificar contrato existente por CNPJ:', error);
              return false;
            })
          : Promise.resolve(false),
      ]);

      if (shouldCheckDrafts) {
        setClientDraftsLoading(false);
      }

      const hasDrafts = clientDrafts.length > 0;
      const hasActiveDuplicateContract =
        !!dupCheck.contract &&
        dupCheck.hasDuplicate &&
        normalizeContractStatus(dupCheck.contract.status) !== EContractStatus.Cancelado;
      const hasRealDuplicate = hasActiveDuplicateContract || hasExistingByCnpj;

      if (hasDrafts && !hasRealDuplicate) {
        openClientDraftsModal(clientDrafts);
      } else {
        if (hasActiveDuplicateContract && dupCheck.contract) {
          setDuplicateContract(dupCheck.contract);
          setShowDuplicateModal(true);
        }
        if (hasExistingByCnpj && fullClient.cnpj) {
          setExistingContractCnpj(fullClient.cnpj);
          setShowExistingContractModal(true);
        }
        if (hasDrafts) {
          openClientDraftsModal(clientDrafts);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar dados completos do cliente:', error);
      setClientDraftsLoading(false);
      // Se falhar, usa os dados básicos que já temos
      saleTokenLockedRef.current = false;
      setSelectedClient(client as ClientResponse);
      setClientSearchQuery('');
      setError('');
      void fetchAndSetTimClientData(client.cnpj);
      // Em caso de erro, a verificação de ICCIDs será feita pelo useEffect que monitora availableICcIds
      setFastchipDisabled(false);
      setFastchipDisabledReason('');
      setContractData(prev => ({
        ...prev,
        clientId: client.id
      }));
    }
  };

  // Limpar seleção de cliente
  const handleClientClear = () => {
    setShowClientDraftsModal(false);
    setClientDraftsList([]);
    setSelectedClient(null);
    setTimClientData(null);
    setFastchip(false);
    setFastchipDisabled(false);
    setFastchipDisabledReason('');
    setNeedsIeMonitoring(false);
    setToken('');
    saleTokenLockedRef.current = false;
    setContractData(prev => ({
      ...prev,
      clientId: undefined
    }));
  };

  const buildDraftPayload = (): CreateContractRequestDto | null => {
    if (!selectedClient?.id) return null;
    const sessionUserId = getUserId();
    if (!sessionUserId) return null;

    let contractUserId = sessionUserId;
    if (canAssignResponsibleConsultant && responsibleConsultantUserId) {
      contractUserId = responsibleConsultantUserId;
    }

    const explicitSaleToken = normalizeContractSaleTokenForForm(token, fastchip);

    return {
      clientId: selectedClient.id,
      userId: contractUserId,
      diaVencimento,
      tipoFatura,
      envioFatura,
      origemVenda,
      fastchip,
      ...(explicitSaleToken ? { token: explicitSaleToken } : {}),
      internalNotes: contractInternalNotes.trim() || undefined,
      deliveryAddress: {
        cep: cep || '',
        cidade: cidade || '',
        estado: estado || '',
        endereco: normalizeDeliveryAddressStreetNameForApi(endereco),
        numero: normalizeDeliveryAddressStreetNumberForApi(numero),
        bairro: bairro || '',
        complemento: complemento || undefined,
        referencia: referencia || undefined,
      },
      administrator: {
        nomeCompleto: adminNomeCompleto || '',
        cpf: adminCpf || '',
        rg: adminRg || '000000000',
        email: adminEmail || '',
        telefone1: adminTelefone1 || '',
        telefone2: adminTelefone2 || undefined,
        dataNascimento: new Date('2000-08-18'),
      },
      representatives: representantes.map((rep) => ({
        nome: rep.nome,
        cpf: rep.cpf?.trim() || '',
        rg: rep.rg?.trim() || undefined,
        telefone: normalizeRepresentativePhoneInput(rep.telefone ?? ''),
        email: (rep.email ?? '').trim(),
      })),
      authorizedReceivers: recebedores.map((rec) => ({
        nome: rec.nome,
        qualificacao: rec.qualificacao?.trim() || undefined,
        cpf: rec.cpf?.trim() || '',
        rg: rec.rg?.trim() || undefined,
      })),
      witnesses: testemunhas.map((test) => ({
        nome: test.nome,
        cpf: (test.cpf || '').trim(),
        rg: test.rg?.trim() || undefined,
      })),
      lines: linhas.map((linha) => {
        let dddToSend = linha.ddd;
        let numeroToSend = linha.numero?.trim() || '';
        if (linha.negociacao === 'Renegociação' && linha.ddd && linha.numero) {
          numeroToSend = renegociacaoLocalDigitsFromFormField(linha.ddd, linha.numero);
          dddToSend = linha.ddd;
        }
        return {
          negociacao: linha.negociacao || undefined,
          ddd: dddToSend || undefined,
          numero: numeroToSend || undefined,
          tipoVenda: linha.tipoVenda || 'Venda',
          planId: linha.planId?.trim() || '',
          aparelho: linha.aparelho?.trim() || undefined,
          valorChip: linha.valorChip ? parseFloat(linha.valorChip.toString()) : 0,
          valorMensal: parseFloat(linha.valorMensal.toString()),
          quantidade: 1,
          chipCcid:
            linha.negociacao === 'Aditivo' && fastchip ? linha.chipCcid?.trim() || undefined : undefined,
          excluirDoContrato: linha.excluirDoContrato || false,
          requerSuspensao: linha.requerSuspensao || false,
          trocaLinha: linha.trocaLinha || false,
          seraUsadaPeloCliente:
            linha.seraUsadaPeloCliente !== null && linha.seraUsadaPeloCliente !== undefined
              ? linha.seraUsadaPeloCliente
              : undefined,
          ehPortabilidade: linha.ehPortabilidade || false,
          isEsim: linha.negociacao === 'Aditivo' && fastchip ? linha.isEsim ?? false : undefined,
          ofertas: [],
        };
      }),
    };
  };

  const clearDraftContractId = () => {
    draftContractIdRef.current = null;
    setDraftContractId(null);
  };

  const isDraftNotFoundError = (error: unknown): boolean => {
    const msg = extractApiErrorMessage(error, '').toLowerCase();
    const status = (error as { response?: { status?: number } })?.response?.status;
    return (
      status === 404 ||
      msg.includes('rascunho não encontrado') ||
      msg.includes('rascunho nao encontrado')
    );
  };

  const persistContractDraft = async (): Promise<'created' | 'updated' | undefined> => {
    return draftPersisterRef.current.enqueue(async () => {
      if (draftPersistSuspendedRef.current) return undefined;
      if (editingContractId) return undefined;
      if (!canPersistContractDraft(teamContractRules)) return undefined;
      if (!selectedClient || linhas.length === 0) return undefined;
      const payload = buildDraftPayload();
      if (!payload) return undefined;

      if (draftContractIdRef.current) {
        try {
          await contractService.updateContractDraft(draftContractIdRef.current, payload);
          return 'updated';
        } catch (error) {
          // Rascunho sumiu (finalize/cleanup/corrida) — limpa id para o Salvar usar createContract.
          if (isDraftNotFoundError(error)) {
            clearDraftContractId();
            return undefined;
          }
          throw error;
        }
      }

      const created = await contractService.createContractDraft(payload);
      if (draftPersistSuspendedRef.current) {
        // Salvar já em andamento: não reatacha id de rascunho órfão.
        return undefined;
      }
      draftContractIdRef.current = created.id;
      setDraftContractId(created.id);
      return 'created';
    });
  };

  const cancelScheduledDraftSave = () => {
    if (draftSaveTimerRef.current) {
      clearTimeout(draftSaveTimerRef.current);
      draftSaveTimerRef.current = null;
    }
  };

  /** Falha de rascunho não bloqueia avanço nem o salvar final do contrato. */
  const warnDraftSaveFailedButContinue = (error: unknown, logContext: string) => {
    // 404 durante corrida com Salvar: silencioso (já há fallback no submit).
    if (draftPersistSuspendedRef.current || isDraftNotFoundError(error)) {
      console.warn(logContext, error);
      if (isDraftNotFoundError(error)) clearDraftContractId();
      return;
    }
    console.error(logContext, error);
    showWarning(
      'Não foi possível salvar o rascunho, mas você pode continuar. O contrato poderá ser salvo normalmente ao final.'
    );
  };

  const tryPersistContractDraftWithoutBlocking = async (logContext: string): Promise<void> => {
    try {
      cancelScheduledDraftSave();
      await persistContractDraft();
    } catch (error) {
      warnDraftSaveFailedButContinue(error, logContext);
    }
  };

  /** Persistência de rascunho em background — não atrasa troca de aba. */
  const persistContractDraftInBackground = (
    logContext: string,
    options?: { notifyCreated?: boolean }
  ) => {
    if (draftPersistSuspendedRef.current) return;
    cancelScheduledDraftSave();
    void persistContractDraft()
      .then((result) => {
        if (options?.notifyCreated && result === 'created') {
          showInfo('Rascunho criado');
        }
      })
      .catch((error) => {
        warnDraftSaveFailedButContinue(error, logContext);
      });
  };

  /** Antes do Salvar: para novos saves e espera o que já estava em voo. */
  const prepareDraftPersistForContractSave = async () => {
    draftPersistSuspendedRef.current = true;
    cancelScheduledDraftSave();
    draftPersisterRef.current.discardPending();
    try {
      await draftPersisterRef.current.flush();
    } catch (error) {
      // Flush só para sincronizar; 404/limpeza não deve impedir o Salvar.
      if (isDraftNotFoundError(error)) {
        clearDraftContractId();
      } else {
        console.warn('Falha ao sincronizar rascunho antes do salvar:', error);
      }
    }
  };

  const schedulePersistContractDraft = () => {
    if (editingContractId || linhas.length === 0) return;
    if (!canPersistContractDraft(teamContractRules)) return;
    cancelScheduledDraftSave();
    draftSaveTimerRef.current = setTimeout(() => {
      draftSaveTimerRef.current = null;
      void persistContractDraft().catch((error) => {
        warnDraftSaveFailedButContinue(error, 'Erro no auto-save do rascunho:');
      });
    }, 500);
  };

  // Verificar se há dados preenchidos no formulário
  const hasFilledData = () => {
    return !!(
      selectedClient ||
      linhas.length > 0 ||
      numeroP2B ||
      comprometimentoMinimo ||
      fastchip ||
      cep ||
      cidade ||
      estado ||
      endereco ||
      numero ||
      bairro ||
      adminNomeCompleto ||
      adminCpf ||
      adminRg ||
      adminEmail ||
      contractInternalNotes.trim()
    );
  };

  const closeAndResetModal = () => {
    setIsCreateModalOpen(false);
    setEditingContractId(null);
    setContractCanEditLines(false);
    setContractModalMode('create');
    setEditingGroupContracts(null);
    setEditingGroupSecondContractLines(null);
    setEditingGroupIndex(0);
    setDuplicatedFromContractId(null);
    resetForm();
  };

  /** Fecha o modal de contrato sem fechar a modal de detalhe das linhas (pós-save lines-only). */
  const closeContractFormOnly = () => {
    setIsCreateModalOpen(false);
    setEditingContractId(null);
    setContractCanEditLines(false);
    setContractModalMode('create');
    setEditingGroupContracts(null);
    setEditingGroupSecondContractLines(null);
    setEditingGroupIndex(0);
    setDuplicatedFromContractId(null);
    setPendingDraftIccidValidation(false);
    setShowConvertToEntregaModal(false);
    setPendingEntregaConversionLines([]);
    resetForm();
  };

  // Handler para tentativa de fechamento
  const handleCloseAttempt = async () => {
    if (editingContractId) {
      if (hasFilledData()) {
        setShowCloseConfirmation(true);
      } else {
        closeAndResetModal();
      }
      return;
    }

    cancelScheduledDraftSave();

    if (linhas.length > 0 && canPersistContractDraft(teamContractRules)) {
      try {
        await persistContractDraft();
        await loadContracts(1);
        showInfo('Rascunho salvo');
        closeAndResetModal();
        return;
      } catch (error) {
        warnDraftSaveFailedButContinue(error, 'Erro ao salvar rascunho ao fechar:');
        // Não bloqueia o fechamento: segue para confirmação se houver dados preenchidos.
      }
    }

    if (hasFilledData()) {
      setShowCloseConfirmation(true);
    } else {
      closeAndResetModal();
    }
  };

  // Confirmar fechamento
  const handleConfirmClose = () => {
    setShowCloseConfirmation(false);
    setPendingCloseAction(null);
    closeAndResetModal();
  };

  // Cancelar fechamento
  const handleCancelClose = () => {
    setShowCloseConfirmation(false);
    setPendingCloseAction(null);
  };

  // Handler para clicar em "NOVO Contrato" - verifica contratos pendentes antes de abrir modal
  const handleNewContractClick = async () => {
    // Verificar se é Seller, Team Manager ou TeamLeader (Admin não deve ser bloqueado)
    const isAdmin = hasRole('Admin');
    const isSeller = hasRole('Seller');
    const isTeamManager = hasRole('Team Manager') || hasRole('TeamManager');
    const isTeamLeader = hasRole('TeamLeader');

    // Apenas verificar contratos pendentes se for Seller, Team Manager ou TeamLeader (não Admin)
    if (!isAdmin && (isSeller || isTeamManager || isTeamLeader)) {
      setCheckingPendingContracts(true);
      try {
        const pending = await contractService.getPendingSellerContracts();

        if (pending.length > 0) {
          showError(
            `Você tem ${pending.length} ${pending.length === 1 ? 'contrato próprio' : 'contratos próprios'} criado${pending.length === 1 ? '' : 's'} que precisa${pending.length === 1 ? '' : 'm'} ter o status atualizado antes de criar um novo contrato.`
          );
          setPendingContracts(pending);
          setCheckingPendingContracts(false);
          return;
        }
        setPendingContracts([]);
      } catch (error) {
        console.error('Erro ao verificar contratos pendentes:', error);
        showError('Erro ao verificar contratos pendentes. Tente novamente.');
        setCheckingPendingContracts(false);
        return;
      } finally {
        setCheckingPendingContracts(false);
      }
    }
    
    // Se passou na validacao (ou e Admin), abre o modal com regras atualizadas
    setContractModalMode('create');
    await openContractModalWithRules('cliente');
  };

  // Resetar formulário (estado do hook + estado da página)
  const resetForm = () => {
    saleTokenLockedRef.current = false;
    formReset();
    setActiveTab('cliente');
    setContractData({});
    setSelectedClient(null);
    setTimClientData(null);
    setClientSearchQuery('');
    setFastchipDisabled(false);
    setFastchipDisabledReason('');
    setClientPartners([]);
    setShowQsaModal(false);
    setEditingContractId(null);
    setEditingGroupContracts(null);
    setEditingGroupSecondContractLines(null);
    setEditingGroupIndex(0);
    setShowEditGroupSelectionModal(false);
    setSelectedGroupForEdit(null);
    setShowFastchipAddressModal(false);
    setHasSeenFastchipAddressModal(false);
    setShowClusterConfirmModal(false);
    pendingClusterConfirmActionRef.current = null;
    setResponsibleConsultantUserId('');
    setEditingResponsibleConsultant(false);
    setResponsibleConsultantEditValue('');
    setContractInternalNotes('');
    setContractInternalNotesOccurrence('');
    setMeuTimPasswordVisibility({});
    setMeuTimVerificationStatus('idle');
    setMeuTimVerificationMessage('');
    setMeuTimVerifiedSignature(null);
    setPendingSuspensionCanConvert(true);
    draftContractIdRef.current = null;
    setDraftContractId(null);
    setShowClientDraftsModal(false);
    setClientDraftsList([]);
  };

  // Interface para resposta da API ViaCEP
  interface ViaCepResponse {
    cep: string;
    logradouro: string;
    complemento: string;
    bairro: string;
    localidade: string;
    uf: string;
    ibge: string;
    gia: string;
    ddd: string;
    siafi: string;
    erro?: boolean;
  }

  // Buscar CEP via API
  const buscarCep = async (cepValue: string) => {
    const cepNumerico = cepValue.replace(/\D/g, '');

    if (cepNumerico.length !== 8) {
      return;
    }

    setCepLoading(true);

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cepNumerico}/json/`);

      if (!response.ok) {
        throw new Error('Erro na requisição');
      }

      const data: ViaCepResponse = await response.json();

      if (data.erro) {
        throw new Error('CEP não encontrado');
      }

      // Preencher campos automaticamente
      setCidade(data.localidade);
      setEstado(data.uf);
      setEndereco(data.logradouro);
      setBairro(data.bairro);
      setComplemento(data.complemento || '');

    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      // Limpar campos se houver erro
      setCidade('');
      setEstado('');
      setEndereco('');
      setBairro('');
      setComplemento('');
    } finally {
      setCepLoading(false);
    }
  };

  // Função auxiliar para formatar CEP ao carregar de dados externos
  const formatarCepParaInput = (cep: string | undefined): string => {
    if (!cep || cep.trim() === '' || cep === '00000-000' || cep === '00000000' || cep.replace(/\D/g, '') === '00000000') {
      return '';
    }
    // Remove todos os caracteres não numéricos (incluindo pontos e traços)
    const cepLimpo = cep.replace(/\D/g, '');
    // Se tiver 8 dígitos, formata com traço XXXXX-XXX (sem ponto)
    if (cepLimpo.length === 8) {
      return `${cepLimpo.slice(0, 5)}-${cepLimpo.slice(5)}`;
    }
    // Retorna o CEP limpo se não tiver 8 dígitos
    return cepLimpo;
  };

  /**
   * Mantém o endereço do formulário sincronizado com o endereço cadastral do cliente
   * quando o contrato não é Fastchip. Isso evita erro de CEP ao salvar sem abrir a aba Endereço.
   */
  useEffect(() => {
    if (fastchip || !selectedClient) return;

    const clientCep = formatarCepParaInput(selectedClient.cep);
    const clientEstado = selectedClient.estado || '';
    const clientCidade = selectedClient.cidade || '';
    const clientEndereco = selectedClient.logradouro || '';
    const clientNumero = coerceDeliveryAddressStreetNumber(selectedClient.numero);
    const clientBairro = selectedClient.bairro || '';
    const clientComplemento = selectedClient.complemento || '';

    const hasAddressMismatch =
      cep !== clientCep ||
      estado !== clientEstado ||
      cidade !== clientCidade ||
      endereco !== clientEndereco ||
      numero !== clientNumero ||
      bairro !== clientBairro ||
      complemento !== clientComplemento;

    if (!hasAddressMismatch) return;

    setCep(clientCep);
    setEstado(clientEstado);
    setCidade(clientCidade);
    setEndereco(clientEndereco);
    setNumero(clientNumero);
    setBairro(clientBairro);
    setComplemento(clientComplemento);
  }, [
    fastchip,
    selectedClient,
    cep,
    estado,
    cidade,
    endereco,
    numero,
    bairro,
    complemento,
    setCep,
    setEstado,
    setCidade,
    setEndereco,
    setNumero,
    setBairro,
    setComplemento,
  ]);

  // Função auxiliar para limpar valores padrão inválidos
  const limparValorPadrao = (valor: string | undefined): string => {
    if (!valor || valor.trim() === '' || 
        valor === 'Não informado' || 
        valor === 'Logradouro não informado' ||
        valor === 'Bairro não informado' ||
        valor === 'Cidade não informada' ||
        valor === 'Estado não informado') {
      return '';
    }
    return valor;
  };

  // Handler para mudança do CEP
  const handleCepChange = (value: string) => {
    // Formatar CEP com máscara
    const formattedCep = value
      .replace(/\D/g, '')
      .replace(/(\d{5})(\d{3})/, '$1-$2')
      .substring(0, 9);

    setCep(formattedCep);

    // Remover formatação para busca
    const cepLimpo = formattedCep.replace(/\D/g, '');

    // Buscar automaticamente quando CEP estiver completo
    if (cepLimpo.length === 8) {
      buscarCep(cepLimpo);
    }
  };

  // Handler dedicado ao form inline de edicao do endereco de entrega no modal de
  // detalhes das linhas. Usa o mesmo ViaCEP do form de criacao mas atualiza o
  // objeto editingDeliveryAddressData (em vez dos states individuais cidade/estado/...).
  const handleEditingDeliveryAddressCepChange = async (value: string) => {
    const formattedCep = value
      .replace(/\D/g, '')
      .replace(/(\d{5})(\d{3})/, '$1-$2')
      .substring(0, 9);

    setEditingDeliveryAddressData((prev) => ({ ...prev, cep: formattedCep }));

    const cepLimpo = formattedCep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;

    setEditingDeliveryAddressCepLoading(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      if (!response.ok) throw new Error('Erro na requisicao');
      const data: ViaCepResponse = await response.json();
      if (data.erro) throw new Error('CEP nao encontrado');
      // Mantem numero/referencia que sao especificos do endereco; sobrescreve o resto.
      setEditingDeliveryAddressData((prev) => ({
        ...prev,
        cidade: data.localidade || prev.cidade,
        estado: data.uf || prev.estado,
        endereco: data.logradouro || prev.endereco,
        bairro: data.bairro || prev.bairro,
        complemento: data.complemento || prev.complemento,
      }));
    } catch (error) {
      console.error('Erro ao buscar CEP (edicao inline):', error);
    } finally {
      setEditingDeliveryAddressCepLoading(false);
    }
  };

  // Atualizar dados do contrato
  const updateContractData = () => {
    setContractData(prev => ({
      ...prev,
      diaVencimento,
      numeroP2B: numeroP2B || undefined,
      comprometimentoMinimo: comprometimentoMinimo ? parseFloat(comprometimentoMinimo) : undefined,
      fastchip
    }));
  };

  const isClienteCadastralInativo = () =>
    !!selectedClient &&
    !!selectedClient.situacaoCadastral &&
    selectedClient.situacaoCadastral !== 'ATIVA';

  /** Bloqueia criação quando a regra do time exige cliente ATIVO na Receita e o contrato não é Fastchip. */
  const checkClienteCadastralAtivoBlock = (): boolean => {
    if (editingContractId) return false;
    if (!teamContractRules.requireClienteAtivo) return false;
    if (!isClienteCadastralInativo()) return false;
    if (fastchip) return false;
    const msg =
      `O cliente "${selectedClient?.razaoSocial}" não está com situação cadastral ATIVA na Receita. ` +
      'Contratos Fastchip podem ser criados — marque Fastchip na aba Contrato. ' +
      'Contratos por entrega (sem Fastchip) exigem cliente ATIVO.';
    setTabValidationError(msg);
    alert(msg);
    return true;
  };

  const linhasSuspensaoDraft = useMemo(
    () =>
      linhas.filter((linha) => linha.requerSuspensao === true || linha.excluirDoContrato === true),
    [linhas]
  );

  const hasRenegociacaoSuspensaoDraft = useMemo(
    () => linhasSuspensaoDraft.some((linha) => linha.negociacao === 'Renegociação'),
    [linhasSuspensaoDraft]
  );

  const hasSuspensaoDraft = useMemo(() => {
    if (editingContractId) return linhasSuspensaoDraft.length > 0;
    return temSuspensao === true || linhasSuspensaoDraft.length > 0;
  }, [editingContractId, temSuspensao, linhasSuspensaoDraft.length]);

  const meuTimTabVisible = hasRenegociacaoSuspensaoDraft;

  // TEMPORÁRIO (pedido Eder — bater meta): DH por entrega com suspensão não exige Meu TIM
  // (mesma política opcional do Fastchip). Demais contratos sem Fastchip seguem obrigatórios.
  const meuTimStepRequired =
    meuTimTabVisible && hasSuspensaoDraft && !fastchip && !dhDeliverySuspensionAllowed;
  const meuTimStepOptional =
    meuTimTabVisible && hasSuspensaoDraft && (fastchip || dhDeliverySuspensionAllowed);

  const formatLinhaIdentificacao = useCallback((linha: ContractLineFormItem, idx: number) => {
    const telefone = linha.numero?.trim()
      ? `${(linha.ddd || '').trim()} ${linha.numero.trim()}`.trim()
      : '';
    if (telefone) return telefone;
    if (linha.chipCcid?.trim()) return `ICCID ${linha.chipCcid.trim()}`;
    return `Linha ${idx + 1}`;
  }, []);

  const getMeuTimLineKey = useCallback((linha: ContractLineFormItem, idx: number) => {
    return linha.id || `draft-${idx}`;
  }, []);

  const meuTimSharedCredential = useMemo(() => {
    const source =
      linhasSuspensaoDraft.find(
        (linha) =>
          !!linha.meuTimLogin?.trim() ||
          !!linha.meuTimSenha?.trim()
      ) ?? linhasSuspensaoDraft[0];
    return {
      login: source?.meuTimLogin ?? '',
      senha: source?.meuTimSenha ?? '',
    };
  }, [linhasSuspensaoDraft]);

  const meuTimCredentialSignature = useMemo(
    () =>
      `${selectedClient?.id ?? 'sem-cliente'}:${meuTimSharedCredential.login.trim()}|${meuTimSharedCredential.senha.trim()}`,
    [selectedClient?.id, meuTimSharedCredential.login, meuTimSharedCredential.senha]
  );

  const hasCompleteMeuTimCredentials = useMemo(
    () =>
      linhasSuspensaoDraft.length > 0 &&
      !!meuTimSharedCredential.login?.trim() &&
      !!meuTimSharedCredential.senha?.trim(),
    [linhasSuspensaoDraft.length, meuTimSharedCredential.login, meuTimSharedCredential.senha]
  );

  const buildFastchipIccidPoolForLine = useCallback(
    (line: ContractLineFormItem): ICcIdResponse[] => buildIccidPoolForLineDraft(line),
    [buildIccidPoolForLineDraft]
  );

  const rebalanceFastchipIccids = useCallback(
    (sourceLines: ContractLineFormItem[]) => {
      const nextLines = sourceLines.map((line) => ({ ...line }));
      const aditivoIndexes = nextLines
        .map((line, index) => ({ line, index }))
        .filter(
          ({ line }) => line.negociacao === 'Aditivo' && !(line.excluirDoContrato ?? false)
        );

      const ccidToIndexes = new Map<string, number[]>();
      for (const { line, index } of aditivoIndexes) {
        const current = (line.chipCcid || '').trim();
        if (!current) continue;
        const list = ccidToIndexes.get(current) ?? [];
        list.push(index);
        ccidToIndexes.set(current, list);
      }

      const reserved = new Set<string>();
      const unresolvedLineLabels: string[] = [];
      let reassignedCount = 0;
      let duplicatesDetected = false;

      for (let i = 0; i < aditivoIndexes.length; i++) {
        const index = aditivoIndexes[i].index;
        let line = nextLines[index];
        let dddForPool = (line.ddd || '').trim();
        if (!dddForPool) {
          const inferredDdd = inferDddForSuspensionAutoAditivo(nextLines);
          if (inferredDdd) {
            dddForPool = inferredDdd;
            line = { ...line, ddd: inferredDdd };
            nextLines[index] = line;
          }
        }

        const current = (line.chipCcid || '').trim();
        const pool = buildFastchipIccidPoolForLine({ ...line, ddd: dddForPool });
        const poolIds = new Set(pool.map((ic) => ic.ccId));
        const typedPoolSource = line.isEsim === true ? esimIccidPool : physicalIccidPool;
        const duplicateIndexes = current ? ccidToIndexes.get(current) ?? [] : [];
        const keepDuplicateOwner = duplicateIndexes.length === 0 || duplicateIndexes[0] === index;
        if (duplicateIndexes.length > 1) duplicatesDetected = true;

        if (current && keepDuplicateOwner && !reserved.has(current)) {
          if (poolIds.has(current)) {
            reserved.add(current);
            continue;
          }
          if (
            typedPoolSource.some((ic) => ic.ccId === current && ic.disponivel === true)
          ) {
            reserved.add(current);
            continue;
          }
        }

        const replacement = pool.find((ic) => !reserved.has(ic.ccId))?.ccId;
        if (replacement) {
          if (replacement !== current) {
            nextLines[index] = { ...line, chipCcid: replacement };
            reassignedCount++;
          }
          reserved.add(replacement);
        } else {
          if (current) {
            nextLines[index] = { ...line, chipCcid: '' };
            reassignedCount++;
          }
          unresolvedLineLabels.push(formatLinhaIdentificacao(line, i));
        }
      }

      return {
        nextLines,
        reassignedCount,
        unresolvedLineLabels,
        duplicatesDetected,
      };
    },
    [buildFastchipIccidPoolForLine, formatLinhaIdentificacao, esimIccidPool, physicalIccidPool]
  );

  const convertDraftToNonFastchip = useCallback(
    (reason?: string) => {
      setFastchip(false);
      setLinhaAtual((prev) =>
        prev.negociacao === 'Aditivo'
          ? { ...prev, chipCcid: '', valorChip: '7.00', isEsim: false }
          : prev
      );
      setLinhaEditando((prev) => ({ ...prev, chipCcid: '', valorChip: '7.00', isEsim: false }));
      setLinhas((prev) =>
        prev.map((line) =>
          line.negociacao === 'Aditivo'
            ? { ...line, chipCcid: '', valorChip: 7, isEsim: false }
            : line
        )
      );
      showWarning(
        reason
          ? `${reason} O contrato foi convertido para sem Fastchip (entrega da operadora).`
          : 'Contrato convertido para sem Fastchip (entrega da operadora).'
      );
    },
    [setFastchip, setLinhaAtual, setLinhaEditando, setLinhas, showWarning]
  );

  const openConvertToEntregaModal = useCallback((lineLabels: string[]) => {
    setPendingEntregaConversionLines(lineLabels);
    setShowConvertToEntregaModal(true);
  }, []);

  const handleConvertToEntregaConfirm = useCallback(() => {
    convertDraftToNonFastchip('Estoque Fastchip insuficiente.');
    setShowConvertToEntregaModal(false);
    setPendingEntregaConversionLines([]);
  }, [convertDraftToNonFastchip]);

  const handleConvertToEntregaCancel = useCallback(() => {
    setShowConvertToEntregaModal(false);
    setPendingEntregaConversionLines([]);
  }, []);

  const handleFastchipStockApiError = useCallback(
    (error: unknown, fallbackMessage: string) => {
      const fastchipStockErr = parseFastchipStockApiError(error);
      if (fastchipStockErr?.suggestFastchipFalse) {
        showError(fastchipStockErr.message || fallbackMessage);
        openConvertToEntregaModal(fastchipStockErr.affectedLines ?? []);
        return true;
      }
      return false;
    },
    [openConvertToEntregaModal, showError]
  );

  const ensureFastchipIccidConsistency = useCallback(
    (source: 'auto' | 'linhas' | 'save' | 'draft') => {
      if (!fastchip) {
        fastchipAutoAdjustSignatureRef.current = '';
        return { ok: true, adjusted: false };
      }
      if (backendFastchipAllocationMode) {
        // No modo backend não existe pool/atribuição local de ICCID para balancear.
        fastchipAutoAdjustSignatureRef.current = '';
        return { ok: true, adjusted: false };
      }

      if (
        source === 'auto' &&
        pendingDraftIccidValidation &&
        (iccidPoolsLoading ||
          iccidPoolRefreshNonce === 0 ||
          iccidPoolsLoadedNonce !== iccidPoolRefreshNonce)
      ) {
        return { ok: true, adjusted: false };
      }

      const resolution = rebalanceFastchipIccids(linhas);
      if (resolution.reassignedCount > 0) {
        setLinhas(resolution.nextLines);
      }

      const unresolvedSignature = resolution.unresolvedLineLabels.join('|');
      const shouldNotifyUnresolved =
        unresolvedSignature.length > 0 &&
        (source !== 'auto' || fastchipAutoAdjustSignatureRef.current !== unresolvedSignature);

      const shouldPromptConvertToEntrega =
        source === 'draft' ||
        source === 'save' ||
        source === 'linhas' ||
        (source === 'auto' && pendingDraftIccidValidation);

      if (shouldNotifyUnresolved && !shouldPromptConvertToEntrega) {
        showWarning(
          `Sem ICCIDs suficientes para manter Fastchip em todas as linhas Aditivo. Linhas afetadas: ${resolution.unresolvedLineLabels.join(', ')}.`
        );
      }

      if (unresolvedSignature.length > 0) {
        fastchipAutoAdjustSignatureRef.current = unresolvedSignature;
        if (shouldPromptConvertToEntrega) {
          showError(buildFastchipIccidErrorToast(resolution.unresolvedLineLabels));
          openConvertToEntregaModal(resolution.unresolvedLineLabels);
          setPendingDraftIccidValidation(false);
        }
        return { ok: false, adjusted: resolution.reassignedCount > 0 };
      }

      if (source !== 'auto' && resolution.reassignedCount > 0) {
        showInfo('ICCID duplicado detectado e reatribuído automaticamente com chips do mesmo cluster.');
      }

      fastchipAutoAdjustSignatureRef.current = '';
      return { ok: true, adjusted: resolution.reassignedCount > 0 };
    },
    [
      fastchip,
      rebalanceFastchipIccids,
      linhas,
      setLinhas,
      showWarning,
      showInfo,
      showError,
      openConvertToEntregaModal,
      pendingDraftIccidValidation,
      iccidPoolsLoading,
      iccidPoolRefreshNonce,
      iccidPoolsLoadedNonce,
      setPendingDraftIccidValidation,
      editingContractId,
      convertDraftToNonFastchip,
      backendFastchipAllocationMode,
    ]
  );

  const runDraftIccidValidation = useCallback(() => {
    if (!fastchip || !hasActiveFastchipAditivoLines(linhas, fastchip)) {
      setPendingDraftIccidValidation(false);
      return;
    }
    const result = ensureFastchipIccidConsistency('draft');
    if (result.ok) {
      setPendingDraftIccidValidation(false);
    }
  }, [fastchip, linhas, ensureFastchipIccidConsistency, setPendingDraftIccidValidation]);

  useEffect(() => {
    // Em edição de contrato existente, não reatribuir ICCID automaticamente ao abrir/carregar
    // o formulário — isso alterava a aba Linhas sem o usuário entrar nela.
    if (editingContractId) return;
    void ensureFastchipIccidConsistency('auto');
  }, [linhas, fastchip, editingContractId, ensureFastchipIccidConsistency]);

  useEffect(() => {
    if (!pendingDraftIccidValidation) return;
    if (!isCreateModalOpen || !draftContractId || editingContractId) return;
    if (!fastchip || !hasActiveFastchipAditivoLines(linhas, fastchip)) {
      setPendingDraftIccidValidation(false);
      return;
    }
    if (!selectedClient?.estado || iccidsLoading || iccidPoolsLoading) return;
    if (iccidPoolRefreshNonce === 0 || iccidPoolsLoadedNonce !== iccidPoolRefreshNonce) return;

    runDraftIccidValidation();
  }, [
    pendingDraftIccidValidation,
    isCreateModalOpen,
    draftContractId,
    editingContractId,
    fastchip,
    linhas,
    selectedClient?.estado,
    iccidsLoading,
    iccidPoolsLoading,
    iccidPoolRefreshNonce,
    iccidPoolsLoadedNonce,
    physicalIccidPool,
    esimIccidPool,
    runDraftIccidValidation,
  ]);

  const updateMeuTimSharedCredential = useCallback(
    (field: 'meuTimLogin' | 'meuTimSenha', value: string) => {
      setLinhas((prev) =>
        prev.map((linha) => {
          if (!(linha.requerSuspensao === true || linha.excluirDoContrato === true)) return linha;
          return {
            ...linha,
            [field]: value,
          };
        })
      );
    },
    [setLinhas]
  );

  useEffect(() => {
    if (linhasSuspensaoDraft.length === 0) return;

    const login = meuTimSharedCredential.login ?? '';
    const senha = meuTimSharedCredential.senha ?? '';
    if (!login.trim() && !senha.trim()) return;

    setLinhas((prev) => {
      let changed = false;
      const next = prev.map((linha) => {
        if (!(linha.requerSuspensao === true || linha.excluirDoContrato === true)) return linha;
        if ((linha.meuTimLogin ?? '') === login && (linha.meuTimSenha ?? '') === senha) return linha;
        changed = true;
        return {
          ...linha,
          meuTimLogin: login,
          meuTimSenha: senha,
        };
      });
      return changed ? next : prev;
    });
  }, [linhasSuspensaoDraft.length, meuTimSharedCredential.login, meuTimSharedCredential.senha, setLinhas]);

  const toggleMeuTimPasswordVisibility = useCallback((lineKey: string) => {
    setMeuTimPasswordVisibility((prev) => ({
      ...prev,
      [lineKey]: !prev[lineKey],
    }));
  }, []);

  const handleVerifyMeuTimAccess = useCallback(async () => {
    if (!selectedClient?.id) {
      setMeuTimVerificationStatus('error');
      setMeuTimVerificationMessage('Selecione um cliente antes de verificar o acesso Meu TIM.');
      return;
    }

    if (!hasCompleteMeuTimCredentials) {
      setMeuTimVerificationStatus('error');
      setMeuTimVerificationMessage(
        'Preencha login e senha Meu TIM do cliente antes de verificar.'
      );
      return;
    }

    try {
      setMeuTimVerificationStatus('loading');
      setMeuTimVerificationMessage('Verificando acesso Meu TIM agora (login real no portal, até 45s)...');
      // Fatia 0 GCM: verify on-demand real (nunca enqueue "de fé") — só marca sucesso
      // quando o worker devolve status 'ok' de um login real no portal Meu TIM.
      const response = await clientService.verifyMeuTimHealthNow(selectedClient.id);
      const mapped = mapMeuTimHealthCheckResponse(response);

      if (mapped.status === 'ok') {
        setMeuTimVerificationStatus('success');
        setMeuTimVerifiedSignature(meuTimCredentialSignature);
        setMeuTimVerificationMessage(mapped.message);
        showSuccess(mapped.message);
      } else {
        // Soft-fail (RN da fatia 0): a verificação foi tentada de verdade com as
        // credenciais atuais, mas não deu "ok" (credencial inválida / captcha /
        // timeout / erro portal). Não bloqueia o fluxo — só avisa e pede validação
        // manual depois no modal de detalhes das linhas.
        setMeuTimVerificationStatus('soft_failed');
        setMeuTimVerificationMessage(
          `${mapped.message} O acesso Meu TIM NÃO foi confirmado — você pode continuar o cadastro, mas valide manualmente depois no modal "Detalhes das linhas" (botão "Verificar acesso agora").`
        );
        setMeuTimVerifiedSignature(meuTimCredentialSignature);
        showWarning(mapped.message);
      }
    } catch (err) {
      const mapped = mapMeuTimVerifyOnDemandError(err);
      setMeuTimVerificationStatus('soft_failed');
      setMeuTimVerificationMessage(
        `${mapped.message} O acesso Meu TIM NÃO foi confirmado — você pode continuar o cadastro, mas valide manualmente depois no modal "Detalhes das linhas" (botão "Verificar acesso agora").`
      );
      setMeuTimVerifiedSignature(meuTimCredentialSignature);
      showWarning(mapped.message);
    }
  }, [
    hasCompleteMeuTimCredentials,
    meuTimCredentialSignature,
    selectedClient?.id,
    showSuccess,
    showWarning,
  ]);

  useEffect(() => {
    if (!linhasSuspensaoDraft.length) {
      if (meuTimVerificationStatus !== 'idle') {
        setMeuTimVerificationStatus('idle');
        setMeuTimVerificationMessage('');
        setMeuTimVerifiedSignature(null);
      }
      return;
    }

    if (
      (meuTimVerificationStatus === 'success' || meuTimVerificationStatus === 'soft_failed') &&
      meuTimVerifiedSignature !== meuTimCredentialSignature
    ) {
      setMeuTimVerificationStatus('idle');
      setMeuTimVerificationMessage('');
      setMeuTimVerifiedSignature(null);
    }
  }, [
    linhasSuspensaoDraft.length,
    meuTimCredentialSignature,
    meuTimVerificationStatus,
    meuTimVerifiedSignature,
  ]);

  const validateMeuTimStep = () => {
    setTabValidationError('');

    if (!meuTimStepRequired) return true;
    if (linhasSuspensaoDraft.length === 0) {
      setTabValidationError(
        'Marque pelo menos uma linha para suspensão na aba "Linhas a Desativar" antes de continuar para Meu TIM.'
      );
      return false;
    }

    if (!hasCompleteMeuTimCredentials) {
      setTabValidationError('Preencha a credencial Meu TIM do cliente (login e senha) antes de avançar.');
      return false;
    }

    if (meuTimStepRequired) {
      // Soft-fail (fatia 0 GCM): uma tentativa real de verificação com as credenciais
      // atuais — sucesso OU falha (credencial inválida/captcha/timeout/erro portal) —
      // já libera o avanço. Só bloqueia quem nunca tentou verificar (idle) ou tem
      // pendência client-side (error: sem cliente/credenciais incompletas).
      const jaTentouComCredencialAtual =
        (meuTimVerificationStatus === 'success' || meuTimVerificationStatus === 'soft_failed') &&
        meuTimVerifiedSignature === meuTimCredentialSignature;

      if (!jaTentouComCredencialAtual) {
        const mensagemPendencia =
          meuTimVerificationStatus === 'loading'
            ? 'Aguarde a verificação do Meu TIM terminar antes de avançar/salvar.'
            : meuTimVerificationStatus === 'error'
            ? 'A última verificação do Meu TIM não pôde ser feita (pendência com cliente/credenciais). Corrija e clique em "Verificar acesso" novamente.'
            : 'Clique em "Verificar acesso" na aba Meu TIM antes de avançar/salvar contratos sem Fastchip. O resultado real (mesmo se falhar) já libera o avanço.';
        setTabValidationError(
          mensagemPendencia
        );
        return false;
      }
    }

    return true;
  };

  // Validar dados do contrato
  const validateContractData = () => {
    setTabValidationError('');

    if (checkClienteCadastralAtivoBlock()) {
      return false;
    }

    if (!editingContractId && !bloqueioSuspensaoNovosContratos && temSuspensao === null) {
      const msgSusp =
        'Responda se o contrato terá suspensão de linhas antes de avançar (cartão laranja no topo da aba Contrato).';
      setTabValidationError(msgSusp);
      alert(msgSusp);
      return false;
    }

    if (!editingContractId) {
      if (fastchip && linhas.some((linha) => lineHasAnyHandset(linha))) {
        const msgAparelho =
          'Contratos com aparelho (incluindo WTTX) devem ser por entrega da operadora. Desmarque Fastchip ou remova os aparelhos das linhas.';
        setTabValidationError(msgAparelho);
        alert(msgAparelho);
        return false;
      }

      const normalizedClusterToken = normalizeContractSaleTokenForForm(token, fastchip);
      if (!normalizedClusterToken) {
        const msgTok = token.trim()
          ? 'O token de cluster não é válido para esta configuração (Fastchip ou operacional TIM). Escolha uma opção da lista na aba Contrato.'
          : 'Selecione um token de cluster na lista da aba Contrato antes de avançar. Ao criar um contrato novo o campo é obrigatório.';
        setTabValidationError(msgTok);
        alert(msgTok);
        return false;
      }
    }

    if (!editingContractId && canAssignResponsibleConsultant && !responsibleConsultantUserId) {
      const msgConsultor = 'Selecione o consultor responsável na aba Contrato.';
      setTabValidationError(msgConsultor);
      alert(msgConsultor);
      return false;
    }

    return true;
  };

  /**
   * Avalia se a regra de IE inapta bloqueia o avanço atual.
   * Retorna true quando devemos PARAR e abrir o modal de bypass.
   * Retorna false quando está liberado (fastchip=true, MEI, IE ok, bypass já confirmado, etc.)
   *
   * ATENÇÃO: só avalia quando activeTab === 'contrato' pois é ali que fastchip e o tipo de
   * negociação são definidos. Na tab linhasDesativar, se o contrato virou Fastchip (por S7),
   * o S7c já limpou o bypass — a avaliação retornará 'ok'.
   */
  const checkIeBlockAndMaybeOpen = (onClear: () => void): boolean => {
    if (ieBypassConfirmed || ieBypassConfirmedRef.current) return false; // Usuário já confirmou
    // Contratos Fastchip não entram na regra de IE inapta (apenas entrega TIM + Aditivo).
    if (fastchip) return false;
    const policy = evaluateIePolicy(selectedClient, fastchip, linhas);
    if (policy === 'ok') return false;
    // Precisamos bloquear: abrir modal e registrar o que fazer depois
    pendingIeBypassActionRef.current = onClear;
    setShowIeBypassModal(true);
    return true;
  };

  const checkClusterConfirmAndMaybeOpen = (onProceed: () => void): boolean => {
    pendingClusterConfirmActionRef.current = onProceed;
    setShowClusterConfirmModal(true);
    return true;
  };

  const proceedFromContratoTab = (nextTabKey: string | undefined) => {
    const advance = () => {
      updateContractData();
      if (nextTabKey) setActiveTab(nextTabKey);
      // Regras do time em background — não bloqueia a troca de aba.
      void refreshTeamContractRulesQuietly();
    };
    if (
      checkIeBlockAndMaybeOpen(() => {
        void advance();
      })
    ) {
      return;
    }
    void advance();
  };

  // Atualizar dados do endereço
  const updateAddressData = () => {
    setContractData(prev => ({
      ...prev,
      deliveryAddress: {
        cep,
        cidade,
        estado,
        endereco: normalizeDeliveryAddressStreetNameForApi(endereco),
        numero: normalizeDeliveryAddressStreetNumberForApi(numero),
        bairro,
        complemento: complemento || undefined,
        referencia: referencia || undefined
      }
    }));
  };

  // Validar dados do endereço
  const validateAddressData = () => {
    setTabValidationError('');

    if (!cep.trim()) {
      setTabValidationError('Por favor, informe o CEP.');
      return false;
    }
    if (!cidade.trim()) {
      setTabValidationError('Por favor, informe a cidade.');
      return false;
    }
    if (!estado.trim()) {
      setTabValidationError('Por favor, informe o estado.');
      return false;
    }
    if (!isValidDeliveryAddressStreetName(endereco)) {
      setTabValidationError(
        endereco.trim()
          ? 'Logradouro inválido.'
          : DELIVERY_ADDRESS_STREET_NAME_EMPTY_ERROR
      );
      return false;
    }
    if (!isFilledDeliveryAddressStreetNumber(numero)) {
      setTabValidationError(DELIVERY_ADDRESS_STREET_NUMBER_EMPTY_ERROR);
      return false;
    }
    if (!isValidDeliveryAddressStreetNumber(numero)) {
      setTabValidationError('Número do endereço inválido.');
      return false;
    }
    if (!bairro.trim()) {
      setTabValidationError('Por favor, informe o bairro.');
      return false;
    }
    return true;
  };

  // Atualizar dados do responsável pela auditoria
  const updateAdministratorData = () => {
    setContractData(prev => ({
      ...prev,
      administrator: {
        nomeCompleto: adminNomeCompleto.trim(),
        cpf: adminCpf.trim(),
        rg: adminRg.trim(),
        email: adminEmail.trim() || undefined,
        telefone1: adminTelefone1.trim() || undefined,
        telefone2: adminTelefone2.trim() || undefined,
        dataNascimento: new Date('2000-08-18')
      }
    }));
  };

  // Validar dados do responsável pela auditoria
  const validateAdministratorData = () => {
    setTabValidationError('');

    if (!adminNomeCompleto.trim()) {
      setTabValidationError('Por favor, informe o nome completo do responsável pela auditoria.');
      return false;
    }
    if (!adminCpf.trim() || !isValidCpfLength(adminCpf)) {
      setTabValidationError('Por favor, informe um CPF válido do responsável pela auditoria (11 dígitos).');
      return false;
    }
    if (!adminEmail.trim()) {
      setTabValidationError('Por favor, informe o e-mail do responsável pela auditoria.');
      return false;
    }
    // Validação básica de e-mail
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(adminEmail.trim())) {
      setTabValidationError('Por favor, informe um e-mail válido.');
      return false;
    }
    // Validar telefone1 (obrigatório)
    const telefone1Limpo = adminTelefone1.trim();
    if (!telefone1Limpo) {
      setTabValidationError('Por favor, informe o telefone principal do responsável pela auditoria.');
      return false;
    }
    // Telefone deve ter 10 (fixo) ou 11 dígitos (celular)
    const telefone1Digitos = telefone1Limpo.replace(/\D/g, '');
    if (telefone1Digitos.length !== 10 && telefone1Digitos.length !== 11) {
      setTabValidationError('O telefone principal deve conter 10 dígitos (fixo) ou 11 dígitos (celular). Exemplos: (11) 3456-7890 ou (11) 99999-9999');
      return false;
    }

    // Validar telefone2 (opcional, mas se preenchido deve ter 10 ou 11 dígitos)
    const telefone2Limpo = adminTelefone2.trim();
    if (telefone2Limpo) {
      const telefone2Digitos = telefone2Limpo.replace(/\D/g, '');
      if (telefone2Digitos.length !== 10 && telefone2Digitos.length !== 11) {
        setTabValidationError('O telefone secundário deve conter 10 dígitos (fixo) ou 11 dígitos (celular). Exemplos: (11) 3456-7890 ou (11) 88888-8888');
        return false;
      }
    }
    return true;
  };

  // Validar dados dos representantes
  const validateRepresentativesData = () => {
    setTabValidationError('');

    if (!representantes || representantes.length === 0) {
      setTabValidationError('O contrato deve ter pelo menos um representante legal.');
      return false;
    }

    // Validar Nome e CPF de todos os representantes
    for (let i = 0; i < representantes.length; i++) {
      const rep = representantes[i];
      
      // Validar Nome obrigatório
      if (!rep || !rep.nome || rep.nome.trim() === '') {
        setTabValidationError('O nome do representante legal é obrigatório.');
        return false;
      }
      
      // Validar CPF obrigatório com 11 dígitos
      if (!rep.cpf || !isValidCpfLength(rep.cpf)) {
        const maskedHint = rep.cpfHintFromQsa
          ? ` O CPF vindo da Receita estava mascarado (${rep.cpfHintFromQsa}).`
          : '';
        setTabValidationError(
          `Informe o CPF completo (11 dígitos) do representante "${rep.nome}".${maskedHint}`
        );
        return false;
      }

      const emailRep = (rep.email || '').trim();
      if (!emailRep || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailRep)) {
        setTabValidationError(
          `Informe o e-mail do representante "${rep.nome}" (não vem do QSA da Receita; aparece na proposta comercial).`
        );
        return false;
      }

      const telNorm = normalizeRepresentativePhoneInput(rep.telefone || '');
      if (telNorm.length !== 10 && telNorm.length !== 11) {
        setTabValidationError(
          `Informe o telefone do representante "${rep.nome}" com 10 ou 11 dígitos (não vem do QSA da Receita; aparece na proposta comercial).`
        );
        return false;
      }
    }

    return true;
  };

  const modalTabs = React.useMemo(() => {
    // 'dadosTim' só aparece quando a consulta por CNPJ encontrou dados na base TIM.
    const baseTabs = timDataTabVisible
      ? CONTRACT_FORM_TABS
      : CONTRACT_FORM_TABS.filter((tab) => tab.key !== 'dadosTim');
    if (contractModalMode === 'linesOnly') {
      const keys = ['linhas', 'linhasDesativar', ...(meuTimTabVisible ? ['meuTim'] : [])];
      return baseTabs.filter((tab) => keys.includes(tab.key));
    }
    return baseTabs;
  }, [contractModalMode, meuTimTabVisible, timDataTabVisible]);

  useEffect(() => {
    if (contractModalMode === 'linesOnly' && activeTab === 'meuTim' && !meuTimTabVisible) {
      setActiveTab('linhasDesativar');
    }
  }, [contractModalMode, activeTab, meuTimTabVisible, setActiveTab]);

  useEffect(() => {
    if (activeTab === 'dadosTim' && !timDataTabVisible) {
      setActiveTab('cliente');
    }
  }, [activeTab, timDataTabVisible, setActiveTab]);

  // Ações do modal (em grupo: um único "Salvar" replica dados para os dois contratos)
  const isEditingGroup = editingGroupContracts && editingGroupContracts.length > 1;

  const handleNextTab = () => {
    // Validação da tab atual antes de avançar
    if (activeTab === 'cliente') {
      if (!selectedClient) {
        alert('Por favor, selecione um cliente antes de continuar.');
        return;
      }
      if (!editingContractId && clientRequiresExistingDraft()) {
        setShowClientDraftsModal(true);
        return;
      }
    }

    if (activeTab === 'contrato') {
      if (!validateContractData()) {
        return;
      }
      if (!editingContractId) {
        const currentIndex = modalTabs.findIndex((tab) => tab.key === activeTab);
        const nextTab = modalTabs[currentIndex + 1]?.key;
        if (nextTab === 'linhas') {
          if (checkClusterConfirmAndMaybeOpen(() => proceedFromContratoTab(nextTab))) {
            return;
          }
        } else if (
          checkIeBlockAndMaybeOpen(() => {
            updateContractData();
            if (nextTab) setActiveTab(nextTab);
            void refreshTeamContractRulesQuietly();
          })
        ) {
          return;
        }
      }
      updateContractData();
    }

    if (activeTab === 'linhas') {
      // Validação básica - verificar se há pelo menos uma linha
      if (linhas.length === 0) {
        alert('Por favor, adicione pelo menos uma linha ao contrato.');
        setIsSavingContract(false);
        return;
      }
      
      // Validação de duplicatas
      if (teamContractRules.blockDuplicateCcid && hasDuplicateCcid(linhas)) {
        const consistency = ensureFastchipIccidConsistency('linhas');
        if (!consistency.ok || consistency.adjusted) {
          alert('Detectamos ICCID duplicado e tentamos reatribuir automaticamente. Revise as linhas e tente novamente.');
          return;
        }
        alert('Existem CCIDs duplicados nas linhas de Aditivo. Corrija os duplicados antes de prosseguir.');
        return;
      }
      
      if (teamContractRules.blockDuplicatePhone && hasDuplicatePhone(linhas)) {
        alert('Existem números de telefone duplicados nas linhas de Renegociação. Corrija os duplicados antes de prosseguir.');
        return;
      }
      
      // Validação: verificar se todas as linhas Aditivo têm seraUsadaPeloCliente definido
      if (teamContractRules.requireSeraUsadaPeloCliente) {
        const linhasAditivoSemFlag = linhas.filter(l => l.negociacao === 'Aditivo' && (l.seraUsadaPeloCliente === null || l.seraUsadaPeloCliente === undefined));
        if (linhasAditivoSemFlag.length > 0) {
          alert(`Existem ${linhasAditivoSemFlag.length} linha(s) de Aditivo sem informação sobre uso pelo cliente. Por favor, edite as linhas e informe se serão usadas pelo cliente.`);
          return;
        }
      }
    }

    // Tab linhasDesativar não precisa de validação obrigatória - é opcional

    if (activeTab === 'meuTim') {
      if (!validateMeuTimStep()) {
        return;
      }
    }

    if (activeTab === 'endereco') {
      if (!validateAddressData()) {
        return;
      }
      updateAddressData();

      // Verificar se é Fastchip e se o modal ainda não foi visto
      const currentIndex = modalTabs.findIndex(tab => tab.key === activeTab);
      const nextTab = modalTabs[currentIndex + 1];
      
      if (nextTab && nextTab.key === 'administrador' && fastchip && !hasSeenFastchipAddressModal) {
        // Mostrar modal antes de avançar
        setShowFastchipAddressModal(true);
        return; // Não avançar até o modal ser confirmado
      }
    }

    if (activeTab === 'administrador') {
      if (!validateAdministratorData()) {
        return;
      }
      updateAdministratorData();
    }

    if (activeTab === 'representantes') {
      if (!validateRepresentativesData()) {
        return;
      }
    }

    const currentIndex = modalTabs.findIndex(tab => tab.key === activeTab);
    if (currentIndex < modalTabs.length - 1) {
      // Troca de aba imediata; regras do time e rascunho seguem em background.
      setActiveTab(modalTabs[currentIndex + 1].key);
      void refreshTeamContractRulesQuietly();

      if (!editingContractId && activeTab === 'linhas' && canPersistContractDraft(teamContractRules)) {
        persistContractDraftInBackground('Erro ao criar rascunho:', { notifyCreated: true });
      } else if (!editingContractId && draftContractId) {
        persistContractDraftInBackground('Erro ao atualizar rascunho ao avançar aba:');
      }
    }
  };

  const handlePreviousTab = () => {
    const currentIndex = modalTabs.findIndex(tab => tab.key === activeTab);
    if (currentIndex > 0) {
      setActiveTab(modalTabs[currentIndex - 1].key);
      void refreshTeamContractRulesQuietly();
      if (!editingContractId && draftContractId) {
        persistContractDraftInBackground('Erro ao atualizar rascunho ao voltar aba:');
      }
    }
  };

  // Sem useMemo: handlers de validação leem estado do formulário (ex.: temSuspensao) que muda
  // dentro da mesma aba; memoizar com deps incompletas deixava "Próximo"/"Salvar" com closure obsoleta.
  const modalActions = (() => {
    const isLinesOnly = contractModalMode === 'linesOnly';
    const isSaveTab = isLinesOnly || activeTab === 'observacoesInternas';
    const isFirstTab = activeTab === modalTabs[0]?.key;
    const isLastTab = activeTab === modalTabs[modalTabs.length - 1]?.key;
    // Em edição completa, Salvar não depende de canMutateContractLines:
    // Assinado (e outros) pode editar QSA/auditoria/etc. sem liberar alteração de linhas.
    const saveDisabled = isLinesOnly
      ? isSavingContract
      : !isSaveTab || isSavingContract;

    return [
      {
        key: 'cancel',
        content: 'Cancelar',
        icon: 'cancel',
        onClick: () => handleCloseAttempt(),
      },
      {
        key: 'previous',
        content: <span style={{ marginRight: '8px' }}>Anterior</span>,
        icon: 'arrow left',
        onClick: () => {
          void handlePreviousTab();
        },
        disabled: isFirstTab,
      },
      {
        key: 'next',
        content: <span style={{ marginLeft: '8px' }}>Próximo</span>,
        icon: 'arrow right',
        primary: true,
        onClick: () => {
          void handleNextTab();
        },
        disabled: isLastTab,
      },
      {
        key: 'save',
        content: isSavingContract ? 'Salvando...' : isLinesOnly ? 'Salvar linhas' : 'Salvar Contrato',
        icon: isSavingContract ? 'spinner' : 'save',
        primary: true,
        loading: isSavingContract,
        onClick: () => handleSaveContract(),
        disabled: saveDisabled,
      },
    ];
  })();

  // Validar aba atual
  const validateCurrentTab = (tabKey: string): boolean => {
    setTabValidationError(''); // Limpar erro anterior

    if (tabKey === 'cliente' && !selectedClient) {
      setTabValidationError('Por favor, selecione um cliente antes de continuar.');
      return false;
    }

    if (tabKey === 'contrato' && !validateContractData()) {
      return false;
    }

    if (tabKey === 'endereco' && !validateAddressData()) {
      return false;
    }

    if (tabKey === 'meuTim' && !validateMeuTimStep()) {
      return false;
    }

    if (tabKey === 'administrador' && !validateAdministratorData()) {
      return false;
    }

    if (tabKey === 'representantes' && !validateRepresentativesData()) {
      return false;
    }

    return true;
  };

  // Handler para mudança de aba com validação
  const handleTabChange = (newTabKey: string) => {
    if (newTabKey === activeTab) return;

    if (!editingContractId && clientRequiresExistingDraft()) {
      setShowClientDraftsModal(true);
      return;
    }

    const currentIndex = modalTabs.findIndex(tab => tab.key === activeTab);
    const newIndex = modalTabs.findIndex(tab => tab.key === newTabKey);

    const goToTab = (tabKey: string, persistDraft = true) => {
      setActiveTab(tabKey);
      void refreshTeamContractRulesQuietly();
      if (persistDraft && !editingContractId && draftContractId) {
        persistContractDraftInBackground('Erro ao atualizar rascunho ao mudar de aba:');
      }
    };

    // Se estiver indo para uma aba anterior, permitir sem validação
    if (newIndex < currentIndex) {
      goToTab(newTabKey);
      return;
    }

    // Se estiver indo para uma aba posterior, validar a aba atual primeiro
    if (newIndex > currentIndex) {
      if (!validateCurrentTab(activeTab)) {
        return;
      }
      if (activeTab === 'contrato' && !editingContractId) {
        if (newTabKey === 'linhas') {
          if (checkClusterConfirmAndMaybeOpen(() => proceedFromContratoTab(newTabKey))) {
            return;
          }
        } else if (
          checkIeBlockAndMaybeOpen(() => {
            goToTab(newTabKey);
          })
        ) {
          return;
        }
      }
    }

    if (!editingContractId && activeTab === 'linhas' && newIndex > currentIndex) {
      if (canPersistContractDraft(teamContractRules)) {
        setActiveTab(newTabKey);
        void refreshTeamContractRulesQuietly();
        persistContractDraftInBackground('Erro ao salvar rascunho ao mudar de aba:', {
          notifyCreated: true,
        });
      } else {
        goToTab(newTabKey);
      }
      return;
    }

    goToTab(newTabKey);
  };

  const validateLinesForSave = (): boolean => {
    const lineFieldError = getFirstContractLineFieldValidationError(linhas, plans);
    if (lineFieldError) {
      showError(lineFieldError.message);
      setTabValidationError(lineFieldError.message);
      setActiveTab('linhas');
      return false;
    }

    const hasSuspensionMarksOnSave =
      linhasParaDesativar.size > 0 ||
      linhas.some((l) => l.requerSuspensao || l.excluirDoContrato);
    if (hasSuspensionMarksOnSave && fluxoSuspensaoLinhasBloqueado) {
      showError(mensagemOrientacaoSuspensao1056);
      setTabValidationError(mensagemOrientacaoSuspensao1056);
      setActiveTab('linhasDesativar');
      return false;
    }
    // TEMPORÁRIO (pedido Eder — bater meta): permite DH por entrega sem Fastchip.
    if (
      hasSuspensionMarksOnSave &&
      !suspensionAllowedForContract &&
      !shouldSkipSuspensionSaveBlock(hasRole('Admin'), editingContractId)
    ) {
      const msg =
        'Marcação de linhas para suspensão só é permitida em contratos Fastchip. Remova a marcação de suspensão ou use um contrato Fastchip.';
      showError(msg);
      setTabValidationError(msg);
      setActiveTab('linhasDesativar');
      return false;
    }

    if (fastchip) {
      const consistency = ensureFastchipIccidConsistency('save');
      if (!consistency.ok) {
        setActiveTab('linhas');
        return false;
      }
    }

    if (teamContractRules.blockDuplicateCcid && hasDuplicateCcid(linhas)) {
      const consistency = ensureFastchipIccidConsistency('save');
      setActiveTab('linhas');
      if (consistency.adjusted) {
        showInfo('ICCID duplicado foi reatribuído automaticamente. Revise as linhas antes de salvar novamente.');
      }
      return false;
    }

    if (teamContractRules.blockDuplicatePhone && hasDuplicatePhone(linhas)) {
      alert('Existem números de telefone duplicados nas linhas de Renegociação. Corrija os duplicados antes de prosseguir.');
      setActiveTab('linhas');
      return false;
    }

    if (teamContractRules.requireSeraUsadaPeloCliente) {
      const linhasAditivoSemFlag = linhas.filter(
        (l) =>
          l.negociacao === 'Aditivo' &&
          (l.seraUsadaPeloCliente === null || l.seraUsadaPeloCliente === undefined)
      );
      if (linhasAditivoSemFlag.length > 0) {
        alert(
          `Existem ${linhasAditivoSemFlag.length} linha(s) de Aditivo sem informação sobre uso pelo cliente. Por favor, edite as linhas e informe se serão usadas pelo cliente.`
        );
        setActiveTab('linhas');
        return false;
      }
    }

    const linhasSuspensas = linhasParaDesativar.size;
    if (teamContractRules.requireAditivosForSuspendedLines && linhasSuspensas > 0) {
      const aditivosNecessarios = calcularAditivosNecessarios(linhasSuspensas);
      const linhasAditivoValidas = linhas.filter(
        (l) => l.negociacao === 'Aditivo' && l.ddd && l.planId && !l.excluirDoContrato
      ).length;

      if (linhasAditivoValidas < aditivosNecessarios) {
        showError(
          `Regra de aditivos não atendida: ${linhasSuspensas} ${linhasSuspensas === 1 ? 'linha suspensa requer' : 'linhas suspensas requerem'} ${aditivosNecessarios} ${aditivosNecessarios === 1 ? 'aditivo' : 'aditivos'}, mas apenas ${linhasAditivoValidas} ${linhasAditivoValidas === 1 ? 'foi encontrado' : 'foram encontrados'}. Por favor, adicione mais linhas aditivas na aba "Linhas" antes de salvar.`
        );
        setActiveTab('linhas');
        return false;
      }
    }

    const portabilidadeErr = validateAllPortabilidadeLines(linhas);
    if (portabilidadeErr) {
      showError(portabilidadeErr);
      setActiveTab('linhas');
      return false;
    }

    return true;
  };

  const handleConfirmEmptyContractSave = async () => {
    const saveFn = pendingContractSaveRef.current;
    if (!saveFn) {
      setEmptyContractConfirmOpen(false);
      return;
    }
    setIsSavingContract(true);
    try {
      await saveFn();
    } catch (error: any) {
      const conflictMessage = error?.response?.data?.message ?? '';
      if (
        error?.response?.status === 409 &&
        typeof conflictMessage === 'string' &&
        conflictMessage.toLowerCase().includes('linhas')
      ) {
        showError(conflictMessage);
      } else if (
        error?.response?.status === 409 ||
        conflictMessage.includes('concorrência') ||
        conflictMessage.includes('alterado por outro usuário')
      ) {
        showError('O contrato foi alterado por outro usuário. Recarregando...');
        if (editingContractId) {
          const contract = await contractService.getContractById(editingContractId);
          setContractUpdatedAt(
            contract.updatedAt ? new Date(contract.updatedAt).toISOString() : null
          );
          if (editingGroupContracts && editingGroupContracts.length > 1) {
            await loadContractForEditingConsolidated(editingGroupContracts, {
              modalMode: contractModalMode === 'linesOnly' ? 'linesOnly' : 'full',
              initialTab: 'linhas',
            });
          } else {
            await loadContractForEditing(contract, {
              modalMode: contractModalMode === 'linesOnly' ? 'linesOnly' : 'full',
              initialTab: 'linhas',
            });
          }
        }
      } else {
        console.error('Erro ao salvar contrato:', error);
        showError(error?.response?.data?.message || 'Erro ao salvar contrato. Tente novamente.');
      }
    } finally {
      pendingContractSaveRef.current = null;
      setEmptyContractConfirmOpen(false);
      setIsSavingContract(false);
    }
  };

  const handleSaveContract = async () => {
    try {
      // Ativar loading
      setIsSavingContract(true);

      if (!editingContractId && clientRequiresExistingDraft()) {
        setShowClientDraftsModal(true);
        setIsSavingContract(false);
        return;
      }
      
      // Verificar se o usuário está autenticado
      if (!isAuthenticated) {
        alert('Usuário não autenticado. Faça login novamente.');
        setIsSavingContract(false);
        return;
      }

      const userId = getUserId();
      if (!userId) {
        alert('ID do usuário não encontrado. Faça login novamente.');
        setIsSavingContract(false);
        return;
      }

      if (contractModalMode === 'linesOnly' && editingContractId) {
        if (!validateLinesForSave()) {
          setIsSavingContract(false);
          return;
        }

        if (meuTimStepRequired && !validateMeuTimStep()) {
          setIsSavingContract(false);
          setActiveTab('meuTim');
          return;
        }

        const performLinesOnlySave = async () => {
          const prepared = await prepareGroupStructureForLineSave();
          if (!prepared) return;

          const { groupContracts, linesToSave } = prepared;
          const detailContractId = highlightedContractId || editingContractId;
          const detailInfoSnapshot = selectedContractInfo;
          const results = [];

          if (groupContracts && groupContracts.length > 1) {
            for (const gc of sortGroupContractsForLinePatch(groupContracts, linesToSave)) {
              const patch = buildLinesOnlyPatchPayloadForGroup(
                linesToSave,
                fastchip,
                groupContracts,
                gc.id,
                gc.updatedAt ? new Date(gc.updatedAt).toISOString() : undefined,
                token
              );
              results.push(await contractService.patchContract(gc.id, patch));
            }
          } else {
            const patch = buildLinesOnlyPatchPayload(
              linesToSave,
              fastchip,
              contractUpdatedAt || undefined,
              token
            );
            results.push(await contractService.patchContract(editingContractId, patch));
          }

          results.forEach((result) => patchContractListItem(result));
          const cancelledCount = results.filter((r) => r.wasDeleted).length;
          showSuccess(
            cancelledCount > 0
              ? `Linhas atualizadas. ${cancelledCount} contrato(s) cancelado(s) por não terem linhas.`
              : 'Linhas atualizadas com sucesso!'
          );
          closeContractFormOnly();
          setContractUpdatedAt(null);
          await refreshUiAfterContractLineSave(results, {
            anchorContractId: detailContractId,
            previousInfo: detailInfoSnapshot,
          });
        };

        const emptyContracts = getContractsThatWillBeEmpty(
          linhas,
          fastchip,
          editingContractId,
          editingGroupContracts && editingGroupContracts.length > 1 ? editingGroupContracts : undefined
        );

        if (emptyContracts.length > 0) {
          pendingContractSaveRef.current = performLinesOnlySave;
          setEmptyContractsOnSave(emptyContracts);
          setEmptyContractConfirmOpen(true);
          setIsSavingContract(false);
          return;
        }

        try {
          await performLinesOnlySave();
          setIsSavingContract(false);
          return;
        } catch (error: any) {
          const conflictMessage = error?.response?.data?.message ?? '';
          if (
            error?.response?.status === 409 &&
            typeof conflictMessage === 'string' &&
            conflictMessage.toLowerCase().includes('linhas')
          ) {
            showError(conflictMessage);
            setIsSavingContract(false);
            return;
          }
          if (
            error?.response?.status === 409 ||
            conflictMessage.includes('concorrência') ||
            conflictMessage.includes('alterado por outro usuário')
          ) {
            showError('O contrato foi alterado por outro usuário. Recarregando...');
            if (editingContractId) {
              const contract = await contractService.getContractById(editingContractId);
              setContractUpdatedAt(
                contract.updatedAt ? new Date(contract.updatedAt).toISOString() : null
              );
              if (editingGroupContracts && editingGroupContracts.length > 1) {
                await loadContractForEditingConsolidated(editingGroupContracts, {
                  modalMode: 'linesOnly',
                  initialTab: 'linhas',
                });
              } else {
                await loadContractForEditing(contract, {
                  modalMode: 'linesOnly',
                  initialTab: 'linhas',
                });
              }
            }
            setIsSavingContract(false);
            return;
          }
          throw error;
        }
      }

      if (!editingContractId && checkClienteCadastralAtivoBlock()) {
        setIsSavingContract(false);
        setActiveTab('contrato');
        return;
      }

      // S8-IE: safety-net de IE inapta no submit (caso o usuário tenha pulado abas via clique direto).
      if (!editingContractId) {
        if (
          checkIeBlockAndMaybeOpen(() => {
            // Retentar salvar após confirmação do bypass
            handleSaveContract();
          })
        ) {
          setIsSavingContract(false);
          return;
        }
      }

      // S8: bloqueios defensivos no submit para contrato novo.
      if (!editingContractId) {
        if (!bloqueioSuspensaoNovosContratos && temSuspensao === null) {
          showError('Responda na aba "Contrato" se este contrato terá suspensão de linhas antes de salvar.');
          setIsSavingContract(false);
          setActiveTab('contrato');
          return;
        }
        const normalizedClusterTokenOnSave = normalizeContractSaleTokenForForm(token, fastchip);
        if (!normalizedClusterTokenOnSave) {
          const msgSaveTok = token.trim()
            ? 'O token de cluster não é válido para esta configuração (Fastchip ou operacional TIM). Escolha uma opção válida na aba Contrato antes de salvar.'
            : 'Selecione um token de cluster na lista da aba Contrato antes de salvar. Ao criar contrato novo o preenchimento é obrigatório.';
          setTabValidationError(msgSaveTok);
          showError(msgSaveTok);
          alert(msgSaveTok);
          setIsSavingContract(false);
          setActiveTab('contrato');
          return;
        }
      }

      // Em Assinado (e similares) a edição de linhas fica bloqueada; validações/mutações
      // de linha não devem impedir salvar QSA, auditoria, endereço etc.
      const allowLineMutationsOnSave = !editingContractId || canMutateContractLines;

      if (allowLineMutationsOnSave) {
        const hasSuspensionMarksOnSave =
          linhasParaDesativar.size > 0 ||
          linhas.some((l) => l.requerSuspensao || l.excluirDoContrato);
        if (hasSuspensionMarksOnSave && fluxoSuspensaoLinhasBloqueado) {
          showError(mensagemOrientacaoSuspensao1056);
          setTabValidationError(mensagemOrientacaoSuspensao1056);
          setIsSavingContract(false);
          setActiveTab('linhasDesativar');
          return;
        }
        // TEMPORÁRIO (pedido Eder — bater meta): permite DH por entrega sem Fastchip.
        if (
          hasSuspensionMarksOnSave &&
          !suspensionAllowedForContract &&
          !shouldSkipSuspensionSaveBlock(hasRole('Admin'), editingContractId)
        ) {
          const msg =
            'Marcação de linhas para suspensão só é permitida em contratos Fastchip. Remova a marcação de suspensão ou use um contrato Fastchip.';
          showError(msg);
          setTabValidationError(msg);
          setIsSavingContract(false);
          setActiveTab('linhasDesativar');
          return;
        }

        if (hasActiveFastchipAditivoLines(linhas, fastchip)) {
          const consistency = ensureFastchipIccidConsistency('save');
          if (!consistency.ok) {
            setIsSavingContract(false);
            setActiveTab('linhas');
            return;
          }
          if (consistency.adjusted) {
            setIsSavingContract(false);
            setActiveTab('linhas');
            showInfo('ICCID duplicado foi reatribuído automaticamente. Revise as linhas antes de salvar novamente.');
            return;
          }
        } else if (teamContractRules.blockDuplicateCcid && hasDuplicateCcid(linhas)) {
          const consistency = ensureFastchipIccidConsistency('save');
          if (!consistency.ok) {
            setIsSavingContract(false);
            setActiveTab('linhas');
            return;
          }
          if (consistency.adjusted) {
            setIsSavingContract(false);
            setActiveTab('linhas');
            showInfo('ICCID duplicado foi reatribuído automaticamente. Revise as linhas antes de salvar novamente.');
            return;
          }
          setIsSavingContract(false);
          setActiveTab('linhas');
          showError('Existem CCIDs duplicados nas linhas de Aditivo. Corrija os duplicados antes de salvar.');
          return;
        }

        if (!validateMeuTimStep()) {
          setIsSavingContract(false);
          setActiveTab('meuTim');
          return;
        }

        // Validar regra de aditivos: verificar se há aditivos suficientes (R1)
        const linhasSuspensas = linhasParaDesativar.size;
        if (teamContractRules.requireAditivosForSuspendedLines && linhasSuspensas > 0) {
          const aditivosNecessarios = calcularAditivosNecessarios(linhasSuspensas);
          const linhasAditivoValidas = linhas.filter(l =>
            l.negociacao === 'Aditivo' &&
            l.ddd &&
            l.planId &&
            !l.excluirDoContrato
          ).length;

          if (linhasAditivoValidas < aditivosNecessarios) {
            showError(
              `Regra de aditivos não atendida: ${linhasSuspensas} ${linhasSuspensas === 1 ? 'linha suspensa requer' : 'linhas suspensas requerem'} ${aditivosNecessarios} ${aditivosNecessarios === 1 ? 'aditivo' : 'aditivos'}, mas apenas ${linhasAditivoValidas} ${linhasAditivoValidas === 1 ? 'foi encontrado' : 'foram encontrados'}. Por favor, adicione mais linhas aditivas na aba "Linhas" antes de salvar.`
            );
            setIsSavingContract(false);
            setActiveTab('linhas'); // Ir para aba de linhas para facilitar correção
            return;
          }
        }

        const portabilidadeErr = validateAllPortabilidadeLines(linhas);
        if (portabilidadeErr) {
          showError(portabilidadeErr);
          setIsSavingContract(false);
          setActiveTab('linhas');
          return;
        }

        // Validar campos obrigatórios das linhas ativas antes da edição completa (plano, DDD, número etc.)
        const lineFieldErrorBeforeFullEdit = getFirstContractLineFieldValidationError(linhas, plans);
        if (lineFieldErrorBeforeFullEdit) {
          showError(lineFieldErrorBeforeFullEdit.message);
          setTabValidationError(lineFieldErrorBeforeFullEdit.message);
          setIsSavingContract(false);
          setActiveTab('linhas');
          return;
        }
      }

      // Se está editando, usar updateContract
      if (editingContractId) {
        const snapshot: ContractUpdateFormSnapshot = {
          updatedAt: contractUpdatedAt || undefined,
          tipoContratacao,
          diaVencimento,
          valorTotalMensal,
          numeroP2B: numeroP2B || '',
          comprometimentoMinimo: comprometimentoMinimo || '',
          fastchip,
          tipoFatura,
          envioFatura,
          origemVenda,
          token,
          deliveryAddress: {
            cep,
            cidade,
            estado,
            endereco: normalizeDeliveryAddressStreetNameForApi(endereco),
            numero: normalizeDeliveryAddressStreetNumberForApi(numero),
            bairro,
            complemento: complemento || '',
            referencia: referencia || '',
          },
          administrator: {
            nomeCompleto: adminNomeCompleto,
            cpf: adminCpf,
            rg: adminRg.trim() || '000000000',
            email: adminEmail,
            telefone1: adminTelefone1,
            telefone2: adminTelefone2,
          },
          representatives: representantes,
          authorizedReceivers: recebedores,
          witnesses: testemunhas,
          lines: linhas,
        };

        try {
          const performFullContractSave = async () => {
            const results = [];

            if (!allowLineMutationsOnSave) {
              // Sem mutação de linhas: PATCH só dos campos de composição/dados.
              // Evita EnsureCanEditLines / WouldChangeLines por drift do formulário.
              const groupContracts =
                editingGroupContracts && editingGroupContracts.length > 1
                  ? editingGroupContracts
                  : null;
              const targets = groupContracts ?? [{ id: editingContractId, updatedAt: contractUpdatedAt }];
              for (const gc of targets) {
                const updateData = buildContractUpdatePayload({
                  ...snapshot,
                  updatedAt: gc.updatedAt
                    ? new Date(gc.updatedAt).toISOString()
                    : snapshot.updatedAt,
                }) as UpdateContractRequestDto & { internalNotes?: string; lines?: unknown };
                delete updateData.lines;
                // Em grupo, ids de representante/recebedor/testemunha são do contrato âncora;
                // recria as composições a partir do formulário em cada contrato do grupo.
                if (groupContracts) {
                  updateData.representatives = updateData.representatives?.map(
                    ({ id: _id, ...rest }) => rest
                  );
                  updateData.authorizedReceivers = updateData.authorizedReceivers?.map(
                    ({ id: _id, ...rest }) => rest
                  );
                  updateData.witnesses = updateData.witnesses?.map(({ id: _id, ...rest }) => rest);
                }
                updateData.internalNotes = contractInternalNotes.trim() || undefined;
                results.push(await contractService.patchContract(gc.id, updateData));
              }
              showSuccess(
                targets.length > 1
                  ? `Grupo atualizado com sucesso! (${targets.length} contratos)`
                  : 'Contrato atualizado com sucesso!'
              );
            } else {
              const prepared = await prepareGroupStructureForLineSave();
              if (!prepared) return;

              const { groupContracts, linesToSave } = prepared;
              const snapshotWithLines: ContractUpdateFormSnapshot = { ...snapshot, lines: linesToSave };
              if (groupContracts && groupContracts.length > 1) {
                for (const gc of sortGroupContractsForLinePatch(groupContracts, linesToSave)) {
                  const contractUpdateData = buildContractUpdatePayloadForGroup(
                    snapshotWithLines,
                    groupContracts,
                    gc.id,
                    gc.updatedAt ? new Date(gc.updatedAt).toISOString() : undefined
                  ) as any;
                  contractUpdateData.internalNotes = contractInternalNotes.trim() || undefined;
                  results.push(await contractService.patchContract(gc.id, contractUpdateData));
                }
                const cancelledCount = results.filter((r) => r.wasDeleted).length;
                showSuccess(
                  cancelledCount > 0
                    ? `Grupo atualizado. ${cancelledCount} contrato(s) cancelado(s) por não terem linhas.`
                    : 'Grupo atualizado com sucesso!'
                );
              } else {
                const updateData = buildContractUpdatePayload(snapshotWithLines) as any;
                updateData.internalNotes = contractInternalNotes.trim() || undefined;
                const result = await contractService.patchContract(editingContractId, updateData);
                results.push(result);
                showSuccess(
                  result.wasDeleted
                    ? 'Contrato cancelado e excluído por não ter linhas.'
                    : 'Contrato atualizado com sucesso!'
                );
              }
            }
            const detailContractId = highlightedContractId || editingContractId;
            const detailInfoSnapshot = selectedContractInfo;
            results.forEach((result) => patchContractListItem(result));
            closeAndResetModal();
            setContractUpdatedAt(null);
            await refreshUiAfterContractLineSave(results, {
              anchorContractId: detailContractId,
              previousInfo: detailInfoSnapshot,
            });
          };

          if (allowLineMutationsOnSave) {
            const emptyContracts = getContractsThatWillBeEmpty(
              linhas,
              fastchip,
              editingContractId,
              editingGroupContracts && editingGroupContracts.length > 1 ? editingGroupContracts : undefined
            );
            if (emptyContracts.length > 0) {
              pendingContractSaveRef.current = performFullContractSave;
              setEmptyContractsOnSave(emptyContracts);
              setEmptyContractConfirmOpen(true);
              setIsSavingContract(false);
              return;
            }
          }

          await performFullContractSave();
          setIsSavingContract(false);
          return;
        } catch (error: any) {
          const conflictMessage = error?.response?.data?.message ?? '';
          // Bloqueio de alteração de linhas por status/histórico
          if (
            error?.response?.status === 409 &&
            typeof conflictMessage === 'string' &&
            conflictMessage.toLowerCase().includes('linhas')
          ) {
            showError(conflictMessage);
            setIsSavingContract(false);
            return;
          }
          // Tratamento de erro de concorrência
          if (error?.response?.status === 409 || conflictMessage.includes('concorrência') || conflictMessage.includes('alterado por outro usuário')) {
            showError('O contrato foi alterado por outro usuário. Recarregando...');
            // Recarregar contrato e permitir nova tentativa
            if (editingContractId) {
              const contract = await contractService.getContractById(editingContractId);
              setContractUpdatedAt(contract.updatedAt ? new Date(contract.updatedAt).toISOString() : null);
              await loadContractForEditing(contract);
            }
            setIsSavingContract(false);
            return;
          }
          throw error;
        }
      }

      // Validar que deve ter pelo menos um representante
      if (!representantes || representantes.length === 0) {
        alert('O contrato deve ter pelo menos um representante legal.');
        setIsSavingContract(false);
        return;
      }

      // Validações dos representantes (CPF, e-mail e telefone — proposta comercial)
      for (let i = 0; i < representantes.length; i++) {
        const rep = representantes[i];
        if (!rep || !rep.cpf || !isValidCpfLength(rep.cpf)) {
          alert(`CPF obrigatório para o representante "${rep?.nome || 'desconhecido'}". Deve ter 11 dígitos.`);
          setIsSavingContract(false);
          return;
        }
        const em = (rep.email || '').trim();
        if (!em || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
          alert(
            `E-mail obrigatório e válido para o representante "${rep?.nome || 'desconhecido'}" (proposta comercial).`
          );
          setIsSavingContract(false);
          return;
        }
        const td = normalizeRepresentativePhoneInput(rep.telefone || '');
        if (td.length !== 10 && td.length !== 11) {
          alert(
            `Telefone obrigatório (10 ou 11 dígitos, apenas números) para o representante "${rep?.nome || 'desconhecido'}" (proposta comercial).`
          );
          setIsSavingContract(false);
          return;
        }
      }

      // Validações dos CPFs de recebedores (todos os recebedores precisam de CPF)
      for (let i = 0; i < recebedores.length; i++) {
        const rec = recebedores[i];
        if (!rec || !rec.cpf || !isValidCpfLength(rec.cpf)) {
          alert(`CPF obrigatório para o recebedor "${rec?.nome || 'desconhecido'}". Deve ter 11 dígitos.`);
          setIsSavingContract(false);
          return;
        }
      }

      // Validar se todos os dados obrigatórios estão preenchidos
      if (!selectedClient) {
        alert('Por favor, selecione um cliente.');
        setIsSavingContract(false);
        return;
      }


      if (!cep || cep.replace(/\D/g, '').length < 8) {
        alert('Por favor, informe um CEP válido (8 dígitos).');
        setIsSavingContract(false);
        return;
      }

      if (
        !cidade?.trim() ||
        !estado?.trim() ||
        !isValidDeliveryAddressStreetName(endereco) ||
        !isFilledDeliveryAddressStreetNumber(numero) ||
        !bairro?.trim()
      ) {
        alert('Por favor, preencha todos os campos obrigatórios do endereço.');
        setIsSavingContract(false);
        return;
      }

      // Quando contrato nao e Fastchip, o endereco de entrega deve bater com o do cliente
      // (Receita Federal). Operadora TIM envia para o endereco do CNPJ.
      if (!fastchip && selectedClient) {
        const normalizeCep = (v: string | null | undefined) => (v || '').replace(/\D/g, '');
        const norm = (v: string | null | undefined) => (v || '').trim().toLowerCase();
        const mismatch =
          normalizeCep(cep) !== normalizeCep(selectedClient.cep) ||
          norm(estado) !== norm(selectedClient.estado) ||
          norm(cidade) !== norm(selectedClient.cidade) ||
          norm(endereco) !== norm(selectedClient.logradouro) ||
          norm(numero) !== norm(selectedClient.numero) ||
          norm(bairro) !== norm(selectedClient.bairro);
        if (mismatch) {
          alert(
            'Endereço de entrega deve ser igual ao endereço do CNPJ na Receita Federal para contratos sem Fastchip. ' +
              'Marque a opção Fastchip na aba Contrato para usar um endereço diferente.',
          );
          setActiveTab('endereco');
          setIsSavingContract(false);
          return;
        }
      }

      if (!adminNomeCompleto?.trim() || !adminCpf?.trim() || !adminEmail?.trim()) {
        alert('Por favor, preencha todos os campos obrigatórios do responsável pela auditoria.');
        setIsSavingContract(false);
        return;
      }

      // Validar telefone do administrador
      if (!adminTelefone1?.trim()) {
        alert('Por favor, informe o telefone principal do responsável pela auditoria.');
        setIsSavingContract(false);
        setActiveTab('administrador'); // Voltar para a tab do administrador
        return;
      }

      if (!isValidPhoneFormat(adminTelefone1)) {
        alert('Telefone principal inválido! Deve ter 10 dígitos (fixo) ou 11 dígitos (celular com 9 na frente).\n\n' +
              `Telefone digitado: ${formatPhoneProgressive(adminTelefone1)}\n` +
              `Quantidade de dígitos: ${adminTelefone1.length}`);
        setIsSavingContract(false);
        setActiveTab('administrador'); // Voltar para a tab do administrador
        return;
      }

      // Validar telefone secundário se preenchido
      if (adminTelefone2?.trim() && !isValidPhoneFormat(adminTelefone2)) {
        alert('Telefone secundário inválido! Deve ter 10 dígitos (fixo) ou 11 dígitos (celular com 9 na frente).\n\n' +
              `Telefone digitado: ${formatPhoneProgressive(adminTelefone2)}\n` +
              `Quantidade de dígitos: ${adminTelefone2.length}`);
        setIsSavingContract(false);
        setActiveTab('administrador'); // Voltar para a tab do administrador
        return;
      }

      if (linhas.length === 0) {
        alert('Por favor, adicione pelo menos uma linha ao contrato.');
        return;
      }

      // Validar campos obrigatórios das linhas ativas (plano, DDD, número etc.)
      const lineFieldError = getFirstContractLineFieldValidationError(linhas, plans);
      if (lineFieldError) {
        alert(lineFieldError.message);
        setIsSavingContract(false);
        setActiveTab('linhas');
        return;
      }

      // Validações de CPF serão feitas pelo backend
      // Nota: Representantes e recebedores podem não ter CPF válido no frontend

      // ===== VERIFICAÇÃO DE LINHAS MISTAS =====
      // Pular verificação se for 1º Venda (todas as linhas são Aditivo)
      if (!isPrimeiraVenda) {
      // Verificar se há linhas de ambos tipos (Aditivo e Renegociação)
      const hasAditivo = linhas.some(l => !l.numero || l.numero.trim() === '');
      const hasRenegociacao = linhas.some(l => l.numero && l.numero.trim() !== '');

      if (hasAditivo && hasRenegociacao && shouldSeparateContracts === undefined) {
        setShowSeparateModal(true);
        setIsSavingContract(false);
        return; // Pausar criação até o usuário decidir
        }
      }

      // Normalizar o CEP antes de enviar
      let normalizedCep: string;
      try {
        normalizedCep = normalizeCep(cep);
      } catch (error) {
        alert(`Erro na validação do CEP: ${error instanceof Error ? error.message : 'CEP inválido'}`);
        return;
      }

      const explicitSaleToken = normalizeContractSaleTokenForForm(token, fastchip);
      const hasMeuTimCredentialsToCheck = linhas.some(
        (linha) =>
          (linha.requerSuspensao || linha.excluirDoContrato) &&
          !!linha.meuTimLogin?.trim() &&
          !!linha.meuTimSenha?.trim()
      );

      let contractUserId = userId;
      if (canAssignResponsibleConsultant) {
        if (!responsibleConsultantUserId) {
          showError('Selecione o consultor responsável na aba Contrato.');
          setIsSavingContract(false);
          setActiveTab('contrato');
          return;
        }
        contractUserId = responsibleConsultantUserId;
      }

      // Construir o DTO de criação do contrato
      const contractPayload: CreateContractRequestDto = {
        clientId: selectedClient.id,
        userId: contractUserId,
        tipoContratacao: isPrimeiraVenda ? "1º Venda" : undefined, // Enviar tipo explícito se for 1º Venda
        diaVencimento,
        // valorTotalMensal será calculado automaticamente pelo backend
        numeroP2B: numeroP2B || undefined,
        comprometimentoMinimo: comprometimentoMinimo ? parseFloat(comprometimentoMinimo) : undefined,
        fastchip,
        ...(explicitSaleToken ? { token: explicitSaleToken } : {}),
        // Bypass de IE inapta — só envia quando confirmado E ainda faz sentido (fastchip=false).
        // Se virou Fastchip por suspensão depois, o auto-clear effect deixa ieBypassConfirmed=false.
        ignoreIeBlock: ieBypassConfirmed && !fastchip ? true : undefined,
        ieBypassReason: ieBypassConfirmed && !fastchip ? ieBypassReason : undefined,
        tipoFatura,
        envioFatura,
        origemVenda,
        internalNotes: contractInternalNotes.trim() || undefined,
        internalNotesOccurrence: contractInternalNotesOccurrence.trim() || undefined,
        duplicateReason: contractData.duplicateReason || undefined,
        shouldSeparate: isPrimeiraVenda ? false : (shouldSeparateContracts || false), // Não separar se for 1º Venda
        deliveryAddress: {
          cep: normalizedCep, // Usar CEP normalizado
          cidade,
          estado,
          endereco: normalizeDeliveryAddressStreetNameForApi(endereco),
          numero: normalizeDeliveryAddressStreetNumberForApi(numero),
          bairro,
          complemento: complemento || undefined,
          referencia: referencia || undefined
        },
        administrator: {
          nomeCompleto: adminNomeCompleto.trim(),
          cpf: adminCpf.trim(),
          rg: adminRg.trim() || '000000000', // RG padrão se não informado
          email: adminEmail.trim() || undefined,
          telefone1: adminTelefone1.trim() || undefined,
          telefone2: adminTelefone2.trim() || undefined,
          dataNascimento: new Date('2000-08-18')
        },
        representatives: representantes.map(rep => ({
          nome: rep.nome,
          cpf: rep.cpf?.trim() || '',
          rg: rep.rg?.trim() || undefined,
          telefone: normalizeRepresentativePhoneInput(rep.telefone ?? ''),
          email: (rep.email ?? '').trim(),
        })),
        authorizedReceivers: recebedores.map(rec => {
          // Para recebedores manuais, temos email e telefone
          const isManual = 'manual' in rec && rec.manual;
          return {
            nome: rec.nome,
            qualificacao: rec.qualificacao?.trim() || undefined,
            dataNascimento: undefined,
            cpf: rec.cpf?.trim() || '',
            rg: rec.rg?.trim() || undefined,
            email: isManual && 'email' in rec ? (rec as any).email?.trim() || undefined : undefined,
            telefone: isManual && 'telefone' in rec ? (rec as any).telefone?.trim() || undefined : undefined,
            celular: undefined,
            nomePai: undefined,
            nomeMae: undefined,
            naturalidade: undefined
          };
        }),
        witnesses: testemunhas.map(test => ({
          nome: test.nome,
          cpf: (test.cpf || '').trim(),
          rg: test.rg?.trim() || undefined
        })),
        lines: linhas.map(linha => {
          // Para Renegociação, garantir que o número não contenha o DDD antes de enviar
          let dddToSend = linha.ddd;
          let numeroToSend = linha.numero?.trim() || '';
          
          if (linha.negociacao === 'Renegociação' && linha.ddd && linha.numero) {
            numeroToSend = renegociacaoLocalDigitsFromFormField(linha.ddd, linha.numero);
            dddToSend = linha.ddd;
          }
          
          const mappedLine = {
            negociacao: linha.negociacao || undefined,
            ddd: dddToSend || undefined,
            numero: numeroToSend || undefined,
            tipoVenda: linha.tipoVenda || undefined,
            planId: (linha.planId && linha.planId.trim() !== '') ? linha.planId.trim() : undefined,
            aparelho: linha.aparelho?.trim() || undefined,
            handsetDeviceId: linha.handsetDeviceId || undefined,
            aparelhoValorTotal: linha.aparelhoValorTotal,
            aparelhoValorMensal: linha.aparelhoValorMensal,
            valorChip: linha.valorChip ? parseFloat(linha.valorChip.toString()) : undefined,
            valorMensal: parseFloat(linha.valorMensal.toString()),
            quantidade: 1, // Sempre 1
            // Alocação de ICCID: backend atômico (SKIP LOCKED) quando a flag estiver habilitada,
            // ou caminho legado (chipCcid selecionado no front) caso contrário.
            ...(linha.negociacao === 'Aditivo' && fastchip
              ? backendAllocationEnabled
                ? (() => {
                    // Resolve o cluster canônico para esta linha e cliente.
                    const estadoCliente = selectedClient?.estado;
                    const clusterParaLinha =
                      estadoCliente === 'PR'
                        ? resolvePrClusterForLine(linha.ddd) // 'ER' ou 'MH'
                        : isHbCpMarketEstado(estadoCliente) && effectiveHbCpCluster
                          ? effectiveHbCpCluster // 'HB', 'CP' ou 'TPCL'
                          : undefined; // estados regionais: filtrar por estado
                    return {
                      chipCcid: undefined,
                      allocateFastchipIccid: true,
                      fastchipCluster: clusterParaLinha ?? undefined,
                      fastchipEstado: estadoCliente ?? undefined,
                    };
                  })()
                : { chipCcid: linha.chipCcid?.trim() || undefined }
              : { chipCcid: undefined }),
            excluirDoContrato: linha.excluirDoContrato || false,
            requerSuspensao: linha.requerSuspensao || false,
            trocaLinha: linha.trocaLinha || false,
            meuTimLogin: linha.meuTimLogin || undefined,
            meuTimSenha: linha.meuTimSenha || undefined,
            dataDesativacao: linha.marcarParaDesativar && linha.dataDesativacao ? linha.dataDesativacao : undefined,
            seraUsadaPeloCliente: linha.seraUsadaPeloCliente !== null && linha.seraUsadaPeloCliente !== undefined ? linha.seraUsadaPeloCliente : undefined,
            ehPortabilidade: linha.ehPortabilidade || false,
            portabilidadeNomeCompleto: linha.ehPortabilidade ? (linha.portabilidadeNomeCompleto || undefined) : undefined,
            portabilidadeCpf: linha.ehPortabilidade ? (linha.portabilidadeCpf || undefined) : undefined,
            portabilidadeOperadora: linha.ehPortabilidade ? (linha.portabilidadeOperadora || undefined) : undefined,
            portabilidadeLinha: linha.ehPortabilidade ? (linha.portabilidadeLinha || undefined) : undefined,
            portabilidadeCnpjCedente: linha.ehPortabilidade ? (linha.portabilidadeCnpjCedente || undefined) : undefined,
            portabilidadeRazaoSocialCedente: linha.ehPortabilidade
              ? (linha.portabilidadeRazaoSocialCedente || undefined)
              : undefined,
            portabilidadeRepresentantes:
              linha.ehPortabilidade && linha.portabilidadeRepresentantes && linha.portabilidadeRepresentantes.length > 0
                ? linha.portabilidadeRepresentantes.map((rep: any) => ({ nome: rep.nome, cpf: rep.cpf }))
                : undefined,
            isEsim: linha.negociacao === 'Aditivo' && fastchip ? (linha.isEsim ?? false) : undefined,
            ofertas: [] // TODO: Implementar ofertas se necessário
          };
          return mappedLine;
        })
      };

      // Evita corrida: update de rascunho em background vs finalize no Salvar.
      if (!editingContractId) {
        await prepareDraftPersistForContractSave();
      }

      // Preferir ref (mais atual que o state) e cair para create se o rascunho sumiu.
      const draftIdForFinalize = !editingContractId ? draftContractIdRef.current : null;
      let createdContract;
      if (draftIdForFinalize) {
        try {
          createdContract = await contractService.finalizeContractDraft(
            draftIdForFinalize,
            contractPayload
          );
        } catch (finalizeError) {
          if (!isDraftNotFoundError(finalizeError)) {
            throw finalizeError;
          }
          console.warn(
            'Rascunho não encontrado no finalize — criando contrato diretamente:',
            finalizeError
          );
          clearDraftContractId();
          createdContract = await contractService.createContract(contractPayload);
        }
      } else {
        createdContract = await contractService.createContract(contractPayload);
      }

      clearDraftContractId();

      // Fase 1: dispara health-check Meu TIM sem bloqueio duro quando houver credenciais no fluxo de suspensão.
      if (selectedClient?.id && hasMeuTimCredentialsToCheck) {
        try {
          await clientService.enqueueMeuTimHealthCheck([selectedClient.id]);
        } catch (enqueueError) {
          console.warn('Falha ao enfileirar health-check Meu TIM após criação de contrato:', enqueueError);
          showWarning(
            'Contrato salvo, mas não foi possível enfileirar o health-check Meu TIM automaticamente. Você pode disparar manualmente em Clientes.'
          );
        }
      }
      
      // Sucesso - mostrar tela de sucesso
      setCreatedContractId(createdContract.id);
      setCreatedContractGroupId(createdContract.groupId || null);
      setShowSuccessModal(true);
      
      // Limpar estados de separação
      setShouldSeparateContracts(undefined);
      setShouldProcessSave(false);
      
      // Fechar modal e limpar formulário
      closeAndResetModal();
      
    } catch (error: any) {
      console.error('Erro ao salvar contrato:', error);

      // RF12: estoque Fastchip insuficiente detectado pelo backend na alocação atômica.
      // O backend devolve HTTP 409 com code = INSUFFICIENT_FASTCHIP_STOCK e suggestFastchipFalse.
      // Oferecemos converter o contrato para sem Fastchip (entrega pela operadora; qualquer seller pode aceitar).
      const stockData = error?.response?.data;
      if (
        error?.response?.status === 409 &&
        stockData &&
        typeof stockData === 'object' &&
        stockData.code === 'INSUFFICIENT_FASTCHIP_STOCK'
      ) {
        const escopo = [stockData.cluster, stockData.estado].filter(Boolean).join(' / ');
        const detalhe =
          typeof stockData.requested === 'number' && typeof stockData.available === 'number'
            ? ` Solicitado: ${stockData.requested}, disponível: ${stockData.available}.`
            : '';
        const baseMsg =
          (typeof stockData.message === 'string' && stockData.message.trim()) ||
          `Estoque Fastchip insuficiente${escopo ? ` para ${escopo}` : ''}.`;

        if (stockData.suggestFastchipFalse && !editingContractId) {
          const converter = window.confirm(
            `${baseMsg}${detalhe}\n\nDeseja converter o contrato para sem Fastchip agora (envio direto pela operadora)?`
          );
          if (converter) {
            convertDraftToNonFastchip('Estoque Fastchip insuficiente.');
          } else {
            showWarning(`${baseMsg}${detalhe} Reduza as linhas Aditivo Fastchip ou converta para sem Fastchip.`);
          }
        } else {
          showWarning(`${baseMsg}${detalhe}`);
        }
        return;
      }
      
      // Função auxiliar para extrair mensagem de erro de diferentes formatos
      const extractErrorMessage = (err: any): string => {
        // Caso 1: Resposta do Axios com data
        if (err?.response?.data) {
          const data = err.response.data;
          
          // String direta
          if (typeof data === 'string') {
            return data;
          }
          
          // Objeto com message
          if (data.message && typeof data.message === 'string') {
            return data.message;
          }
          
          // Objeto com title (ProblemDetails do ASP.NET Core)
          if (data.title && typeof data.title === 'string') {
            return data.title;
          }
          
          // Objeto com detail (ProblemDetails do ASP.NET Core)
          if (data.detail && typeof data.detail === 'string') {
            return data.detail;
          }
          
          // Erros de validação (errors array do ASP.NET)
          if (data.errors && typeof data.errors === 'object') {
            const validationErrors: string[] = [];
            Object.keys(data.errors).forEach(key => {
              const fieldErrors = data.errors[key];
              if (Array.isArray(fieldErrors)) {
                validationErrors.push(...fieldErrors);
              } else if (typeof fieldErrors === 'string') {
                validationErrors.push(fieldErrors);
              }
            });
            if (validationErrors.length > 0) {
              return validationErrors.join('; ');
            }
          }
          
          // Tentar extrair mensagem de array de erros
          if (Array.isArray(data)) {
            return data.map(e => typeof e === 'string' ? e : e.message || e.title || JSON.stringify(e)).join('; ');
          }
        }
        
        // Caso 2: Mensagem direta do erro
        if (err?.message && typeof err.message === 'string') {
          return err.message;
        }
        
        // Caso 3: Fallback genérico
        return 'Erro ao criar contrato. Verifique os dados e tente novamente.';
      };
      
      let errorMessage = extractErrorMessage(error);
      
      // Mapear mensagens de erro comuns para melhorar clareza
      const errorMessageMap: { [key: string]: string } = {
        'iccid já usado': 'O ICCID informado já está em uso. Por favor, selecione outro ICCID disponível.',
        'iccid already in use': 'O ICCID informado já está em uso. Por favor, selecione outro ICCID disponível.',
        'ccid já existe': 'O CCID informado já existe no sistema. Por favor, selecione outro ICCID disponível.',
        'ccid already exists': 'O CCID informado já existe no sistema. Por favor, selecione outro ICCID disponível.',
        'iccid não encontrado': 'O ICCID informado não foi encontrado. Por favor, verifique o código e tente novamente.',
        'iccid not found': 'O ICCID informado não foi encontrado. Por favor, verifique o código e tente novamente.',
        'iccid indisponível': 'O ICCID informado não está disponível. Por favor, selecione outro ICCID disponível.',
        'iccid unavailable': 'O ICCID informado não está disponível. Por favor, selecione outro ICCID disponível.',
      };
      
      // Normalizar mensagem para buscar no mapa (case insensitive)
      const normalizedMessage = errorMessage.toLowerCase().trim();
      for (const [key, value] of Object.entries(errorMessageMap)) {
        if (normalizedMessage.includes(key)) {
          errorMessage = value;
          break;
        }
      }
      
      // Exibir mensagem de erro usando toast
      if (!handleFastchipStockApiError(error, errorMessage)) {
        showError(errorMessage);
      }
    } finally {
      draftPersistSuspendedRef.current = false;
      // Desativar loading sempre
      setIsSavingContract(false);
    }
  };

  // ===== HANDLERS DOS MODAIS =====
  
  // Handler do modal de duplicidade
  const handleDuplicateConfirm = (reason: string) => {
    // Armazenar o motivo no estado do contractData
    setContractData(prev => ({ ...prev, duplicateReason: reason }));
    setShowDuplicateModal(false);
    setDuplicateContract(null);
    // Reprocessar criação chamando handleSaveContract novamente
    setTimeout(() => handleSaveContract(), 100);
  };

  const handleDuplicateCancel = () => {
    setShowDuplicateModal(false);
    setDuplicateContract(null);
    setIsSavingContract(false);
    // Limpar seleção do cliente para forçar nova seleção
    handleClientClear();
  };

  // Handlers do modal de separação
  const handleSeparateConfirm = async () => {
    setShouldSeparateContracts(true);
    setShowSeparateModal(false);
    setShouldProcessSave(true); // Ativar flag para processar salvamento
  };

  // Handler do modal de confirmação Fastchip
  const handleFastchipAddressConfirm = () => {
    setShowFastchipAddressModal(false);
    setHasSeenFastchipAddressModal(true);
    
    // Após confirmar, avançar para a próxima aba (Responsável pela Auditoria)
    const currentIndex = modalTabs.findIndex(tab => tab.key === activeTab);
    if (currentIndex < modalTabs.length - 1) {
      setActiveTab(modalTabs[currentIndex + 1].key);
    }
  };

  const handleKeepTogether = async () => {
    setShouldSeparateContracts(false);
    setShowSeparateModal(false);
    setShouldProcessSave(true); // Ativar flag para processar salvamento
  };

  const handleSeparateCancel = () => {
    setShowSeparateModal(false);
    setShouldSeparateContracts(undefined);
    setIsSavingContract(false);
  };

  // Helper para expandir/colapsar grupos
  const toggleGroupExpansion = async (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
      // Não precisa mais buscar da API - os contratos já estão em memória
      // no array contract.groupContracts de cada contrato pai
    }
    
    setExpandedGroups(newExpanded);
  };

  const handleCloseLinesModal = () => {
    linesModalRequestSequenceRef.current += 1;
    linesModalOpenContractIdRef.current = null;
    linesModalAbortRef.current?.abort();
    linesModalAbortRef.current = null;
    clearCqvScriptState();
    setShowLinesModal(false);
    setLinesModalLoading(false);
    setLinesDetailCanEditLines(false);
    setOpeningLinesOnlyEdit(false);
    setLinesModalError(null);
    setHighlightedContractId(null);
    setTrocaDeCarteiraFromModalLoading(false);
    setIdPedido1('');
    setIdPedido2('');
    setLinhasAptas(new Map());
    setEditingAdminInModal(false);
    setEditingAdminData({ nomeCompleto: '', cpf: '', telefone1: '' });
    setEditingConsultantInModal(false);
    setResponsibleConsultantEditInModal('');
    setEditingRepInModal(null);
    setEditingRepData({ nome: '', cpf: '', telefone: '', email: '' });
    setSavingInlineEdit(false);
    setLinesModalGroupContracts([]);
    setLinesModalTrocaSolicitadaAfterAprovadoCqv(false);
    setShowClusterTokensEditModal(false);
    setShowLineAuditHistoryModal(false);
    setLineAuditHistoryContractId(null);
    setLineAuditHistoryGroupId(null);
    correctionCycleRequestSeqRef.current += 1;
    setCorrectionCycle(null);
    setCorrectionCycleLoading(false);
    setCorrectionActionKind(null);
    setCorrectionActionReason('');
    setCorrectionActionSubmitting(false);
  };

  /**
   * Busca o estado do ciclo de correção manual (SLA 2). Usada ao abrir o modal (T07) e após
   * cada `start`/`end` bem-sucedido (T08). `targetContractId` explícito evita ler o
   * `selectedContractInfo` "antigo" da closure logo após `setSelectedContractInfo`.
   */
  const refreshCorrectionCycle = useCallback(
    async (targetContractId?: string) => {
      const contractIdForFetch = targetContractId ?? selectedContractInfo?.id;
      if (!contractIdForFetch) return;
      const seq = ++correctionCycleRequestSeqRef.current;
      setCorrectionCycleLoading(true);
      try {
        const state = await contractService.getCorrectionCycle(contractIdForFetch);
        if (correctionCycleRequestSeqRef.current !== seq) return;
        setCorrectionCycle(state);
      } catch (error) {
        if (correctionCycleRequestSeqRef.current !== seq) return;
        console.error('Erro ao carregar estado do ciclo de correção:', error);
        setCorrectionCycle(null);
        showError('Não foi possível carregar o estado do ciclo de correção. O status exibido pode estar desatualizado.');
      } finally {
        if (correctionCycleRequestSeqRef.current === seq) {
          setCorrectionCycleLoading(false);
        }
      }
    },
    [selectedContractInfo?.id]
  );

  /**
   * Confirma o formulário inline (T08): abre ou fecha o ciclo de correção, com motivo opcional.
   * `requestIdAtSubmit` guarda a "sessão" do modal (bumped por `handleShowLinesModal`/
   * `handleCloseLinesModal`) no momento do disparo: se o usuário trocar de contrato ou fechar
   * o modal antes da resposta chegar, o refresh do ciclo de correção é ignorado — evita
   * sobrescrever o estado do contrato ERRADO que está visível agora com o resultado de A.
   */
  const handleSubmitCorrectionAction = async () => {
    const contractId = selectedContractInfo?.id;
    if (!contractId || !correctionActionKind) return;
    const requestIdAtSubmit = linesModalRequestSequenceRef.current;
    const isStillShowingThisContract = () =>
      linesModalRequestSequenceRef.current === requestIdAtSubmit &&
      selectedContractInfoIdRef.current === contractId;
    const reason = correctionActionReason.trim();
    const body: ContractCorrectionActionRequest = reason ? { reason } : {};
    setCorrectionActionSubmitting(true);
    try {
      if (correctionActionKind === 'start') {
        await contractService.startCorrectionCycle(contractId, body);
        showSuccess('Correção iniciada.');
      } else {
        await contractService.endCorrectionCycle(contractId, body);
        showSuccess('Correção finalizada.');
      }
      if (isStillShowingThisContract()) {
        setCorrectionActionKind(null);
        setCorrectionActionReason('');
        await refreshCorrectionCycle(contractId);
      }
    } catch (error: unknown) {
      showError(error instanceof Error ? error.message : 'Não foi possível concluir a ação.');
      if (isStillShowingThisContract()) {
        await refreshCorrectionCycle(contractId);
      }
    } finally {
      setCorrectionActionSubmitting(false);
    }
  };

  const linesModalTokenTargets = useMemo(() => {
    const fastchipFlag = !!selectedContractInfo?.fastchip;
    const pick = (c: any, label: string): ContractTokenTarget | null => {
      if (!c?.id) return null;
      return {
        contractId: c.id,
        label,
        token: c.token ?? '',
        fastchip: c.fastchip ?? fastchipFlag,
      };
    };
    if (linesModalGroupContracts.length > 0) {
      const aditivo = linesModalGroupContracts.find(
        (c) => c.contractType === 'Aditivo' || c.tipoContratacao === 'Aditivo'
      );
      const reneg = linesModalGroupContracts.find(
        (c) => c.contractType === 'Renegociação' || c.tipoContratacao === 'Renegociação'
      );
      return {
        aditivo: pick(aditivo, 'Contrato Aditivo'),
        renegociacao: pick(reneg, 'Contrato Renegociação'),
      };
    }
    const tipo = selectedContractInfo?.contractType || selectedContractInfo?.tipoContratacao;
    if (tipo === 'Aditivo') {
      return { aditivo: pick(selectedContractInfo, 'Contrato Aditivo'), renegociacao: null };
    }
    if (tipo === 'Renegociação') {
      return { aditivo: null, renegociacao: pick(selectedContractInfo, 'Contrato Renegociação') };
    }
    return {
      aditivo: pick(selectedContractInfo, 'Contrato'),
      renegociacao: null,
    };
  }, [linesModalGroupContracts, selectedContractInfo]);

  const handleSaveClusterTokens = async (payload: {
    aditivoToken?: string;
    renegociacaoToken?: string;
  }) => {
    if (!linesModalCanEditClusterToken) {
      showError('A edição de cluster/token não está disponível no modal de linhas.');
      return;
    }
    setClusterTokensSaving(true);
    try {
      const tasks: Promise<unknown>[] = [];
      if (linesModalTokenTargets.aditivo && payload.aditivoToken !== undefined) {
        tasks.push(
          patchContractWithCacheInvalidation(linesModalTokenTargets.aditivo.contractId, {
            token: payload.aditivoToken,
          }, selectedContractInfo?.groupId)
        );
      }
      if (linesModalTokenTargets.renegociacao && payload.renegociacaoToken !== undefined) {
        tasks.push(
          patchContractWithCacheInvalidation(linesModalTokenTargets.renegociacao.contractId, {
            token: payload.renegociacaoToken,
          }, selectedContractInfo?.groupId)
        );
      }
      await Promise.all(tasks);
      invalidateListAndModalQueries(selectedContractInfo?.id, selectedContractInfo?.groupId);
      showSuccess('Tokens atualizados.');
      setShowClusterTokensEditModal(false);
      if (selectedContractInfo?.id) {
        await handleShowLinesModal(selectedContractInfo.id, selectedContractInfo, {
          softRefresh: true,
        });
      }
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Erro ao gravar tokens.');
    } finally {
      setClusterTokensSaving(false);
    }
  };

  // Função para abrir modal de linhas (softRefresh: recarrega dados mantendo o modal aberto, ex.: após CQV)
  const handleShowLinesModal = async (
    contractId: string,
    contractInfo: any,
    options?: { softRefresh?: boolean }
  ) => {
    const softRefresh = options?.softRefresh === true;

    if (softRefresh) {
      if (
        !showLinesModalRef.current ||
        linesModalOpenContractIdRef.current !== contractId
      ) {
        return;
      }
      invalidateListAndModalQueries(contractId, contractInfo.groupId);
    } else {
      linesModalOpenContractIdRef.current = contractId;
      clearCqvScriptState();
      linesModalAbortRef.current?.abort();
    }

    const requestId = ++linesModalRequestSequenceRef.current;
    const isStaleRequest = () => requestId !== linesModalRequestSequenceRef.current;

    const modalAbortController = new AbortController();
    if (!softRefresh) {
      linesModalAbortRef.current = modalAbortController;
    } else if (linesModalOpenContractIdRef.current === contractId) {
      linesModalAbortRef.current?.abort();
      linesModalAbortRef.current = modalAbortController;
    }
    const modalSignal = modalAbortController.signal;

    if (isStaleRequest()) {
      return;
    }

    setHighlightedContractId(contractId);
    if (!softRefresh) {
      setSelectedLinesForSwap(new Set());
      setSelectedContractLines([]);
      setSelectedContractInfo(null);
      setShowLinesModal(true);
      setCqvRequestMode(ECqvRequestMode.Ligacao);
    }
    setLinesModalLoading(true);
    setLinesModalError(null);
    let trocaHistoryContractIds: string[] = [contractId];

    try {
      const plansPromise = plans.length === 0 ? loadPlans() : Promise.resolve();
      let fullContractInfo = contractInfo;
      let linesFromModal: any[] | null = null;
      let groupContractsForEligibility: Array<{ canEditContractLines?: boolean }> = [];
      /** `undefined` = modal não devolveu o campo; evita GET extra de elegibilidade quando já veio no DTO. */
      let soloModalEligibility: boolean | undefined;

      if (contractInfo.isGroupParent && contractInfo.groupId) {
        try {
          const [, groupResponse] = await Promise.all([
            plansPromise,
            queryClient.fetchQuery({
              queryKey: contractsQueryKeys.lineDetailsByGroup(contractInfo.groupId),
              queryFn: () =>
                contractService.getGroupLineDetailsModal(contractInfo.groupId, {
                  signal: modalSignal,
                }),
              staleTime: CONTRACT_MODAL_CACHE_STALE_TIME_MS,
            }),
          ]);
          if (isStaleRequest()) {
            return;
          }
          if (groupResponse && groupResponse.length > 0) {
            const first = groupResponse[0];
            const anchorContract = pickGroupModalAnchorContract(groupResponse, contractId);
            const mergedGroupStatus = groupResponse.some(
              (c: any) => normalizeContractStatus(c.status) === EContractStatus.VtmeCriado
            )
              ? EContractStatus.VtmeCriado
              : groupResponse.some(
                    (c: any) => normalizeContractStatus(c.status) === EContractStatus.AprovadoCQV
                  )
                ? EContractStatus.AprovadoCQV
                : groupResponse.some(
                      (c: any) =>
                        normalizeContractStatus(c.status) ===
                        EContractStatus.TrocaDeCarteiraSolicitada
                    )
                  ? EContractStatus.TrocaDeCarteiraSolicitada
                  : groupResponse.some(
                        (c: any) =>
                          normalizeContractStatus(c.status) ===
                          EContractStatus.VerificandoCarteira
                      )
                    ? EContractStatus.VerificandoCarteira
                    : groupResponse.some(
                          (c: any) =>
                            normalizeContractStatus(c.status) ===
                            EContractStatus.TrocaDeCarteira
                        )
                      ? EContractStatus.TrocaDeCarteira
                    : normalizeContractStatus(first.status) ?? first.status ?? contractInfo.status;
            // Merge VTME de todos os contratos do grupo para exibir corretamente (qualquer um pode ter os dados)
            const idPedido1 = groupResponse.find((c: any) => c.idPedidoContrato1)?.idPedidoContrato1 ?? contractInfo.idPedidoContrato1 ?? first.idPedidoContrato1;
            const idPedido2 = groupResponse.find((c: any) => c.idPedidoContrato2)?.idPedidoContrato2 ?? contractInfo.idPedidoContrato2 ?? first.idPedidoContrato2;
            const dataVtme = groupResponse.find((c: any) => c.dataConfirmacaoVtme)?.dataConfirmacaoVtme ?? contractInfo.dataConfirmacaoVtme ?? first.dataConfirmacaoVtme;
            const vtmeConfirmado = groupResponse.some((c: any) => c.vtmeConfirmado) || contractInfo.vtmeConfirmado || first.vtmeConfirmado;
            // Em grupos, prioriza o deliveryAddress do contrato Aditivo (onde o chip e entregue),
            // com fallback para o primeiro disponivel ou o original passado para o modal.
            const aditivoComEndereco = groupResponse.find(
              (c: any) => (c.tipoContratacao === 'Aditivo' || c.contractType === 'Aditivo') && !!c.deliveryAddress
            );
            const primeiroComEndereco = groupResponse.find((c: any) => !!c.deliveryAddress);
            const groupDeliveryAddress =
              aditivoComEndereco?.deliveryAddress ??
              primeiroComEndereco?.deliveryAddress ??
              first.deliveryAddress ??
              contractInfo.deliveryAddress;
            const groupInternalNotes =
              groupResponse
                .map((c: any) => (readContractInternalNotes(c) || '').trim())
                .find((value: string) => value.length > 0) ||
              (readContractInternalNotes(first) || '').trim() ||
              (readContractInternalNotes(contractInfo) || '').trim();
            fullContractInfo = {
              ...contractInfo,
              administrator:
                anchorContract.administrator ?? first.administrator ?? contractInfo.administrator,
              representatives: mergeLineModalRepresentatives(
                anchorContract.representatives ?? first.representatives,
                contractInfo.representatives
              ),
              authorizedReceivers:
                anchorContract.authorizedReceivers ??
                first.authorizedReceivers ??
                contractInfo.authorizedReceivers,
              userId: anchorContract.userId ?? anchorContract.user?.id ?? first.userId ?? contractInfo.userId,
              user: anchorContract.user ?? first.user ?? contractInfo.user,
              clientRazaoSocial:
                anchorContract.clientRazaoSocial ??
                (anchorContract as { client?: { razaoSocial?: string } }).client?.razaoSocial,
              clientCnpj:
                anchorContract.clientCnpj ??
                (anchorContract as { client?: { cnpj?: string } }).client?.cnpj,
              clientId:
                anchorContract.clientId ??
                (anchorContract as { client?: { id?: string } }).client?.id,
              clientEstado: anchorContract.clientEstado ?? first.clientEstado ?? contractInfo.clientEstado,
              clientCidade: anchorContract.clientCidade ?? first.clientCidade ?? contractInfo.clientCidade,
              clientClassification:
                anchorContract.clientClassification ??
                readClientClassification((anchorContract as any).client) ??
                first.clientClassification ??
                contractInfo.clientClassification ??
                readClientClassification((first as any).client),
              clientInternalNotes:
                anchorContract.clientInternalNotes ??
                readClientInternalNotes((anchorContract as any).client) ??
                first.clientInternalNotes ??
                contractInfo.clientInternalNotes ??
                readClientInternalNotes((first as any).client),
              internalNotes: groupInternalNotes || contractInfo.internalNotes,
              idPedidoContrato1: idPedido1,
              idPedidoContrato2: idPedido2,
              vtmeConfirmado: !!vtmeConfirmado,
              dataConfirmacaoVtme: dataVtme,
              valorTotalMensal: first.valorTotalMensal ?? contractInfo.valorTotalMensal,
              fastchip: first.fastchip ?? contractInfo.fastchip,
              token: aditivoComEndereco?.token ?? primeiroComEndereco?.token ?? first.token ?? contractInfo.token,
              fastchipEnviadoEm: first.fastchipEnviadoEm ?? contractInfo.fastchipEnviadoEm,
              deliveryAddress: groupDeliveryAddress,
              status: mergedGroupStatus,
              tipoContratacao: first.tipoContratacao || contractInfo.tipoContratacao,
              contractType: first.contractType ?? contractInfo.contractType,
            };
            setSelectedContractInfo(fullContractInfo);
            const groupSt = normalizeContractStatus(fullContractInfo.status);
            const groupPosCqv =
              groupSt === EContractStatus.AprovadoCQV ||
              groupSt === EContractStatus.VtmeCriado ||
              isTrocaDeCarteiraFlowStatus(groupSt) ||
              !!fullContractInfo.vtmeConfirmado;
            if (groupPosCqv) {
              setIdPedido1((prev) => fullContractInfo.idPedidoContrato1 || prev || '');
              setIdPedido2((prev) => fullContractInfo.idPedidoContrato2 || prev || '');
            } else {
              setIdPedido1('');
              setIdPedido2('');
              setLinhasAptas(new Map());
            }
            const allLines = groupResponse.flatMap((c: any) =>
              (c.linhas || []).map((line: any) => ({
                ...line,
                contractId: c.id,
                contractType: c.contractType || c.tipoContratacao,
              }))
            );
            setSelectedContractLines(allLines);
            setLinesModalGroupContracts(groupResponse);
            groupContractsForEligibility = groupResponse;
            trocaHistoryContractIds = groupResponse.map((c: any) => c.id).filter(Boolean);
            if (!isStaleRequest()) {
              applyLinesModalCqvClientValidation(groupResponse, contractInfo);
            }
            if (groupPosCqv) {
              const initialLinhasAptas = new Map<string, boolean>();
              allLines.forEach((line: any) => {
                if (line.negociacao === 'Renegociação') {
                  initialLinhasAptas.set(line.id, convertIsAptoToBoolean(line.isApto));
                }
              });
              setLinhasAptas(initialLinhasAptas);
            }
          }
        } catch (error) {
          try {
            await plansPromise;
          } catch {
            /* planos são auxiliares ao script CQV; falha não impede o modal */
          }
          if (isStaleRequest()) {
            return;
          }
          console.error('Erro ao carregar detalhes do grupo, usando fallback por contrato:', error);
          if (!isStaleRequest()) {
            clearCqvScriptState();
          }
          fullContractInfo = contractInfo;
          setSelectedContractInfo(fullContractInfo);
          const allLines: any[] = [];
          for (const subContract of contractInfo.groupContracts || []) {
            if (isStaleRequest()) {
              return;
            }
            try {
              const response = await contractService.getContractLines(subContract.id);
              if (isStaleRequest()) {
                return;
              }
              allLines.push(...response.map((line: any) => ({
                ...line,
                contractType: subContract.contractType || subContract.tipoContratacao,
                contractId: subContract.id,
              })));
            } catch (err) {
              console.error(`Erro ao carregar linhas do contrato ${subContract.id}:`, err);
            }
          }
          setSelectedContractLines(allLines);
          const fbSt = normalizeContractStatus(fullContractInfo.status);
          trocaHistoryContractIds = (contractInfo.groupContracts || [])
            .map((c: any) => c.id)
            .filter(Boolean);
          if (trocaHistoryContractIds.length === 0) {
            trocaHistoryContractIds = [contractId];
          }
          const fbPosCqv =
            fbSt === EContractStatus.AprovadoCQV ||
            fbSt === EContractStatus.VtmeCriado ||
            isTrocaDeCarteiraFlowStatus(fbSt) ||
            !!fullContractInfo.vtmeConfirmado;
          if (fbPosCqv) {
            setIdPedido1((prev) => fullContractInfo.idPedidoContrato1 || prev || '');
            setIdPedido2((prev) => fullContractInfo.idPedidoContrato2 || prev || '');
            const initialLinhasAptas = new Map<string, boolean>();
            allLines.forEach((line: any) => {
              if (line.negociacao === 'Renegociação') {
                initialLinhasAptas.set(line.id, convertIsAptoToBoolean(line.isApto));
              }
            });
            setLinhasAptas(initialLinhasAptas);
          }
        }
      } else {
        try {
          const [, modalData] = await Promise.all([
            plansPromise,
            queryClient.fetchQuery({
              queryKey: contractsQueryKeys.lineDetailsByContract(contractId),
              queryFn: () =>
                contractService.getContractForLineDetailsModal(contractId, {
                  signal: modalSignal,
                }),
              staleTime: CONTRACT_MODAL_CACHE_STALE_TIME_MS,
            }),
          ]);
          if (isStaleRequest()) {
            return;
          }
          soloModalEligibility = modalData.canEditContractLines;
          const headerStatus =
            normalizeContractStatus(modalData.status) ?? normalizeContractStatus(contractInfo.status);
          fullContractInfo = {
            ...contractInfo,
            administrator: modalData.administrator ?? contractInfo.administrator,
            representatives: mergeLineModalRepresentatives(
              modalData.representatives,
              contractInfo.representatives
            ),
            authorizedReceivers: modalData.authorizedReceivers || contractInfo.authorizedReceivers,
            userId: modalData.userId ?? modalData.user?.id ?? contractInfo.userId,
            user: modalData.user ?? contractInfo.user,
            clientRazaoSocial:
              modalData.clientRazaoSocial ??
              (modalData as { client?: { razaoSocial?: string } }).client?.razaoSocial,
            clientCnpj:
              modalData.clientCnpj ??
              (modalData as { client?: { cnpj?: string } }).client?.cnpj,
            clientId:
              modalData.clientId ??
              (modalData as { client?: { id?: string } }).client?.id,
            clientEstado: modalData.clientEstado ?? contractInfo.clientEstado,
            clientCidade: modalData.clientCidade ?? contractInfo.clientCidade,
            clientClassification:
              modalData.clientClassification ??
              contractInfo.clientClassification ??
              readClientClassification(contractInfo.client),
            clientInternalNotes:
              modalData.clientInternalNotes ??
              contractInfo.clientInternalNotes ??
              readClientInternalNotes(contractInfo.client),
            internalNotes:
              modalData.internalNotes ||
              readContractInternalNotes(modalData) ||
              contractInfo.internalNotes ||
              readContractInternalNotes(contractInfo),
            isGroupParent: contractInfo.isGroupParent,
            groupContracts: contractInfo.groupContracts,
            idPedidoContrato1: modalData.idPedidoContrato1 || contractInfo.idPedidoContrato1,
            idPedidoContrato2: modalData.idPedidoContrato2 || contractInfo.idPedidoContrato2,
            vtmeConfirmado: modalData.vtmeConfirmado || contractInfo.vtmeConfirmado,
            dataConfirmacaoVtme: modalData.dataConfirmacaoVtme || contractInfo.dataConfirmacaoVtme,
            valorTotalMensal: modalData.valorTotalMensal ?? contractInfo.valorTotalMensal,
            fastchip: modalData.fastchip ?? contractInfo.fastchip,
            token: modalData.token ?? contractInfo.token,
            fastchipEnviadoEm: modalData.fastchipEnviadoEm ?? contractInfo.fastchipEnviadoEm,
            // Card "Endereco de Entrega" do modal de detalhes consome este campo.
            deliveryAddress: modalData.deliveryAddress ?? contractInfo.deliveryAddress,
            status: headerStatus !== null && headerStatus !== undefined ? headerStatus : contractInfo.status,
            tipoContratacao: modalData.tipoContratacao || contractInfo.tipoContratacao,
            contractType: modalData.contractType || contractInfo.contractType,
          };
          if (modalData.linhas && Array.isArray(modalData.linhas)) {
            linesFromModal = modalData.linhas;
          }
          setLinesModalGroupContracts([]);
          if (!isStaleRequest()) {
            applyLinesModalCqvClientValidation([modalData], contractInfo);
          }
        } catch (error) {
          try {
            await plansPromise;
          } catch {
            /* planos são auxiliares ao script CQV; falha não impede o modal */
          }
          if (isStaleRequest()) {
            return;
          }
          console.error('Erro ao buscar dados do modal, usando informações disponíveis:', error);
          fullContractInfo = contractInfo;
          if (!isStaleRequest()) {
            clearCqvScriptState();
          }
        }

        setSelectedContractInfo(fullContractInfo);
        const soloSt = normalizeContractStatus(fullContractInfo.status);
        const soloPosCqv =
          soloSt === EContractStatus.AprovadoCQV ||
          soloSt === EContractStatus.VtmeCriado ||
          isTrocaDeCarteiraFlowStatus(soloSt) ||
          !!fullContractInfo.vtmeConfirmado;
        if (soloPosCqv) {
          setIdPedido1((prev) => fullContractInfo.idPedidoContrato1 || prev || '');
          setIdPedido2((prev) => fullContractInfo.idPedidoContrato2 || prev || '');
        } else {
          setIdPedido1('');
          setIdPedido2('');
          setLinhasAptas(new Map());
        }

        if (linesFromModal != null) {
          setSelectedContractLines(
            linesFromModal.map((line: any) => ({
              ...line,
              contractId: line.contractId ?? contractId,
            }))
          );
          if (soloPosCqv) {
            const initialLinhasAptas = new Map<string, boolean>();
            linesFromModal.forEach((line: any) => {
              if (line.negociacao === 'Renegociação') {
                initialLinhasAptas.set(line.id, convertIsAptoToBoolean(line.isApto));
              }
            });
            setLinhasAptas(initialLinhasAptas);
          }
        } else {
          const response = await contractService.getContractLines(contractId);
          if (isStaleRequest()) {
            return;
          }
          setSelectedContractLines(
            response.map((line: any) => ({
              ...line,
              contractId: line.contractId ?? contractId,
            }))
          );
          if (soloPosCqv) {
            const initialLinhasAptas = new Map<string, boolean>();
            response.forEach((line: any) => {
              if (line.negociacao === 'Renegociação') {
                initialLinhasAptas.set(line.id, convertIsAptoToBoolean(line.isApto));
              }
            });
            setLinhasAptas(initialLinhasAptas);
          }
        }
      }

      const clientIdForObservations =
        fullContractInfo?.clientId ?? contractInfo.clientId ?? contractInfo.client?.id ?? null;
      const correctionCycleTargetId = fullContractInfo?.id ?? contractId;
      const trocaStatus = normalizeContractStatus(fullContractInfo?.status);
      const shouldLoadCorrectionCycle =
        isAprovadoCqvOrBeyondPipelineStatus(fullContractInfo?.status) && hasRole('Admin');

      if (!shouldLoadCorrectionCycle) {
        correctionCycleRequestSeqRef.current += 1;
        setCorrectionCycle(null);
        setCorrectionCycleLoading(false);
      }

      const parallelTailTasks: Promise<void>[] = [];

      if (clientIdForObservations) {
        parallelTailTasks.push(loadLineDetailsClientObservationsForClient(clientIdForObservations));
      }

      if (shouldLoadCorrectionCycle) {
        parallelTailTasks.push(refreshCorrectionCycle(correctionCycleTargetId));
      }

      if (trocaStatus === EContractStatus.TrocaDeCarteiraSolicitada) {
        parallelTailTasks.push(
          (async () => {
            try {
              const histories = await Promise.all(
                trocaHistoryContractIds.map((id) => contractService.getContractStatusHistory(id))
              );
              if (isStaleRequest()) {
                return;
              }
              const blocked = histories.some((history) =>
                contractStatusHistoryIndicatesTrocaSolicitadaAfterAprovadoCqv(history)
              );
              setLinesModalTrocaSolicitadaAfterAprovadoCqv(blocked);
            } catch (historyError) {
              if (isStaleRequest()) {
                return;
              }
              console.error(
                'Erro ao carregar histórico para regra de edição em Troca Solicitada:',
                historyError
              );
              setLinesModalTrocaSolicitadaAfterAprovadoCqv(false);
            }
          })()
        );
      } else {
        setLinesModalTrocaSolicitadaAfterAprovadoCqv(false);
      }

      await Promise.all(parallelTailTasks);

      if (isStaleRequest()) {
        return;
      }

      let canEdit = false;
      if (groupContractsForEligibility.length > 0) {
        canEdit = groupContractsForEligibility.every((gc) => gc.canEditContractLines ?? false);
      } else if (soloModalEligibility !== undefined) {
        canEdit = soloModalEligibility ?? false;
      } else {
        try {
          const eligibilityId = fullContractInfo?.id ?? contractId;
          const eligibilityContract = await contractService.getContractById(eligibilityId);
          if (isStaleRequest()) {
            return;
          }
          canEdit = eligibilityContract.canEditContractLines ?? false;
        } catch (eligibilityError) {
          if (isStaleRequest()) {
            return;
          }
          console.error('Erro ao carregar elegibilidade de edição de linhas:', eligibilityError);
          canEdit = false;
        }
      }
      setLinesDetailCanEditLines(canEdit);
      setSelectedContractInfo((prev: typeof selectedContractInfo) =>
        prev ? { ...prev, canEditContractLines: canEdit } : prev
      );
    } catch (error) {
      if (isStaleRequest()) {
        return;
      }
      console.error('Erro ao carregar linhas do contrato:', error);
      if (!isAbortError(error)) {
        const message = extractApiErrorMessage(
          error,
          'Não foi possível carregar os detalhes das linhas. Tente novamente.'
        );
        setLinesModalError(message);
        showError(message);
      }
    } finally {
      if (!isStaleRequest()) {
        setLinesModalLoading(false);
      }
    }
  };

  const resolveContractInfoForLinesModalRefresh = useCallback(
    async (anchorContractId: string, previousInfo: any | null): Promise<any | null> => {
      if (!anchorContractId) return null;

      const groupId = previousInfo?.groupId;
      const isGroupView = Boolean(
        groupId &&
          (previousInfo?.isGroupParent ||
            linesModalGroupContracts.length > 1 ||
            (previousInfo?.groupContracts?.length ?? 0) > 1)
      );

      if (isGroupView && groupId) {
        const groupList = await contractService.getContractsByGroupId(groupId);
        const members = sortContractsForVtmeGroupOrder(
          groupList.map((contract) => mapContractResponseToListDto(contract))
        );
        if (members.length > 1) {
          const leader = members[0];
          return {
            ...(previousInfo ?? {}),
            ...leader,
            id: leader.id,
            groupId,
            isGroupParent: true,
            groupContracts: members,
            clientRazaoSocial: leader.clientRazaoSocial ?? previousInfo?.clientRazaoSocial,
            clientCnpj: leader.clientCnpj ?? previousInfo?.clientCnpj,
            clientNomeFantasia: leader.clientNomeFantasia ?? previousInfo?.clientNomeFantasia,
          };
        }
      }

      const modalData = await contractService.getContractForLineDetailsModal(anchorContractId);
      return {
        ...(previousInfo ?? {}),
        ...modalData,
        id: anchorContractId,
        groupId: previousInfo?.groupId,
        isGroupParent: previousInfo?.isGroupParent,
        groupContracts: previousInfo?.groupContracts,
        clientRazaoSocial: modalData.clientRazaoSocial ?? previousInfo?.clientRazaoSocial,
        clientCnpj: modalData.clientCnpj ?? previousInfo?.clientCnpj,
        clientNomeFantasia: previousInfo?.clientNomeFantasia,
      };
    },
    [linesModalGroupContracts.length]
  );

  const refreshUiAfterContractLineSave = useCallback(
    async (
      results: PatchContractResultDto[],
      context: { anchorContractId: string; previousInfo: any | null }
    ) => {
      results.forEach((result) => patchContractListItem(result));

      const deletedIds = new Set(
        results.filter((result) => result.wasDeleted).map((result) => result.contractId)
      );
      const anchorDeleted = deletedIds.has(context.anchorContractId);

      await loadContracts(currentPage);

      if (!showLinesModal) return;
      if (linesModalOpenContractIdRef.current !== context.anchorContractId) return;

      if (anchorDeleted) {
        handleCloseLinesModal();
        return;
      }

      const freshInfo = await resolveContractInfoForLinesModalRefresh(
        context.anchorContractId,
        context.previousInfo ?? selectedContractInfo
      );
      if (!freshInfo) return;

      await handleShowLinesModal(context.anchorContractId, freshInfo, { softRefresh: true });
    },
    [
      currentPage,
      loadContracts,
      patchContractListItem,
      resolveContractInfoForLinesModalRefresh,
      selectedContractInfo,
      showLinesModal,
    ]
  );

  const handleOpenCancelSuspensionModal = async (contractId: string, contractInfo: any) => {
    setCancelSuspensionModalOpen(true);
    setCancelSuspensionContractInfo(contractInfo);
    setCancelSuspensionLines([]);
    setCancelSuspensionLoading(true);
    try {
      const lines = await contractService.getContractLines(contractId);
      setCancelSuspensionLines(lines);
    } catch (error: any) {
      console.error('Erro ao carregar linhas para cancelamento de suspensão:', error);
      showError(error.message || 'Erro ao carregar linhas do contrato');
    } finally {
      setCancelSuspensionLoading(false);
    }
  };

  const handleConfirmCancelSuspensions = async (lineIds: string[]) => {
    const result = await contractLineService.cancelSuspensionBatch(lineIds);
    showSuccess(`${result.updated} solicitação(ões) de suspensão cancelada(s).`);
    setCancelSuspensionModalOpen(false);
    setCancelSuspensionLines([]);
    setCancelSuspensionContractInfo(null);
    await loadContracts();
  };

  const handleSendEmail = async (email: string) => {
    if (!sendingEmailContractIds?.length) return;

    setSendingEmail(true);
    try {
      for (const id of sendingEmailContractIds) {
        await contractService.sendContractEmail(id, email);
      }
      showSuccess(
        sendingEmailContractIds.length > 1
          ? `${sendingEmailContractIds.length} e-mails enviados (Aditivo e Renegociação), na ordem do VTME.`
          : 'Email enviado com sucesso!'
      );
      setShowSendEmailModal(false);
      setSendingEmailContractIds(null);
    } catch (error: any) {
      showError(error.message || 'Erro ao enviar email. Por favor, tente novamente.');
      throw error;
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDownloadGroupPdfs = async (groupId: string) => {
    try {
      await contractService.downloadGroupPdfs(groupId);
    } catch (error) {
      console.error('Erro ao baixar PDFs do grupo:', error);
      alert('Erro ao baixar PDFs do grupo');
    }
  };

  // Handler para salvar ICCID
  const handleSaveIccid = async (lineId: string, contractId: string) => {
    if (!editingIccid || editingIccid.lineId !== lineId) return;
    
    setUpdatingIccid(lineId);
    try {
      // Normalizar ICCID: remover todos os espaços e caracteres não numéricos
      let newIccidValue: string | null = editingIccid.value.trim().replace(/\s+/g, '').replace(/[^\d]/g, '');
      
      // Se ficou vazio após normalização, enviar null
      if (newIccidValue === '') {
        newIccidValue = null;
      }
      
      await updateLineIccidWithCacheInvalidation(contractId, lineId, newIccidValue);
      
      // Atualizar a linha na lista
      setSelectedContractLines(prev => 
        prev.map(line => 
          line.id === lineId 
            ? { ...line, chipCcid: newIccidValue }
            : line
        )
      );
      
      setEditingIccid(null);
    } catch (error: any) {
      console.error('Erro ao atualizar ICCID:', error);
      // Extrair mensagem de erro do backend
      const errorMessage = error?.response?.data?.message || error?.message || 'Erro ao atualizar ICCID';
      alert(errorMessage);
      // Restaurar valor original em caso de erro
      setEditingIccid(null);
    } finally {
      setUpdatingIccid(null);
    }
  };

  const handleCancelIccidEdit = () => {
    setEditingIccid(null);
  };

  const handleFastchipSwapConfirm = async (swaps: Array<{ lineId: string; contractId?: string; newIccid: string | null; motivoTroca: string; fastUsado: boolean }>) => {
    const fallbackContractId = selectedContractInfo?.id;
    if (!fallbackContractId && !swaps.every(s => s.contractId)) return;

    const grouped: Record<string, Array<{ lineId: string; newIccid: string | null; motivoTroca?: string; fastUsado?: boolean }>> = {};
    for (const swap of swaps) {
      const cid = swap.contractId || fallbackContractId;
      if (!cid) continue;
      if (!grouped[cid]) grouped[cid] = [];
      grouped[cid].push({
        lineId: swap.lineId,
        newIccid: swap.newIccid,
        motivoTroca: swap.motivoTroca,
        fastUsado: swap.fastUsado,
      });
    }

    for (const [contractId, items] of Object.entries(grouped)) {
      await contractService.batchUpdateLineIccid(contractId, items);
    }

    setSelectedContractLines(prev =>
      prev.map(line => {
        const swap = swaps.find(s => s.lineId === line.id);
        if (swap) {
          return {
            ...line,
            chipCcidAntigo: line.chipCcid,
            chipCcid: swap.newIccid,
            motivoTrocaFastchip: swap.motivoTroca,
            dataTrocaFastchip: new Date().toISOString(),
          };
        }
        return line;
      })
    );

    setSelectedLinesForSwap(new Set());
    setShowFastchipSwapModal(false);
    showSuccess(`${swaps.length} FastChip${swaps.length > 1 ? 's' : ''} troca${swaps.length > 1 ? 'dos' : 'do'} com sucesso!`);
  };

  const hasAparelhoInDraft = useMemo(
    () => linhas.some((linha) => lineHasAnyHandset(linha)),
    [linhas]
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dadosTim':
        return timClientData ? (
          <ContractFormDadosTimTab
            timClientData={timClientData}
            partners={selectedClient?.partners ?? []}
          />
        ) : null;
      case 'cliente':
        return (
          <ContractFormClienteTab
            editingContractId={editingContractId}
            selectedClient={selectedClient ?? null}
            clientObservations={selectedClientObservations}
            clientObservationsLoading={selectedClientObservationsLoading}
            onClearClient={handleClientClear}
            clientSearchQuery={clientSearchQuery}
            setClientSearchQuery={setClientSearchQuery}
            loadClients={loadClients}
            clientsLoading={clientsLoading}
            searchingCnpj={searchingCnpj}
            onSearchCnpj={handleSearchCnpj}
            cnpjInputError={cnpjInputError}
            onClientSearchBlur={handleClientSearchBlur}
            error={error}
            clients={clients}
            onSelectClient={handleClientSelect}
          />
        );
      case 'contrato': {
        const lockFastchipPorAparelho = !editingContractId && hasAparelhoInDraft;
        const effectiveFastchipDisabled = fastchipDisabled || lockFastchipPorAparelho;
        const effectiveFastchipDisabledReason = lockFastchipPorAparelho
          ? 'há linha(s) com aparelho; aparelho exige entrega da operadora (fastchip=false)'
          : fastchipDisabledReason;
        return (
          <ContractFormContratoDadosTab
            editingContractId={editingContractId}
            tipoContratacao={tipoContratacao}
            isPrimeiraVenda={isPrimeiraVenda}
            setIsPrimeiraVenda={setIsPrimeiraVenda}
            comprometimentoMinimo={comprometimentoMinimo}
            suggestedClusterDisplay={suggestedClusterBanner.token || suggestedClusterDisplay}
            suggestedClusterLoading={suggestedClusterLoading}
            token={token}
            onTokenChange={(v) => {
              saleTokenLockedRef.current = v.trim() !== '';
              setToken(v);
            }}
            diaVencimento={diaVencimento}
            setDiaVencimento={setDiaVencimento}
            numeroP2B={numeroP2B}
            setNumeroP2B={setNumeroP2B}
            fastchip={fastchip}
            setFastchip={(value) => {
              const nextValue = typeof value === 'function' ? value(fastchip) : value;
              if (nextValue && hasAparelhoInDraft) {
                showWarning(
                  'Contrato com aparelho (incluindo WTTX) deve ser por entrega da operadora. Remova os aparelhos para ativar Fastchip.'
                );
                return;
              }
              setFastchip(value);
            }}
            fastchipDisabled={effectiveFastchipDisabled}
            fastchipDisabledReason={effectiveFastchipDisabledReason}
            hasAparelhoInDraft={hasAparelhoInDraft}
            temSuspensao={temSuspensao}
            setTemSuspensao={setTemSuspensao}
            allowsSuspensionWithoutFastchip={dhDeliverySuspensionAllowed}
            suspensaoBloqueadaContrato={suspensaoBloqueadaContrato}
            ocultarCardSuspensaoNovosContratos={bloqueioSuspensaoNovosContratos}
            mensagemOrientacao1056={mensagemOrientacaoSuspensao1056}
            clientEstado={selectedClient?.estado}
            fastchipUnavailableForRegion={fastchipDisabled}
            fastchipUnavailableReason={fastchipDisabledReason}
            needsIeMonitoring={needsIeMonitoring}
            tipoFatura={tipoFatura}
            envioFatura={envioFatura}
            origemVenda={origemVenda}
            prClusterDddMismatchWarning={prClusterDddMismatchWarning}
            tokenLocationContext={
              selectedClient
                ? {
                    clienteRazaoSocial: selectedClient.razaoSocial ?? '',
                    clienteCidade: selectedClient.cidade ?? '',
                    clienteEstado: selectedClient.estado ?? '',
                    entregaCidade: cidade ?? '',
                    entregaEstado: estado ?? '',
                    suggestedCluster: suggestedClusterBanner.token,
                    suggestedClusterSource: suggestedClusterBanner.source,
                    suggestedClusterLoading: suggestedClusterLoading,
                    prClusterHint: selectedClient.estado === 'PR' && fastchip ? effectivePrCluster : undefined,
                    hbCpClusterHint:
                      fastchip && isHbCpMarketEstado(selectedClient.estado) ? effectiveHbCpCluster : undefined,
                  }
                : null
            }
            showResponsibleConsultantField={!editingContractId && canAssignResponsibleConsultant}
            responsibleConsultantUserId={responsibleConsultantUserId}
            onResponsibleConsultantChange={setResponsibleConsultantUserId}
            onResponsibleConsultantVisible={handleConsultantFieldVisible}
            onResponsibleConsultantFocus={handleConsultantFieldInteraction}
            consultantDropdownOptions={consultantDropdownOptions}
            loadingConsultantOptions={loadingConsultantOptions}
          />
        );
      }
      case 'endereco':
        return (
          <ContractFormEnderecoTab
            selectedClient={selectedClient ?? null}
            cep={cep}
            setCep={setCep}
            estado={estado}
            setEstado={setEstado}
            cidade={cidade}
            setCidade={setCidade}
            endereco={endereco}
            setEndereco={setEndereco}
            numero={numero}
            setNumero={setNumero}
            bairro={bairro}
            setBairro={setBairro}
            complemento={complemento}
            setComplemento={setComplemento}
            referencia={referencia}
            setReferencia={setReferencia}
            onCepChange={handleCepChange}
            cepLoading={cepLoading}
            fastchip={fastchip}
          />
        );
      case 'administrador':
        return (
          <ContractFormAdministradorTab
            adminNomeCompleto={adminNomeCompleto}
            setAdminNomeCompleto={setAdminNomeCompleto}
            adminCpf={adminCpf}
            setAdminCpf={setAdminCpf}
            adminRg={adminRg}
            setAdminRg={setAdminRg}
            adminEmail={adminEmail}
            setAdminEmail={setAdminEmail}
            adminTelefone1={adminTelefone1}
            setAdminTelefone1={setAdminTelefone1}
            adminTelefone2={adminTelefone2}
            setAdminTelefone2={setAdminTelefone2}
          />
        );
      case 'representantes':
        return (
          <ContractFormRepresentativesTab
            representantes={representantes}
            setRepresentantes={setRepresentantes}
            representanteManual={representanteManual}
            setRepresentanteManual={setRepresentanteManual}
            selectedClient={selectedClient ?? null}
            clientPartners={clientPartners}
          />
        );
      case 'recebedores':
        return (
          <ContractFormRecebedoresTab
            recebedores={recebedores}
            setRecebedores={setRecebedores}
            recebedorManual={recebedorManual}
            setRecebedorManual={setRecebedorManual}
            selectedClient={selectedClient ?? null}
            clientPartners={clientPartners}
          />
        );
      case 'testemunhas':
        return (
          <ContractFormTestemunhasTab
            testemunhas={testemunhas}
            setTestemunhas={setTestemunhas}
            testemunhaAtual={testemunhaAtual}
            setTestemunhaAtual={setTestemunhaAtual}
          />
        );
      case 'linhas':
        return renderLinhasTab();
      case 'linhasDesativar':
        return (
          <>
          <ContractFormLinhasDesativarTab
            linhas={linhas}
            setLinhas={setLinhas}
            linhasParaDesativar={linhasParaDesativar}
            setLinhasParaDesativar={setLinhasParaDesativarGuarded}
            toggleLinhaDesativar={toggleLinhaDesativarGuarded}
            calcularDataDesativacao={calcularDataDesativacao}
            teamContractRules={teamContractRules}
            calcularAditivosNecessarios={calcularAditivosNecessarios}
            fastchip={fastchip}
            allowsSuspensionWithoutFastchip={dhDeliverySuspensionAllowed}
            substituirPorOrientacao1056={bloqueioSuspensaoNovosContratos}
            mensagemOrientacao1056={mensagemOrientacaoSuspensao1056}
            suspensaoBloqueadaContrato={suspensaoBloqueadaContrato}
            isAdminCorrectionWithoutFastchip={shouldSkipFastchipConversionModal(
              hasRole('Admin'),
              editingContractId
            )}
            onAddLinhaManual={(ddd, numero) => {
              if (fluxoSuspensaoLinhasBloqueado) {
                showError(mensagemOrientacaoSuspensao1056);
                return;
              }
              // TEMPORÁRIO (pedido Eder — bater meta): DH por entrega marca sem converter.
              if (!fastchip && !dhDeliverySuspensionAllowed) {
                if (tryOpenSuspensionConversionModal([], { ddd, numero })) return;
              }
              const id = `manual-suspensao-${Date.now()}-${Math.random()}`;
              setLinhas((prev) => [
                ...prev,
                {
                  id,
                  negociacao: 'Renegociação',
                  ddd,
                  numero,
                  tipoVenda: 'Sem aparelho',
                  planId: RP_6GB_PLAN_ID,
                  planName: RP_6GB_PLAN_NAME,
                  aparelho: '',
                  valorChip: 0,
                  valorMensal: 0,
                  chipCcid: '',
                  quantidade: 1,
                  excluirDoContrato: true,
                  requerSuspensao: true,
                  trocaLinha: false,
                  meuTimLogin: '',
                  meuTimSenha: '',
                  protocolo: '',
                  seraUsadaPeloCliente: null,
                  ehPortabilidade: false,
                  portabilidadeNomeCompleto: '',
                  portabilidadeCpf: '',
                  portabilidadeOperadora: '',
                  portabilidadeLinha: '',
                  portabilidadeCnpjCedente: '',
                  portabilidadeRazaoSocialCedente: '',
                  portabilidadeRepresentantes: [],
                  ofertas: [],
                  marcarParaDesativar: true,
                  dataDesativacao: calcularDataDesativacao(),
                } as ContractLineFormItem,
              ]);
              setLinhasParaDesativar((prev) => {
                const next = new Set(prev);
                next.add(id);
                return next;
              });
            }}
          />
          {editingContractId && (
            <ContractClientCancellationLinesPanel
              contractId={editingContractId}
              clientId={selectedClient?.id}
              compact
              lines={linhas
                .filter((l) => l.id)
                .map((l) => ({
                  id: l.id!,
                  ddd: l.ddd ?? '',
                  numero: l.numero ?? '',
                }))}
            />
          )}
          </>
        );
      case 'meuTim': {
        const sharedPasswordVisible = !!meuTimPasswordVisibility.shared;
        return (
          <div style={{ padding: '20px' }}>
            <Header as="h3" style={{ color: '#2c3e50', marginBottom: '12px' }}>
              <Icon name="mobile alternate" />
              Validação Meu TIM
            </Header>

            {deveExibirBannerPoliticaSuspensao(suspensionPolicy) && (
              <SuspensionBlockBanner
                variant="info"
                message={[
                  suspensionPolicy?.policyReferenceDateDescription,
                  suspensionPolicy?.suspensionCutoffDate
                    ? `Data de corte: ${suspensionPolicy.suspensionCutoffDate}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              />
            )}
            {fluxoSuspensaoLinhasBloqueado && hasSuspensaoDraft && (
              <SuspensionBlockBanner message={mensagemOrientacaoSuspensao1056} />
            )}

            {!hasSuspensaoDraft && (
              <Message info>
                <Message.Header>Sem suspensão no contrato</Message.Header>
                <p>Este passo não exige ação porque não há linhas marcadas para suspensão.</p>
              </Message>
            )}

            {meuTimStepRequired && (
              <Message warning>
                <Message.Header>Preenchimento obrigatório</Message.Header>
                <p>
                  Como o contrato possui suspensão e está <strong>sem Fastchip</strong>, informe uma credencial Meu TIM
                  única do cliente antes de avançar.
                </p>
              </Message>
            )}

            {meuTimStepOptional && (
              <Message info>
                <Message.Header>Preenchimento opcional</Message.Header>
                <p>
                  {dhDeliverySuspensionAllowed && !fastchip ? (
                    <>
                      No cluster <strong>DH - RJ/MG/ES</strong> por entrega, a verificação Meu TIM é{' '}
                      <strong>opcional</strong>. Você pode avançar/salvar sem clicar em “Verificar acesso”.
                    </>
                  ) : (
                    <>
                      O contrato possui suspensão com <strong>Fastchip</strong>. Você pode preencher as credenciais Meu
                      TIM agora para já disparar o health-check automático após salvar.
                    </>
                  )}
                </p>
              </Message>
            )}

            {meuTimStepRequired && (
              <>
                {!hasCompleteMeuTimCredentials ? (
                  <Message warning size="small">
                    <Message.Header>Pendência: credenciais incompletas</Message.Header>
                    <p>Preencha login e senha Meu TIM do cliente.</p>
                  </Message>
                ) : meuTimVerificationStatus === 'success' &&
                  meuTimVerifiedSignature === meuTimCredentialSignature ? (
                  <Message positive size="small">
                    <Message.Header>Validação concluída</Message.Header>
                    <p>As credenciais atuais foram verificadas com sucesso. O fluxo está liberado para avançar.</p>
                  </Message>
                ) : meuTimVerificationStatus === 'soft_failed' &&
                  meuTimVerifiedSignature === meuTimCredentialSignature ? (
                  <Message warning size="small">
                    <Message.Header>Acesso Meu TIM não confirmado</Message.Header>
                    <p>
                      A verificação foi feita, mas <strong>não confirmou o acesso</strong> (credencial inválida,
                      captcha/bloqueio, timeout ou erro no portal). Você pode continuar o cadastro do contrato — mas
                      valide manualmente depois no modal <strong>"Detalhes das linhas"</strong> (botão "Verificar
                      acesso agora") antes da suspensão real.
                    </p>
                  </Message>
                ) : (
                  <Message info size="small">
                    <Message.Header>Pendência: verificar acesso</Message.Header>
                    <p>
                      Clique em <strong>Verificar acesso</strong> para validar as credenciais atuais antes de continuar.
                    </p>
                  </Message>
                )}
              </>
            )}

            {linhasSuspensaoDraft.length > 0 && hasCompleteMeuTimCredentials && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                <Button
                  color="blue"
                  icon
                  labelPosition="left"
                  onClick={() => void handleVerifyMeuTimAccess()}
                  loading={meuTimVerificationStatus === 'loading'}
                  disabled={meuTimVerificationStatus === 'loading'}
                >
                  <Icon name="shield alternate" />
                  Verificar acesso
                </Button>
                {meuTimVerificationStatus === 'success' && (
                  <Label color="green" basic>
                    <Icon name="check circle" />
                    Verificação válida
                  </Label>
                )}
                {meuTimVerificationStatus === 'soft_failed' && (
                  <Label color="orange" basic title="Não bloqueia o cadastro — valide manualmente depois.">
                    <Icon name="warning circle" />
                    Acesso não confirmado (não bloqueia)
                  </Label>
                )}
                {meuTimVerificationStatus === 'error' && (
                  <Label color="red" basic>
                    <Icon name="warning sign" />
                    Falha na verificação
                  </Label>
                )}
              </div>
            )}

            {meuTimVerificationMessage && (
              <Message
                size="small"
                positive={meuTimVerificationStatus === 'success'}
                warning={meuTimVerificationStatus === 'soft_failed'}
                negative={meuTimVerificationStatus === 'error'}
                info={meuTimVerificationStatus === 'loading' || meuTimVerificationStatus === 'idle'}
              >
                {meuTimVerificationMessage}
              </Message>
            )}

            {linhasSuspensaoDraft.length > 0 ? (
              <>
                <Segment>
                  <Form>
                    <Form.Field>
                      <label>Credencial Meu TIM do cliente</label>
                    </Form.Field>
                    <Grid stackable>
                      <Grid.Row columns={2}>
                        <Grid.Column>
                          <Form.Field required={meuTimStepRequired}>
                            <label>Login Meu TIM</label>
                            <Input
                              fluid
                              placeholder="Login Meu TIM"
                              value={meuTimSharedCredential.login}
                              onChange={(e) =>
                                updateMeuTimSharedCredential('meuTimLogin', e.target.value)
                              }
                            />
                          </Form.Field>
                        </Grid.Column>
                        <Grid.Column>
                          <Form.Field required={meuTimStepRequired}>
                            <label>Senha Meu TIM</label>
                            <Input
                              fluid
                              type={sharedPasswordVisible ? 'text' : 'password'}
                              placeholder="Senha Meu TIM"
                              value={meuTimSharedCredential.senha}
                              icon={
                                <Icon
                                  name={sharedPasswordVisible ? 'eye slash' : 'eye'}
                                  link
                                  onClick={() => toggleMeuTimPasswordVisibility('shared')}
                                  title={sharedPasswordVisible ? 'Ocultar senha' : 'Mostrar senha'}
                                />
                              }
                              onChange={(e) =>
                                updateMeuTimSharedCredential('meuTimSenha', e.target.value)
                              }
                            />
                          </Form.Field>
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Form>
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                    Esta credencial será reutilizada para todas as linhas marcadas para suspensão deste contrato.
                  </div>
                </Segment>

                <Table celled striped>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell width={10}>Linha</Table.HeaderCell>
                      <Table.HeaderCell width={6}>Credencial aplicada</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {linhasSuspensaoDraft.map((linha, idx) => {
                      const lineKey = getMeuTimLineKey(linha, idx);

                      return (
                        <Table.Row key={lineKey}>
                          <Table.Cell>
                            <strong>{formatLinhaIdentificacao(linha, idx)}</strong>
                            <div style={{ marginTop: '6px', color: '#666', fontSize: '0.85em' }}>
                              {linha.negociacao || 'Sem negociação'}
                              {linha.planName ? ` • ${linha.planName}` : ''}
                            </div>
                          </Table.Cell>
                          <Table.Cell>
                            {hasCompleteMeuTimCredentials ? (
                              <Label color="green" basic>
                                <Icon name="check circle" />
                                Credencial compartilhada aplicada
                              </Label>
                            ) : (
                              <Label color="orange" basic>
                                <Icon name="exclamation triangle" />
                                Aguardando preenchimento
                              </Label>
                            )}
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Table>
              </>
            ) : hasSuspensaoDraft ? (
              <Message warning>
                <Message.Header>Nenhuma linha marcada para suspensão</Message.Header>
                <p>Volte na aba "Linhas a Desativar" e selecione as linhas que serão suspensas.</p>
              </Message>
            ) : null}
          </div>
        );
      }
      case 'observacoesInternas':
        return (
          <div style={{ padding: '20px' }}>
            <Header as="h3" style={{ color: '#2c3e50', marginBottom: '12px' }}>
              <Icon name="sticky note outline" />
              Observações Internas do Contrato
            </Header>
            <Message info>
              <Message.Header>Visibilidade interna</Message.Header>
              <p>
                Estes registros ficam no histórico do cliente (data, colaborador, ocorrência e observação).
                Não aparecem em documentos externos (PDF, DocuSign, e-mails, WhatsApp ou CQV externo).
              </p>
            </Message>
            <Form>
              <Form.Field>
                <label>Ocorrência</label>
                <Form.Input
                  placeholder="Ex.: Criação de contrato, contexto comercial, orientação operacional..."
                  value={contractInternalNotesOccurrence}
                  onChange={(_, data) => setContractInternalNotesOccurrence(String(data.value ?? ''))}
                />
              </Form.Field>
              <Form.Field>
                <label>Observação (opcional)</label>
                <TextArea
                  placeholder="Detalhes adicionais para o histórico do cliente..."
                  value={contractInternalNotes}
                  onChange={(e: React.FormEvent<HTMLTextAreaElement>) =>
                    setContractInternalNotes(e.currentTarget.value)
                  }
                  rows={8}
                  style={{ width: '100%', minHeight: 180, resize: 'vertical' }}
                />
              </Form.Field>
            </Form>
          </div>
        );
      default:
        return <div>Tab não encontrada</div>;
    }
  };

  // Debug: monitorar mudanças no estado do modal
  useEffect(() => {
  }, [isCreateModalOpen, activeTab]);

  // Carregar clientes quando a modal for aberta e a tab cliente for selecionada
  useEffect(() => {
    if (isCreateModalOpen && activeTab === 'cliente' && clients.length === 0) {
      loadClients();
    }
  }, [isCreateModalOpen, activeTab, clients.length]);

  // Carregar planos quando a modal for aberta e a tab linhas for selecionada
  useEffect(() => {
    if (isAuthenticated && handsetCatalog.length === 0) {
      void loadHandsetCatalog();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isCreateModalOpen && activeTab === 'linhas' && plans.length === 0) {
      loadPlans();
    }
  }, [isCreateModalOpen, activeTab, plans.length]);

  // Filtrar planos quando a negociação mudar ou quando os planos forem carregados
  useEffect(() => {
    if (plans.length > 0) {
      filtrarPlanos(linhaAtual.negociacao);
    }
  }, [linhaAtual.negociacao, plans]);

  // Filtrar planos quando a negociação mudar no modal de edição
  useEffect(() => {
    if (plans.length > 0 && showEditLinhaModal) {
      filtrarPlanos(linhaEditando.negociacao);
    }
  }, [linhaEditando.negociacao, plans, showEditLinhaModal]);

  // Inicializar datas padrão quando o modal for aberto
  useEffect(() => {
    if (isCreateModalOpen && activeTab === 'contrato') {
      const today = new Date().toISOString().split('T')[0]; // Formato YYYY-MM-DD
    }
  }, [isCreateModalOpen, activeTab]);

  // Carregar partners do cliente quando necessário
  useEffect(() => {
    if (selectedClient && (activeTab === 'administrador' || activeTab === 'representantes' || activeTab === 'recebedores')) {
      // Usar dados reais do cliente selecionado (dados já disponíveis localmente)
      setClientPartners(selectedClient.partners || []);
    } else {
      setClientPartners([]);
    }
  }, [selectedClient, activeTab]);

  // Limpar DDD quando cliente/estado de Fastchip efetivo mudarem.
  useEffect(() => {
    // Não limpar DDD se for Renegociação (permite DDDs diferentes)
    if (linhaAtual.negociacao === 'Renegociação') {
      return;
    }
    // Só validar/limpar DDD pela lista quando Fastchip estiver realmente ativo.
    if (!fastchipFlowEnabled || !linhaAtual.ddd) {
      return;
    }
    const prCluster =
      selectedClient?.estado === 'PR' ? effectivePrCluster : undefined;
    const validDdds = getValidDddsWithICcIdFallback(
      selectedClient?.estado || '',
      fastchipFlowEnabled,
      prCluster
    );
    if (!validDdds.includes(linhaAtual.ddd)) {
      setLinhaAtual((prev) => ({ ...prev, ddd: '' }));
    }
  }, [
    selectedClient?.estado,
    fastchipFlowEnabled,
    linhaAtual.negociacao,
    linhaAtual.ddd,
    availableICcIds,
    effectivePrCluster,
    getValidDddsWithICcIdFallback,
  ]);


  // Carregar contratos ao montar. Com clientCnpj na URL, aplica o mesmo filtro
  // da busca por cliente e não dispara a lista geral (evita corrida e estado velho).
  useEffect(() => {
    if (!isAuthenticated) return;
    const clientCnpj = new URLSearchParams(window.location.search).get('clientCnpj')?.trim();
    if (clientCnpj) {
      applyFilters({ clientSearch: clientCnpj });
      return;
    }
    loadContracts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Verificar se há dados de simulação e abrir modal automaticamente
  useEffect(() => {
    if (simulationData && isAuthenticated) {
      
      // Buscar cliente pelo ID ou CNPJ
      const loadClientFromSimulation = async () => {
        try {
          let client: ClientResponse | null = null;
          
          // Tentar buscar pelo ID primeiro
          if (simulationData.clienteId) {
            try {
              client = await clientService.getClientById(simulationData.clienteId);
            } catch (error) {
              console.warn('Erro ao buscar cliente por ID, tentando por CNPJ...', error);
            }
          }
          
          // Se não encontrou pelo ID, tentar buscar e salvar por CNPJ
          if (!client && simulationData.clienteCnpj) {
            try {
              const result = await clientService.searchAndSaveClient(simulationData.clienteCnpj);
              if (result.success && result.data) {
                client = result.data;
              }
            } catch (error) {
              console.warn('Erro ao buscar cliente por CNPJ:', error);
            }
          }
          
          if (client) {
            // Selecionar o cliente
            setSelectedClient(client);
            
            // Carregar planos para preencher valores mensais
            await loadPlans();
            
            // Buscar planos atualizados
            const allPlans = await planService.getAllPlans();
            
            // Preencher linhas de renovação
            const linhasRenovacao = simulationData.linhasRenovacao.map((lr) => {
              const plan = allPlans.find(p => p.id === lr.planId);
              // Extrair DDD e número do número completo
              // Garantir que números de 11 dígitos (DDD + 9 dígitos) sejam tratados corretamente
              const { ddd, number } = extractDddFromPhone(lr.numeroLinha);
              
              // Validação adicional: se não extraiu DDD mas o número tem 11 dígitos, forçar extração
              const cleanedNumero = cleanPhone(lr.numeroLinha);
              let dddFinal = ddd;
              let numeroFinal = number;
              
              if (!dddFinal && cleanedNumero.length === 11) {
                // Se não extraiu DDD mas tem 11 dígitos, pegar os 2 primeiros como DDD
                dddFinal = cleanedNumero.substring(0, 2);
                numeroFinal = cleanedNumero.substring(2);
              } else if (!dddFinal && cleanedNumero.length === 10) {
                // Se tem 10 dígitos, também pode ser DDD + 8 dígitos (telefone fixo)
                dddFinal = cleanedNumero.substring(0, 2);
                numeroFinal = cleanedNumero.substring(2);
              }
              
              let numeroLimpoFinal = numeroFinal || '';

              // Verificar se esta linha deve ser suspensa (usar a flag do objeto ou fallback para array)
              const deveSuspender = (lr as any).estaSuspensa || simulationData.linhasParaSuspender?.includes(lr.numeroLinha) || false;
              
              return {
                id: `renovacao-${Date.now()}-${Math.random()}`,
                negociacao: 'Renegociação',
                ddd: dddFinal || '', // DDD extraído do número
                numero: numeroLimpoFinal, // Apenas o número (sem DDD) - garantido
                tipoVenda: 'Sem aparelho',
                planId: lr.planId,
                plano: plan?.name || '',
                aparelho: '',
                valorChip: 7.00,
                valorMensal: plan?.totalMonthlyPrice || 0,
                chipCcid: '',
                quantidade: 1,
                excluirDoContrato: deveSuspender, // Marcar como true se for suspensa
                requerSuspensao: deveSuspender,
                meuTimLogin: deveSuspender && simulationData.credenciaisMeuTim ? simulationData.credenciaisMeuTim.login : '',
                meuTimSenha: deveSuspender && simulationData.credenciaisMeuTim ? simulationData.credenciaisMeuTim.senha : ''
              };
            });
            
            // Preencher linhas aditivas
            let linhasAditivo = simulationData.linhasAditivo.map((la) => {
              const plan = allPlans.find(p => p.id === la.planId);
              
              return {
                id: `aditivo-${Date.now()}-${Math.random()}`,
                negociacao: 'Aditivo',
                ddd: la.ddd,
                numero: la.numero || '',
                tipoVenda: 'Sem aparelho',
                planId: la.planId,
                plano: plan?.name || '',
                aparelho: '',
                valorChip: la.fastchip ? 0.00 : 7.00,
                valorMensal: plan?.totalMonthlyPrice || 0,
                chipCcid: la.iccid || '',
                quantidade: 1,
                seraUsadaPeloCliente: la.seraUsadaPeloCliente ?? null
              };
            });
            
            // Detectar se veio do simulador (tem aditivos já calculados)
            // Se veio do simulador, NÃO adicionar aditivos extras (já estão corretos)
            const vemDoSimulador = simulationData?.linhasAditivo && simulationData.linhasAditivo.length > 0;
            
            // Aplicar regra de aditivos APENAS se NÃO veio do simulador e se a regra estiver ativa para o time
            if (teamContractRules.requireAditivosForSuspendedLines && !vemDoSimulador) {
              const linhasSuspensas = simulationData.linhasParaSuspender?.length || 0;
              const aditivosNecessarios = calcularAditivosNecessarios(linhasSuspensas);
              const aditivosAtuais = linhasAditivo.length;
              
              // Só adicionar aditivos automáticos se realmente necessário E se não houver aditivos suficientes
              if (aditivosNecessarios > 0 && aditivosNecessarios > aditivosAtuais) {
              // Adicionar linhas aditivas automaticamente
              const aditivosParaAdicionar = aditivosNecessarios - aditivosAtuais;
              const planosAditivo = filterAditivoEligiblePlans(allPlans);
              
              // Buscar plano mais barato para adicionar automaticamente
              const planoMaisBarato = planosAditivo.length > 0 
                ? planosAditivo.reduce((prev, curr) => 
                    (curr.totalMonthlyPrice < prev.totalMonthlyPrice) ? curr : prev
                  )
                : null;
              
              if (planoMaisBarato) {
                for (let i = 0; i < aditivosParaAdicionar; i++) {
                  linhasAditivo.push({
                    id: `aditivo-auto-${Date.now()}-${Math.random()}-${i}`,
                    negociacao: 'Aditivo',
                    ddd: '',
                    numero: '',
                    tipoVenda: 'Sem aparelho',
                    planId: planoMaisBarato.id,
                    plano: planoMaisBarato.name,
                    planName: planoMaisBarato.name,
                    aparelho: '',
                    valorChip: 7.00,
                    valorMensal: planoMaisBarato.totalMonthlyPrice,
                    chipCcid: '',
                    quantidade: 1,
                    seraUsadaPeloCliente: null
                  } as any);
                }
                
                showSuccess(
                  `${aditivosParaAdicionar} ${aditivosParaAdicionar === 1 ? 'linha aditiva foi adicionada' : 'linhas aditivas foram adicionadas'} automaticamente (${linhasSuspensas} ${linhasSuspensas === 1 ? 'linha suspensa requer' : 'linhas suspensas requerem'} ${aditivosNecessarios} ${aditivosNecessarios === 1 ? 'aditivo' : 'aditivos'})`
                );
              }
            }
            }
            
            // Se houver fastchip nas linhas aditivas, ativar ANTES de combinar
            const temFastchip = simulationData.linhasAditivo.some(la => la.fastchip);
            if (temFastchip) {
              setFastchip(true);
            }
            
            // Combinar todas as linhas (normalizar para ContractLineFormItem com campos obrigatórios)
            const defaults = {
              ofertas: [],
              excluirDoContrato: false,
              requerSuspensao: false,
              trocaLinha: false,
              meuTimLogin: '',
              meuTimSenha: '',
              protocolo: '',
              seraUsadaPeloCliente: null as boolean | null,
              ehPortabilidade: false,
              portabilidadeNomeCompleto: '',
              portabilidadeCpf: '',
              portabilidadeOperadora: '',
              portabilidadeLinha: '',
              portabilidadeCnpjCedente: '',
              portabilidadeRazaoSocialCedente: '',
              portabilidadeRepresentantes: [] as PortabilidadeRepresentanteFields[]
            };
            const mergedSimulationLines = [...linhasRenovacao, ...linhasAditivo].map((l) => ({
              ...defaults,
              ...l,
            }));
            setLinhas(mergedSimulationLines);
            setLinhasParaDesativar(collectLinhasParaDesativarIds(mergedSimulationLines));
            
            // Abrir modal e ir para aba de contrato (para que o usuário veja o fastchip marcado)
            await openContractModalWithRules('contrato');
            
            // Limpar dados de simulação do contexto
            clearSimulationData();
            
            showSuccess('Dados da simulação carregados! Verifique o contrato e as linhas.');
          } else {
            showError('Cliente não encontrado. Por favor, selecione o cliente manualmente.');
            clearSimulationData();
          }
        } catch (error: any) {
          console.error('Erro ao carregar dados da simulação:', error);
          showError('Erro ao carregar dados da simulação. Por favor, preencha o contrato manualmente.');
          clearSimulationData();
        }
      };
      
      loadClientFromSimulation();
    }
  }, [simulationData, isAuthenticated]);

  // Processar salvamento após decisão do modal de separação
  useEffect(() => {
    if (shouldProcessSave && shouldSeparateContracts !== undefined) {
      setShouldProcessSave(false); // Resetar flag imediatamente para evitar loops
      handleSaveContract(); // Continuar processo de salvamento
    }
  }, [shouldProcessSave, shouldSeparateContracts]);

  // Handler para mudança de página
  // Handler para baixar todos os contratos em ZIP
  const handleDownloadAllContracts = async (contractId: string) => {
    try {
      setIsDownloadingContracts(true);
      await contractService.downloadAllContractsZip(contractId);
      // Recarregar a lista de contratos após download
      loadContracts();
    } catch (error) {
      console.error('Erro ao baixar ZIP dos contratos:', error);
      alert('Erro ao baixar ZIP dos contratos. Tente novamente.');
    } finally {
      setIsDownloadingContracts(false);
    }
  };

  const isCarteiraJobReadyForPoll = (
    ultimo: {
      found?: boolean;
      jobId?: string | null;
      jobStatus?: string | null;
      createdAt?: string | null;
    } | null,
    opts?: { startedJobId?: string | null; pollStartedAtMs?: number }
  ): boolean => {
    if (!ultimo?.found) return false;
    const terminal =
      ultimo.jobStatus === 'completed' || ultimo.jobStatus === 'failed';
    if (!terminal) return false;
    if (opts?.startedJobId) {
      return ultimo.jobId === opts.startedJobId;
    }
    // Só aceitar job deste disparo (evita job completed antigo com cache — UI ficava em 63).
    if (opts?.pollStartedAtMs != null && ultimo.createdAt) {
      const jobMs = new Date(ultimo.createdAt).getTime();
      if (jobMs < opts.pollStartedAtMs - 3000) return false;
    }
    return true;
  };

  const showCarteiraAutomacaoFeedback = useCallback(
    (ultimo: {
      usuarioMensagemAlerta?: string | null;
      bloqueado?: boolean;
      bloqueadoCooldown?: boolean;
      resultado?: string | null;
      statusChanged?: boolean;
      mensagemEnviada?: boolean;
      whatsappJaEnviado?: boolean;
      whatsappMensagemAlerta?: string | null;
      error?: string | null;
      jobStatus?: string | null;
    } | null) => {
      if (ultimo?.bloqueadoCooldown) {
        const cooldownMsg =
          ultimo?.usuarioMensagemAlerta ||
          ultimo?.error ||
          'Troca de carteira não permitida: solicitação respondida recentemente.';
        showError(cooldownMsg, { autoClose: 12000 });
        return;
      }
      const msg =
        ultimo?.usuarioMensagemAlerta ||
        ultimo?.whatsappMensagemAlerta ||
        ultimo?.error ||
        null;
      const resultado = (ultimo?.resultado || '').toLowerCase();
      const whatsappEnviadoAgora = Boolean(ultimo?.mensagemEnviada);
      const whatsappAguardando = Boolean(ultimo?.whatsappJaEnviado && !whatsappEnviadoAgora);

      if (!msg) {
        if (ultimo?.jobStatus === 'failed') {
          showError('Falha na verificação de carteira no VTME.');
        } else if (resultado === 'outra_filial' && ultimo?.statusChanged && whatsappEnviadoAgora) {
          showWarning('Status alterado para Troca de Carteira Solicitada. WhatsApp enviado.', {
            autoClose: 12000,
          });
        } else if (resultado === 'outra_filial' && ultimo?.statusChanged) {
          showError(
            'Status alterado, mas o WhatsApp não foi confirmado. Verifique a Central/Z-API.',
            { autoClose: 12000 }
          );
        }
        return;
      }

      const toastOpts = { autoClose: 12000 as const };

      if (
        resultado === 'nossa_carteira' ||
        resultado === 'nao_encontrado' ||
        resultado === 'indeterminado' ||
        (ultimo?.bloqueado && resultado !== 'outra_filial')
      ) {
        showSuccess(msg, toastOpts);
        return;
      }

      if (resultado === 'outra_filial' && (whatsappEnviadoAgora || whatsappAguardando)) {
        showWarning(msg, toastOpts);
        return;
      }

      if (resultado === 'outra_filial' && ultimo?.statusChanged) {
        showError(msg, toastOpts);
        return;
      }

      if (ultimo?.jobStatus === 'failed' || ultimo?.error) {
        showError(msg, toastOpts);
        return;
      }

      showWarning(msg, { autoClose: 10000 });
    },
    [showWarning, showSuccess, showError]
  );

  const resolveGroupIdForCarteira = useCallback(
    (contractId: string): string | null | undefined => {
      const fromList = contracts?.items?.find((c: any) => c.id === contractId);
      if (fromList?.groupId) return fromList.groupId as string;
      if (
        selectedContractInfo?.id === contractId &&
        (selectedContractInfo as { groupId?: string })?.groupId
      ) {
        return (selectedContractInfo as { groupId?: string }).groupId;
      }
      return null;
    },
    [contracts?.items, selectedContractInfo]
  );

  const pollCarteiraAutomacaoUntilDone = useCallback(
    async (
      contractId: string,
      opts?: {
        startedJobId?: string | null;
        pollStartedAtMs?: number;
        groupId?: string | null;
        pollRunId?: number;
        signal?: AbortSignal;
      }
    ) => {
      const groupId = opts?.groupId ?? resolveGroupIdForCarteira(contractId);
      const fastPoll = opts?.pollStartedAtMs != null;
      const pollTimeoutMs = fastPoll ? CARTEIRA_AUTOMACAO_POLL_TIMEOUT_MS : 75_000;
      const deadlineMs = (opts?.pollStartedAtMs ?? Date.now()) + pollTimeoutMs;
      const maxAttempts = fastPoll
        ? CARTEIRA_AUTOMACAO_POLL_MAX_ATTEMPTS_SAFETY
        : 25;
      const isActive = () =>
        opts?.pollRunId == null || isCarteiraPollRunActive(opts.pollRunId);

      let attempt = 0;
      while (attempt < maxAttempts && Date.now() < deadlineMs) {
        if (!isActive() || opts?.signal?.aborted) {
          return null;
        }
        const ultimo = await contractService.getCarteiraAutomacaoUltimoResultado(
          contractId,
          groupId,
          {
            fresh: carteiraAutomacaoPollUseFresh(attempt),
            signal: opts?.signal,
          }
        );
        if (isCarteiraJobReadyForPoll(ultimo, opts)) {
          return ultimo;
        }
        attempt += 1;
        const remainingMs = deadlineMs - Date.now();
        if (remainingMs <= 0 || attempt >= maxAttempts) {
          break;
        }
        const waitMs = Math.min(
          carteiraAutomacaoPollIntervalMs(attempt, fastPoll),
          remainingMs
        );
        await new Promise((resolve) => setTimeout(resolve, waitMs));
      }

      if (!isActive() || opts?.signal?.aborted) {
        return null;
      }
      return contractService.getCarteiraAutomacaoUltimoResultado(contractId, groupId, {
        fresh: true,
        signal: opts?.signal,
      });
    },
    [resolveGroupIdForCarteira, isCarteiraPollRunActive]
  );

  /** Troca manual bloqueada pela ATC (cliente na nossa carteira) — não aplicar fallback. */
  const isTrocaCarteiraBloqueadaPelaAutomacao = (
    ultimo: {
      resultado?: string | null;
      bloqueado?: boolean;
    } | null
  ): boolean =>
    ultimo?.resultado === 'nossa_carteira' ||
    Boolean(ultimo?.bloqueado && ultimo?.resultado === 'nossa_carteira');

  /** Verificação ATC concluída com sucesso (status já alterado ou WhatsApp enviado). */
  const isTrocaCarteiraAutomacaoSucesso = (
    ultimo: {
      resultado?: string | null;
      statusChanged?: boolean;
      mensagemEnviada?: boolean;
      whatsappJaEnviado?: boolean;
    } | null
  ): boolean =>
    Boolean(
      ultimo?.mensagemEnviada ||
        ultimo?.whatsappJaEnviado ||
        (ultimo?.resultado === 'outra_filial' &&
          ultimo?.statusChanged &&
          Boolean(ultimo?.mensagemEnviada || ultimo?.whatsappJaEnviado))
    );

  const resolveContractIdsForGroupUi = useCallback(
    (contractId: string, groupId?: string | null): string[] => {
      if (!groupId) return [contractId];
      const fromList =
        contracts?.items
          ?.filter((c: ContractListDto) => c.groupId === groupId)
          .map((c: ContractListDto) => c.id) ?? [];
      if (fromList.length === 0) return [contractId];
      return fromList.includes(contractId) ? fromList : [...fromList, contractId];
    },
    [contracts?.items]
  );

  const fetchContractIdsForCarteiraUi = useCallback(
    async (contractId: string, groupId?: string | null): Promise<string[]> => {
      if (!groupId) return [contractId];
      try {
        const fromApi = await contractService.getContractsByGroupId(groupId);
        if (fromApi.length > 0) {
          const ids = fromApi.map((c) => c.id);
          return ids.includes(contractId) ? ids : [...ids, contractId];
        }
      } catch {
        /* lista local */
      }
      return resolveContractIdsForGroupUi(contractId, groupId);
    },
    [resolveContractIdsForGroupUi]
  );

  const syncContractStatusAfterCarteiraAutomacao = useCallback(
    async (contractId: string, groupId?: string | null) => {
      const ids = await fetchContractIdsForCarteiraUi(contractId, groupId);
      patchContractsStatus(ids, EContractStatus.VerificandoCarteira);
      setSelectedContractInfo((prev: any) => {
        if (!prev) return prev;
        const inScope =
          prev.id === contractId ||
          (groupId && (prev as { groupId?: string }).groupId === groupId);
        if (!inScope) return prev;
        return { ...prev, status: EContractStatus.VerificandoCarteira };
      });

      await loadContracts(currentPage);
      try {
        if (groupId) {
          const groupRows = await contractService.getContractsByGroupId(groupId);
          const aggregateStatus = (() => {
            if (groupRows.some((c) => normalizeContractStatus(c.status) === EContractStatus.VerificandoCarteira)) {
              return EContractStatus.VerificandoCarteira;
            }
            if (groupRows.some((c) => normalizeContractStatus(c.status) === EContractStatus.Assinado)) {
              return EContractStatus.Assinado;
            }
            const first = groupRows[0];
            return first ? normalizeContractStatus(first.status) ?? first.status : undefined;
          })();
          setSelectedContractInfo((prev: any) => {
            if (!prev) return prev;
            const inScope =
              prev.id === contractId ||
              (prev as { groupId?: string }).groupId === groupId;
            return inScope
              ? { ...prev, status: aggregateStatus, groupContracts: groupRows }
              : prev;
          });
        } else {
          const fresh = await contractService.getContractById(contractId);
          const st = normalizeContractStatus(fresh.status);
          setSelectedContractInfo((prev: any) =>
            prev?.id === contractId ? { ...prev, status: st } : prev
          );
        }
      } catch {
        /* best-effort */
      }
    },
    [loadContracts, currentPage, patchContractsStatus, fetchContractIdsForCarteiraUi]
  );

  /** Após poll/ATC: status real na API → lista + modal (evita UI presa em 63). */
  const refreshCarteiraStatusNaUiFromApi = useCallback(
    async (contractId: string, groupId?: string | null) => {
      try {
        if (groupId) {
          const groupRows = await contractService.getContractsByGroupId(groupId);
          for (const row of groupRows) {
            const st = normalizeContractStatus(row.status);
            if (st != null) {
              patchContractsStatus([row.id], st as EContractStatus);
            }
          }
          const aggregateStatus = (() => {
            if (
              groupRows.some(
                (c) => normalizeContractStatus(c.status) === EContractStatus.VerificandoCarteira
              )
            ) {
              return EContractStatus.VerificandoCarteira;
            }
            if (
              groupRows.some(
                (c) => normalizeContractStatus(c.status) === EContractStatus.TrocaDeCarteira
              )
            ) {
              return EContractStatus.TrocaDeCarteira;
            }
            if (
              groupRows.some(
                (c) => normalizeContractStatus(c.status) === EContractStatus.Assinado
              )
            ) {
              return EContractStatus.Assinado;
            }
            const first = groupRows[0];
            return first ? normalizeContractStatus(first.status) ?? first.status : undefined;
          })();
          setSelectedContractInfo((prev: any) => {
            if (!prev) return prev;
            const inScope =
              prev.id === contractId ||
              (prev as { groupId?: string }).groupId === groupId;
            return inScope
              ? { ...prev, status: aggregateStatus, groupContracts: groupRows }
              : prev;
          });
        } else {
          const fresh = await contractService.getContractById(contractId);
          const st = normalizeContractStatus(fresh.status);
          if (st != null) {
            patchContractsStatus([contractId], st as EContractStatus);
          }
          setSelectedContractInfo((prev: any) =>
            prev?.id === contractId ? { ...prev, ...fresh, status: st ?? fresh.status } : prev
          );
        }
      } catch {
        /* best-effort: loadContracts abaixo */
      }
      await loadContracts(currentPage);
    },
    [loadContracts, currentPage, patchContractsStatus]
  );

  const resolveContractIdsForCarteiraStatus = useCallback(
    async (contractId: string, groupId?: string | null): Promise<string[]> => {
      if (!groupId) return [contractId];
      try {
        const fromApi = await contractService.getContractsByGroupId(groupId);
        if (fromApi.length > 0) return fromApi.map((c) => c.id);
      } catch {
        /* lista local */
      }
      return resolveContractIdsForGroupUi(contractId, groupId);
    },
    [resolveContractIdsForGroupUi]
  );

  const contractAindaVerificandoCarteira = useCallback(
    async (contractId: string, groupId?: string | null): Promise<boolean> => {
      try {
        if (groupId) {
          const groupRows = await contractService.getContractsByGroupId(groupId);
          if (
            groupRows.some(
              (c) => normalizeContractStatus(c.status) === EContractStatus.VerificandoCarteira
            )
          ) {
            return true;
          }
        }
        const fresh = await contractService.getContractById(contractId);
        return normalizeContractStatus(fresh.status) === EContractStatus.VerificandoCarteira;
      } catch {
        return false;
      }
    },
    []
  );

  const applyCarteiraStatusAposErroVerificacao = useCallback(
    async (
      contractId: string,
      groupId: string | null | undefined,
      statusAntes: number | undefined,
      erro: string,
      options?: {
        statusEsperado?: EContractStatus;
        changeReasonOverride?: string;
      }
    ): Promise<boolean> => {
      const aindaEmVerificacao = await contractAindaVerificandoCarteira(contractId, groupId);
      if (!aindaEmVerificacao) {
        await loadContracts(currentPage);
        return true;
      }
      const target = resolveStatusAlvoPosErroVerificacao(
        statusAntes,
        options?.statusEsperado
      ) as EContractStatus;
      const changeReason =
        options?.changeReasonOverride?.trim() ||
        (options?.statusEsperado != null
          ? CARTEIRA_ATC_ERRO_CONEXAO_MSG
          : buildChangeReasonPosErroVerificacao(erro, target));
      try {
        const contractIds = await resolveContractIdsForCarteiraStatus(contractId, groupId);
        for (const id of contractIds) {
          await updateContractStatusWithCacheInvalidation(id, target, {
            changeReason,
            updatedBy: CARTEIRA_AUTOMACAO_UPDATED_BY,
          }, groupId);
        }
        patchContractsStatus(contractIds, target);
        await loadContracts(currentPage);
        return true;
      } catch (error) {
        console.error('Erro ao corrigir status após falha na verificação de carteira:', error);
        showError('Não foi possível atualizar o status do contrato após a falha na verificação.');
        return false;
      }
    },
    [
      resolveContractIdsForCarteiraStatus,
      contractAindaVerificandoCarteira,
      loadContracts,
      currentPage,
      patchContractsStatus,
      showError,
    ]
  );

  const finalizarPollVerificacaoCarteira = useCallback(
    async (
      contractId: string,
      groupId: string | null | undefined,
      statusAntesVerificacao: number | undefined,
      pollStartedAtMs: number,
      startedJobId: string | null | undefined,
      options?: {
        statusEsperado?: EContractStatus;
        pollRunId?: number;
        signal?: AbortSignal;
      }
    ): Promise<boolean> => {
      const resolvedGroupId = groupId ?? resolveGroupIdForCarteira(contractId);
      const automationStarted = Boolean(startedJobId);
      const statusEsperado = options?.statusEsperado;
      const pollActive = () =>
        options?.pollRunId == null || isCarteiraPollRunActive(options.pollRunId);

      const ultimo = await pollCarteiraAutomacaoUntilDone(contractId, {
        startedJobId: startedJobId ?? undefined,
        pollStartedAtMs,
        groupId: resolvedGroupId,
        pollRunId: options?.pollRunId,
        signal: options?.signal,
      });

      if (!pollActive() || options?.signal?.aborted) {
        return false;
      }

      if (isTrocaCarteiraBloqueadaPelaAutomacao(ultimo) || ultimo?.bloqueadoCooldown) {
        showCarteiraAutomacaoFeedback(ultimo);
        await refreshCarteiraStatusNaUiFromApi(contractId, resolvedGroupId);
        return false;
      }

      if (isTrocaCarteiraAutomacaoSucesso(ultimo)) {
        showCarteiraAutomacaoFeedback(ultimo);
        await refreshCarteiraStatusNaUiFromApi(contractId, resolvedGroupId);
        return true;
      }

      if (carteiraVerificacaoFalhouOuExpirou(ultimo, automationStarted)) {
        const vtmeAindaProcessando =
          isCarteiraVerificacaoTimeoutComJobEmAndamento(ultimo);
        const erroMsg = resolveCarteiraVerificacaoErroMensagem(ultimo);
        await applyCarteiraStatusAposErroVerificacao(
          contractId,
          resolvedGroupId,
          statusAntesVerificacao,
          erroMsg,
          {
            statusEsperado,
            changeReasonOverride: vtmeAindaProcessando
              ? CHANGE_REASON_VTME_AINDA_PROCESSANDO
              : undefined,
          }
        );
        if (vtmeAindaProcessando) {
          showWarning(
            buildCarteiraVerificacaoTimeoutEmAndamentoToast(statusEsperado),
            { autoClose: 16000 }
          );
        } else {
          showError(
            statusEsperado != null
              ? `${CARTEIRA_ATC_ERRO_CONEXAO_MSG} O status do contrato foi atualizado conforme solicitado.`
              : `${erroMsg} O status do contrato foi restaurado.`,
            { autoClose: 14000 }
          );
        }
        await refreshCarteiraStatusNaUiFromApi(contractId, resolvedGroupId);
        return false;
      }

      showCarteiraAutomacaoFeedback(ultimo);
      await refreshCarteiraStatusNaUiFromApi(contractId, resolvedGroupId);
      return !ultimo?.bloqueado;
    },
    [
      showError,
      showWarning,
      pollCarteiraAutomacaoUntilDone,
      showCarteiraAutomacaoFeedback,
      currentPage,
      resolveGroupIdForCarteira,
      applyCarteiraStatusAposErroVerificacao,
      refreshCarteiraStatusNaUiFromApi,
      isCarteiraPollRunActive,
    ]
  );

  const processarCarteiraVerificacaoIniciada = useCallback(
    async (
      contractId: string,
      groupId: string | null | undefined,
      carteiraVerificacao:
        | { iniciada?: boolean; jobId?: string | null; erro?: string | null }
        | null
        | undefined,
      statusAntesVerificacao: number | undefined,
      options?: { statusEsperado?: EContractStatus }
    ): Promise<void> => {
      const resolvedGroupIdEarly = groupId ?? resolveGroupIdForCarteira(contractId);

      if (!carteiraVerificacao) {
        await refreshCarteiraStatusNaUiFromApi(contractId, resolvedGroupIdEarly);
        return;
      }

      if (carteiraVerificacao.iniciada === false) {
        showError(
          carteiraVerificacao.erro || CARTEIRA_ATC_ERRO_CONEXAO_MSG,
          { autoClose: 14000 }
        );
        await refreshCarteiraStatusNaUiFromApi(contractId, resolvedGroupIdEarly);
        return;
      }

      if (!carteiraVerificacao.iniciada || !carteiraVerificacao.jobId) return;

      const resolvedGroupId = groupId ?? resolveGroupIdForCarteira(contractId);
      await syncContractStatusAfterCarteiraAutomacao(contractId, resolvedGroupId);
      showInfo('Verificando carteira no VTME...', { autoClose: 8000 });
      const { runId: pollRunId, signal } = beginCarteiraPollRun();
      await finalizarPollVerificacaoCarteira(
        contractId,
        resolvedGroupId,
        statusAntesVerificacao,
        Date.now(),
        carteiraVerificacao.jobId,
        {
          statusEsperado: options?.statusEsperado,
          pollRunId,
          signal,
        }
      );
    },
    [
      showError,
      showInfo,
      currentPage,
      resolveGroupIdForCarteira,
      syncContractStatusAfterCarteiraAutomacao,
      finalizarPollVerificacaoCarteira,
      refreshCarteiraStatusNaUiFromApi,
      beginCarteiraPollRun,
    ]
  );

  const contratoPendenteAssinatura = (status: number | string | null | undefined): boolean => {
    const st = normalizeContractStatus(status);
    return st === EContractStatus.Criado || st === EContractStatus.NaoAssinado;
  };

  const marcarContratosAssinadoComVerificacaoCarteira = useCallback(
    async (
      members: Array<{ id: string; status?: number | string | null }>,
      options: {
        anchorContractId?: string;
        groupId?: string | null;
      }
    ): Promise<void> => {
      if (members.length === 0) return;

      const sorted = sortContractsForVtmeGroupOrder(members);
      const anchorId =
        options.anchorContractId && sorted.some((c) => c.id === options.anchorContractId)
          ? options.anchorContractId
          : sorted[0].id;
      const anchor = sorted.find((c) => c.id === anchorId) ?? sorted[0];
      const others = sorted.filter((c) => c.id !== anchorId);

      for (const c of others) {
        if (contratoPendenteAssinatura(c.status)) {
          await updateContractStatusWithCacheInvalidation(c.id, EContractStatus.Assinado, {
            suppressTrocaCarteiraAutomation: true,
          }, options.groupId ?? null);
        }
      }

      const statusAntes =
        normalizeContractStatus(anchor.status) ?? EContractStatus.Criado;
      const statusResult = await updateContractStatusWithCacheInvalidation(
        anchorId,
        EContractStatus.Assinado,
        undefined,
        options.groupId ?? null
      );
      await processarCarteiraVerificacaoIniciada(
        anchorId,
        options.groupId ?? undefined,
        statusResult.carteiraVerificacao,
        statusAntes,
        { statusEsperado: EContractStatus.Assinado }
      );
      await refreshCarteiraStatusNaUiFromApi(anchorId, options.groupId ?? undefined);
    },
    [processarCarteiraVerificacaoIniciada, refreshCarteiraStatusNaUiFromApi]
  );

  const handleTrocaCarteiraComVerificacao = useCallback(
    async (contractId: string, groupId?: string | null): Promise<boolean> => {
      const resolvedGroupId = groupId ?? resolveGroupIdForCarteira(contractId);
      const contractRowAntes = contracts?.items?.find((c: any) => c.id === contractId);
      const statusAntesRaw =
        normalizeContractStatus(contractRowAntes?.status) ??
        (selectedContractInfo?.id === contractId
          ? normalizeContractStatus(
              (selectedContractInfo as { status?: number | string | null }).status
            )
          : null);
      const statusAntesVerificacao =
        statusAntesRaw != null ? (statusAntesRaw as EContractStatus) : undefined;

      try {
        const statusResult = await updateContractStatusWithCacheInvalidation(
          contractId,
          EContractStatus.TrocaDeCarteira,
          undefined,
          resolvedGroupId ?? null
        );
        showSuccess(
          'Solicitação de troca de carteira registrada. O contrato entrou em Verificando Carteira no VTME.'
        );
        await processarCarteiraVerificacaoIniciada(
          contractId,
          resolvedGroupId,
          statusResult.carteiraVerificacao,
          statusAntesVerificacao,
          { statusEsperado: EContractStatus.TrocaDeCarteira }
        );
        return true;
      } catch (error) {
        console.error('Erro ao solicitar troca de carteira com verificação:', error);
        const msg =
          error instanceof Error && error.message
            ? error.message
            : 'Erro ao alterar status para Troca de Carteira';
        showError(msg);
        await loadContracts(currentPage);
        return false;
      }
    },
    [
      showError,
      resolveGroupIdForCarteira,
      processarCarteiraVerificacaoIniciada,
      currentPage,
      contracts?.items,
      selectedContractInfo,
    ]
  );

  // Troca de status na lista: chama PUT /contracts/{id}/status (updateContractStatus).
  // Admin: todos os destinos. Seller/TeamLeader: só Assinado, Não assinado, Cancelado (API).
  // TeamLeader com contrato em VTME Criado pode passar para esses três (regra extra no ContractService).
  const handleChangeStatus = async (contractId: string, newStatus: EContractStatus): Promise<boolean> => {
    const contractRow = contracts?.items?.find((c: any) => c.id === contractId);
    const currentSt = normalizeContractStatus(contractRow?.status);
    if (isCarteiraListaStatusChangeBlocked(currentSt, { isAdmin: hasRole('Admin') })) {
      showWarning(
        'Contrato em verificação ou aguardando retorno de troca de carteira. Use Aprovar ou Recusar CQV na modal do contrato.',
        { autoClose: 12000 }
      );
      return false;
    }

    // Quando muda para Recusado CQV pelo dropdown da tabela, exigimos motivo via modal
    // (mesmo fluxo do botao "Recusar CQV" do modal de detalhes).
    if (newStatus === EContractStatus.RecusadoCQV) {
      setPendingRejectionContractId(contractId);
      setPendingRejectionKind('cqv');
      setShowRejectModal(true);
      return false;
    }

    if (newStatus === EContractStatus.RecusadoQualidade) {
      setPendingRejectionContractId(contractId);
      setPendingRejectionKind('qualidade');
      setShowRejectModal(true);
      return false;
    }

    if (newStatus === EContractStatus.RecusadoCCV) {
      setPendingRejectionContractId(contractId);
      setPendingRejectionKind('ccv');
      setShowRejectModal(true);
      return false;
    }

    if (newStatus === EContractStatus.RecusadoCVP) {
      setPendingRejectionContractId(contractId);
      setPendingRejectionKind('cvp');
      setShowRejectModal(true);
      return false;
    }

    if (newStatus === EContractStatus.TrocaDeCarteiraNaoAutorizada) {
      setPendingRejectionContractId(contractId);
      setPendingRejectionKind('trocaCarteira');
      setShowRejectModal(true);
      return false;
    }

    if (newStatus === EContractStatus.TrocaDeCarteira) {
      const contractForTroca = contracts?.items?.find((c: any) => c.id === contractId);
      const groupIdTroca = contractForTroca?.groupId as string | undefined;
      return handleTrocaCarteiraComVerificacao(contractId, groupIdTroca);
    }

    if (newStatus === EContractStatus.AprovadoCQV) {
      const cnpjColeta = String(contractRow?.clientCnpj || contractRow?.client?.cnpj || '').replace(/\D/g, '');
      if (cnpjColeta.length === 14) {
        try {
          const fluxo = await consultarFluxo(cnpjColeta);
          if (devePerguntarColeta(fluxo)) {
            const enviarMesmoAssim = await new Promise<boolean>((resolver) => {
              setAlertaColeta({ fluxo, resolver });
            });
            if (!enviarMesmoAssim) return false;
          }
        } catch {
          // A consulta fora do ar não segura a aprovação.
        }
      }
    }

    try {
      // Encontrar o contrato na lista para verificar se faz parte de um grupo
      const contract = contracts?.items?.find((c: any) => c.id === contractId);

      let updatedContractIds: string[] = [];

      if (contract?.groupId) {
        let groupContracts: any[];

        if (
          newStatus === EContractStatus.AprovadoCQV ||
          newStatus === EContractStatus.QualidadeOk ||
          newStatus === EContractStatus.AprovadoCCV
        ) {
          // Mesmo fluxo do modal «Confirmar CQV»: todos os IDs do grupo via API + membros da linha pai (evita perder Aditivo).
          // Cada PUT /status → AprovadoCQV dispara na API o e-mail de dados para VTME por contrato.
          let resolved: any[] = [];
          try {
            const fromApi = await contractService.getContractsByGroupId(contract.groupId);
            if (fromApi.length > 0) {
              resolved = [...fromApi];
            }
          } catch (err) {
            console.error('Erro ao buscar contratos do grupo para Aprovado CQV:', err);
          }
          const contractRow = contract as any;
          resolved = mergeContractArraysById(resolved, contractRow.groupContracts || []);
          if (resolved.length === 0) {
            resolved = contracts?.items?.filter((c: any) => c.groupId === contract.groupId) || [];
          }
          if (resolved.length === 0) {
            resolved = [contract];
          }
          groupContracts = sortContractsForVtmeGroupOrder(resolved);
        } else if (newStatus === EContractStatus.Assinado) {
          let resolved: any[] = [];
          try {
            const fromApi = await contractService.getContractsByGroupId(contract.groupId);
            if (fromApi.length > 0) {
              resolved = [...fromApi];
            }
          } catch (err) {
            console.error('Erro ao buscar contratos do grupo para Assinado:', err);
          }
          const contractRow = contract as any;
          resolved = mergeContractArraysById(resolved, contractRow.groupContracts || []);
          if (resolved.length === 0) {
            resolved = contracts?.items?.filter((c: any) => c.groupId === contract.groupId) || [];
          }
          if (resolved.length === 0) {
            resolved = [contract];
          }
          groupContracts = sortContractsForVtmeGroupOrder(resolved);
          await marcarContratosAssinadoComVerificacaoCarteira(groupContracts, {
            anchorContractId: contractId,
            groupId: contract.groupId as string,
          });
        } else {
          groupContracts = contracts?.items?.filter((c: any) => c.groupId === contract.groupId) || [];
        }

        if (newStatus !== EContractStatus.Assinado) {
          for (const groupContract of groupContracts) {
            await updateContractStatusWithCacheInvalidation(
              groupContract.id,
              newStatus,
              undefined,
              contract.groupId as string
            );
          }
        }
        updatedContractIds = groupContracts.map((groupContract) => groupContract.id).filter(Boolean);
        const cqvEmailNote =
          newStatus === EContractStatus.AprovadoCQV
            ? ' O e-mail com os dados para o VTME foi enviado (um por contrato).'
            : '';
        const successMsg =
          newStatus === EContractStatus.Assinado
            ? groupContracts.length > 1
              ? `Status de ${groupContracts.length} contrato(s) do grupo alterado para "${getStatusText(newStatus)}" com sucesso!`
              : `Contrato marcado como "${getStatusText(newStatus)}".`
            : `Status de ${groupContracts.length} contrato(s) do grupo alterado para "${getStatusText(newStatus)}" com sucesso!${cqvEmailNote}`;
        showSuccess(successMsg);
      } else {
        updatedContractIds = [contractId];
        if (newStatus === EContractStatus.Assinado) {
          await marcarContratosAssinadoComVerificacaoCarteira(
            [{ id: contractId, status: contractRow?.status }],
            { anchorContractId: contractId }
          );
        } else {
          const statusResult = await updateContractStatusWithCacheInvalidation(
            contractId,
            newStatus
          );
          void statusResult;
        }
        const cqvEmailNote =
          newStatus === EContractStatus.AprovadoCQV
            ? ' O e-mail com os dados para o VTME foi enviado.'
            : '';
        showSuccess(`Status do contrato alterado para "${getStatusText(newStatus)}" com sucesso!${cqvEmailNote}`);
      }

      const activeStatusFilter = filters.status;
      const refreshListFromApi =
        activeStatusFilter !== undefined &&
        activeStatusFilter !== null &&
        activeStatusFilter !== newStatus;
      const automacaoCarteiraNoStatus = newStatus === EContractStatus.Assinado;

      if (automacaoCarteiraNoStatus && updatedContractIds[0]) {
        const groupIdRefresh =
          (contract as { groupId?: string } | undefined)?.groupId ??
          contracts?.items?.find((c: any) => c.id === contractId)?.groupId;
        await refreshCarteiraStatusNaUiFromApi(
          updatedContractIds[0],
          groupIdRefresh as string | undefined
        );
      } else if (refreshListFromApi) {
        await loadContracts(currentPage);
      } else {
        patchContractStatuses(updatedContractIds, newStatus);
      }
      return true;
    } catch (error) {
      console.error('Erro ao alterar status do contrato:', error);
      const msg =
        error instanceof Error && error.message
          ? error.message
          : 'Erro ao alterar status do contrato';
      showError(msg);
      return false;
    }
  };

  /** Abre confirmação (modal React — `window.confirm` falha ou some com o modal de linhas aberto). */
  const handleTrocaCarteiraFromLinesModal = () => {
    const cid = highlightedContractId ?? selectedContractInfo?.id ?? null;
    if (!cid || !selectedContractInfo || isContractCancelled(selectedContractInfo)) return;
    setShowTrocaCarteiraConfirmModal(true);
  };

  const handleReconsultarCarteiraVtmeFromLinesModal = async () => {
    const cid = highlightedContractId ?? selectedContractInfo?.id ?? null;
    if (!cid || !selectedContractInfo || isContractCancelled(selectedContractInfo)) return;
    if (linesModalStatusNorm !== EContractStatus.TrocaDeCarteiraSolicitada) return;

    setReconsultarCarteiraVtmeLoading(true);
    try {
      const result = await contractService.reconsultarCarteiraVtme(cid);
      if (!result) {
        showError(
          'Não foi possível reconsultar a carteira no VTME. Verifique se a automação está ativa.',
          { autoClose: 14000 }
        );
        return;
      }
      if (result.success) {
        const notify = result.solicitacaoEncerrada ? showSuccess : showInfo;
        notify(result.mensagemUsuario || 'Reconsulta concluída.', { autoClose: 14000 });
        const fresh = await contractService.getContractById(cid);
        await handleShowLinesModal(
          cid,
          { ...selectedContractInfo, ...fresh },
          { softRefresh: true }
        );
        await loadContracts(currentPage);
      } else {
        showWarning(
          result.mensagemUsuario || 'A troca ainda não foi realizada no VTME.',
          { autoClose: 12000 }
        );
      }
    } catch (error) {
      console.error('Erro na reconsulta de carteira VTME:', error);
      showError('Erro ao reconsultar carteira no VTME.');
    } finally {
      setReconsultarCarteiraVtmeLoading(false);
    }
  };

  const handleConfirmTrocaCarteiraFromLinesModal = async () => {
    setShowTrocaCarteiraConfirmModal(false);
    const cid = highlightedContractId ?? selectedContractInfo?.id ?? null;
    if (!cid || !selectedContractInfo || isContractCancelled(selectedContractInfo)) return;

    setTrocaDeCarteiraFromModalLoading(true);
    try {
      const groupId = (selectedContractInfo as { groupId?: string | null })?.groupId;
      const ok = await handleTrocaCarteiraComVerificacao(cid, groupId ?? undefined);
      if (ok) {
        const fresh = await contractService.getContractById(cid);
        const snapshot = { ...selectedContractInfo, ...fresh };
        await handleShowLinesModal(cid, snapshot, { softRefresh: true });
        if (UNITY_TROCA_CARTEIRA_MESSAGE_MODAL_ENABLED) {
          const ultimo = await contractService.getCarteiraAutomacaoUltimoResultado(
            cid,
            resolveGroupIdForCarteira(cid)
          );
          const whatsappPelaAutomacao =
            ultimo?.mensagemEnviada || ultimo?.whatsappJaEnviado;
          if (!whatsappPelaAutomacao && !ultimo?.bloqueado) {
            setUnityTrocaCarteiraMessageBody(buildUnityTrocaCarteiraWhatsappMessage(snapshot));
            setShowUnityTrocaCarteiraMessageModal(true);
          }
        }
      }
    } finally {
      setTrocaDeCarteiraFromModalLoading(false);
    }
  };

  const handleCopyUnityTrocaCarteiraMessage = async () => {
    const text = unityTrocaCarteiraMessageBody.trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      showSuccess('Mensagem copiada. Cole no WhatsApp para a Unity.');
    } catch {
      showError(
        'Não foi possível copiar automaticamente. Selecione o texto abaixo e use Ctrl+C (ou Cmd+C).'
      );
    }
  };

  const handleSaveAdminInline = async () => {
    if (!selectedContractInfo?.id) return;
    if (!linesModalCanInlineEdit) {
      showError('Este contrato não pode ser editado no modal de linhas neste status.');
      return;
    }
    const adminValidation = validateAdminInlineEdit(editingAdminData);
    setEditingAdminFieldErrors(adminValidation);
    if (hasInlineFieldErrors(adminValidation)) {
      showError('Corrija os campos destacados do responsável antes de salvar.');
      return;
    }
    setSavingInlineEdit(true);
    try {
      const admin = selectedContractInfo.administrator || {};
      const nomeCompleto = editingAdminData.nomeCompleto.trim();
      const cpf = cleanCpf(editingAdminData.cpf);
      const telefone1 = cleanPhone(editingAdminData.telefone1);
      const adminPayload = {
        administrator: {
          nomeCompleto,
          cpf,
          telefone1,
          rg: admin.rg ?? '',
          email: admin.email ?? '',
          telefone2: admin.telefone2 ?? undefined,
          dataNascimento: admin.dataNascimento ? new Date(admin.dataNascimento) : undefined,
        },
      };

      if (selectedContractInfo.groupId) {
        const groupContracts = await contractService.getContractsByGroupId(selectedContractInfo.groupId);
        for (const c of groupContracts) {
          await patchContractWithCacheInvalidation(c.id, {
            ...adminPayload,
            updatedAt: c.updatedAt ? new Date(c.updatedAt).toISOString() : undefined,
          }, selectedContractInfo.groupId);
        }
        setSelectedContractInfo((prev: any) => ({
          ...prev,
          administrator: {
            ...prev?.administrator,
            nomeCompleto,
            cpf,
            telefone1,
          },
          updatedAt: new Date().toISOString(),
        }));
        setEditingAdminInModal(false);
        setEditingAdminFieldErrors({});
        showSuccess(
          groupContracts.length > 1
            ? `Responsável pela auditoria atualizado em ${groupContracts.length} contrato(s).`
            : 'Responsável pela auditoria atualizado com sucesso!'
        );
      } else {
        const freshContract = await contractService.getContractById(selectedContractInfo.id);
        await patchContractWithCacheInvalidation(selectedContractInfo.id, {
          ...adminPayload,
          updatedAt: freshContract.updatedAt
            ? new Date(freshContract.updatedAt).toISOString()
            : undefined,
        }, selectedContractInfo.groupId);
        setSelectedContractInfo((prev: any) => ({
          ...prev,
          administrator: {
            ...prev?.administrator,
            nomeCompleto,
            cpf,
            telefone1,
          },
          updatedAt: new Date().toISOString(),
        }));
        setEditingAdminInModal(false);
        setEditingAdminFieldErrors({});
        showSuccess('Responsável pela auditoria atualizado com sucesso!');
      }
    } catch (error: unknown) {
      showError(
        extractApiErrorMessage(error, 'Erro ao salvar alterações do responsável pela auditoria.')
      );
    } finally {
      setSavingInlineEdit(false);
    }
  };

  const handleSaveConsultantInline = async () => {
    if (!selectedContractInfo?.id || !responsibleConsultantEditInModal) return;
    if (!canEditConsultantInLinesModal) {
      showError('Consultor responsável não pode ser alterado neste status do contrato.');
      return;
    }
    const currentUserId = selectedContractInfo.user?.id ?? selectedContractInfo.userId;
    if (
      currentUserId &&
      String(currentUserId).toLowerCase() === responsibleConsultantEditInModal.toLowerCase()
    ) {
      setEditingConsultantInModal(false);
      setResponsibleConsultantEditInModal('');
      return;
    }

    setSavingInlineEdit(true);
    try {
      let contractIds: string[] = [selectedContractInfo.id];
      if (selectedContractInfo.groupId) {
        if (linesModalGroupContracts.length > 0) {
          contractIds = linesModalGroupContracts.map((c: { id: string }) => c.id);
        } else {
          const group = await contractService.getContractsByGroupId(selectedContractInfo.groupId);
          contractIds = group.map((c) => c.id);
        }
      }

      for (const cid of contractIds) {
        await contractService.updateResponsibleConsultant(cid, responsibleConsultantEditInModal);
      }

      const opt = consultantDropdownOptions.find((o) => o.value === responsibleConsultantEditInModal);
      const updatedUser = {
        id: responsibleConsultantEditInModal,
        nome: opt?.name ?? selectedContractInfo.user?.nome ?? '',
        email: selectedContractInfo.user?.email ?? '',
        username: selectedContractInfo.user?.username ?? '',
        usuarioVtme: opt?.usuarioVtme ?? selectedContractInfo.user?.usuarioVtme ?? null,
      };
      const snapshot = {
        ...selectedContractInfo,
        userId: responsibleConsultantEditInModal,
        user: updatedUser,
      };

      setSelectedContractInfo(snapshot);
      setEditingConsultantInModal(false);
      setResponsibleConsultantEditInModal('');
      showSuccess(
        contractIds.length > 1
          ? `Consultor responsável atualizado em ${contractIds.length} contrato(s).`
          : 'Consultor responsável atualizado com sucesso!'
      );
      void loadContracts();
      await handleShowLinesModal(selectedContractInfo.id, snapshot, { softRefresh: true });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      showError(
        err?.response?.data?.message ||
          err?.message ||
          'Erro ao atualizar consultor responsável.',
      );
    } finally {
      setSavingInlineEdit(false);
    }
  };

  const handleSaveRepInline = async () => {
    if (!selectedContractInfo?.id || !editingRepInModal) return;
    if (!linesModalCanInlineEdit) {
      showError('Este contrato não pode ser editado no modal de linhas neste status.');
      return;
    }
    const repValidation = validateRepInlineEdit(editingRepData);
    setEditingRepFieldErrors(repValidation);
    if (hasInlineFieldErrors(repValidation)) {
      showError('Corrija os campos destacados do representante antes de salvar.');
      return;
    }
    setSavingInlineEdit(true);
    try {
      const priorReps: any[] = selectedContractInfo.representatives ?? [];
      const saveResult = await runInlineRepresentativeSave(
        {
          contractId: selectedContractInfo.id,
          groupId: selectedContractInfo.groupId,
          representatives: priorReps,
          editingRepInModal,
          editingRepData,
        },
        {
          fetchPersistedRepresentatives: (id) => contractService.getContractRepresentatives(id),
          getContractById: (id) => contractService.getContractById(id),
          getContractsByGroupId: (groupId) => contractService.getContractsByGroupId(groupId),
          patchContract: (id, data) =>
            patchContractWithCacheInvalidation(id, data, selectedContractInfo.groupId),
        }
      );

      const snapshot = {
        ...selectedContractInfo,
        representatives: saveResult.updatedRepresentatives,
        updatedAt: saveResult.updatedAt,
      };
      setSelectedContractInfo(snapshot);
      setEditingRepInModal(null);
      setEditingRepFieldErrors({});

      if (selectedContractInfo.groupId) {
        const count = saveResult.groupContractCount ?? 1;
        showSuccess(
          count > 1
            ? `Representante legal atualizado em ${count} contrato(s).`
            : 'Representante legal atualizado com sucesso!'
        );
      } else {
        showSuccess('Representante legal atualizado com sucesso!');
      }

      await handleShowLinesModal(selectedContractInfo.id, snapshot, { softRefresh: true });
    } catch (error: unknown) {
      showError(
        extractApiErrorMessage(error, 'Erro ao salvar alterações do representante legal.')
      );
    } finally {
      setSavingInlineEdit(false);
    }
  };

  const handleSaveReceiverInline = async () => {
    if (!selectedContractInfo?.id || !editingReceiverInModal) return;
    if (!linesModalCanInlineEdit) {
      showError('Este contrato não pode ser editado no modal de linhas neste status.');
      return;
    }
    const receiverValidation = validateReceiverInlineEdit(editingReceiverData);
    setEditingReceiverFieldErrors(receiverValidation);
    if (hasInlineFieldErrors(receiverValidation)) {
      showError('Corrija os campos destacados do recebedor antes de salvar.');
      return;
    }
    const nomeReceiver = editingReceiverData.nome.trim();
    const cpfReceiver = cleanCpf(editingReceiverData.cpf);
    const qualificacaoReceiver = editingReceiverData.qualificacao.trim();
    const rgReceiver = editingReceiverData.rg.trim();
    setSavingInlineEdit(true);
    try {
      const updatedReceivers = (selectedContractInfo.authorizedReceivers || []).map((r: any) =>
        r.id === editingReceiverInModal
          ? {
              ...r,
              nome: nomeReceiver,
              cpf: cpfReceiver,
              qualificacao: qualificacaoReceiver,
              rg: rgReceiver,
            }
          : r
      );
      const receiversPayload = updatedReceivers.map((r: any) => ({
        id: r.id,
        nome: r.nome,
        qualificacao: r.qualificacao,
        cpf: r.cpf,
        rg: r.rg,
        email: r.email,
        telefone: r.telefone,
        celular: r.celular,
        nomePai: r.nomePai,
        nomeMae: r.nomeMae,
        naturalidade: r.naturalidade,
      }));

      if (selectedContractInfo.groupId) {
        const groupContracts = await contractService.getContractsByGroupId(selectedContractInfo.groupId);
        for (const c of groupContracts) {
          await contractService.patchContract(c.id, {
            updatedAt: c.updatedAt ? new Date(c.updatedAt).toISOString() : undefined,
            authorizedReceivers: receiversPayload,
          });
        }
        setSelectedContractInfo((prev: any) => ({
          ...prev,
          authorizedReceivers: updatedReceivers,
          updatedAt: new Date().toISOString(),
        }));
        setEditingReceiverInModal(null);
        setEditingReceiverFieldErrors({});
        showSuccess(
          groupContracts.length > 1
            ? `Recebedor autorizado atualizado em ${groupContracts.length} contrato(s).`
            : 'Recebedor autorizado atualizado com sucesso!'
        );
      } else {
        await contractService.patchContract(selectedContractInfo.id, {
          updatedAt: selectedContractInfo.updatedAt
            ? new Date(selectedContractInfo.updatedAt).toISOString()
            : undefined,
          authorizedReceivers: receiversPayload,
        });
        setSelectedContractInfo((prev: any) => ({
          ...prev,
          authorizedReceivers: updatedReceivers,
          updatedAt: new Date().toISOString(),
        }));
        setEditingReceiverInModal(null);
        setEditingReceiverFieldErrors({});
        showSuccess('Recebedor autorizado atualizado com sucesso!');
      }
    } catch (error: unknown) {
      showError(
        extractApiErrorMessage(error, 'Erro ao salvar alterações do recebedor autorizado.')
      );
    } finally {
      setSavingInlineEdit(false);
    }
  };

  const handleStartEditDeliveryAddress = () => {
    if (!linesModalCanInlineEdit) {
      showError('Este contrato não pode ser editado no modal de linhas neste status.');
      return;
    }
    if (!selectedContractInfo?.deliveryAddress) return;
    const da = selectedContractInfo.deliveryAddress;
    setEditingDeliveryAddressData({
      cep: da.cep || '',
      estado: da.estado || '',
      cidade: da.cidade || '',
      endereco: da.endereco || '',
      numero: coerceDeliveryAddressStreetNumber(da.numero),
      bairro: da.bairro || '',
      complemento: da.complemento || '',
      referencia: da.referencia || '',
    });
    setEditingDeliveryAddressFieldErrors({});
    setEditingDeliveryAddress(true);
  };

  const handleCancelEditDeliveryAddress = () => {
    setEditingDeliveryAddress(false);
    setEditingDeliveryAddressFieldErrors({});
  };

  const handleSaveDeliveryAddressInline = async () => {
    if (!selectedContractInfo?.id) return;
    if (!linesModalCanInlineEdit) {
      showError('Este contrato não pode ser editado no modal de linhas neste status.');
      return;
    }
    const data = editingDeliveryAddressData;
    const addressValidation = validateDeliveryAddressInlineEdit(data);
    setEditingDeliveryAddressFieldErrors(addressValidation);
    if (hasInlineFieldErrors(addressValidation)) {
      showError('Corrija os campos destacados do endereço antes de salvar.');
      return;
    }
    setSavingInlineEdit(true);
    try {
      const payload = {
        cep: data.cep,
        estado: data.estado,
        cidade: data.cidade,
        endereco: normalizeDeliveryAddressStreetNameForApi(data.endereco),
        numero: normalizeDeliveryAddressStreetNumberForApi(data.numero),
        bairro: data.bairro,
        complemento: data.complemento || undefined,
        referencia: data.referencia || undefined,
      };

      if (selectedContractInfo.groupId) {
        const groupContracts = await contractService.getContractsByGroupId(selectedContractInfo.groupId);
        for (const c of groupContracts) {
          await patchContractWithCacheInvalidation(c.id, {
            updatedAt: c.updatedAt ? new Date(c.updatedAt).toISOString() : undefined,
            deliveryAddress: payload,
          }, selectedContractInfo.groupId);
        }
        setSelectedContractInfo((prev: any) => ({
          ...prev,
          deliveryAddress: { ...prev?.deliveryAddress, ...payload },
          updatedAt: new Date().toISOString(),
        }));
        setEditingDeliveryAddress(false);
        setEditingDeliveryAddressFieldErrors({});
        showSuccess(
          groupContracts.length > 1
            ? `Endereço de entrega atualizado em ${groupContracts.length} contrato(s).`
            : 'Endereço de entrega atualizado com sucesso!'
        );
      } else {
        const freshContract = await contractService.getContractById(selectedContractInfo.id);
        await patchContractWithCacheInvalidation(selectedContractInfo.id, {
          updatedAt: freshContract.updatedAt
            ? new Date(freshContract.updatedAt).toISOString()
            : undefined,
          deliveryAddress: payload,
        }, selectedContractInfo.groupId);
        setSelectedContractInfo((prev: any) => ({
          ...prev,
          deliveryAddress: { ...prev?.deliveryAddress, ...payload },
          updatedAt: new Date().toISOString(),
        }));
        setEditingDeliveryAddress(false);
        setEditingDeliveryAddressFieldErrors({});
        showSuccess('Endereço de entrega atualizado com sucesso!');
      }
    } catch (error: unknown) {
      showError(extractApiErrorMessage(error, 'Erro ao salvar endereço de entrega.'));
    } finally {
      setSavingInlineEdit(false);
    }
  };

  /**
   * Busca o pedido de CQV mais recente do contrato aberto (T12) — decide se mostra
   * "Solicitar CQV" ou "Reenviar CQV" com o motivo da recusa anterior.
   * Falha silenciosa (loga só) — não deve travar a abertura do modal por causa disso.
   */
  useEffect(() => {
    const contractId = selectedContractInfo?.id;
    if (!contractId) {
      setLatestCqvRequest(null);
      return;
    }
    let cancelled = false;
    cqvRequestService
      .getHistory(contractId)
      .then((history) => {
        if (!cancelled) setLatestCqvRequest(history[0] ?? null);
      })
      .catch((err) => {
        console.error('Erro ao buscar histórico de solicitações de CQV:', err);
        if (!cancelled) setLatestCqvRequest(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedContractInfo?.id]);

  /**
   * Solicitação automática de CQV com SLA de 10min — substitui o roteiro copiado/colado
   * manualmente no WhatsApp. Envia ao grupo de CQV via Z-API (backend), inicia o relógio
   * de SLA, e grava ocorrência no histórico do contrato/cliente.
   * Ver specs/solicitacao-cqv-automatica-sla/about.md.
   */
  const handleRequestCqv = async (resendOfId?: string) => {
    if (!selectedContractInfo?.id) return;
    if (isContractCancelled(selectedContractInfo)) {
      showError('Não é possível solicitar CQV de contratos cancelados');
      return;
    }

    setRequestingCqv(true);
    try {
      const result = await cqvRequestService.requestCqv(selectedContractInfo.id, resendOfId, cqvRequestMode);
      setLatestCqvRequest(result);
      const slaMinutes = result.mode === ECqvRequestMode.WhatsApp ? 30 : 10;
      if (cqvRequestReachedGroup(result)) {
        showSuccess(
          resendOfId
            ? `CQV reenviada ao grupo (${CQV_REQUEST_MODE_LABELS[result.mode]}). SLA de ${slaMinutes} minutos reiniciado.`
            : `Solicitação de CQV enviada ao grupo (${CQV_REQUEST_MODE_LABELS[result.mode]}). SLA de ${slaMinutes} minutos iniciado.`
        );
      } else {
        const channelErrors = [result.whatsAppError, result.telegramError]
          .filter((error): error is string => Boolean(error && !/desligado/i.test(error)));
        showError(
          `Pedido de CQV registrado, mas o envio ao grupo CQV falhou: ${channelErrors.join(' / ') || 'erro desconhecido'}. Avise a supervisão.`
        );
      }
    } catch (err: any) {
      console.error('Erro ao solicitar CQV:', err);
      const backendMessage = err?.response?.data?.error;
      showError(
        typeof backendMessage === 'string' && backendMessage.trim()
          ? backendMessage
          : 'Não foi possível registrar a solicitação de CQV. Tente novamente.'
      );
    } finally {
      setRequestingCqv(false);
    }
  };

  // Handler para confirmar CQV (Aprovado)
  const handleConfirmCQV = () => {
    if (isContractCancelled(selectedContractInfo)) {
      showError('Não é possível fazer CQV de contratos cancelados');
      return;
    }
    setShowConfirmCQVModal(true);
  };

  const applyCqvEmTrocaSolicitadaResult = async (
    result: Awaited<ReturnType<typeof contractService.aprovarCqvEmTrocaSolicitada>> | null,
    contractId: string,
    mode: 'aprovado' | 'recusado'
  ) => {
    if (!result) {
      showError('Erro ao processar CQV em Troca de Carteira Solicitada');
      return;
    }
    if (result.success) {
      const finalStatus =
        result.finalStatus != null
          ? result.finalStatus
          : EContractStatus.TrocaDeCarteiraSolicitada;
      setSelectedContractInfo((prev: any) =>
        prev ? { ...prev, status: finalStatus } : prev
      );
      const ids = result.contractsUpdated?.length
        ? result.contractsUpdated
        : [contractId];
      patchContractStatuses(ids, finalStatus);
      showSuccess(
        result.message ||
          (mode === 'aprovado'
            ? 'CQV aprovado; contrato permanece aguardando resposta WhatsApp.'
            : 'CQV recusado; contrato permanece aguardando resposta WhatsApp.')
      );
      await loadContracts(currentPage);
      return;
    }
    if (result.errorCode === 'atc_sync_failed') {
      try {
        const sync = await contractService.sincronizarAtcCqvEmTrocaSolicitada(contractId);
        if (sync?.success) {
          setSelectedContractInfo((prev: any) =>
            prev
              ? { ...prev, status: EContractStatus.TrocaDeCarteiraSolicitada }
              : prev
          );
          showSuccess(sync.message || 'Sincronização com a automação concluída.');
          await loadContracts(currentPage);
          return;
        }
      } catch {
        // fallback silencioso; erro original abaixo
      }
    }
    showError(result.message || 'Erro ao processar CQV em Troca de Carteira Solicitada');
  };

  const handleConfirmCQVConfirm = async () => {
    setShowConfirmCQVModal(false);
    setConfirmingCQV(true);

    const contractIdForTroca =
      highlightedContractId ?? selectedContractInfo?.id ?? null;
    const isTrocaSolicitadaCqv = grupoTemMembroEmTrocaSolicitada;

    try {
      if (isTrocaSolicitadaCqv && contractIdForTroca) {
        const result = await contractService.aprovarCqvEmTrocaSolicitada(contractIdForTroca);
        await applyCqvEmTrocaSolicitadaResult(result, contractIdForTroca, 'aprovado');
        return;
      }

      let contractsToProcess: any[] = [];

      // Mesmo critério de handleChangeStatus(AprovadoCQV): qualquer contrato com groupId dispara PUT em todos os membros (e e-mail na API por contrato).
      if (selectedContractInfo?.groupId) {
        let resolved: any[] = [];
        try {
          const fromApi = await contractService.getContractsByGroupId(selectedContractInfo.groupId);
          if (fromApi.length > 0) {
            resolved = [...fromApi];
          }
        } catch (err) {
          console.error('Erro ao buscar contratos do grupo para Confirmar CQV (modal):', err);
        }
        resolved = mergeContractArraysById(resolved, selectedContractInfo.groupContracts || []);
        if (resolved.length === 0) {
          resolved = contracts?.items?.filter((c: any) => c.groupId === selectedContractInfo.groupId) || [];
        }
        if (resolved.length === 0 && highlightedContractId) {
          resolved = [{ id: highlightedContractId, user: selectedContractInfo?.user }];
        }
        contractsToProcess = sortContractsForVtmeGroupOrder(resolved);
      } else {
        contractsToProcess = [{ id: highlightedContractId!, user: selectedContractInfo?.user }];
      }

      // Cada PUT /status → AprovadoCQV dispara na API SendVtmeOperationalEmailAsync (dados para VTME). RPA/webhook ao confirmar «VTME Criado».
      for (const contract of contractsToProcess) {
        await updateContractStatusWithCacheInvalidation(
          contract.id,
          EContractStatus.AprovadoCQV,
          undefined,
          selectedContractInfo?.groupId
        );
      }

      setSelectedContractInfo((prev: any) =>
        prev ? { ...prev, status: EContractStatus.AprovadoCQV } : prev
      );
      patchContractStatuses(contractsToProcess.map((contract) => contract.id).filter(Boolean), EContractStatus.AprovadoCQV);

      showSuccess(
        `Contrato${contractsToProcess.length > 1 ? 's' : ''} aprovado${contractsToProcess.length > 1 ? 's' : ''} na CQV. O e-mail com os dados para o VTME foi enviado. Quando o pedido estiver criado na VTME, use «VTME Criado» para atualizar o status e acionar o processamento.`
      );
    } catch (error) {
      console.error('Erro ao aprovar contrato CQV:', error);
      showError('Erro ao aprovar contrato CQV');
    } finally {
      setConfirmingCQV(false);
    }
  };

  const refreshLinesModalAfterQualityCcv = async (
    processedIds: string[],
    nextStatus: EContractStatus
  ) => {
    const idSet = new Set(processedIds.filter(Boolean));
    const patchMembers = (members: any[]) =>
      members.map((c) => (c?.id && idSet.has(c.id) ? { ...c, status: nextStatus } : c));

    setLinesModalGroupContracts((prev) => (prev.length > 0 ? patchMembers(prev) : prev));
    setSelectedContractInfo((prev: any) => {
      if (!prev) return prev;
      const openedProcessed = Boolean(prev.id && idSet.has(prev.id));
      return {
        ...prev,
        status: openedProcessed ? nextStatus : prev.status,
        groupContracts: Array.isArray(prev.groupContracts)
          ? patchMembers(prev.groupContracts)
          : prev.groupContracts,
      };
    });
    patchContractStatuses(Array.from(idSet), nextStatus);
    await reloadLineDetailsClientObservations();
    const openedId = highlightedContractId ?? selectedContractInfo?.id;
    if (!openedId || !idSet.has(openedId)) return;
    try {
      const refreshed = await contractService.getContractForLineDetailsModal(openedId);
      const notes = refreshed?.internalNotes || readContractInternalNotes(refreshed) || '';
      if (notes) {
        setSelectedContractInfo((prev: any) =>
          prev ? { ...prev, internalNotes: notes } : prev
        );
      }
    } catch {
      // classificação já persistida; notas atualizam no próximo open
    }
  };

  const handleQualidadeOk = async () => {
    if (isContractCancelled(selectedContractInfo)) {
      showError('Não é possível classificar qualidade de contratos cancelados');
      return;
    }
    setConfirmingCQV(true);
    try {
      let contractsToProcess: any[] = [];

      if (selectedContractInfo?.groupId) {
        let resolved: any[] = [];
        try {
          const fromApi = await contractService.getContractsByGroupId(selectedContractInfo.groupId);
          if (fromApi.length > 0) {
            resolved = [...fromApi];
          }
        } catch (err) {
          console.error('Erro ao buscar contratos do grupo para Qualidade OK (modal):', err);
        }
        resolved = mergeContractArraysById(resolved, selectedContractInfo.groupContracts || []);
        if (resolved.length === 0) {
          resolved = contracts?.items?.filter((c: any) => c.groupId === selectedContractInfo.groupId) || [];
        }
        if (resolved.length === 0 && highlightedContractId) {
          resolved = [{ id: highlightedContractId, user: selectedContractInfo?.user }];
        }
        contractsToProcess = sortContractsForVtmeGroupOrder(resolved);
      } else {
        contractsToProcess = [{ id: highlightedContractId!, user: selectedContractInfo?.user }];
      }

      const hasDeclaredStatus = contractsToProcess.some(
        (c) => c?.status != null && c.status !== ''
      );
      if (hasDeclaredStatus) {
        contractsToProcess = contractsToProcess.filter(
          (c) => normalizeContractStatus(c.status) === EContractStatus.AprovadoCQV
        );
      }
      if (contractsToProcess.length === 0) {
        showError('Nenhum contrato do grupo está em Aprovado CQV para classificar Qualidade OK.');
        return;
      }

      for (const contract of contractsToProcess) {
        await contractService.updateContractStatus(contract.id, EContractStatus.QualidadeOk);
      }

      await refreshLinesModalAfterQualityCcv(
        contractsToProcess.map((contract) => contract.id).filter(Boolean),
        EContractStatus.QualidadeOk
      );

      showSuccess(
        `Contrato${contractsToProcess.length > 1 ? 's' : ''} classificado${contractsToProcess.length > 1 ? 's' : ''} como Qualidade OK.`
      );
    } catch (error) {
      console.error('Erro ao classificar Qualidade OK:', error);
      showError(extractApiErrorMessage(error, 'Erro ao classificar Qualidade OK'));
    } finally {
      setConfirmingCQV(false);
    }
  };

  const handleAprovadoCcv = async () => {
    if (isContractCancelled(selectedContractInfo)) {
      showError('Não é possível classificar CCV de contratos cancelados');
      return;
    }
    setConfirmingCQV(true);
    try {
      let contractsToProcess: any[] = [];

      if (selectedContractInfo?.groupId) {
        let resolved: any[] = [];
        try {
          const fromApi = await contractService.getContractsByGroupId(selectedContractInfo.groupId);
          if (fromApi.length > 0) {
            resolved = [...fromApi];
          }
        } catch (err) {
          console.error('Erro ao buscar contratos do grupo para Aprovado CCV (modal):', err);
        }
        resolved = mergeContractArraysById(resolved, selectedContractInfo.groupContracts || []);
        if (resolved.length === 0) {
          resolved = contracts?.items?.filter((c: any) => c.groupId === selectedContractInfo.groupId) || [];
        }
        if (resolved.length === 0 && highlightedContractId) {
          resolved = [{ id: highlightedContractId, user: selectedContractInfo?.user }];
        }
        contractsToProcess = sortContractsForVtmeGroupOrder(resolved);
      } else {
        contractsToProcess = [{ id: highlightedContractId!, user: selectedContractInfo?.user }];
      }

      const hasDeclaredStatus = contractsToProcess.some(
        (c) => c?.status != null && c.status !== ''
      );
      if (hasDeclaredStatus) {
        contractsToProcess = contractsToProcess.filter(
          (c) => normalizeContractStatus(c.status) === EContractStatus.QualidadeOk
        );
      }
      if (contractsToProcess.length === 0) {
        showError('Nenhum contrato do grupo está em Qualidade OK para classificar Aprovado CCV.');
        return;
      }

      for (const contract of contractsToProcess) {
        await contractService.updateContractStatus(contract.id, EContractStatus.AprovadoCCV);
      }

      await refreshLinesModalAfterQualityCcv(
        contractsToProcess.map((contract) => contract.id).filter(Boolean),
        EContractStatus.AprovadoCCV
      );

      showSuccess(
        `Contrato${contractsToProcess.length > 1 ? 's' : ''} classificado${contractsToProcess.length > 1 ? 's' : ''} como Aprovado CCV.`
      );
    } catch (error) {
      console.error('Erro ao classificar Aprovado CCV:', error);
      showError(extractApiErrorMessage(error, 'Erro ao classificar Aprovado CCV'));
    } finally {
      setConfirmingCQV(false);
    }
  };

  const handleRejectCQV = () => {
    if (isContractCancelled(selectedContractInfo)) {
      showError('Não é possível fazer CQV de contratos cancelados');
      return;
    }
    setPendingRejectionKind('cqv');
    setShowRejectModal(true);
  };

  const handleRejectQualidadeFromLinesModal = () => {
    if (isContractCancelled(selectedContractInfo)) {
      showError('Não é possível recusar contratos cancelados');
      return;
    }
    setPendingRejectionKind('qualidade');
    setPendingRejectionContractId(null);
    setShowRejectModal(true);
  };

  const handleRejectCcvFromLinesModal = () => {
    if (isContractCancelled(selectedContractInfo)) {
      showError('Não é possível recusar contratos cancelados');
      return;
    }
    setPendingRejectionKind('ccv');
    setPendingRejectionContractId(null);
    setShowRejectModal(true);
  };

  const handleRejectCvpFromLinesModal = () => {
    if (isContractCancelled(selectedContractInfo)) {
      showError('Não é possível recusar contratos cancelados');
      return;
    }
    setPendingRejectionKind('cvp');
    setPendingRejectionContractId(null);
    setShowRejectModal(true);
  };

  const handleRejectTrocaCarteiraFromLinesModal = () => {
    if (isContractCancelled(selectedContractInfo)) {
      showError('Não é possível recusar contratos cancelados');
      return;
    }
    setPendingRejectionKind('trocaCarteira');
    setPendingRejectionContractId(null);
    setShowRejectModal(true);
  };

  const handleRejectCQVConfirm = async (rejectionReason: string) => {
    
    setShowRejectModal(false);
    setRejectingCQV(true);

    const rejectionKind = pendingRejectionKind;
    const reasonForEmail =
      rejectionKind === 'trocaCarteira'
        ? `[Troca de Carteira — não autorizada]\n${rejectionReason}`
        : rejectionReason;
    const targetRejectStatus =
      rejectionKind === 'qualidade'
        ? EContractStatus.RecusadoQualidade
        : rejectionKind === 'ccv'
          ? EContractStatus.RecusadoCCV
          : rejectionKind === 'cvp'
            ? EContractStatus.RecusadoCVP
            : rejectionKind === 'trocaCarteira'
              ? EContractStatus.TrocaDeCarteiraNaoAutorizada
              : EContractStatus.RecusadoCQV;

    const isFromDropdown = pendingRejectionContractId !== null;
    const contractIdForTrocaReject =
      highlightedContractId ?? selectedContractInfo?.id ?? null;
    const isTrocaSolicitadaCqvReject =
      rejectionKind === 'cqv' && grupoTemMembroEmTrocaSolicitada && !isFromDropdown;

    if (isTrocaSolicitadaCqvReject && contractIdForTrocaReject) {
      try {
        const result = await contractService.recusarCqvEmTrocaSolicitada(
          contractIdForTrocaReject,
          rejectionReason
        );
        await applyCqvEmTrocaSolicitadaResult(result, contractIdForTrocaReject, 'recusado');
      } catch (error) {
        console.error('Erro ao recusar CQV em troca solicitada:', error);
        showError('Erro ao recusar CQV em Troca de Carteira Solicitada');
      } finally {
        setRejectingCQV(false);
        setPendingRejectionKind('cqv');
      }
      return;
    }

    // Quando o fluxo veio do dropdown da tabela (sem abrir modal de detalhes),
    // resolvemos o contrato direto da lista e seguimos o mesmo pipeline.
    const dropdownContractId = pendingRejectionContractId;
    setPendingRejectionContractId(null);

    try {
      let contractsToProcess: any[] = [];

      if (isFromDropdown && dropdownContractId) {
        const fromList = contracts?.items?.find((c: any) => c.id === dropdownContractId);
        if (fromList?.groupId) {
          let resolved: any[] = [];
          try {
            const fromApi = await contractService.getContractsByGroupId(fromList.groupId);
            if (fromApi.length > 0) {
              resolved = Array.from(new Map(fromApi.map((c: any) => [c.id, c])).values());
            }
          } catch (err) {
            console.error('Erro ao buscar contratos do grupo para Recusar CQV (dropdown):', err);
          }
          if (resolved.length === 0) {
            resolved = contracts?.items?.filter((c: any) => c.groupId === fromList.groupId) || [];
          }
          if (resolved.length === 0) {
            resolved = [fromList];
          }
          contractsToProcess = resolved;
        } else {
          contractsToProcess = [fromList ?? { id: dropdownContractId, user: undefined }];
        }
      } else if (selectedContractInfo?.groupId) {
        let resolved: any[] = [];
        try {
          const fromApi = await contractService.getContractsByGroupId(selectedContractInfo.groupId);
          if (fromApi.length > 0) {
            resolved = Array.from(new Map(fromApi.map((c: any) => [c.id, c])).values());
          }
        } catch (err) {
          console.error('Erro ao buscar contratos do grupo para Recusar CQV (modal):', err);
        }
        if (resolved.length === 0 && selectedContractInfo.groupContracts?.length) {
          resolved = Array.from(
            new Map(selectedContractInfo.groupContracts.map((c: any) => [c.id, c])).values()
          );
        }
        if (resolved.length === 0) {
          resolved = contracts?.items?.filter((c: any) => c.groupId === selectedContractInfo.groupId) || [];
        }
        if (resolved.length === 0 && highlightedContractId) {
          resolved = [{ id: highlightedContractId, user: selectedContractInfo?.user }];
        }
        contractsToProcess = resolved;
      } else {
        contractsToProcess = [{ id: highlightedContractId!, user: selectedContractInfo?.user }];
      }

      if (rejectionKind === 'qualidade' || rejectionKind === 'ccv') {
        const originStatus =
          rejectionKind === 'qualidade'
            ? EContractStatus.AprovadoCQV
            : EContractStatus.QualidadeOk;
        const hasDeclaredStatus = contractsToProcess.some(
          (c) => c?.status != null && c.status !== ''
        );
        if (hasDeclaredStatus) {
          contractsToProcess = contractsToProcess.filter(
            (c) => normalizeContractStatus(c.status) === originStatus
          );
        }
        if (contractsToProcess.length === 0) {
          showError(
            rejectionKind === 'qualidade'
              ? 'Nenhum contrato do grupo está em Aprovado CQV para recusar Qualidade.'
              : 'Nenhum contrato do grupo está em Qualidade OK para recusar CCV.'
          );
          return;
        }
      }

      // Atualizar status de todos os contratos
      // changeReason precisa ir preenchido pra todo tipo de recusa, não só qualidade/ccv —
      // sem isso o backend registra um motivo genérico no histórico (ex.: "Alteração manual"),
      // que é o que a reconciliação da Solicitação de CQV lê pra mandar no grupo (achado a
      // pedido do Davi, 2026-09-02: a recusa via botão "Recusar CQV" perdia o motivo digitado).
      for (const contract of contractsToProcess) {
        await updateContractStatusWithCacheInvalidation(contract.id, targetRejectStatus, {
          changeReason: rejectionKind === 'trocaCarteira' ? reasonForEmail : rejectionReason,
        }, selectedContractInfo?.groupId);
      }

      // Recusa CCV: UpdateStatus na API já envia o e-mail [CCV] por contrato.
      // Não chamar send-qualidade-rejection-email / sendContractRejectionEmail no cliente.
      if (rejectionKind === 'ccv') {
        showSuccess(
          contractsToProcess.length > 1
            ? `Contratos recusados CCV (${contractsToProcess.length}).`
            : 'Contrato recusado CCV.'
        );
      } else {
      // Enviar email para cada contrato do grupo (ou único contrato se individual)
      const totalContracts = contractsToProcess.length;
      let emailSent = false;
      let lastResult: {
        success: boolean;
        emailSent: boolean;
        centralNotified?: boolean;
        reason?: string;
      } | null = null;
      let emailResults: Array<{
        contractId: string;
        success: boolean;
        notified: boolean;
        skipped?: boolean;
        error?: string;
      }> = [];

      try {
        emailResults = [];

        // Troca carteira: Central 1× + e-mail HTML detalhado 1× por grupo (1º contrato).
        const isTrocaRecusa = rejectionKind === 'trocaCarteira';

        type RejectionNotifyResult = {
          success: boolean;
          emailSent: boolean;
          centralNotified?: boolean;
          reason?: string;
        };

        const sendEmailWithTimeout = async (
          contract: any,
          timeoutMs: number = 30000,
        ): Promise<RejectionNotifyResult> => {
          const timeoutPromise = new Promise<RejectionNotifyResult>((_, reject) => {
            setTimeout(() => reject(new Error('Timeout ao enviar email')), timeoutMs);
          });

          const creatorEmail = contract.user?.email || selectedContractInfo?.user?.email;

          if (!creatorEmail && !isTrocaRecusa) {
            return { success: false, emailSent: false, reason: 'Creator email not available' };
          }

          const sendRejectionEmail = (): Promise<RejectionNotifyResult> => {
            if (rejectionKind === 'qualidade') {
              return contractService.sendContractQualidadeRejectionEmail(contract.id, reasonForEmail, creatorEmail);
            }
            if (rejectionKind === 'cvp') {
              return contractService.sendContractCvpRejectionEmail(contract.id, reasonForEmail, creatorEmail);
            }
            return contractService.sendContractRejectionEmail(
              contract.id,
              reasonForEmail,
              creatorEmail,
              false,
            );
          };

          return Promise.race([sendRejectionEmail(), timeoutPromise]);
        };

        const emailPromises = contractsToProcess.map((contract, index) => {
          if (isTrocaRecusa && index > 0) {
            return Promise.resolve({
              contractId: contract.id,
              success: true,
              notified: true,
              skipped: true,
            });
          }

          return sendEmailWithTimeout(contract)
            .then(result => {
              const notified = !!(result.emailSent || result.centralNotified);
              return {
                contractId: contract.id,
                success: notified,
                notified,
                error: notified ? undefined : result.reason,
              };
            })
            .catch(error => ({
              contractId: contract.id,
              success: false,
              notified: false,
              error: error.message,
            }));
        });

        const results = await Promise.allSettled(emailPromises);

        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            emailResults.push(result.value);
          } else {
            emailResults.push({
              contractId: contractsToProcess[index]?.id || 'unknown',
              success: false,
              notified: false,
              error: result.reason?.toString() || 'Erro desconhecido',
            });
          }
        });

        const primaryResult = emailResults.find(r => !r.skipped) ?? emailResults[0];
        const trocaNotified = isTrocaRecusa && emailResults.some(r => r.notified);
        const emailsSent = isTrocaRecusa
          ? (trocaNotified ? 1 : 0)
          : emailResults.filter(r => r.success).length;
        const emailsFailed = isTrocaRecusa
          ? (trocaNotified ? 0 : 1)
          : emailResults.filter(r => !r.success).length;

        emailSent = emailsSent > 0;
        lastResult = {
          success: true,
          emailSent: !!primaryResult?.notified && !isTrocaRecusa,
          centralNotified: trocaNotified,
          reason: emailsFailed > 0 ? `${emailsFailed} de ${totalContracts} notificações falharam` : undefined,
        };

        const okPlural = isTrocaRecusa
          ? `Troca de carteira não autorizada (${totalContracts}). Consultor e supervisor notificados.`
          : `Contratos recusados (${totalContracts}) e supervisor notificado`;
        const warnFailPlural = isTrocaRecusa
          ? `Troca de carteira não autorizada (${totalContracts}), mas falha ao notificar consultor e supervisor`
          : `Contratos recusados (${totalContracts}), mas falha ao notificar o supervisor`;
        const warnPartialPlural = isTrocaRecusa
          ? warnFailPlural
          : `Contratos recusados (${totalContracts}). ${emailsSent} de ${totalContracts} emails enviados. ${emailsFailed} falharam.`;

        if (isTrocaRecusa) {
          if (trocaNotified) {
            showSuccess(
              totalContracts > 1
                ? okPlural
                : 'Troca de carteira não autorizada. Consultor e supervisor notificados.',
            );
          } else if (primaryResult?.error === 'Recipient email not available') {
            showWarning(
              'Troca de carteira não autorizada. Consultor e supervisor sem e-mail/WhatsApp cadastrado.',
            );
          } else {
            showWarning(
              totalContracts > 1
                ? warnFailPlural
                : 'Troca de carteira não autorizada, mas falha ao notificar consultor e supervisor',
            );
          }
        } else if (totalContracts > 1) {
          if (emailsSent === totalContracts) {
            showSuccess(okPlural);
          } else if (emailsSent > 0) {
            showWarning(warnPartialPlural);
          } else {
            showWarning(warnFailPlural);
          }
        } else {
          if (emailSent) {
            showSuccess('Contrato recusado e supervisor notificado');
          } else if (lastResult?.reason === 'Recipient email not available' || lastResult?.reason === 'Creator email not available') {
            showWarning('Contrato recusado. Supervisor (e criador) não possui email cadastrado.');
          } else {
            showWarning('Contrato recusado, mas falha ao notificar o supervisor');
          }
        }
      } catch (error: any) {
        console.error(`Erro ao enviar emails de recusa:`, error);
        lastResult = { success: false, emailSent: false, reason: 'Erro ao enviar emails' };
        showError('Erro ao enviar emails de recusa');
      }
      }

      if (!isFromDropdown) {
        handleCloseLinesModal();
      }
      await loadContracts(currentPage);
    } catch (error) {
      console.error('Erro ao recusar contrato:', error);
      showError(extractApiErrorMessage(error, 'Erro ao recusar contrato'));
    } finally {
      setRejectingCQV(false);
      setPendingRejectionKind('cqv');
    }
  };

  // Lógica de envio da confirmação VTME (chamada diretamente ou após confirmação do aviso)
  const doConfirmarVtme = async () => {
    if (!highlightedContractId || !selectedContractInfo) return;

    const linhasRenegociacao = selectedContractLines.filter((line: any) => line.negociacao === 'Renegociação');

    const linhasAptasArray = linhasRenegociacao.map((line: any) => {
      const aptoValue = linhasAptas.has(line.id)
        ? linhasAptas.get(line.id) === true
        : convertIsAptoToBoolean(line.isApto);
      return {
        linhaId: line.id,
        apto: aptoValue
      };
    });

    setConfirmingVtme(true);
    try {
      await confirmarVtmeWithCacheInvalidation(highlightedContractId, {
        idPedidoContrato1: idPedido1 || null,
        idPedidoContrato2: idPedido2 || null,
        linhasAptas: linhasAptasArray
      }, selectedContractInfo?.groupId);

      showSuccess(
        'VTME criado: status atualizado, e-mail com os dados enviado para processamento e integração VTME acionada.'
      );
      handleCloseLinesModal();
      await loadContracts(currentPage);
    } catch (error: any) {
      console.error('Erro ao concluir VTME Criado:', error);
      showError(error.message || 'Erro ao concluir VTME Criado');
    } finally {
      setConfirmingVtme(false);
    }
  };

  // VTME Criado — valida campos e exibe aviso se necessário
  const handleConfirmarVtme = async () => {
    if (!highlightedContractId || !selectedContractInfo) {
      showError('Contrato não selecionado');
      return;
    }

    const stVtme = normalizeContractStatus(selectedContractInfo.status);
    if (
      stVtme !== EContractStatus.AprovadoCQV &&
      !isTrocaDeCarteiraFlowStatus(stVtme) &&
      !isQualityCcvPipelineStatus(stVtme)
    ) {
      showError('Apenas contratos com status Aprovado CQV, Troca de Carteira ou Qualidade/CCV podem usar VTME Criado');
      return;
    }

    // Validar que todas as linhas na lista são realmente de renegociação
    const linhasRenegociacao = selectedContractLines.filter((line: any) => line.negociacao === 'Renegociação');
    const linhasInvalidas = linhasRenegociacao.filter(
      line => line.negociacao !== 'Renegociação'
    );
    if (linhasInvalidas.length > 0) {
      showError('Apenas linhas de renegociação podem ser marcadas como aptas');
      return;
    }

    // Verificar campos de ID de pedido obrigatórios conforme tipo do contrato
    const tipo = selectedContractInfo?.contractType || selectedContractInfo?.tipoContratacao || '';
    const missing: string[] = [];

    if (tipo.includes('Aditivo') && !idPedido1) {
      missing.push('Id do Pedido Aditivo');
    }
    if (tipo.includes('Renegociação') && !idPedido2) {
      missing.push('Id do Pedido Renegociação');
    }

    if (missing.length > 0) {
      setVtmeMissingFields(missing);
      setShowVtmeWarningModal(true);
      return;
    }

    await doConfirmarVtme();
  };

  const handleSavePedidoIds = async () => {
    const contractId = highlightedContractId || selectedContractInfo?.id;
    if (!contractId) return;
    if (!hasRole('Admin')) {
      showError('Apenas Admin pode corrigir IDs de pedido após VTME confirmado.');
      return;
    }

    setSavingPedidoIds(true);
    try {
      await updatePedidoIdsWithCacheInvalidation(contractId, {
        idPedidoContrato1: idPedido1.trim() || null,
        idPedidoContrato2: idPedido2.trim() || null,
      }, selectedContractInfo?.groupId);
      showSuccess('IDs dos pedidos atualizados com sucesso.');
      if (selectedContractInfo?.id) {
        await handleShowLinesModal(selectedContractInfo.id, selectedContractInfo, {
          softRefresh: true,
        });
      }
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Erro ao salvar IDs dos pedidos.';
      showError(msg);
    } finally {
      setSavingPedidoIds(false);
    }
  };

  // Função auxiliar para carregar contrato para edição
  type LoadContractForEditingOptions = {
    modalMode?: 'full' | 'linesOnly';
    initialTab?: string;
    skipOpenModal?: boolean;
  };

  const loadContractForEditing = async (
    contract: ContractResponseDto,
    options?: LoadContractForEditingOptions
  ) => {
    const modalMode = options?.modalMode ?? 'full';
    try {
      const fullContract = await contractService.getContractById(contract.id);
      setContractCanEditLines(fullContract.canEditContractLines ?? false);
      setContractUpdatedAt(fullContract.updatedAt ? new Date(fullContract.updatedAt).toISOString() : null);
      const fullClientData = await clientService.getClientById(fullContract.client.id);
      const hydratedToken = hydrateSaleTokenFromContract(fullContract.token, fullContract.fastchip ?? false);
      saleTokenLockedRef.current = hydratedToken.trim() !== '';
      setSelectedClient(fullClientData);
      formLoadFromContract(fullContract, fullClientData);
      if (hydratedToken) {
        setToken(hydratedToken);
      }
      setContractInternalNotes(fullContract.internalNotes || '');
      setFastchipDisabled(false);
      setFastchipDisabledReason('');
      setEditingContractId(fullContract.id);
      setContractModalMode(modalMode);
      if (!options?.skipOpenModal) {
        const tab = options?.initialTab ?? (modalMode === 'linesOnly' ? 'linhas' : 'contrato');
        await openContractModalWithRules(tab);
      }
    } catch (error) {
      console.error('Erro ao carregar contrato para edição:', error);
      showError('Erro ao carregar contrato para edição');
      throw error;
    }
  };
  loadContractForEditingRef.current = loadContractForEditing;

  const loadContractForDraftContinue = async (contract: ContractResponseDto) => {
    try {
      const fullClientData = await clientService.getClientById(contract.client.id);
      const contractFastchip = contract.fastchip ?? false;
      const needsDraftIccidValidation =
        contractFastchip &&
        (contract.linhas || []).some(
          (line) => line.negociacao === 'Aditivo' && !line.excluirDoContrato
        );

      if (needsDraftIccidValidation) {
        setIccidPoolRefreshNonce((nonce) => nonce + 1);
        setPendingDraftIccidValidation(true);
      } else {
        setPendingDraftIccidValidation(false);
      }

      const hydratedToken = hydrateSaleTokenFromContract(contract.token, contractFastchip);
      saleTokenLockedRef.current = hydratedToken.trim() !== '';
      setSelectedClient(fullClientData);
      if (contract.userId) {
        setResponsibleConsultantUserId(contract.userId);
      }
      formLoadFromContract(contract, fullClientData);
      if (hydratedToken) {
        setToken(hydratedToken);
      }
      setContractInternalNotes(contract.internalNotes || '');
      setFastchipDisabled(false);
      setFastchipDisabledReason('');
      setEditingContractId(null);
      draftContractIdRef.current = contract.id;
      setDraftContractId(contract.id);
      setShowClientDraftsModal(false);
      const initialTab = resolveFirstIncompleteContractFormTab(
        buildTabProgressSnapshotFromContract(contract, fullClientData, {
          hydratedToken,
          canAssignResponsibleConsultant,
          teamContractRules,
          meuTimVerified: false,
        })
      );
      await openContractModalWithRules(initialTab);
    } catch (error) {
      console.error('Erro ao carregar rascunho:', error);
      showError('Erro ao carregar rascunho do contrato');
      throw error;
    }
  };

  const handleContinueDraft = async (contractId: string) => {
    try {
      const contract = await contractService.getContractById(contractId);
      await loadContractForDraftContinue(contract);
    } catch (error) {
      console.error('Erro ao continuar rascunho:', error);
      showError('Erro ao continuar rascunho');
    }
  };

  const handleDeleteDraft = async (contractId: string) => {
    if (!window.confirm('Excluir este rascunho permanentemente?')) return;
    try {
      await contractService.deleteContractDraft(contractId);
      showInfo('Rascunho excluído');
      if (draftContractId === contractId) {
        draftContractIdRef.current = null;
        setDraftContractId(null);
      }
      await loadContracts(currentPage);
    } catch (error) {
      console.error('Erro ao excluir rascunho:', error);
      showError('Erro ao excluir rascunho');
    }
  };

  const handleClientDraftContinue = async (draftId: string) => {
    setShowClientDraftsModal(false);
    await handleContinueDraft(draftId);
  };

  const handleClientDraftCreateNew = () => {
    setShowClientDraftsModal(false);
    draftContractIdRef.current = null;
    setDraftContractId(null);
  };

  const handleClientDraftsModalClose = () => {
    setShowClientDraftsModal(false);
  };

  const handleChooseAnotherClient = () => {
    setShowClientDraftsModal(false);
    setClientDraftsList([]);
    draftContractIdRef.current = null;
    setDraftContractId(null);
    setSelectedClient(null);
    setTimClientData(null);
    setClientSearchQuery('');
    setContractData((prev) => ({
      ...prev,
      clientId: undefined,
    }));
    setActiveTab('cliente');
  };

  // Carrega grupo completo para edição, mesclando linhas de todos os contratos com sourceContractId
  const loadContractForEditingConsolidated = async (
    groupContracts: ContractResponseDto[],
    options?: LoadContractForEditingOptions
  ) => {
    const primaryContract = groupContracts[0];
    const modalMode = options?.modalMode ?? 'full';
    setEditingGroupContracts(groupContracts);
    await loadContractForEditing(primaryContract, { ...options, modalMode, skipOpenModal: true });

    // Mesclar linhas de todos os contratos, adicionando sourceContractId para rastrear origem
    const allLines: any[] = [];
    for (const contract of groupContracts) {
      const contractLines = (contract.linhas || []).map((l: any) => ({
        id: l.id,
        negociacao: l.negociacao || '',
        ddd: l.ddd || '',
        numero: l.numero || '',
        chipCcid: l.chipCcid || '',
        tipoVenda: l.tipoVenda || '',
        planId: l.planId || '',
        planName: l.planName ?? l.plano,
        plano: l.planName ?? l.plano ?? '',
        aparelho: l.aparelho || '',
        valorChip: l.valorChip || 0,
        valorMensal: l.valorTotalMensal ?? l.valorMensal ?? 0,
        valorTotalMensal: l.valorTotalMensal ?? l.valorMensal ?? 0,
        quantidade: l.quantidade,
        ofertas: l.ofertas || [],
        excluirDoContrato: l.excluirDoContrato || false,
        requerSuspensao: l.requerSuspensao || false,
        trocaLinha: l.trocaLinha || false,
        meuTimLogin: l.meuTimLogin || '',
        meuTimSenha: l.meuTimSenha || '',
        protocolo: l.protocolo || '',
        seraUsadaPeloCliente: l.seraUsadaPeloCliente,
        ehPortabilidade: l.ehPortabilidade || false,
        portabilidadeNomeCompleto: l.portabilidadeNomeCompleto || '',
        portabilidadeCpf: l.portabilidadeCpf || '',
        portabilidadeOperadora: l.portabilidadeOperadora || '',
        portabilidadeLinha: l.portabilidadeLinha || '',
        sourceContractId: contract.id,
        lineOriginContractId: contract.id,
      }));
      allLines.push(...contractLines);
    }
    setLinhas(allLines);
    setLinhasParaDesativar(collectLinhasParaDesativarIds(allLines));
    setEditingContractId(primaryContract.id);
    if (groupContracts.length > 1) {
      setContractCanEditLines(groupContracts.every((gc) => gc.canEditContractLines ?? false));
    }
    setContractModalMode(modalMode);
    if (!options?.skipOpenModal) {
      const tab = options?.initialTab ?? (modalMode === 'linesOnly' ? 'linhas' : 'contrato');
      await openContractModalWithRules(tab);
    }
  };

  const handleOpenLinesOnlyEdit = async () => {
    if (!selectedContractInfo?.id) return;
    if (!canEditContractLinesByRole) {
      showError('Você não tem permissão para editar linhas do contrato.');
      return;
    }
    if (!linesDetailCanEditLines) {
      showError('Este contrato não permite edição de linhas no status atual.');
      return;
    }
    setOpeningLinesOnlyEdit(true);
    try {
      if (
        selectedContractInfo.isGroupParent &&
        selectedContractInfo.groupId &&
        linesModalGroupContracts.length > 1
      ) {
        const groupList = await contractService.getContractsByGroupId(selectedContractInfo.groupId);
        const groupContracts = await Promise.all(
          groupList.map((c) => contractService.getContractById(c.id))
        );
        await loadContractForEditingConsolidated(groupContracts, {
          modalMode: 'linesOnly',
          initialTab: 'linhas',
        });
      } else {
        const contract = await contractService.getContractById(selectedContractInfo.id);
        await loadContractForEditing(contract, {
          modalMode: 'linesOnly',
          initialTab: 'linhas',
        });
      }
    } catch (error) {
      console.error('Erro ao abrir edição de linhas:', error);
      showError('Erro ao abrir edição de linhas do contrato');
    } finally {
      setOpeningLinesOnlyEdit(false);
    }
  };

  const handleOpenLineAuditHistory = () => {
    if (!selectedContractInfo?.id) return;
    if (isLinesModalGroupView && selectedContractInfo.groupId) {
      setLineAuditHistoryMode('group');
      setLineAuditHistoryGroupId(String(selectedContractInfo.groupId));
      setLineAuditHistoryContractId(null);
    } else {
      setLineAuditHistoryMode('solo');
      setLineAuditHistoryContractId(String(selectedContractInfo.id));
      setLineAuditHistoryGroupId(null);
    }
    setShowLineAuditHistoryModal(true);
  };

  // Handler para confirmar edição após aviso
  const handleConfirmEditAfterWarning = async () => {
    if (!contractToEdit) return;
    
    try {
      await contractService.notifyContractEdit(contractToEdit.id);
      
      if (editingGroupContracts && editingGroupContracts.length > 1) {
        setEditingGroupIndex(0);
        await loadContractForEditingConsolidated(editingGroupContracts);
      } else {
        await loadContractForEditing(contractToEdit);
      }
      
      setShowEditWarningModal(false);
      setContractToEdit(null);
    } catch (error) {
      console.error('Erro ao processar edição:', error);
      showError('Erro ao processar edição');
    }
  };

  // Handler para cancelar edição após aviso
  const handleCancelEditAfterWarning = () => {
    setShowEditWarningModal(false);
    setContractToEdit(null);
    setEditingGroupContracts(null);
    setEditingGroupSecondContractLines(null);
    setEditingGroupIndex(0);
  };

  // Handler de clique em Editar: se for grupo com mais de um contrato, mostra modal de seleção
  const handleEditClick = async (contractId: string) => {
    try {
      const contract = await contractService.getContractById(contractId);
      
      if (contract.groupId) {
        const groupContracts = await contractService.getContractsByGroupId(contract.groupId);
        
        if (groupContracts && groupContracts.length > 1) {
          setSelectedGroupForEdit(groupContracts);
          setShowEditGroupSelectionModal(true);
          return;
        }
      }
      
      await handleEditContract(contractId);
    } catch (error) {
      console.error('Erro ao abrir edição:', error);
      showError('Erro ao carregar contrato para edição');
    }
  };

  // Editar um contrato individual de dentro do grupo (sem lógica de grupo)
  const handleEditSingleFromGroup = async (contractId: string) => {
    setShowEditGroupSelectionModal(false);
    setSelectedGroupForEdit(null);
    try {
      await handleEditContract(contractId);
    } catch (error) {
      console.error('Erro ao abrir edição individual:', error);
      showError('Erro ao carregar contrato para edição');
    }
  };

  // Editar grupo completo: consolida linhas de todos os contratos
  const handleEditGroupComplete = async (groupContracts: ContractResponseDto[]) => {
    setShowEditGroupSelectionModal(false);
    setSelectedGroupForEdit(null);
    
    try {
      const anyOver24h = groupContracts.some(
        (c) => (Date.now() - new Date(c.createdAt).getTime()) / (1000 * 60 * 60) > 24
      );

      if (anyOver24h) {
        setContractToEdit(groupContracts[0]);
        setEditingGroupContracts(groupContracts);
        setEditingGroupIndex(0);
        setShowEditWarningModal(true);
        return;
      }

      setEditingGroupContracts(groupContracts);
      setEditingGroupIndex(0);
      await loadContractForEditingConsolidated(groupContracts);
      await openContractModalWithRules('contrato');
    } catch (error) {
      console.error('Erro ao abrir edição de grupo:', error);
      showError('Erro ao carregar grupo para edição');
    }
  };

  // Handler para confirmar criação de novo contrato (cancelando o anterior)
  const handleExistingContractConfirm = async () => {
    if (!existingContractCnpj) {
      setShowExistingContractModal(false);
      return;
    }

    try {
      // Cancelar o contrato anterior
      await contractService.cancelPreviousContractByCnpj(existingContractCnpj);
      showSuccess('Contrato anterior cancelado. Você pode continuar criando o novo contrato.');
      setShowExistingContractModal(false);
      setExistingContractCnpj(null);
    } catch (error) {
      console.error('Erro ao cancelar contrato anterior:', error);
      showError('Erro ao cancelar contrato anterior. Tente novamente.');
    }
  };

  // Handler para cancelar criação quando há contrato existente
  const handleExistingContractCancel = () => {
    setShowExistingContractModal(false);
    setExistingContractCnpj(null);
    // Limpar seleção do cliente para forçar nova seleção
    handleClientClear();
  };

  const handleMarkFastchipEnviado = async (
    contractId: string,
    contractStatus?: EContractStatus | number | null
  ) => {
    if (!canMarkFastchipEnviadoForStatus(contractStatus)) {
      window.alert(
        `Só é possível marcar o Fast Chip como enviado quando o status do contrato for Aprovado CQV, VTME Criado ou Ativo.\n\nStatus atual: ${getStatusText(contractStatus)}.`
      );
      return;
    }
    if (!window.confirm('Deseja marcar o Fast Chip deste contrato como enviado?')) return;
    try {
      await contractService.markFastchipEnviado(contractId);
      showSuccess('Fast Chip marcado como enviado!');
      if (showLinesModal && (highlightedContractId === contractId || selectedContractInfo?.id === contractId)) {
        setSelectedContractInfo((prev: any) =>
          prev ? { ...prev, fastchipEnviadoEm: new Date().toISOString() } : prev
        );
      }
      await loadContracts(currentPage);
    } catch (err: any) {
      showError(err.message || 'Erro ao marcar Fast Chip como enviado');
    }
  };

  // Handler para duplicar contrato
  const handleDuplicateContract = async (contractId: string) => {
    try {
      setDuplicatingContractId(contractId);
      const contract = await contractService.getContractById(contractId);
      
      // NOVO: Verificar se tem groupId (propriedade que vem da API)
      if (contract.groupId) {
        // Buscar todos os contratos do grupo
        const groupContracts = await contractService.getContractsByGroupId(contract.groupId);
        
        if (groupContracts && groupContracts.length > 0) {
          // Criar objeto similar ao DisplayContract para o modal
          const groupForDuplicate = {
            ...contract,
            isGroupParent: true,
            groupContracts: groupContracts.map(gc => {
              // Filtrar linhas válidas (não excluídas)
              const linhasValidas = gc.linhas?.filter(l => !l.excluirDoContrato) || [];
              
              // Calcular totalLinhas somando as QUANTIDADES de cada linha
              const totalLinhas = linhasValidas.reduce((sum, l) => sum + (l.quantidade || 1), 0);
              
              let valorTotalMensal = 0;
              
              if (linhasValidas.length > 0) {
                valorTotalMensal = linhasValidas.reduce((sum, l) => {
                  const valorLinha = (l as any).valorTotalMensal || (l as any).valorMensal || 0;
                  return sum + (valorLinha * (l.quantidade || 1));
                }, 0);
              } else {
                valorTotalMensal = gc.valorTotalMensal || 0;
              }
              
              return {
                id: gc.id,
                tipoContratacao: gc.tipoContratacao,
                totalLinhas: totalLinhas,
                valorTotalMensal: valorTotalMensal,
                contractType: gc.contractType,
                createdAt: gc.createdAt,
                status: gc.status
              };
            })
          };
          
          setSelectedGroupForDuplicate(groupForDuplicate);
          setShowDuplicateGroupModal(true);
          setDuplicatingContractId(null);
          return;
        }
      }
      
      // Buscar informações completas do cliente
      const fullClientData = await clientService.getClientById(contract.client.id);
      
      // Popular todos os estados do formulário (copiar dados)
      setSelectedClient(fullClientData);
      setTipoContratacao(contract.tipoContratacao);
      setResponsibleConsultantUserId(contract.userId);
      setDiaVencimento(contract.diaVencimento);
      setValorTotalMensal(contract.valorTotalMensal || 0);
      setNumeroP2B(contract.numeroP2B || '');
      setComprometimentoMinimo(contract.comprometimentoMinimo?.toString() || '0');
      setFastchip(contract.fastchip);
      setFastchipDisabled(false);
      setFastchipDisabledReason('');
      setTipoFatura(contract.tipoFatura || 'Mensal');
      setEnvioFatura(contract.envioFatura || 'Email');
      setOrigemVenda(contract.origemVenda || 'Site');
      const dupTok = normalizeContractSaleTokenForForm(contract.token, contract.fastchip);
      setToken(dupTok);
      saleTokenLockedRef.current = dupTok !== '';
      
      // Endereço de entrega
      if (contract.deliveryAddress) {
        setCep(formatarCepParaInput(contract.deliveryAddress.cep));
        setCidade(contract.deliveryAddress.cidade);
        setEstado(contract.deliveryAddress.estado);
        setEndereco(contract.deliveryAddress.endereco);
        setNumero(contract.deliveryAddress.numero);
        setBairro(contract.deliveryAddress.bairro);
        setComplemento(contract.deliveryAddress.complemento || '');
        setReferencia(contract.deliveryAddress.referencia || '');
      }
      
      // Representante Legal
      if (contract.administrator) {
        setAdminNomeCompleto(contract.administrator.nomeCompleto);
        setAdminCpf(contract.administrator.cpf);
        setAdminRg(contract.administrator.rg);
        setAdminEmail(contract.administrator.email || '');
        setAdminTelefone1(contract.administrator.telefone1 || '');
        setAdminTelefone2(contract.administrator.telefone2 || '');
        setAdminSource('manual');
      }
      
      // Representantes (remover IDs - usar ID temporário)
      setRepresentantes(contract.representatives.map(r => ({
        id: `temp-${Date.now()}-${Math.random()}`, // ID temporário para satisfazer o tipo
        nome: r.nome,
        qualificacao: '',
        cpf: r.cpf,
        rg: r.rg || '',
        email: r.email || '',
        telefone: normalizeRepresentativePhoneInput(r.telefone || ''),
        manual: false
      })));
      
      // Recebedores autorizados (remover IDs - usar ID temporário)
      setRecebedores(contract.authorizedReceivers.map(r => ({
        id: `temp-${Date.now()}-${Math.random()}`, // ID temporário para satisfazer o tipo
        nome: r.nome,
        qualificacao: r.qualificacao || '',
        cpf: r.cpf,
        rg: r.rg || '',
        email: r.email || '',
        telefone: r.telefone || '',
        celular: r.celular || '',
        manual: false
      })));
      
      // Testemunhas (remover IDs - usar ID temporário)
      setTestemunhas(contract.witnesses.map(t => ({
        id: `temp-${Date.now()}-${Math.random()}`, // ID temporário para satisfazer o tipo
        nome: t.nome,
        cpf: t.cpf,
        rg: t.rg || ''
      })));
      
      // Linhas (remover IDs e limpar ICCIDs, filtrar linhas excluídas)
      setLinhas(contract.linhas
        .filter(l => !l.excluirDoContrato) // Filtrar linhas excluídas
        .map(l => ({
          negociacao: l.negociacao || '',
          ddd: l.ddd || '',
          numero: l.numero || '',
          chipCcid: '', // Limpar ICCIDs (não copiar)
          tipoVenda: l.tipoVenda || '',
          planId: l.planId || '',
          aparelho: l.aparelho || '',
          valorChip: l.valorChip || 0,
          valorMensal: l.valorMensal || 0,
          quantidade: l.quantidade,
          ofertas: l.ofertas || [],
          excluirDoContrato: false,
          requerSuspensao: false,
          trocaLinha: false,
          meuTimLogin: '',
          meuTimSenha: '',
          protocolo: '',
          seraUsadaPeloCliente: (l as any).seraUsadaPeloCliente ?? null,
          ehPortabilidade: (l as any).ehPortabilidade ?? false,
          portabilidadeNomeCompleto: (l as any).portabilidadeNomeCompleto ?? '',
          portabilidadeCpf: (l as any).portabilidadeCpf ?? '',
          portabilidadeOperadora: (l as any).portabilidadeOperadora ?? '',
          portabilidadeLinha: (l as any).portabilidadeLinha ?? ''
        })));
      
      // Definir ID do contrato original para mostrar banner
      setDuplicatedFromContractId(contractId);
      
      // Abrir modal de criação (não edição)
      setEditingContractId(null);
      await openContractModalWithRules('cliente');
      
      showSuccess('Dados do contrato copiados com sucesso! Revise todos os dados antes de salvar. ICCIDs não foram copiados.');
    } catch (error) {
      console.error('Erro ao duplicar contrato:', error);
      showError('Erro ao duplicar contrato');
    } finally {
      setDuplicatingContractId(null);
    }
  };

  // Handler para duplicar grupo completo
  const handleDuplicateGroupComplete = async (groupContracts: any[]) => {
    try {
      if (!groupContracts || groupContracts.length === 0) {
        showError('Nenhum contrato encontrado no grupo');
        return;
      }

      // Obter groupId do primeiro contrato ou do objeto selecionado
      const groupId = groupContracts[0]?.groupId || (selectedGroupForDuplicate as any)?.groupId;
      setDuplicatingGroupId(groupId || null);
      setShowDuplicateGroupModal(false);
      
      const errors: string[] = [];

      // Duplicar cada contrato do grupo sequencialmente
      for (let i = 0; i < groupContracts.length; i++) {
        const subContract = groupContracts[i];
        try {
          setDuplicatingContractId(subContract.id);
          
          // Buscar dados completos do contrato
          const fullContract = await contractService.getContractById(subContract.id);
          
          // Buscar informações completas do cliente
          const fullClientData = await clientService.getClientById(fullContract.client.id);
          
          // Preparar dados para duplicação (mesma lógica de handleDuplicateContract)
          const userId = getUserId();
          if (!userId) {
            throw new Error('Usuário não autenticado');
          }
          if (!fullContract.deliveryAddress) {
            throw new Error('Contrato não possui endereço de entrega');
          }
          if (!fullContract.administrator) {
            throw new Error('Contrato não possui responsável pela auditoria');
          }
          const groupDupSaleToken = normalizeContractSaleTokenForForm(
            fullContract.token ?? '',
            fullContract.fastchip
          );
          const inheritedResponsibleUserId = fullContract.userId;
          const contractPayload: CreateContractRequestDto = {
            clientId: fullClientData.id,
            userId: inheritedResponsibleUserId,
            tipoContratacao: fullContract.tipoContratacao,
            diaVencimento: fullContract.diaVencimento,
            numeroP2B: fullContract.numeroP2B || undefined,
            comprometimentoMinimo: fullContract.comprometimentoMinimo || undefined,
            fastchip: fullContract.fastchip,
            tipoFatura: fullContract.tipoFatura || 'Mensal',
            envioFatura: fullContract.envioFatura || 'Email',
            origemVenda: fullContract.origemVenda || 'Site',
            ...(groupDupSaleToken ? { token: groupDupSaleToken } : {}),
            shouldSeparate: false, // Não separar ao duplicar grupo completo
            deliveryAddress: {
              cep: fullContract.deliveryAddress.cep,
              cidade: fullContract.deliveryAddress.cidade,
              estado: fullContract.deliveryAddress.estado,
              endereco: fullContract.deliveryAddress.endereco,
              numero: fullContract.deliveryAddress.numero,
              bairro: fullContract.deliveryAddress.bairro,
              complemento: fullContract.deliveryAddress.complemento || undefined,
              referencia: fullContract.deliveryAddress.referencia || undefined
            },
            administrator: {
              nomeCompleto: fullContract.administrator.nomeCompleto,
              cpf: fullContract.administrator.cpf,
              rg: fullContract.administrator.rg,
              email: fullContract.administrator.email || undefined,
              telefone1: fullContract.administrator.telefone1 || undefined,
              telefone2: fullContract.administrator.telefone2 || undefined,
              dataNascimento: fullContract.administrator.dataNascimento || new Date('2000-01-01')
            },
            representatives: fullContract.representatives.map(r => ({
              nome: r.nome,
              cpf: r.cpf,
              rg: r.rg || undefined,
              email: (r.email ?? '').trim(),
              telefone: (r.telefone ?? '').trim(),
            })),
            authorizedReceivers: fullContract.authorizedReceivers.map(r => ({
              nome: r.nome,
              qualificacao: r.qualificacao || undefined,
              cpf: r.cpf,
              rg: r.rg || undefined,
              email: r.email || undefined,
              telefone: r.telefone || undefined,
              celular: r.celular || undefined
            })),
            witnesses: fullContract.witnesses.map(t => ({
              nome: t.nome,
              cpf: t.cpf,
              rg: t.rg || undefined
            })),
            lines: fullContract.linhas
              .filter(l => !l.excluirDoContrato)
              .map(l => ({
                negociacao: l.negociacao || undefined,
                ddd: l.ddd || undefined,
                numero: l.numero || undefined,
                // Não copiar chipCcid (ICCIDs)
                tipoVenda: l.tipoVenda || undefined,
                planId: l.planId || undefined,
                aparelho: l.aparelho || undefined,
                valorChip: l.valorChip || undefined,
                valorMensal: l.valorMensal || 0, // Obrigatório
                quantidade: l.quantidade,
                ofertas: l.ofertas || []
              }))
          };

          // Criar novo contrato
          await contractService.createContract(contractPayload);
          
        } catch (error: any) {
          console.error(`Erro ao duplicar contrato ${subContract.id}:`, error);
          errors.push(`Contrato ${subContract.tipoContratacao || subContract.id}: ${error.message || 'Erro desconhecido'}`);
        } finally {
          setDuplicatingContractId(null);
        }
      }

      // Fechar modal e mostrar resultado
      setDuplicatingGroupId(null);
      
      if (errors.length === 0) {
        showSuccess(`Grupo completo duplicado com sucesso! ${groupContracts.length} contratos criados.`);
        await loadContracts(currentPage);
      } else if (errors.length < groupContracts.length) {
        showError(`Alguns contratos foram duplicados com sucesso, mas ocorreram erros: ${errors.join('; ')}`);
        await loadContracts(currentPage);
      } else {
        showError(`Erro ao duplicar grupo completo: ${errors.join('; ')}`);
      }
    } catch (error: any) {
      console.error('Erro ao duplicar grupo completo:', error);
      showError(`Erro ao duplicar grupo completo: ${error.message || 'Erro desconhecido'}`);
      setDuplicatingGroupId(null);
    }
  };

  // Handler para escolher contrato específico do grupo para duplicar
  const handleDuplicateSpecificContract = async (contractId: string) => {
    setShowDuplicateGroupModal(false);
    setSelectedGroupForDuplicate(null);
    // Chamar handleDuplicateContract normalmente (já não é grupo, será duplicado como individual)
    await handleDuplicateContract(contractId);
  };

  // Handlers para modal de rastreamento
  const handleOpenTrackingModal = async (contractId: string, code?: string | null) => {
    const persisted = (code ?? '').trim();
    setTrackingModalContractId(contractId);
    setTrackingModalPersistedCode(persisted);
    setTrackingFetchError(null);
    setTrackingSaveError(null);
    setShowTrackingModal(true);
    setTrackingInfo(null);

    if (persisted) {
      setTrackingLoading(true);
      try {
        const info = await contractService.getContractTracking(contractId);
        setTrackingInfo(info);
      } catch (error: any) {
        const msg =
          error?.response?.data?.message ?? error?.message ?? 'Erro ao buscar informações de rastreamento';
        console.error('Erro ao buscar informações de rastreamento:', error);
        setTrackingFetchError(msg);
      } finally {
        setTrackingLoading(false);
      }
    } else {
      setTrackingLoading(false);
    }
  };

  const handleTrackingSaveCode = async (newCode: string) => {
    const cid = trackingModalContractId;
    if (!cid) return;
    setTrackingSaveLoading(true);
    setTrackingSaveError(null);
    try {
      await contractService.updateTrackingCode(cid, newCode);
      const trimmed = newCode.trim();
      setTrackingModalPersistedCode(trimmed);
      setTrackingFetchError(null);
      setTrackingLoading(true);
      try {
        const info = await contractService.getContractTracking(cid);
        setTrackingInfo(info);
      } catch (error: any) {
        const msg =
          error?.response?.data?.message ?? error?.message ?? 'Erro ao buscar informações de rastreamento';
        setTrackingFetchError(msg);
      } finally {
        setTrackingLoading(false);
      }
      await loadContracts(currentPage);
      setSelectedContractInfo((prev: any) =>
        prev?.id === cid ? { ...prev, trackingCode: trimmed } : prev
      );
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ?? error?.message ?? 'Erro ao salvar código de rastreio';
      setTrackingSaveError(msg);
      throw error;
    } finally {
      setTrackingSaveLoading(false);
    }
  };

  const handleCloseTrackingModal = () => {
    setShowTrackingModal(false);
    setTrackingModalContractId(null);
    setTrackingModalPersistedCode('');
    setTrackingInfo(null);
    setTrackingLoading(false);
    setTrackingFetchError(null);
    setTrackingSaveError(null);
    setTrackingSaveLoading(false);
  };

  // Linhas com id garantido (string) para componentes que exigem id obrigatório
  const linhasComId = React.useMemo(
    () => linhas.map((l, i) => ({ ...l, id: l.id ?? `linha-${i}` })),
    [linhas]
  );

  // Renderizar aba de Linhas
  const renderLinhasTab = () => (
    <ContractFormLinhasTab
      mode={isEditingExistingContract ? "edit" : "create"}
      allowLineMutations={canMutateContractLines}
      linhas={linhasComId as ContractLineEditorItem[]}
      onUpdateLinha={updateLinha}
      onRemoveLinha={removeLinha}
      onRemoveGroup={removeGroup}
      onAddLinha={isEditingExistingContract && canMutateContractLines ? addLinha : undefined}
      clientEstado={selectedClient?.estado}
      fastchip={fastchipFlowEnabled}
      availableICcIdsCount={backendFastchipAllocationMode ? undefined : availableICcIds.length}
      dddOptions={dddOptions}
      getValidDddsWithICcIdFallback={getValidDddsWithICcIdFallback}
      hasDuplicateCcid={hasDuplicateCcid}
      hasDuplicatePhone={hasDuplicatePhone}
      getDuplicateCcidLines={getDuplicateCcidLines}
      getDuplicatePhoneLines={getDuplicatePhoneLines}
      teamContractRules={teamContractRules}
      plans={plans}
      plansFiltrados={plansFiltrados}
      plansLoading={plansLoading}
      onFilterPlans={(negociacao, aparelho) => {
        setPlansFiltrados(
          filterPlansForContractLine(plans, negociacao, aparelho === WTTX_APARELHO_VALUE)
        );
      }}
      onAddDddOption={(ddd) => {
        setDddOptions(prev => {
          const exists = prev.some(opt => opt.value === ddd);
          if (!exists) {
            return [...prev, { key: ddd, text: ddd, value: ddd }];
          }
          return prev;
        });
      }}
    >
      <LinesList
          linhas={linhasComId as any}
          onUpdateLinha={updateLinha}
          onRemoveLinha={removeLinha}
          onRemoveGroup={removeGroup}
          allowLineMutations={canMutateContractLines}
          fastchip={fastchipFlowEnabled}
          hasDuplicateCcid={hasDuplicateCcid}
          hasDuplicatePhone={hasDuplicatePhone}
          getDuplicateCcidLines={getDuplicateCcidLines}
          getDuplicatePhoneLines={getDuplicatePhoneLines}
          teamContractRules={teamContractRules}
        />

      {/* Formulário para adicionar linha */}
      <Segment>
        <Header as="h4" style={{ color: '#2c3e50', marginBottom: '15px' }}>
          <Icon name="plus" />
          Adicionar Linha
        </Header>

        <Grid stackable>
          <Grid.Row>
            <Grid.Column width={4}>
              <Form.Field required>
                <label>Negociação</label>
                <select
                  value={isPrimeiraVenda ? "Aditivo" : linhaAtual.negociacao}
                  onChange={(e) => {
                    if (!isPrimeiraVenda && linhaAtual.aparelho !== 'ROTEADOR WTTX WNC WLD71T5B-PRETO') {
                      setLinhaAtual(prev => ({
                        ...prev,
                        negociacao: e.target.value,
                        planId: '',
                        plano: '',
                        valorMensal: '',
                      }));
                    }
                  }}
                  disabled={isPrimeiraVenda || linhaAtual.aparelho === 'ROTEADOR WTTX WNC WLD71T5B-PRETO'}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: (isPrimeiraVenda || linhaAtual.aparelho === 'ROTEADOR WTTX WNC WLD71T5B-PRETO') ? '1px solid #ccc' : '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: (isPrimeiraVenda || linhaAtual.aparelho === 'ROTEADOR WTTX WNC WLD71T5B-PRETO') ? '#f5f5f5' : 'white',
                    cursor: (isPrimeiraVenda || linhaAtual.aparelho === 'ROTEADOR WTTX WNC WLD71T5B-PRETO') ? 'not-allowed' : 'default'
                  }}
                >
                  <option value="Aditivo">Aditivo</option>
                  <option value="Renegociação">Renegociação</option>
                </select>
                {(isPrimeiraVenda || linhaAtual.aparelho === 'ROTEADOR WTTX WNC WLD71T5B-PRETO') && (
                  <div style={{ fontSize: '11px', color: '#2185d0', marginTop: '2px', fontWeight: 'bold' }}>
                    {isPrimeiraVenda ? 'Tipo fixo para 1º Venda' : 'Tipo fixo para aparelho WTTX'}
                  </div>
                )}
              </Form.Field>
            </Grid.Column>

            {linhaAtual.negociacao === 'Aditivo' && (
              <Grid.Column width={4}>
                <Form.Field required>
                  <label>DDD</label>
                  {fastchipFlowEnabled ? (
                    <>
                      <select
                        value={linhaAtual.ddd}
                        onChange={(e) => setLinhaAtual(prev => ({ ...prev, ddd: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      >
                        <option value="">Selecione o DDD</option>
                        {getValidDddsWithICcIdFallback(selectedClient?.estado || '', fastchipFlowEnabled, effectivePrCluster).map(ddd => (
                          <option key={ddd} value={ddd}>
                            {ddd}
                          </option>
                        ))}
                      </select>
                      {selectedClient && (
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                          {resolveRjMgEsFastchipIccidSearch(selectedClient.estado)
                            ? `${formatRjMgEsFastchipClusterUiLabel(selectedClient.estado)} — ${
                                resolveRjMgEsFastchipIccidSearch(selectedClient.estado)?.usesDhCluster
                                  ? 'DDDs de MG'
                                  : 'DDDs de SP (11–19)'
                              }`
                            : `DDDs disponíveis para ${selectedClient.estado} (Fastchip${selectedClient.estado === 'PR' && effectivePrCluster ? ` – Cluster ${effectivePrCluster}` : ''})`}
                        </div>
                      )}
                      {selectedClient?.estado === 'PR' && linhaAtual.ddd === '42' && selectedClient.cidade && (
                        <div
                          style={{
                            fontSize: '11px',
                            marginTop: '4px',
                            color: isPrMhClusterCity(selectedClient.cidade) ? '#666' : '#2185d0',
                          }}
                        >
                          {isPrMhClusterCity(selectedClient.cidade)
                            ? `DDD 42 — cluster MH (cidade no cluster Guarapuava).`
                            : `DDD 42 — cluster E.R.GESTAO (${selectedClient.cidade} fora do cluster Guarapuava/MH).`}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <Dropdown
                        placeholder="Selecione ou digite o DDD"
                        selection
                        search
                        allowAdditions
                        additionLabel="Usar DDD: "
                        value={linhaAtual.ddd || ''}
                        options={dddOptions}
                        onAddItem={(e, { value }) => {
                          const dddValue = String(value).replace(/\D/g, '').slice(0, 2);
                          if (dddValue.length === 2) {
                            setDddOptions(prev => {
                              const exists = prev.some(opt => opt.value === dddValue);
                              if (!exists) {
                                return [...prev, { key: dddValue, text: dddValue, value: dddValue }];
                              }
                              return prev;
                            });
                            setLinhaAtual(prev => ({ ...prev, ddd: dddValue }));
                          }
                        }}
                        onChange={(e, { value }) => {
                          const dddValue = String(value).replace(/\D/g, '').slice(0, 2);
                          setLinhaAtual(prev => ({ ...prev, ddd: dddValue }));
                        }}
                        style={{ width: '100%' }}
                      />
                      <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                        Selecione ou digite qualquer DDD de 2 dígitos
                      </div>
                    </>
                  )}
                </Form.Field>
              </Grid.Column>
            )}

            {linhaAtual.negociacao === 'Renegociação' && (
              <>
                <Grid.Column width={4}>
                  <Form.Field>
                    <label>DDD</label>
                    <Dropdown
                      placeholder="Selecione ou digite o DDD"
                      selection
                      search
                      allowAdditions
                      additionLabel="Usar DDD: "
                      value={linhaAtual.ddd || ''}
                      options={dddOptions}
                      onAddItem={(e, { value }) => {
                        // Validar que o valor adicionado é um DDD válido (2 dígitos numéricos)
                        const dddValue = String(value).replace(/\D/g, '').slice(0, 2);
                        if (dddValue.length === 2) {
                          // Adicionar o novo DDD às opções se ainda não existir
                          setDddOptions(prev => {
                            const exists = prev.some(opt => opt.value === dddValue);
                            if (!exists) {
                              return [...prev, { key: dddValue, text: dddValue, value: dddValue }];
                            }
                            return prev;
                          });
                          // Atualizar o estado com o novo DDD
                          setLinhaAtual(prev => ({ 
                            ...prev, 
                            ddd: dddValue
                          }));
                        }
                      }}
                      onChange={(e, { value }) => {
                        const dddValue = String(value).replace(/\D/g, '').slice(0, 2);
                        if (dddValue.length === 2) {
                          setLinhaAtual(prev => ({ 
                            ...prev, 
                            ddd: dddValue
                          }));
                        }
                      }}
                      style={{
                        width: '100%'
                      }}
                    />
                    {selectedClient && (
                      <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                        DDDs sugeridos da região de {selectedClient.estado} {fastchipFlowEnabled ? '(Fastchip)' : ''} - ou digite qualquer DDD de 2 dígitos
                      </div>
                    )}
                  </Form.Field>
                </Grid.Column>
                <Grid.Column width={4}>
                  <Form.Field required>
                    <label>Número</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="999999999"
                      value={renegociacaoLocalDigitsFromFormField(linhaAtual.ddd || '', linhaAtual.numero || '')}
                      onChange={(e) => {
                        const update = parseRenegociacaoNumeroFieldInput(linhaAtual.ddd || '', e.target.value);
                        if (update.ddd) {
                          const extractedDdd = update.ddd;
                          setDddOptions((prev) => {
                            const exists = prev.some((opt) => opt.value === extractedDdd);
                            if (exists) return prev;
                            return [...prev, { key: extractedDdd, text: extractedDdd, value: extractedDdd }];
                          });
                        }
                        setLinhaAtual((prev) => ({
                          ...prev,
                          ...(update.ddd ? { ddd: update.ddd } : {}),
                          numero: update.numero,
                        }));
                      }}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                      Digite apenas o número (até 9 dígitos, sem DDD) - DDD deve ser selecionado no campo acima
                    </div>
                  </Form.Field>
                </Grid.Column>
              </>
            )}

            {linhaAtual.negociacao === 'Aditivo' && fastchipFlowEnabled && (
              <Grid.Column width={2}>
                <Form.Field>
                  <label>e-SIM</label>
                  <div style={{ paddingTop: '8px' }}>
                    <Popup
                      disabled={esimToggleAvailable}
                      content={esimUnavailableMessage}
                      trigger={
                        <span style={{ display: 'inline-block' }}>
                          <Checkbox
                            toggle
                            disabled={!esimToggleAvailable}
                            checked={linhaAtual.isEsim ?? false}
                            onChange={(_, data) => {
                              if (!esimToggleAvailable) return;
                              setLinhaAtual(prev => ({
                                ...prev,
                                isEsim: data.checked ?? false,
                                chipCcid: '',
                              }));
                            }}
                            label={linhaAtual.isEsim ? 'Sim' : 'Não'}
                          />
                        </span>
                      }
                    />
                  </div>
                  <div style={{ fontSize: '11px', color: '#6f42c1', marginTop: '4px' }}>
                    Chip virtual via QR Code
                  </div>
                  {!esimToggleAvailable && (
                    <div style={{ fontSize: '11px', color: '#856404', marginTop: '4px' }}>
                      {esimUnavailableMessage}
                    </div>
                  )}
                </Form.Field>
              </Grid.Column>
            )}

            {linhaAtual.negociacao === 'Aditivo' && (
              <Grid.Column width={4}>
                <Form.Field required={fastchipFlowEnabled}>
                  <label>CCID {fastchipFlowEnabled ? '*' : ''}</label>
                  {!fastchipFlowEnabled ? (
                    <div style={{ 
                      width: '100%', 
                      padding: '10px', 
                      border: '1px solid #ccc', 
                      borderRadius: '4px', 
                      fontSize: '14px',
                      backgroundColor: '#f5f5f5',
                      color: '#666',
                      cursor: 'not-allowed'
                    }}>
                      ICCID não é necessário para Aditivo sem Fastchip (chip vem da operadora)
                    </div>
                  ) : backendFastchipAllocationMode ? (
                    <div style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #17a2b8',
                      borderRadius: '4px',
                      fontSize: '14px',
                      color: '#0c5460',
                      backgroundColor: '#d1ecf1'
                    }}>
                      <Icon name="server" />
                      ICCID será alocado no backend ao salvar (SKIP LOCKED)
                    </div>
                  ) : iccidsLoading ? (
                    <div style={{ 
                      width: '100%', 
                      padding: '10px', 
                      border: '1px solid #ccc', 
                      borderRadius: '4px', 
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Loader active inline size="small" />
                      <span style={{ marginLeft: '8px' }}>Carregando ICCIDs...</span>
                    </div>
                  ) : iccidsError ? (
                    <div style={{ 
                      width: '100%', 
                      padding: '10px', 
                      border: '1px solid #dc3545', 
                      borderRadius: '4px', 
                      fontSize: '14px',
                      color: '#dc3545',
                      backgroundColor: '#f8d7da'
                    }}>
                      <div style={{ marginBottom: '8px' }}>
                        Erro ao carregar ICCIDs: {iccidsError}
                      </div>
                      <Button
                        size="tiny"
                        icon="redo"
                        content={iccidsTransientError ? 'Tentar novamente' : 'Recarregar ICCIDs'}
                        onClick={() => void retryLoadIccids()}
                        loading={iccidsLoading}
                        disabled={iccidsLoading}
                      />
                    </div>
                  ) : !selectedClient ? (
                    <div style={{ 
                      width: '100%', 
                      padding: '10px', 
                      border: '1px solid #ffc107', 
                      borderRadius: '4px', 
                      fontSize: '14px',
                      color: '#856404',
                      backgroundColor: '#fff3cd'
                    }}>
                      Selecione um cliente primeiro para carregar ICCIDs disponíveis
                    </div>
                  ) : availableICcIdsForLine.length === 0 ? (
                    <div style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ffc107',
                      borderRadius: '4px',
                      fontSize: '14px',
                      color: '#856404',
                      backgroundColor: '#fff3cd'
                    }}>
                      {linhaAtual.isEsim
                        ? esimUnavailableMessage
                        : `Nenhum ICCID disponível para ${
                            resolveRjMgEsFastchipIccidSearch(selectedClient.estado)
                              ? formatRjMgEsFastchipClusterUiLabel(selectedClient.estado)
                              : selectedClient.estado
                          }`}
                    </div>
                  ) : linhaAtual.negociacao === 'Aditivo' ? (
                    <div style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #28a745',
                      borderRadius: '4px',
                      fontSize: '14px',
                      color: '#155724',
                      backgroundColor: '#d4edda'
                    }}>
                      <Icon name="check circle" />
                      CCID será selecionado automaticamente ({availableICcIdsForLine.length} disponível{availableICcIdsForLine.length !== 1 ? 'is' : ''})
                      {linhaAtual.isEsim && <span style={{ marginLeft: '8px', color: '#6f42c1', fontWeight: 'bold' }}>e-SIM</span>}
                    </div>
                  ) : null}
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                    {backendFastchipAllocationMode
                      ? 'Gerenciado no backend. O frontend não busca nem exibe ICCIDs neste modo.'
                      : (fastchipFlowEnabled ? 'Obrigatório para Aditivo com Fastchip' : 'Não necessário para Aditivo sem Fastchip')}
                    {selectedClient && fastchipFlowEnabled && (
                      resolveRjMgEsFastchipIccidSearch(selectedClient.estado)
                        ? ` - ${formatRjMgEsFastchipClusterUiLabel(selectedClient.estado)} (${
                            resolveRjMgEsFastchipIccidSearch(selectedClient.estado)?.usesDhCluster
                              ? 'usa DDDs de MG'
                              : 'usa DDDs de SP'
                          })`
                        : ` - Disponível para ${selectedClient.estado}`
                    )}
                  </div>
                  {fastchipFlowEnabled && !editingContractId && (
                    <Button
                      basic
                      color="orange"
                      size="tiny"
                      style={{ marginTop: '8px' }}
                      onClick={() => {
                        const confirmConvert = window.confirm(
                          'Deseja converter este contrato para sem Fastchip? Isso removerá os ICCIDs das linhas Aditivo e ajustará o rascunho para entrega da operadora.'
                        );
                        if (confirmConvert) {
                          convertDraftToNonFastchip('Conversão manual solicitada.');
                        }
                      }}
                    >
                      Converter para sem Fastchip
                    </Button>
                  )}
                </Form.Field>
              </Grid.Column>
            )}

            <ContractLineHandsetFields
              catalog={handsetCatalog}
              value={{
                aparelho: linhaAtual.aparelho || '',
                tipoVenda: linhaAtual.tipoVenda || 'Sem aparelho',
                handsetDeviceId: linhaAtual.handsetDeviceId,
                aparelhoValorTotal: linhaAtual.aparelhoValorTotal,
                aparelhoValorMensal: linhaAtual.aparelhoValorMensal,
              }}
              onChange={(patch) => {
                const nextDraft = { ...linhaAtual, ...patch };
                if (isWttxLine(nextDraft)) {
                  nextDraft.negociacao = 'Aditivo';
                }
                if (fastchipFlowEnabled && lineHasAnyHandset(nextDraft)) {
                  setFastchip(false);
                  showWarning(
                    'Ao selecionar aparelho (incluindo WTTX), o Fastchip foi desmarcado. Nesses casos o envio é por entrega da operadora.'
                  );
                }
                setLinhaAtual(nextDraft);
                if (patch.aparelho !== undefined) {
                  filtrarPlanos(nextDraft.negociacao, patch.aparelho);
                }
              }}
            />
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={4}>
              <Form.Field required>
                <label>Plano</label>
                <select
                  value={linhaAtual.planId}
                  onChange={(e) => {
                    const selectedPlan = plansFiltrados.find(p => p.id === e.target.value);
                    setLinhaAtual(prev => ({ 
                      ...prev, 
                      planId: e.target.value,
                      plano: selectedPlan?.name || '',
                      valorMensal:
                        selectedPlan != null
                          ? String(resolvePlanTotalMonthlyPrice(selectedPlan.totalMonthlyPrice))
                          : ''
                    }));
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  disabled={plansLoading}
                >
                  <option value="">Selecione um plano...</option>
                  {plansFiltrados.map(plan => (
                    <option key={plan.id} value={plan.id}>
                      {formatContractPlanOptionLabel(plan)}
                    </option>
                  ))}
                </select>
                {plansLoading && (
                  <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                    <Icon name="spinner" loading />
                    Carregando planos...
                  </div>
                )}
                {!plansLoading && plansFiltrados.length === 0 && plans.length > 0 && (
                  <div style={{ marginTop: '5px', fontSize: '12px', color: '#ff6b6b' }}>
                    Nenhum plano disponível para {linhaAtual.negociacao}
                  </div>
                )}
                {!plansLoading && plans.length === 0 && (
                  <div style={{ marginTop: '5px', fontSize: '12px', color: '#ff6b6b' }}>
                    Nenhum plano disponível
                  </div>
                )}
              </Form.Field>
            </Grid.Column>

            <Grid.Column width={4}>
              <Form.Field required>
                <label>Valor Chip (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="7,00"
                  value={fastchipFlowEnabled ? "0.00" : linhaAtual.valorChip}
                  readOnly={fastchipFlowEnabled}
                  onChange={fastchipFlowEnabled ? undefined : (e) => setLinhaAtual(prev => ({ ...prev, valorChip: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: fastchipFlowEnabled ? '#f5f5f5' : 'white',
                    cursor: fastchipFlowEnabled ? 'not-allowed' : 'text'
                  }}
                />
                {fastchipFlowEnabled && (
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                    Valor fixo para Fastchip: R$ 0,00
                  </div>
                )}
              </Form.Field>
            </Grid.Column>

            <Grid.Column width={3}>
              <Form.Field required>
                <label>Valor Mensal (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="0,00"
                  value={linhaAtual.valorMensal}
                  readOnly
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: '#f5f5f5',
                    cursor: 'not-allowed'
                  }}
                />
                <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                  Valor preenchido automaticamente pelo plano
                </div>
              </Form.Field>
            </Grid.Column>

            <Grid.Column width={3}>
              <Form.Field required>
                <label>Quantidade</label>
                <QuantitySelector
                  value={linhaAtual.quantidade}
                  onChange={(value) => setLinhaAtual(prev => ({ ...prev, quantidade: value }))}
                  min={1}
                  max={99}
                />
                <div style={{ fontSize: '11px', color: '#666', marginTop: '8px' }}>
                  Número de linhas a serem criadas
                </div>
              </Form.Field>
            </Grid.Column>

          </Grid.Row>

          {linhaAtual.negociacao === 'Aditivo' && (
            <Grid.Row>
              <Grid.Column width={16}>
                <Form.Field>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <input
                      type="checkbox"
                      id="ehPortabilidade"
                      checked={linhaAtual.ehPortabilidade}
                      onChange={e => {
                        const checked = e.target.checked;
                        if (checked) {
                          const ultimaPortabilidade = [...linhas]
                            .reverse()
                            .find((l: any) => isPortabilidadeAditivoLine(l));
                          const copied = ultimaPortabilidade
                            ? copyPortabilidadeFieldsFromLine(ultimaPortabilidade)
                            : {};
                          setLinhaAtual(prev => ({
                            ...prev,
                            ehPortabilidade: true,
                            portabilidadeLinha: '',
                            ...copied,
                          }));
                        } else {
                          setLinhaAtual(prev => ({
                            ...prev,
                            ehPortabilidade: false,
                            portabilidadeNomeCompleto: '',
                            portabilidadeCpf: '',
                            portabilidadeOperadora: '',
                            portabilidadeLinha: '',
                            portabilidadeCnpjCedente: '',
                            portabilidadeRazaoSocialCedente: '',
                            portabilidadeRepresentantes: [],
                          }));
                        }
                      }}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <label htmlFor="ehPortabilidade" style={{ fontWeight: 'bold', color: '#2c3e50', fontSize: '15px', cursor: 'pointer', marginBottom: 0 }}>
                      É portabilidade?
                    </label>
                  </div>
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
          )}

          {linhaAtual.negociacao === 'Aditivo' &&
            linhaAtual.ehPortabilidade &&
            linhas.some((l: any) => isPortabilidadeAditivoLine(l)) && (
            <Grid.Row>
              <Grid.Column width={16}>
                <Form.Field>
                  <label style={{ fontSize: '13px' }}>Usar dados de portabilidade de…</label>
                  <select
                    value=""
                    onChange={(e) => {
                      const sourceId = e.target.value;
                      if (!sourceId) return;
                      const source = linhas.find((l: any) => l.id === sourceId);
                      if (!source || !isPortabilidadeAditivoLine(source)) return;
                      setLinhaAtual(prev => ({
                        ...prev,
                        ...copyPortabilidadeFieldsFromLine(source),
                      }));
                    }}
                    style={{ width: '100%', maxWidth: '420px', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                  >
                    <option value="">Selecione uma linha…</option>
                    {linhas.map((l: any, i) =>
                      isPortabilidadeAditivoLine(l) ? (
                        <option key={l.id ?? i} value={l.id}>
                          {[l.ddd, l.numero].filter(Boolean).join(' ') || `Linha ${i + 1}`} —{' '}
                          {l.portabilidadeNomeCompleto || l.portabilidadeRazaoSocialCedente || 'Portabilidade'}
                        </option>
                      ) : null
                    )}
                  </select>
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
          )}

          {linhaAtual.negociacao === 'Aditivo' && linhaAtual.ehPortabilidade && (
            <Grid.Row>
              <Grid.Column width={16}>
                <PortabilidadeDadosForm
                  linha={linhaAtual}
                  outrasLinhas={linhas}
                  onChange={(field, value) => setLinhaAtual((prev) => ({ ...prev, [field]: value }))}
                  onUnificarPortanteEmTodas={(source) => {
                    setLinhas((prev) => applyPortanteUnification(prev, source));
                    setLinhaAtual((prev) => ({
                      ...prev,
                      portabilidadeNomeCompleto: source.portabilidadeNomeCompleto ?? '',
                      portabilidadeCpf: source.portabilidadeCpf ?? '',
                    }));
                    showSuccess('Titular PF→CNPJ aplicado em todas as linhas compatíveis.');
                  }}
                />
              </Grid.Column>
            </Grid.Row>
          )}

          {linhaAtual.negociacao === 'Aditivo' && (
            <Grid.Row>
              <Grid.Column width={16}>
                <Form.Field required>
                  <label style={{ fontWeight: 'bold', color: '#2c3e50', marginBottom: '8px', display: 'block', fontSize: '15px' }}>
                    Será usada pelo cliente?
                  </label>
                  <div style={{ 
                    display: 'flex', 
                    gap: '10px',
                    border: linhaAtual.seraUsadaPeloCliente === null ? '2px solid #ffc107' : '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '8px',
                    backgroundColor: linhaAtual.seraUsadaPeloCliente === null ? '#fff3cd' : '#fff',
                    maxWidth: '400px'
                  }}>
                    <Button
                      type="button"
                      positive={linhaAtual.seraUsadaPeloCliente === true}
                      onClick={() => setLinhaAtual(prev => ({ ...prev, seraUsadaPeloCliente: true }))}
                      style={{
                        flex: 1,
                        fontWeight: linhaAtual.seraUsadaPeloCliente === true ? 'bold' : 'normal',
                        fontSize: '14px'
                      }}
                    >
                      Sim
                    </Button>
                    <Button
                      type="button"
                      negative={linhaAtual.seraUsadaPeloCliente === false}
                      onClick={() => setLinhaAtual(prev => ({ ...prev, seraUsadaPeloCliente: false }))}
                      style={{
                        flex: 1,
                        fontWeight: linhaAtual.seraUsadaPeloCliente === false ? 'bold' : 'normal',
                        fontSize: '14px'
                      }}
                    >
                      Não
                    </Button>
                  </div>
                  {linhaAtual.seraUsadaPeloCliente === null && (
                    <div style={{ fontSize: '11px', color: '#ffc107', marginTop: '4px', fontWeight: 'bold' }}>
                      ⚠️ Selecione uma opção (obrigatório)
                    </div>
                  )}
                  {linhaAtual.seraUsadaPeloCliente !== null && (
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                      {linhaAtual.seraUsadaPeloCliente ? '✓ Linha será usada pelo cliente' : '✓ Linha não será usada pelo cliente'}
                    </div>
                  )}
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
          )}

          <Grid.Row>
            <Grid.Column width={16}>
              <Button
                ref={addLinhaButtonRef}
                color="green"
                onClick={() => {
                    // Validação para renegociação - DDD e número são obrigatórios
                    if (linhaAtual.negociacao === 'Renegociação') {
                      if (!linhaAtual.ddd) {
                        showError('DDD é obrigatório para renegociação.');
                        return;
                      }
                      if (!linhaAtual.numero || linhaAtual.numero.length < 8) {
                        showError('Número é obrigatório para renegociação (mínimo 8 dígitos).');
                        return;
                      }
                    }
                    
                    // Validação para DDD quando negociação = Aditivo
                    if (linhaAtual.negociacao === 'Aditivo' && !linhaAtual.ddd) {
                      showError('DDD é obrigatório para linhas de Aditivo.');
                      return;
                    }
                    
                    // Validação para "Será usada pelo cliente" quando negociação = Aditivo (R2)
                    if (teamContractRules.requireSeraUsadaPeloCliente && linhaAtual.negociacao === 'Aditivo' && linhaAtual.seraUsadaPeloCliente === null) {
                      showError('Por favor, informe se a linha será usada pelo cliente antes de adicionar.');
                      return;
                    }
                    
                    // Validação para DDD válido - Aditivo: quando fastchip, exige DDD da lista do estado; quando não fastchip, exige 2 dígitos
                    if (linhaAtual.negociacao === 'Aditivo' && linhaAtual.ddd) {
                      if (fastchipFlowEnabled && selectedClient) {
                        const validDdds = getValidDddsWithICcIdFallback(selectedClient.estado, fastchipFlowEnabled, effectivePrCluster);
                        if (!validDdds.includes(linhaAtual.ddd)) {
                          const clusterMsg = selectedClient.estado === 'PR' && effectivePrCluster
                            ? ` (Cluster ${effectivePrCluster})`
                            : '';
                          alert(`DDD ${linhaAtual.ddd} não é válido para o estado ${selectedClient.estado} com Fastchip${clusterMsg}.`);
                          return;
                        }
                      } else if (!fastchipFlowEnabled && !isValidDdd(linhaAtual.ddd)) {
                        alert('DDD deve ter 2 dígitos.');
                        return;
                      }
                    }
                    
                    // Verificar estoque local apenas no modo legado (front atribui ICCID).
                    // No modo backend, estoque é validado no POST /contracts (409 INSUFFICIENT_FASTCHIP_STOCK).
                    if (
                      fastchipFlowEnabled &&
                      !backendFastchipAllocationMode &&
                      linhaAtual.negociacao === 'Aditivo'
                    ) {
                      const ccidsEmUsoNoDraft = new Set(
                        linhas
                          .filter((l) => l.negociacao === 'Aditivo' && l.chipCcid)
                          .map((l) => (l.chipCcid || '').trim())
                          .filter((ccid) => ccid.length > 0)
                      );
                      const disponiveisParaNovasLinhas = availableICcIdsForLine.filter(
                        (ic) => !ccidsEmUsoNoDraft.has((ic.ccId || '').trim())
                      ).length;
                      if (linhaAtual.quantidade > disponiveisParaNovasLinhas) {
                        const message = `Não há ICCIDs suficientes para ${linhaAtual.quantidade} linha(s). Disponíveis para novas linhas: ${disponiveisParaNovasLinhas}.`;
                        showWarning(message);
                        if (!editingContractId) {
                          const convertNow = window.confirm(
                            `${message}\n\nDeseja converter o contrato para sem Fastchip (entrega da operadora)?`
                          );
                          if (convertNow) {
                            convertDraftToNonFastchip('Estoque Fastchip insuficiente.');
                          }
                        }
                        return;
                      }
                    }

                    if (fastchipFlowEnabled && lineHasAnyHandset(linhaAtual)) {
                      alert(
                        'Linhas com aparelho (incluindo WTTX) devem ser por entrega da operadora. Desmarque Fastchip antes de adicionar.'
                      );
                      return;
                    }

                    if (linhaAtual.negociacao === 'Aditivo' && linhaAtual.ehPortabilidade) {
                      if (!skipPortabilidadeConflictCheckRef.current) {
                        const portErr = getPortabilidadeValidationError(linhaAtual);
                        if (portErr) {
                          showError(portErr);
                          return;
                        }
                        const conflict = detectPortabilidadeConflict(linhas, linhaAtual);
                        if (conflict.conflict) {
                          setPortabilidadeConflict(conflict);
                          setPortabilidadeConflictAction('add');
                          return;
                        }
                      } else {
                        skipPortabilidadeConflictCheckRef.current = false;
                      }
                    }

                    if (linhaAtual.ddd && linhaAtual.valorMensal && linhaAtual.planId) {
                    // Criar múltiplas linhas baseado na quantidade
                    const novasLinhas: any[] = [];
                    const ccidsUsados: string[] = linhas
                      .filter((l) => l.negociacao === 'Aditivo' && l.chipCcid)
                      .map((l) => (l.chipCcid || '').trim())
                      .filter((ccid) => ccid.length > 0); // Evitar duplicatas no draft + novas linhas
                    const linhasSemCcidAuto: string[] = [];

                    for (let i = 0; i < linhaAtual.quantidade; i++) {
                      // Para Renegociação, juntar DDD + número na hora de criar a linha
                      let dddToUse = linhaAtual.ddd;
                      let numeroToUse: string;
                      
                      if (linhaAtual.negociacao === 'Renegociação') {
                        numeroToUse = renegociacaoLocalDigitsFromFormField(dddToUse, linhaAtual.numero.trim());
                      } else {
                        // Para Aditivo, número não é usado
                        numeroToUse = '';
                      }

                      // CCID local só é usado no modo legado. No modo de alocação backend,
                      // não atribuimos ICCID no draft (será reservado no backend ao salvar).
                      let ccidParaLinha: string | undefined = undefined;
                      if (linhaAtual.negociacao === 'Aditivo' && fastchipFlowEnabled && !backendFastchipAllocationMode) {
                        // Usar availableICcIdsForLine para respeitar filtros de e-SIM e cluster PR
                        const pool = availableICcIdsForLine.filter(ic => !ccidsUsados.includes(ic.ccId));
                        if (pool.length > 0) {
                          ccidParaLinha = pool[Math.floor(Math.random() * pool.length)].ccId;
                        }

                        if (ccidParaLinha) {
                          ccidsUsados.push(ccidParaLinha);
                        }
                      }

                      const novaLinha = {
                        id: `linha-${Date.now()}-${i}`,
                        ...linhaAtual,
                        ddd: dddToUse,
                        numero: numeroToUse, // Armazenar apenas o número (sem DDD)
                        valorChip: parseFloat(linhaAtual.valorChip) || 0,
                        valorMensal: parseFloat(linhaAtual.valorMensal),
                        quantidade: 1, // Cada linha individual tem quantidade 1
                        ccid: ccidParaLinha,
                        chipCcid: ccidParaLinha,
                        sourceContractId: resolveSourceContractIdForNewLine(linhaAtual.negociacao),
                      };
                      if (fastchipFlowEnabled && !backendFastchipAllocationMode && linhaAtual.negociacao === 'Aditivo' && !ccidParaLinha) {
                        linhasSemCcidAuto.push(`linha ${i + 1} (DDD ${dddToUse || '--'})`);
                      }
                      novasLinhas.push(novaLinha);
                    }
                    
                    setLinhas(prev => [...prev, ...novasLinhas]);
                    if (fastchipFlowEnabled && !backendFastchipAllocationMode && linhasSemCcidAuto.length > 0) {
                      const message = `Algumas linhas ficaram sem ICCID automático (${linhasSemCcidAuto.join(', ')}).`;
                      showWarning(message);
                      if (!editingContractId) {
                        const convertNow = window.confirm(
                          `${message}\n\nDeseja converter o contrato para sem Fastchip (entrega da operadora)?`
                        );
                        if (convertNow) {
                          convertDraftToNonFastchip('Estoque Fastchip insuficiente.');
                        }
                      }
                    }
                    setLinhaAtual({
                      negociacao: 'Aditivo',
                      ddd: '',
                      numero: '',
                      tipoVenda: 'Sem aparelho',
                      planId: '',
                      plano: '',
                      aparelho: '',
                      valorChip: '7.00',
                      valorMensal: '',
                      chipCcid: '',
                      quantidade: 1,
                      seraUsadaPeloCliente: null,
                      ehPortabilidade: false,
                      portabilidadeNomeCompleto: '',
                      portabilidadeCpf: '',
                      portabilidadeOperadora: '',
                      portabilidadeLinha: '',
                      isEsim: false,
                    });
                  }
                }}
                  disabled={(() => {
                    const isAditivo = linhaAtual.negociacao === 'Aditivo';
                    const isRenegociacao = linhaAtual.negociacao === 'Renegociação';
                    
                    // Validações básicas
                    const semDdd = isAditivo && !linhaAtual.ddd;
                    const semValorMensal = !linhaAtual.valorMensal;
                    const semPlano = !linhaAtual.planId;
                    const semSeraUsadaPeloCliente = teamContractRules.requireSeraUsadaPeloCliente && isAditivo && (linhaAtual.seraUsadaPeloCliente === null || linhaAtual.seraUsadaPeloCliente === undefined);
                    
                    // Validação de Renegociação
                    const renegociacaoInvalida = isRenegociacao && (!linhaAtual.ddd || !linhaAtual.numero || linhaAtual.numero.length < 8);
                    
                    // Validação de Fastchip + Aditivo: verificar se há CCIDs disponíveis ao invés de exigir chipCcid preenchido
                    const fastchipSemCcidDisponivel =
                      fastchipFlowEnabled && !backendFastchipAllocationMode && isAditivo && availableICcIdsForLine.length === 0;
                    
                    // Validação de DDD por estado (apenas quando fastchip = true)
                    const dddInvalidoPorEstado =
                      fastchipFlowEnabled &&
                      isAditivo &&
                      linhaAtual.ddd &&
                      selectedClient &&
                      selectedClient.estado &&
                      !getValidDddsWithICcIdFallback(
                        selectedClient.estado,
                        fastchipFlowEnabled,
                        effectivePrCluster
                      ).includes(linhaAtual.ddd);
                    // Aditivo sem fastchip: DDD preenchido mas incompleto/inválido (ex.: 1 dígito)
                    const dddIncompletoOuInvalido = isAditivo && linhaAtual.ddd && !isValidDdd(linhaAtual.ddd);

                    // Validação de portabilidade obrigatória (R3)
                    const portabilidadeIncompleta =
                      isAditivo &&
                      linhaAtual.ehPortabilidade &&
                      isPortabilidadeIncompleta(linhaAtual, teamContractRules.requirePortabilidadeCompleta);
                    const fastchipComAparelho = fastchipFlowEnabled && lineHasAnyHandset(linhaAtual);
                    
                    const disabled = !!(
                      semDdd ||
                      semValorMensal ||
                      semPlano ||
                      semSeraUsadaPeloCliente ||
                      renegociacaoInvalida ||
                      fastchipSemCcidDisponivel ||
                      dddInvalidoPorEstado ||
                      dddIncompletoOuInvalido ||
                      portabilidadeIncompleta ||
                      fastchipComAparelho
                    );
                    
                    // Debug log
                    if (fastchipFlowEnabled && isAditivo) {
                    }
                    
                    return disabled;
                  })()}
              >
                <Icon name="plus" />
                Adicionar Linha
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Segment>

      {linhas.length === 0 && (
        <Message info>
          <Icon name="info circle" />
          Adicione pelo menos uma linha ao contrato para continuar.
        </Message>
      )}
    </ContractFormLinhasTab>
  );

  // Calcular data de desativação (120 dias a partir de hoje)
  const calcularDataDesativacao = () => {
    const data = new Date();
    data.setDate(data.getDate() + 120);
    return data.toISOString().split('T')[0]; // Formato YYYY-MM-DD
  };

  const toggleLinhaDesativar = (linhaId: string) => {
    setLinhasParaDesativar(prev => {
      const novoSet = new Set(prev);
      if (novoSet.has(linhaId)) {
        novoSet.delete(linhaId);
      } else {
        novoSet.add(linhaId);
      }
      return novoSet;
    });
  };

  const hasFastchipStockForSuspensionConversion = useCallback(
    (futureSuspendedCount: number) => {
      if (backendFastchipAllocationMode) return true;
      if (fastchipDisabled) return false;
      const aditivosNecessarios = calcularAditivosNecessarios(futureSuspendedCount);
      const ccidsEmUso = new Set(
        linhas
          .map((linha) => (linha.chipCcid || '').trim())
          .filter((ccid) => ccid.length > 0)
      );
      const ccidsDisponiveis = (availableICcIdsForLine || [])
        .map((ic) => (ic?.ccId || '').trim())
        .filter((ccid) => ccid.length > 0 && !ccidsEmUso.has(ccid));
      return ccidsDisponiveis.length >= aditivosNecessarios;
    },
    [backendFastchipAllocationMode, availableICcIdsForLine, fastchipDisabled, linhas]
  );

  // S5: converte o contrato em criacao para Fastchip de forma idempotente.
  // - Marca fastchip=true e temSuspensao=true.
  // - Atribui CCIDs aleatorios (sem duplicar) para Aditivos sem CCID.
  // - Zera valorChip dos Aditivos.
  // - Limpa DDDs invalidos para o estado do cliente.
  const convertContractToFastchip = useCallback(() => {
    setFastchip(true);
    setTemSuspensao(true);

    // Coletamos avisos para informar o operador (DDD invalido, sem CCID disponivel etc).
    // Importante: usar refs locais a esta closure porque setLinhas roda assincrono.
    const dddsLimposPorLinha: Array<{ id: string; numero?: string; dddAntigo: string }> = [];
    let aditivosSemCcid = 0;

    setLinhas(prev => {
      const ccidsEmUso = new Set<string>(
        prev
          .map((l) => (l.chipCcid || '').trim())
          .filter((c) => c.length > 0)
      );
      const disponiveis: string[] = backendFastchipAllocationMode
        ? []
        : (availableICcIds || [])
            .map((ic) => ((ic && (ic as { ccId?: string }).ccId) || '').trim())
            .filter((c: string) => c.length > 0 && !ccidsEmUso.has(c));
      if (!backendFastchipAllocationMode) {
        for (let i = disponiveis.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [disponiveis[i], disponiveis[j]] = [disponiveis[j], disponiveis[i]];
        }
      }

      const dddsValidos = clienteEstadoSafe()
        ? new Set(getValidDddsWithICcIdFallback(clienteEstadoSafe(), true))
        : null;

      const next = prev.map((linha) => {
        if (linha.negociacao !== 'Aditivo') return linha;

        const novo = { ...linha, valorChip: 0 };

        if (!backendFastchipAllocationMode && (!novo.chipCcid || novo.chipCcid.trim() === '')) {
          const candidato = disponiveis.shift();
          if (candidato) {
            novo.chipCcid = candidato;
            ccidsEmUso.add(candidato);
          } else {
            aditivosSemCcid++;
          }
        }

        // DDDs validos para Fastchip podem diferir dos validos sem Fastchip (ex.: PR perde
        // o DDD 46; RJ vira cluster MG). Em vez de zerar silenciosamente, registramos a
        // alteracao para alertar o usuario apos a conversao.
        if (dddsValidos && novo.ddd && !dddsValidos.has(novo.ddd)) {
          dddsLimposPorLinha.push({ id: novo.id || '', numero: novo.numero, dddAntigo: novo.ddd });
          novo.ddd = '';
        }

        return novo;
      });
      return next;
    });

    // Avisos pos-setLinhas. setTimeout garante que os toasts aparecam depois das mudancas
    // de tab/UI disparadas pelo modal de confirmacao.
    setTimeout(() => {
      if (dddsLimposPorLinha.length > 0) {
        const detalhes = dddsLimposPorLinha
          .map((l) => `linha ${l.numero || l.id.slice(0, 6)} (DDD ${l.dddAntigo})`)
          .join(', ');
        showError(
          `Atenção: ${dddsLimposPorLinha.length} linha(s) tinham DDD não permitido para Fastchip e foram limpas. Selecione um DDD válido em: ${detalhes}.`
        );
      }
      if (!backendFastchipAllocationMode && aditivosSemCcid > 0) {
        showError(
          `Atenção: ${aditivosSemCcid} linha(s) Aditivo ficaram sem CCID porque não há ICCIDs disponíveis suficientes. Atribua manualmente ou remova linhas.`
        );
      }
    }, 0);
  }, [backendFastchipAllocationMode, availableICcIds, getValidDddsWithICcIdFallback, setFastchip, setLinhas, setTemSuspensao]);

  function clienteEstadoSafe(): string {
    return (selectedClient?.estado || '').toUpperCase();
  }

  // S6: usado pelo modal e pelo wrapper de toggle. Aplica conversao + marca linhas para desativar.
  const confirmConvertToFastchipAndApply = useCallback(() => {
    if (fastchipDisabled || !pendingSuspensionCanConvert) {
      showError(
        'Fastchip indisponível para a região do cliente no momento. Não é possível marcar suspensão sem converter o contrato para Fastchip.'
      );
      setPendingSuspensionLinhaIds([]);
      setPendingManualSuspensionLine(null);
      setShowConvertToFastchipModal(false);
      return;
    }
    convertContractToFastchip();
    if (pendingManualSuspensionLine) {
      const { ddd, numero } = pendingManualSuspensionLine;
      const id = `manual-suspensao-${Date.now()}-${Math.random()}`;
      setLinhas((prev) => [
        ...prev,
        {
          id,
          negociacao: 'Renegociação',
          ddd,
          numero,
          tipoVenda: 'Sem aparelho',
          planId: RP_6GB_PLAN_ID,
          planName: RP_6GB_PLAN_NAME,
          aparelho: '',
          valorChip: 0,
          valorMensal: 0,
          chipCcid: '',
          quantidade: 1,
          excluirDoContrato: true,
          requerSuspensao: true,
          trocaLinha: false,
          meuTimLogin: '',
          meuTimSenha: '',
          protocolo: '',
          seraUsadaPeloCliente: null,
          ehPortabilidade: false,
          portabilidadeNomeCompleto: '',
          portabilidadeCpf: '',
          portabilidadeOperadora: '',
          portabilidadeLinha: '',
          ofertas: [],
          marcarParaDesativar: true,
          dataDesativacao: calcularDataDesativacao(),
        } as ContractLineFormItem,
      ]);
      setLinhasParaDesativar((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
    } else if (pendingSuspensionLinhaIds.length > 0) {
      setLinhasParaDesativar((prev) => {
        const next = new Set(prev);
        pendingSuspensionLinhaIds.forEach((id) => next.add(id));
        return next;
      });
    }
    setPendingSuspensionLinhaIds([]);
    setPendingManualSuspensionLine(null);
    setPendingSuspensionCanConvert(true);
    setShowConvertToFastchipModal(false);
    showSuccess('Contrato convertido para Fastchip e linhas marcadas para suspensão.');
  }, [
    backendFastchipAllocationMode,
    convertContractToFastchip,
    fastchipDisabled,
    pendingManualSuspensionLine,
    pendingSuspensionCanConvert,
    pendingSuspensionLinhaIds,
    setLinhas,
    setLinhasParaDesativar,
    showError,
    showSuccess,
  ]);

  const clearPendingSuspensionConversion = useCallback(() => {
    setShowConvertToFastchipModal(false);
    setPendingSuspensionLinhaIds([]);
    setPendingManualSuspensionLine(null);
    setPendingSuspensionCanConvert(true);
  }, []);

  const tryOpenSuspensionConversionModal = useCallback(
    (lineIds: string[], manualLine?: { ddd: string; numero: string }) => {
      if (fluxoSuspensaoLinhasBloqueado) {
        showError(mensagemOrientacaoSuspensao1056);
        return true;
      }
      if (shouldSkipFastchipConversionModal(hasRole('Admin'), editingContractId)) return false;
      const futureSuspendedCount = linhasParaDesativar.size + lineIds.length + (manualLine ? 1 : 0);
      if (fastchipDisabled || !hasFastchipStockForSuspensionConversion(futureSuspendedCount)) {
        showError(
          'Sem estoque Fastchip suficiente para converter o contrato. Não é possível marcar linhas para suspensão neste momento.'
        );
        return true;
      }
      setPendingManualSuspensionLine(manualLine ?? null);
      setPendingSuspensionLinhaIds(lineIds);
      setPendingSuspensionCanConvert(true);
      setShowConvertToFastchipModal(true);
      return true;
    },
    [
      fluxoSuspensaoLinhasBloqueado,
      mensagemOrientacaoSuspensao1056,
      editingContractId,
      fastchipDisabled,
      hasFastchipStockForSuspensionConversion,
      hasRole,
      linhasParaDesativar.size,
      showError,
    ]
  );

  // S6: wrapper para toggleLinhaDesativar — com fastchip=false abre modal de conversão,
  // exceto TEMPORÁRIO DH por entrega (pedido Eder — bater meta): marca direto sem converter.
  const toggleLinhaDesativarGuarded = useCallback(
    (linhaId: string) => {
      const jaSelecionada = linhasParaDesativar.has(linhaId);
      if (fluxoSuspensaoLinhasBloqueado && !jaSelecionada) {
        showError(mensagemOrientacaoSuspensao1056);
        return;
      }
      if (!fastchip && !jaSelecionada && !dhDeliverySuspensionAllowed) {
        if (tryOpenSuspensionConversionModal([linhaId])) return;
      }
      toggleLinhaDesativar(linhaId);
    },
    [
      fluxoSuspensaoLinhasBloqueado,
      mensagemOrientacaoSuspensao1056,
      dhDeliverySuspensionAllowed,
      fastchip,
      linhasParaDesativar,
      showError,
      toggleLinhaDesativar,
      tryOpenSuspensionConversionModal,
    ]
  );

  const setLinhasParaDesativarGuarded: React.Dispatch<React.SetStateAction<Set<string>>> = useCallback(
    (value) => {
      const novoSet =
        typeof value === 'function'
          ? (value as (prev: Set<string>) => Set<string>)(linhasParaDesativar)
          : value;
      const adicionadas = Array.from(novoSet).filter((id) => !linhasParaDesativar.has(id));
      if (fluxoSuspensaoLinhasBloqueado && adicionadas.length > 0) {
        showError(mensagemOrientacaoSuspensao1056);
        return;
      }
      if (!fastchip && adicionadas.length > 0 && !dhDeliverySuspensionAllowed) {
        if (tryOpenSuspensionConversionModal(adicionadas)) return;
      }
      setLinhasParaDesativar(novoSet);
    },
    [
      fluxoSuspensaoLinhasBloqueado,
      mensagemOrientacaoSuspensao1056,
      dhDeliverySuspensionAllowed,
      fastchip,
      linhasParaDesativar,
      setLinhasParaDesativar,
      showError,
      tryOpenSuspensionConversionModal,
    ]
  );

  useEffect(() => {
    if (!bloqueioSuspensaoNovosContratos) return;
    if (temSuspensao !== false) setTemSuspensao(false);
  }, [bloqueioSuspensaoNovosContratos, temSuspensao, setTemSuspensao]);

  useEffect(() => {
    if (!fluxoSuspensaoLinhasBloqueado) return;
    if (linhasParaDesativar.size === 0) return;
    setLinhasParaDesativar(new Set());
  }, [fluxoSuspensaoLinhasBloqueado, linhasParaDesativar.size, setLinhasParaDesativar]);

  // S7: mantém "tem suspensão?" sincronizado com a marcação de linhas na criação.
  useEffect(() => {
    if (editingContractId || bloqueioSuspensaoNovosContratos) return;
    const haSuspensa = linhas.some((l) => l.requerSuspensao === true || l.excluirDoContrato === true);
    if (haSuspensa && temSuspensao !== true) {
      setTemSuspensao(true);
    }
  }, [editingContractId, bloqueioSuspensaoNovosContratos, linhas, temSuspensao, setTemSuspensao]);

  // S7b: desmarca Fastchip quando a região não tem ICCIDs — só em criação nova.
  // Rascunhos (draftContractId) e edição preservam o valor salvo no contrato.
  useEffect(() => {
    if (editingContractId || draftContractId) return;
    if (backendFastchipAllocationMode) return;
    if (!fastchipDisabled || !fastchip) return;
    setFastchip(false);
  }, [
    editingContractId,
    draftContractId,
    backendFastchipAllocationMode,
    fastchipDisabled,
    fastchip,
    setFastchip,
  ]);

  useEffect(() => {
    ieBypassConfirmedRef.current = ieBypassConfirmed;
  }, [ieBypassConfirmed]);

  // S7c: auto-clear do bypass de IE quando contrato vira Fastchip.
  // Bypass de IE só faz sentido com fastchip=false. Se o contrato virou Fastchip
  // (manualmente, por suspensão, ou por S7), o bypass perde o propósito e deve ser
  // descartado para não vazar para o backend uma flag obsoleta.
  // Também limpamos quando o usuário troca de cliente (selectedClient muda) — caso
  // contrário o bypass de um cliente anterior fica grudado da memória.
  useEffect(() => {
    if (!ieBypassConfirmed && !ieBypassReason) return;
    if (fastchip) {
      setIeBypassConfirmed(false);
      setIeBypassReason('');
    }
  }, [fastchip, ieBypassConfirmed, ieBypassReason, setIeBypassConfirmed, setIeBypassReason]);

  // S7d: troca de cliente também invalida bypass anterior (cliente diferente, IE diferente).
  useEffect(() => {
    if (!ieBypassConfirmed && !ieBypassReason) return;
    setIeBypassConfirmed(false);
    setIeBypassReason('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient?.id]);

  // Atualizar linhas com flag de desativação
  useEffect(() => {
    if (linhas.length > 0) {
      setLinhas(prev => prev.map(linha => {
        const deveDesativar = linhasParaDesativar.has(linha.id ?? '');
        return {
          ...linha,
          marcarParaDesativar: deveDesativar,
          dataDesativacao: deveDesativar ? calcularDataDesativacao() : undefined,
          excluirDoContrato: deveDesativar,
          requerSuspensao: deveDesativar,
          trocaLinha: deveDesativar ? (linha.trocaLinha || false) : false
        };
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linhasParaDesativar]);

  // Ajustar automaticamente linhas aditivas baseado em linhas suspensas (R1)
  useEffect(() => {
    const size = linhasParaDesativar.size;
    if (linhas.length === 0) return;
    if (!teamContractRules.requireAditivosForSuspendedLines) return;

    const linhasSuspensas = size;
    const aditivosNecessarios = calcularAditivosNecessarios(linhasSuspensas);
    const linhasAditivoAtuais = linhas.filter((l) => l.negociacao === 'Aditivo');
    const aditivosAtuais = linhasAditivoAtuais.length;

    if (aditivosNecessarios <= aditivosAtuais) return;

    const aditivosParaAdicionar = aditivosNecessarios - aditivosAtuais;

    const appendAutoAditivos = (planosAditivo: PlanResponse[]) => {
      if (planosAditivo.length === 0) return;

      const planoMaisBarato = planosAditivo.reduce((prev, curr) =>
        curr.totalMonthlyPrice < prev.totalMonthlyPrice ? curr : prev
      );
      const validDdds = selectedClient?.estado
        ? getValidDddsWithICcIdFallback(
            selectedClient.estado,
            fastchip,
            effectivePrCluster
          )
        : [];
      const dddDefault =
        inferDddForSuspensionAutoAditivo(linhas, linhasParaDesativar) ||
        validDdds[0] ||
        '';

      setLinhas((prev) => {
        const ccidsEmUso = collectUsedIccids(prev);
        const novosAditivos: ContractLineFormItem[] = [];

        for (let i = 0; i < aditivosParaAdicionar; i++) {
          const chipCcid =
            fastchip && dddDefault
              ? pickRandomAvailableIccid(
                  dddDefault,
                  ccidsEmUso,
                  (line) =>
            buildIccidPoolForLineDraft({
              ddd: line.ddd ?? '',
              isEsim: line.isEsim,
            }),
                  false
                )
              : '';
          if (chipCcid) ccidsEmUso.add(chipCcid);

          const targetContractId = resolveSourceContractIdForNewLine('Aditivo');
          novosAditivos.push({
            id: `aditivo-auto-${Date.now()}-${Math.random()}-${i}`,
            negociacao: 'Aditivo',
            ddd: dddDefault,
            numero: '',
            tipoVenda: 'Sem aparelho',
            planId: planoMaisBarato.id,
            plano: planoMaisBarato.name,
            planName: planoMaisBarato.name,
            aparelho: '',
            valorChip: fastchip ? 0 : 7,
            valorMensal: planoMaisBarato.totalMonthlyPrice,
            chipCcid,
            quantidade: 1,
            ofertas: [],
            excluirDoContrato: false,
            requerSuspensao: false,
            trocaLinha: false,
            meuTimLogin: '',
            meuTimSenha: '',
            protocolo: '',
            seraUsadaPeloCliente: teamContractRules.requireSeraUsadaPeloCliente ? true : null,
            ehPortabilidade: false,
            portabilidadeNomeCompleto: '',
            portabilidadeCpf: '',
            portabilidadeOperadora: '',
            portabilidadeLinha: '',
            isEsim: false,
            sourceContractId: targetContractId,
          });
        }

        return [...prev, ...novosAditivos];
      });

      showSuccess(
        `${aditivosParaAdicionar} ${aditivosParaAdicionar === 1 ? 'linha aditiva foi adicionada' : 'linhas aditivas foram adicionadas'} automaticamente (${linhasSuspensas} ${linhasSuspensas === 1 ? 'linha suspensa requer' : 'linhas suspensas requerem'} ${aditivosNecessarios} ${aditivosNecessarios === 1 ? 'aditivo' : 'aditivos'})`
      );
    };

    const planosAditivoFromState = filterAditivoEligiblePlans(plans);
    if (planosAditivoFromState.length > 0) {
      appendAutoAditivos(planosAditivoFromState);
      return;
    }

    planService
      .getAllPlans()
      .then((allPlans) => {
        appendAutoAditivos(filterAditivoEligiblePlans(allPlans));
      })
      .catch((error) => {
        console.error('Erro ao buscar planos para adicionar aditivos:', error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linhasParaDesativar.size, teamContractRules.requireAditivosForSuspendedLines]);

  const handleSaveResponsibleConsultant = async () => {
    if (!viewingContract || !responsibleConsultantEditValue) return;
    if (!canEditConsultantInViewModal) {
      showError('Consultor responsável não pode ser alterado neste status do contrato.');
      return;
    }
    if (responsibleConsultantEditValue === viewingContract.userId) {
      setEditingResponsibleConsultant(false);
      setResponsibleConsultantEditValue('');
      return;
    }
    try {
      setSavingResponsibleConsultant(true);
      const updated = await contractService.updateResponsibleConsultant(
        viewingContract.id,
        responsibleConsultantEditValue,
      );
      setViewingContract(updated);
      setEditingResponsibleConsultant(false);
      setResponsibleConsultantEditValue('');
      showSuccess('Consultor responsável atualizado.');
      void loadContracts();
    } catch (error) {
      showError(
        error instanceof Error ? error.message : 'Erro ao atualizar consultor responsável.',
      );
    } finally {
      setSavingResponsibleConsultant(false);
    }
  };

  // Renderizar modal de visualização detalhada
  const renderViewContractModal = () => (
    <Modal
      open={viewModalOpen}
      onClose={() => {
        setViewModalOpen(false);
        setViewingContract(null);
        setEditingResponsibleConsultant(false);
        setResponsibleConsultantEditValue('');
      }}
      size="large"
      closeIcon
    >
      <Modal.Header>
        <Icon name="eye" />
        Detalhes do Contrato
        {viewingContract && (
          <>
            <span style={{ fontWeight: 'normal', marginLeft: '10px' }}>
              ID: {viewingContract.id}
            </span>
            {viewingContract.token?.trim() && (
              <Label color="brown" size="small" style={{ marginLeft: '10px', verticalAlign: 'middle' }}>
                Token: {formatContractTokenDisplayLabel(viewingContract.token, viewingContract.fastchip)}
              </Label>
            )}
          </>
        )}
      </Modal.Header>
      <Modal.Content scrolling>
        {loadingContractDetails ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Loader active size="large">Carregando detalhes do contrato...</Loader>
          </div>
        ) : viewingContract ? (
          <div>
            {/* Informações do Cliente */}
            <Segment>
              <Header as="h4">
                <Icon name="building" />
                Informações do Cliente
              </Header>
              <Grid stackable>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <p><strong>Razão Social:</strong> {viewingContract.client.razaoSocial}</p>
                    <p><strong>CNPJ:</strong> {viewingContract.clientCnpj}</p>
                    {viewingContract.client.nomeFantasia && (
                      <p><strong>Nome Fantasia:</strong> {viewingContract.client.nomeFantasia}</p>
                    )}
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <p><strong>Estado:</strong> {viewingContract.client.estado}</p>
                    <p><strong>Tipo:</strong> {viewingContract.client.tipoCliente}</p>
                    {viewingContract.client.segmento && (
                      <p><strong>Segmento:</strong> {viewingContract.client.segmento}</p>
                    )}
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Segment>

            {/* Informações do Contrato */}
            <Segment>
              <Header as="h4">
                <Icon name="file alternate" />
                Informações do Contrato
              </Header>
              <Grid stackable>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <p><strong>Tipo de Contratação:</strong> {viewingContract.tipoContratacao}</p>
                    <p><strong>Dia de Vencimento:</strong> {viewingContract.diaVencimento}</p>
                    <p><strong>Tipo de Fatura:</strong> {viewingContract.tipoFatura}</p>
                    <p><strong>Envio de Fatura:</strong> {viewingContract.envioFatura}</p>
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <p><strong>Status:</strong> 
                      {viewingStatusNorm !== null ? (
                        <Label color={
                          viewingStatusNorm === EContractStatus.Ativo ? 'green' :
                          viewingStatusNorm === EContractStatus.Cancelado ? 'red' :
                          viewingStatusNorm === EContractStatus.Suspenso ? 'orange' :
                          viewingStatusNorm === EContractStatus.Finalizado ? 'grey' :
                          viewingStatusNorm === EContractStatus.Assinado ? 'orange' :
                          viewingStatusNorm === EContractStatus.NaoAssinado ? 'orange' :
                          viewingStatusNorm === EContractStatus.EmAnalise ? 'yellow' :
                          viewingStatusNorm === EContractStatus.AprovadoCQV ? 'green' :
                          viewingStatusNorm === EContractStatus.RecusadoCQV ||
                          viewingStatusNorm === EContractStatus.RecusadoQualidade ||
                          viewingStatusNorm === EContractStatus.RecusadoCVP ||
                          viewingStatusNorm === EContractStatus.TrocaDeCarteiraNaoAutorizada ? 'red' :
                          viewingStatusNorm === EContractStatus.TrocaDeCarteiraSolicitada ? 'blue' :
                          viewingStatusNorm === EContractStatus.VerificandoCarteira ? 'teal' :
                          viewingStatusNorm === EContractStatus.TrocaDeCarteira ? 'yellow' :
                          viewingStatusNorm === EContractStatus.VtmeCriado ? 'blue' :
                          'teal'
                        } style={{ marginLeft: '8px' }}>
                          {getStatusText(viewingContract.status)}
                        </Label>
                      ) : backendFastchipAllocationMode ? (
                        <div
                          style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #17a2b8',
                            borderRadius: '4px',
                            fontSize: '14px',
                            color: '#0c5460',
                            backgroundColor: '#d1ecf1',
                            cursor: 'not-allowed'
                          }}
                        >
                          <Icon name="server" />
                          ICCID será alocado no backend ao salvar (não exibido no frontend)
                        </div>
                      ) : (
                        <Label color="grey" style={{ marginLeft: '8px' }}>
                          Sem Status
                        </Label>
                      )}
                    </p>
                    <p><strong>Data de Assinatura:</strong> {viewingContract.dataAssinatura ? new Date(viewingContract.dataAssinatura).toLocaleDateString('pt-BR') : 'Não informada'}</p>
                    <p><strong>Data de Venda:</strong> {viewingContract.dataVenda ? new Date(viewingContract.dataVenda).toLocaleDateString('pt-BR') : 'Não informada'}</p>
                    <p><strong>Valor Total Mensal:</strong> R$ {viewingContract.valorTotalMensal?.toFixed(2) || 'Calculado automaticamente'}</p>
                    <p><strong>Origem da Venda:</strong> {viewingContract.origemVenda}</p>
                  </Grid.Column>
                </Grid.Row>
                {(viewingContract.numeroP2B || viewingContract.comprometimentoMinimo || viewingContract.token) && (
                  <Grid.Row>
                    <Grid.Column width={8}>
                      {viewingContract.numeroP2B && <p><strong>Número P2B:</strong> {viewingContract.numeroP2B}</p>}
                      {viewingContract.comprometimentoMinimo && <p><strong>Comprometimento Mínimo:</strong> R$ {viewingContract.comprometimentoMinimo.toFixed(2)}</p>}
                    </Grid.Column>
                    <Grid.Column width={8}>
                      {viewingContract.token && (
                        <p>
                          <strong>Token:</strong>{' '}
                          {formatContractTokenDisplayLabel(viewingContract.token, viewingContract.fastchip)}
                        </p>
                      )}
                      <p><strong>Fastchip:</strong> {viewingContract.fastchip ? 'Sim' : 'Não'}</p>
                    </Grid.Column>
                  </Grid.Row>
                )}
              </Grid>
            </Segment>

            {/* Endereço de Entrega */}
            <Segment>
              <Header as="h4">
                <Icon name="map marker alternate" />
                Endereço de Entrega
              </Header>
              <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                {viewingContract.deliveryAddress ? (
                  <>
                    <p>
                      <strong>CEP:</strong> {viewingContract.deliveryAddress.cep}<br />
                      <strong>Endereço:</strong> {viewingContract.deliveryAddress.endereco},{' '}
                      {viewingContract.deliveryAddress.numero}
                      {viewingContract.deliveryAddress.complemento &&
                        ` - ${viewingContract.deliveryAddress.complemento}`}
                      <br />
                      <strong>Bairro:</strong> {viewingContract.deliveryAddress.bairro}<br />
                      <strong>Cidade:</strong> {viewingContract.deliveryAddress.cidade}<br />
                      <strong>Estado:</strong> {viewingContract.deliveryAddress.estado}
                    </p>
                    {viewingContract.deliveryAddress.referencia && (
                      <p>
                        <strong>Referência:</strong> {viewingContract.deliveryAddress.referencia}
                      </p>
                    )}
                  </>
                ) : (
                  <p>Não informado</p>
                )}
              </div>
            </Segment>

            {/* Responsável pela Auditoria */}
            <Segment>
              <Header as="h4">
                <Icon name="user secret" />
                Responsável pela Auditoria (Em ligação)
              </Header>
              {viewingContract.administrator ? (
                <Grid stackable>
                  <Grid.Row>
                    <Grid.Column width={8}>
                      <p>
                        <strong>Nome:</strong> {viewingContract.administrator.nomeCompleto}
                      </p>
                      <p>
                        <strong>CPF:</strong> {viewingContract.administrator.cpf}
                      </p>
                      <p>
                        <strong>RG:</strong> {viewingContract.administrator.rg}
                      </p>
                    </Grid.Column>
                    <Grid.Column width={8}>
                      {viewingContract.administrator.email && (
                        <p>
                          <strong>Email:</strong> {viewingContract.administrator.email}
                        </p>
                      )}
                      {viewingContract.administrator.telefone1 && (
                        <p>
                          <strong>Telefone:</strong> {viewingContract.administrator.telefone1}
                        </p>
                      )}
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              ) : (
                <p>Não informado</p>
              )}
            </Segment>

            {/* Linhas do Contrato */}
            {viewingContract.linhas && viewingContract.linhas.length > 0 && (
              <Segment>
                <Header as="h4">
                  <Icon name="list" />
                  Linhas do Contrato ({viewingContract.linhas.length})
                </Header>
                {viewingContract.linhas
                  .filter(linha => !linha.excluirDoContrato) // Filtrar linhas suspensas
                  .map((linha, index) => (
                  <div key={linha.id} style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                    <Header as="h5">Linha {index + 1}</Header>
                    <Grid stackable>
                      <Grid.Row>
                        <Grid.Column width={8}>
                          {linha.negociacao && <p><strong>Negociação:</strong> {linha.negociacao}</p>}
                          {linha.ddd && <p><strong>DDD:</strong> {linha.ddd}</p>}
                          {linha.numero && <p><strong>Número:</strong> {linha.numero}</p>}
                          {linha.tipoVenda && <p><strong>Tipo de Venda:</strong> {linha.tipoVenda}</p>}
                          {linha.plano && <p><strong>Plano:</strong> {linha.plano}</p>}
                        </Grid.Column>
                        <Grid.Column width={8}>
                          <p><strong>Valor Mensal:</strong> R$ {linha.valorMensal.toFixed(2)}</p>
                          {linha.valorChip && <p><strong>Valor Chip:</strong> R$ {linha.valorChip.toFixed(2)}</p>}
                          <p><strong>Quantidade:</strong> {linha.quantidade}</p>
                          {linha.aparelho && <p><strong>Aparelho:</strong> {linha.aparelho}</p>}
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </div>
                ))}
              </Segment>
            )}

            {/* Representantes */}
            {viewingContract.representatives && viewingContract.representatives.length > 0 && (
              <Segment>
                <Header as="h4">
                  <Icon name="users" />
                  Representantes Legais ({viewingContract.representatives.length})
                </Header>
                {viewingContract.representatives.map((rep, index) => (
                  <div key={rep.id} style={{ marginBottom: '10px', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                    <strong>{rep.nome}</strong> - CPF: {rep.cpf}
                    {rep.rg && ` | RG: ${rep.rg}`}
                    {rep.email && ` | E-mail: ${rep.email}`}
                    {rep.telefone && ` | Tel: ${formatPhoneProgressive(rep.telefone)}`}
                  </div>
                ))}
              </Segment>
            )}

            {/* Recebedores Autorizados */}
            {viewingContract.authorizedReceivers && viewingContract.authorizedReceivers.length > 0 && (
              <Segment>
                <Header as="h4">
                  <Icon name="user plus" />
                  Recebedores Autorizados ({viewingContract.authorizedReceivers.length})
                </Header>
                {viewingContract.authorizedReceivers.map((rec, index) => (
                  <div key={rec.id} style={{ marginBottom: '10px', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                    <strong>{rec.nome}</strong>
                    {rec.qualificacao && ` - ${rec.qualificacao}`}
                    <br />
                    <small>CPF: {rec.cpf}</small>
                    {rec.email && <span> | Email: {rec.email}</span>}
                  </div>
                ))}
              </Segment>
            )}

            {/* Testemunhas */}
            {viewingContract.witnesses && viewingContract.witnesses.length > 0 && (
              <Segment>
                <Header as="h4">
                  <Icon name="eye" />
                  Testemunhas ({viewingContract.witnesses.length})
                </Header>
                {viewingContract.witnesses.map((wit, index) => (
                  <div key={wit.id} style={{ marginBottom: '10px', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                    <strong>{wit.nome}</strong> - CPF: {wit.cpf}
                    {wit.rg && ` | RG: ${wit.rg}`}
                  </div>
                ))}
              </Segment>
            )}

            {/* Informações do Sistema */}
            <Segment color="grey">
              <Header as="h4">
                <Icon name="info circle" />
                Informações do Sistema
              </Header>
              <Grid stackable>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <p><strong>ID do Contrato:</strong> {viewingContract.id}</p>
                    <p>
                      <strong>Consultor responsável:</strong>{' '}
                      {viewingContract.user.nome} ({viewingContract.user.username})
                    </p>
                    {viewingContract.createdByUser && (
                      <p>
                        <strong>Cadastrador:</strong>{' '}
                        {viewingContract.createdByUser.nome} ({viewingContract.createdByUser.username})
                      </p>
                    )}
                    {canEditConsultantInViewModal && (
                      <div style={{ marginTop: '8px' }}>
                        {!editingResponsibleConsultant ? (
                          <Button
                            size="tiny"
                            onClick={() => {
                              setEditingResponsibleConsultant(true);
                              setResponsibleConsultantEditValue(viewingContract.userId);
                            }}
                          >
                            Alterar consultor responsável
                          </Button>
                        ) : (
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <ResponsibleConsultantSelect
                              value={responsibleConsultantEditValue}
                              onChange={setResponsibleConsultantEditValue}
                              options={consultantDropdownOptions}
                              loading={loadingConsultantOptions}
                              disabled={savingResponsibleConsultant}
                              onOpen={() => {
                                window.setTimeout(() => handleConsultantFieldInteraction(), 0);
                              }}
                              placeholder="Selecione..."
                              fluid={false}
                              style={{ minWidth: '280px' }}
                            />
                            <Button
                              primary
                              size="tiny"
                              loading={savingResponsibleConsultant}
                              disabled={
                                !responsibleConsultantEditValue || savingResponsibleConsultant
                              }
                              onClick={() => void handleSaveResponsibleConsultant()}
                            >
                              Salvar
                            </Button>
                            <Button
                              size="tiny"
                              disabled={savingResponsibleConsultant}
                              onClick={() => {
                                setEditingResponsibleConsultant(false);
                                setResponsibleConsultantEditValue('');
                              }}
                            >
                              Cancelar
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <p><strong>Criado em:</strong> {new Date(viewingContract.createdAt).toLocaleString('pt-BR')}</p>
                    <p><strong>Status:</strong>
                      {viewingStatusNorm !== null ? (
                        <Label color={
                          viewingStatusNorm === EContractStatus.Ativo ? 'green' :
                          viewingStatusNorm === EContractStatus.Cancelado ? 'red' :
                          viewingStatusNorm === EContractStatus.Suspenso ? 'orange' :
                          viewingStatusNorm === EContractStatus.Finalizado ? 'grey' :
                          viewingStatusNorm === EContractStatus.Assinado ? 'orange' :
                          viewingStatusNorm === EContractStatus.NaoAssinado ? 'orange' :
                          viewingStatusNorm === EContractStatus.EmAnalise ? 'yellow' :
                          viewingStatusNorm === EContractStatus.AprovadoCQV ? 'green' :
                          viewingStatusNorm === EContractStatus.RecusadoCQV ||
                          viewingStatusNorm === EContractStatus.RecusadoQualidade ||
                          viewingStatusNorm === EContractStatus.RecusadoCVP ||
                          viewingStatusNorm === EContractStatus.TrocaDeCarteiraNaoAutorizada ? 'red' :
                          viewingStatusNorm === EContractStatus.TrocaDeCarteiraSolicitada ? 'blue' :
                          viewingStatusNorm === EContractStatus.VerificandoCarteira ? 'teal' :
                          viewingStatusNorm === EContractStatus.TrocaDeCarteira ? 'yellow' :
                          viewingStatusNorm === EContractStatus.VtmeCriado ? 'blue' :
                          'teal'
                        } style={{ marginLeft: '8px' }}>
                          {getStatusText(viewingContract.status)}
                        </Label>
                      ) : (
                        <Label color="grey" style={{ marginLeft: '8px' }}>
                          Sem Status
                        </Label>
                      )}
                    </p>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Segment>
          </div>
        ) : (
          <Message error>
            <Message.Header>Erro</Message.Header>
            <p>Não foi possível carregar os detalhes do contrato.</p>
          </Message>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button
          onClick={() => {
            setViewModalOpen(false);
            setViewingContract(null);
          }}
        >
          <Icon name="close" />
          Fechar
        </Button>
        {viewingContract && (
          <>
            <Popup
              content={viewingStatusNorm === EContractStatus.Cancelado 
                ? "Não é possível baixar contratos cancelados" 
                : "Baixar todos os contratos"}
              trigger={
                <Button
                  color="green"
                  loading={isDownloadingContracts}
                  disabled={isDownloadingContracts || viewingStatusNorm === EContractStatus.Cancelado}
                  onClick={() => handleDownloadAllContracts(viewingContract.id)}
                >
                  <Icon name="download" />
                  Baixar Todos os Contratos (ZIP)
                </Button>
              }
            />
          </>
        )}
      </Modal.Actions>
    </Modal>
  );

  const docusignActiveContact = getDocusignActiveContact();
  const docusignSelectedExistingContact = getDocusignSelectedExistingContact();
  const docusignContactSelectOptions = [
    ...docusignContactOptions.map(contact => ({
      key: contact.id,
      value: contact.id,
      text: `${contact.label} - ${contact.recipientName}`
    })),
    {
      key: 'custom',
      value: 'custom',
      text: 'Preencher novo contato'
    }
  ];
  const docusignProgressColor = docusignProgress?.status === 'success'
    ? 'green'
    : docusignProgress?.status === 'error'
      ? 'red'
      : 'blue';
  const isDocusignGroupSend = docusignSendQueue.length > 1;

  const resolveDocusignModalProgress = (): RpaProgressUpdate | null => {
    const queue =
      postCreateDocusignQueue.length > 0
        ? postCreateDocusignQueue
        : docusignSendQueue.length > 0
          ? docusignSendQueue
          : docusignSelectedContract
            ? [docusignSelectedContract]
            : [];
    const trackingId = docusignTrackingContractIdRef.current;
    const trackingProgress = trackingId ? docusignProgressByContractId[trackingId] : null;
    if (
      trackingProgress &&
      (trackingProgress.status === 'error' || trackingProgress.status === 'success')
    ) {
      return trackingProgress;
    }
    if (
      docusignProgress &&
      (docusignProgress.status === 'error' || docusignProgress.status === 'success')
    ) {
      return docusignProgress;
    }
    for (const contract of queue) {
      const progress = docusignProgressByContractId[contract.id];
      if (progress && (progress.status === 'error' || progress.status === 'success')) {
        return progress;
      }
    }

    const processingCandidates: RpaProgressUpdate[] = [];
    if (trackingProgress && trackingProgress.status === 'processing') {
      processingCandidates.push(trackingProgress);
    }
    for (const contract of queue) {
      const progress = docusignProgressByContractId[contract.id];
      if (progress && progress.status === 'processing') {
        processingCandidates.push(progress);
      }
    }
    if (docusignProgress?.status === 'processing') {
      processingCandidates.push(docusignProgress);
    }
    if (processingCandidates.length > 0) {
      return processingCandidates.reduce((best, current) =>
        (current.progress ?? 0) >= (best.progress ?? 0) ? current : best
      );
    }

    if (trackingProgress) {
      return trackingProgress;
    }
    if (docusignProgress) {
      return docusignProgress;
    }
    for (const contract of queue) {
      const progress = docusignProgressByContractId[contract.id];
      if (progress) {
        return progress;
      }
    }
    return null;
  };

  const docusignModalProgress = resolveDocusignModalProgress();
  const showDocusignProgressSection = Boolean(docusignModalProgress);

  const renderDocusignModalRetryActions = () => {
    if (docusignShowCancelRetryButton) {
      return (
        <Button
          color="orange"
          icon="ban"
          content={
            docusignSending ? 'Cancelando e reiniciando...' : 'Cancelar e tentar novamente'
          }
          loading={docusignSending}
          disabled={docusignSending}
          onClick={handleCancelAndRetryDocusign}
          style={{ marginTop: '12px' }}
        />
      );
    }
    if (!docusignShowRetryButton) {
      return null;
    }
    return (
      <Button
        color="orange"
        icon="redo"
        content={docusignSending ? 'Tentando...' : 'Tentar novamente'}
        loading={docusignSending}
        disabled={docusignSending}
        onClick={handleRetryDocusignOnly}
        style={{ marginTop: '12px' }}
      />
    );
  };

  const renderDocusignModalProgressBar = (progress: RpaProgressUpdate) => (
    <>
      <Progress
        key={`docusign-modal-${progress.contractId}-${progress.stage}-${progress.progress}-${progress.status}-${progress.queuePosition ?? 0}`}
        percent={progress.progress}
        progress
        color={getDocusignProgressColor(progress)}
        active={progress.status === 'processing'}
        success={progress.status === 'success'}
        error={progress.status === 'error'}
      />
      {(progress.status === 'processing' || progress.status === 'error') && (
        <p
          style={{
            marginTop: '8px',
            marginBottom: 0,
            color: progress.status === 'error' ? '#db2828' : '#666',
            fontSize: '0.95em',
          }}
        >
          {progress.status === 'error'
            ? progress.message || 'Erro ao enviar para DocuSign.'
            : formatDocusignProgressStatusText(progress)}
        </p>
      )}
      {progress.status === 'processing' && isDocusignPriorityWaitPhase(progress) && (
        <Message warning size="small" style={{ marginTop: '8px' }}>
          <Icon name="clock outline" />
          {progress.message ||
            'Aguardando conclusão do envio em andamento; sua nova tentativa será a próxima.'}
        </Message>
      )}
      <DocusignPhaseTimeline phases={docusignPhaseTimeline} />
    </>
  );

  const getDocusignProgressColor = (progress: RpaProgressUpdate | null | undefined) => {
    if (progress?.status === 'success') return 'green';
    if (progress?.status === 'error') return 'red';
    return 'blue';
  };

  const canRestoreContracts = hasAnyRole(['Admin', 'TeamLeader']);

  const cancelledContractsOnPageCount = useMemo(() => {
    const items = contracts?.items ?? [];
    return items.filter(
      (contract: ContractListDto) =>
        normalizeContractStatus(contract.status) === EContractStatus.Cancelado
    ).length;
  }, [contracts?.items]);

  const handleToggleContractSelection = useCallback((contractId: string, checked: boolean) => {
    setSelectedContractsForRestore((prev) => {
      const next = new Set(prev);
      if (checked) next.add(contractId);
      else next.delete(contractId);
      return next;
    });
  }, []);

  const handleToggleAllContractSelection = useCallback((checked: boolean, visibleIds: string[]) => {
    setSelectedContractsForRestore((prev) => {
      const next = new Set(prev);
      visibleIds.forEach((contractId) => {
        if (checked) next.add(contractId);
        else next.delete(contractId);
      });
      return next;
    });
  }, []);

  const handleRestoreModalCompleted = useCallback(
    async (result: { restoredContracts: number; failedContracts: number }) => {
      setSelectedContractsForRestore(new Set());
      showSuccess(
        `Restauração concluída: ${result.restoredContracts} restaurado(s), ${result.failedContracts} falha(s).`
      );
      await loadContracts(currentPage);
    },
    [currentPage, loadContracts, showSuccess]
  );

  const handleOpenRestoreModal = useCallback(async () => {
    if (selectedContractsForRestore.size === 0) return;

    const selected = (contracts?.items ?? []).filter((c: ContractListDto) =>
      selectedContractsForRestore.has(c.id)
    );
    if (selected.length === 0) return;

    try {
      setRestoreWindowChecking(true);
      const { expired, eligible } = await checkRestoreWindowForContracts(selected);

      if (expired.length === 0) {
        setRestoreModalContracts(selected);
        setShowRestoreModal(true);
        return;
      }

      setRestoreWindowExpiredItems(expired);
      setRestoreWindowEligibleContracts(eligible);
      setShowRestoreWindowAlert(true);
    } catch (err: unknown) {
      showError(
        err instanceof Error ? err.message : 'Erro ao validar elegibilidade para restauração.'
      );
    } finally {
      setRestoreWindowChecking(false);
    }
  }, [contracts?.items, selectedContractsForRestore, showError]);

  const handleContinueRestoreWithEligible = useCallback(() => {
    setShowRestoreWindowAlert(false);
    if (restoreWindowEligibleContracts.length === 0) return;
    setRestoreModalContracts(restoreWindowEligibleContracts);
    setShowRestoreModal(true);
  }, [restoreWindowEligibleContracts]);

  const docusignBackgroundInfoMessage = (
    <Message info style={{ marginTop: '12px' }}>
      <Icon name="info circle" />
      Você pode fechar esta janela e continuar trabalhando. O envio para o DocuSign continua em
      segundo plano — acompanhe o status na listagem de contratos.
    </Message>
  );

  return (
    <GlassPageWrapper title="Gerenciamento de Contratos" icon="file alternate">

      <Grid>
        <Grid.Column width={16}>
          <div className="contract-header-mobile" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <Button
                primary
                size="large"
                icon="plus"
                content="Novo Contrato"
                onClick={handleNewContractClick}
                loading={checkingPendingContracts}
                disabled={checkingPendingContracts}
                style={getDefaultButtonStyle()}
                {...getButtonHoverEffects('default')}
              />
              <Button
                secondary
                icon="refresh"
                content="Atualizar"
                onClick={() => {
                  void loadContracts(currentPage);
                  void reloadFastchipStockBalance();
                }}
                loading={contractsLoading}
                style={getDefaultButtonStyle()}
                {...getButtonHoverEffects('default')}
              />
            </div>
            {contracts && (
              <div style={{ fontSize: '1.1em', color: '#666', whiteSpace: 'nowrap' }}>
                Total: <strong>{contracts.totalCount}</strong> contratos
              </div>
            )}
          </div>
        </Grid.Column>
      </Grid>

      <ContractsFastchipStockSummary balance={fastchipStockBalance} loading={fastchipStockLoading} />

      <ContractFilters
        clientSearchFilter={clientSearchFilter}
        setClientSearchFilter={setClientSearchFilter}
        tipoContratacaoFilter={tipoContratacaoFilter}
        setTipoContratacaoFilter={setTipoContratacaoFilter}
        valorMinimoFilter={valorMinimoFilter}
        setValorMinimoFilter={setValorMinimoFilter}
        valorMaximoFilter={valorMaximoFilter}
        setValorMaximoFilter={setValorMaximoFilter}
        dataInicioFilter={dataInicioFilter}
        setDataInicioFilter={setDataInicioFilter}
        dataFimFilter={dataFimFilter}
        setDataFimFilter={setDataFimFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        temTrocaLinhaTriFilter={temTrocaLinhaTriFilter}
        setTemTrocaLinhaTriFilter={setTemTrocaLinhaTriFilter}
        temPendenciaSuspensaoTriFilter={temPendenciaSuspensaoTriFilter}
        setTemPendenciaSuspensaoTriFilter={setTemPendenciaSuspensaoTriFilter}
        temAparelhoCatalogoTriFilter={temAparelhoCatalogoTriFilter}
        setTemAparelhoCatalogoTriFilter={setTemAparelhoCatalogoTriFilter}
        effectiveClustersFilter={effectiveClustersFilter}
        setEffectiveClustersFilter={setEffectiveClustersFilter}
        userNameFilter={userNameFilter}
        setUserNameFilter={setUserNameFilter}
        enderecoCidadeFilter={enderecoCidadeFilter}
        setEnderecoCidadeFilter={setEnderecoCidadeFilter}
        enderecoEstadoFilter={enderecoEstadoFilter}
        setEnderecoEstadoFilter={setEnderecoEstadoFilter}
        administratorNomeFilter={administratorNomeFilter}
        setAdministratorNomeFilter={setAdministratorNomeFilter}
        searchTermFilter={searchTermFilter}
        setSearchTermFilter={setSearchTermFilter}
        showAdvancedFilters={showAdvancedFilters}
        setShowAdvancedFilters={setShowAdvancedFilters}
        applyFilters={applyFilters}
        clearFilters={clearFilters}
        contractsLoading={contractsLoading}
        canViewAdvancedFilters={canViewAdvancedFilters}
      />

      {canRestoreContracts && (
        <Segment
          style={{
            marginBottom: '16px',
            padding: '14px 18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '14px',
            borderLeft: `4px solid ${selectedContractsForRestore.size > 0 ? '#6435c9' : '#c4c4c4'}`,
            background:
              selectedContractsForRestore.size > 0
                ? 'rgba(100, 53, 201, 0.07)'
                : 'rgba(0, 0, 0, 0.02)',
          }}
        >
          <div style={{ minWidth: 0, flex: '1 1 240px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <Icon name="undo" color={selectedContractsForRestore.size > 0 ? 'violet' : 'grey'} />
              <strong style={{ fontSize: '1.05em' }}>
                {selectedContractsForRestore.size === 0
                  ? 'Nenhum contrato selecionado'
                  : `${selectedContractsForRestore.size} contrato${selectedContractsForRestore.size === 1 ? '' : 's'} selecionado${selectedContractsForRestore.size === 1 ? '' : 's'}`}
              </strong>
              {selectedContractsForRestore.size > 0 && (
                <Label color="violet" size="small">
                  pronto para restaurar
                </Label>
              )}
            </div>
            <p style={{ margin: '6px 0 0 28px', color: '#666', fontSize: '0.92em' }}>
              Marque contratos <strong>Cancelados</strong> na coluna Restaurar da tabela abaixo.
              Prazo automático: <strong>{RESTORE_CANCELLED_WINDOW_DAYS} dias</strong> após o cancelamento.
              {cancelledContractsOnPageCount === 0 && (
                <> Nenhum cancelado nesta página — use &quot;Ver cancelados&quot;.</>
              )}
            </p>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexWrap: 'wrap',
              flex: '0 0 auto',
            }}
          >
            <Button
              basic
              color="grey"
              icon="filter"
              content="Ver cancelados"
              onClick={() => {
                setShowAdvancedFilters(true);
                applyFilters({ status: EContractStatus.Cancelado });
              }}
              style={getDefaultButtonStyle()}
              {...getButtonHoverEffects('default')}
            />
            {selectedContractsForRestore.size > 0 && (
              <Button
                basic
                icon="times"
                content="Limpar seleção"
                onClick={() => setSelectedContractsForRestore(new Set())}
              />
            )}
            <Button
              color="violet"
              icon="undo"
              content="Restaurar"
              onClick={() => void handleOpenRestoreModal()}
              loading={restoreWindowChecking}
              disabled={selectedContractsForRestore.size === 0 || restoreWindowChecking}
              style={getDefaultButtonStyle()}
              {...getButtonHoverEffects('default')}
            />
          </div>
        </Segment>
      )}

      {/* Tabela de contratos */}
      <ContractTable
        contracts={contracts}
        contractsLoading={contractsLoading}
        contractsError={contractsError}
        onRetryLoad={retryLoadContracts}
        currentPage={currentPage}
        totalPages={contracts?.totalPages ?? 0}
        filters={filters}
        onPageChange={handlePageChange}
        expandedGroups={expandedGroups}
        onToggleGroup={toggleGroupExpansion}
        user={user}
        hasRole={hasRole}
        onViewLines={handleShowLinesModal}
        onOpenTracking={handleOpenTrackingModal}
        onEdit={handleEditClick}
        onContinueDraft={handleContinueDraft}
        onDeleteDraft={handleDeleteDraft}
        onDelete={handleDeleteContract}
        onStatusChange={handleChangeStatus}
        onDownloadAll={handleDownloadAllContracts}
        onDownloadGroup={handleDownloadGroupPdfs}
        onDuplicate={handleDuplicateContract}
        onSendToDocusign={handleOpenDocusignSendModal}
        onSendToDocusignFromMenu={handleOpenDocusignSendModalFromMenu}
        onSendEmail={handleSendContractEmail}
        onViewEditHistory={(contractId: string) => {
          setEditHistoryContractId(contractId);
          setShowEditHistoryModal(true);
        }}
        onMarkFastchipEnviado={handleMarkFastchipEnviado}
        onCancelPendingSuspensions={handleOpenCancelSuspensionModal}
        isDownloadingContracts={isDownloadingContracts}
        duplicatingContractId={duplicatingContractId}
        selectedContractIds={selectedContractsForRestore}
        onToggleContractSelection={handleToggleContractSelection}
        onToggleAllContractSelection={handleToggleAllContractSelection}
        showRestoreSelection={canRestoreContracts}
      />

      <ContractRollbackPreviewModal
        open={showRestoreModal}
        selectedContracts={restoreModalContracts}
        isAdmin={hasRole('Admin')}
        onClose={() => setShowRestoreModal(false)}
        onCompleted={handleRestoreModalCompleted}
      />

      <Modal
        open={showRestoreWindowAlert}
        onClose={() => setShowRestoreWindowAlert(false)}
        size="small"
      >
        <Modal.Header>
          <Icon name="warning sign" color="orange" />
          Restauração automática indisponível
        </Modal.Header>
        <Modal.Content>
          <Message warning>
            <Message.Header>Prazo de {RESTORE_CANCELLED_WINDOW_DAYS} dias excedido</Message.Header>
            <p>
              Contratos cancelados há mais de {RESTORE_CANCELLED_WINDOW_DAYS} dias não podem ser
              recuperados de maneira automática pelo sistema. Para esses casos, é necessário acionar
              suporte ou tratar manualmente fora deste fluxo.
            </p>
          </Message>
          <Segment secondary>
            <strong>Contrato(s) fora do prazo:</strong>
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              {restoreWindowExpiredItems.map((item) => (
                <li key={item.contractId} style={{ marginBottom: '6px' }}>
                  {item.clientRazaoSocial} — {formatCnpj(item.clientCnpj)}
                  {item.cancelledAt && (
                    <span style={{ color: '#666' }}>
                      {' '}
                      (cancelado em {new Date(item.cancelledAt).toLocaleDateString('pt-BR')})
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </Segment>
          {restoreWindowEligibleContracts.length > 0 && (
            <Message info size="small">
              {restoreWindowEligibleContracts.length} contrato(s) selecionado(s) ainda está(ão) dentro
              do prazo e pode(m) seguir no fluxo de restauração.
            </Message>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowRestoreWindowAlert(false)}>
            {restoreWindowEligibleContracts.length > 0 ? 'Cancelar' : 'Entendi'}
          </Button>
          {restoreWindowEligibleContracts.length > 0 && (
            <Button primary onClick={handleContinueRestoreWithEligible}>
              Continuar com {restoreWindowEligibleContracts.length} elegível(eis)
            </Button>
          )}
        </Modal.Actions>
      </Modal>

      <Modal open={docusignUiEnabled && showDocusignResendConfirm} onClose={handleCancelDocusignResend} size="small">
        <Modal.Header>
          <Icon name="warning sign" color="orange" />
          Reenviar pelo DocuSign?
        </Modal.Header>
        <Modal.Content>
          <Message warning>
            <Message.Header>Este contrato já possui envio ou tentativa pelo DocuSign</Message.Header>
            <p>Você pode enviar novamente; uma nova tentativa será registrada no histórico.</p>
          </Message>
          {docusignResendSummary && (
            <Segment secondary>
              <DocusignSendSummaryBlock summary={docusignResendSummary} />
            </Segment>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleCancelDocusignResend}>Cancelar</Button>
          <Button primary onClick={handleConfirmDocusignResend}>
            <Icon name="send" />
            Enviar novamente
          </Button>
        </Modal.Actions>
      </Modal>

      <Modal
        open={docusignUiEnabled && showDocusignGroupMenuConfirm}
        onClose={handleCancelDocusignGroupMenuConfirm}
        size="small"
      >
        <Modal.Header>
          <Icon name="warning sign" color="orange" />
          {docusignGroupMenuConfirmMode === 'all_sent'
            ? 'Contratos já enviados pelo DocuSign'
            : 'Envio parcial pelo DocuSign'}
        </Modal.Header>
        <Modal.Content>
          {docusignGroupMenuConfirmMode === 'all_sent' ? (
            <>
              <Message warning>
                <Message.Header>Todos os contratos deste grupo já foram enviados com sucesso.</Message.Header>
                <p>Deseja enviar novamente mesmo assim? Uma nova tentativa será registrada para cada contrato.</p>
              </Message>
              <Segment secondary>
                {sortContractsForVtmeGroupOrder(docusignGroupMenuMembers).map((member) => (
                  <p key={member.id} style={{ margin: '4px 0' }}>
                    <strong>{getContractTypeLabel(member)}:</strong> Enviado com sucesso
                  </p>
                ))}
              </Segment>
            </>
          ) : (
            <>
              <Message info>
                <Message.Header>Alguns contratos do grupo já foram enviados</Message.Header>
                <p>Escolha se deseja reenviar todos ou apenas os contratos pendentes.</p>
              </Message>
              <Segment secondary>
                {sortContractsForVtmeGroupOrder(docusignGroupMenuMembers).map((member) => {
                  const isSent = docusignGroupMenuSentMembers.some((sent) => sent.id === member.id);
                  return (
                    <p key={member.id} style={{ margin: '4px 0' }}>
                      <strong>{getContractTypeLabel(member)}:</strong>{' '}
                      {isSent ? 'Enviado com sucesso' : getPerContractDocusignSendStateLabel(
                          getPerContractDocusignSendState(member)
                        )}
                    </p>
                  );
                })}
              </Segment>
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleCancelDocusignGroupMenuConfirm}>Cancelar</Button>
          {docusignGroupMenuConfirmMode === 'all_sent' ? (
            <Button primary onClick={handleConfirmDocusignGroupMenuSendAll}>
              <Icon name="send" />
              Enviar novamente
            </Button>
          ) : (
            <>
              <Button onClick={handleConfirmDocusignGroupMenuSendPendingOnly}>
                <Icon name="arrow right" />
                Enviar apenas pendentes
              </Button>
              <Button primary onClick={handleConfirmDocusignGroupMenuSendAll}>
                <Icon name="send" />
                Enviar os dois
              </Button>
            </>
          )}
        </Modal.Actions>
      </Modal>

      <Modal
        open={docusignUiEnabled && showDocusignSendModal}
        onClose={handleCloseDocusignSendModal}
        closeOnDimmerClick={!docusignFieldsReadonly}
        size="small"
      >
        <Modal.Header>
          <Icon name="send" />
          {isDocusignGroupSend ? 'Enviar contratos pelo DocuSign' : 'Enviar contrato pelo DocuSign'}
        </Modal.Header>
        <Modal.Content>
          {docusignSelectedContract && (
            <Message info>
              <Message.Header>{docusignSelectedContract.clientRazaoSocial}</Message.Header>
              <p>
                CNPJ {formatCnpj(docusignSelectedContract.clientCnpj)}
                {isDocusignGroupSend
                  ? ` · ${docusignSendQueue.map((c) => getContractTypeLabel(c)).join(' + ')}`
                  : ` · Contrato ${docusignSelectedContract.tipoContratacao}`}
              </p>
              {isDocusignGroupSend && (
                <p style={{ marginTop: '8px' }}>
                  Cada contrato terá sua própria barra de progresso. Envios em paralelo conforme workers
                  disponíveis no RPA; contratos extras aguardam na fila com posição atualizada.
                </p>
              )}
              {!isDocusignGroupSend &&
                docusignSelectedContract &&
                (docusignSelectedContract as ContractListDto & { groupContracts?: ContractListDto[] })
                  .groupContracts &&
                (docusignSelectedContract as ContractListDto & { groupContracts?: ContractListDto[] })
                  .groupContracts!.length > 1 && (
                  <p style={{ marginTop: '8px' }}>
                    Reenvio parcial: apenas os contratos com erro ou pendentes serão enviados (
                    {docusignSendQueue.map((c) => getContractTypeLabel(c)).join(', ')}).
                  </p>
                )}
            </Message>
          )}

          {docusignModalPriorSend && (
            <Message warning>
              <Message.Header>
                {normalizeDocusignSendStatus(docusignModalPriorSend.status, docusignModalPriorSend.isProcessing) ===
                EDocusignSendHistoryStatus.Success
                  ? 'Este contrato já foi enviado pelo DocuSign'
                  : 'Este contrato já possui tentativa de envio pelo DocuSign'}
              </Message.Header>
              <DocusignSendSummaryBlock
                summary={docusignModalPriorSend}
                footerText="Você pode enviar novamente; uma nova tentativa será registrada no histórico."
              />
            </Message>
          )}

          {docusignLoadingContact ? (
            <Segment placeholder>
              <Dimmer active inverted>
                <Loader>Carregando contato padrão...</Loader>
              </Dimmer>
            </Segment>
          ) : (
            <Form>
              <Form.Select
                label="Contato que receberá o contrato"
                placeholder="Selecione o contato"
                options={docusignContactSelectOptions}
                value={docusignContactMode === 'custom' ? 'custom' : docusignSelectedContactId}
                disabled={docusignFieldsReadonly}
                onChange={(_, data) => {
                  const selectedValue = String(data.value || '');
                  if (selectedValue === 'custom') {
                    setDocusignContactMode('custom');
                    return;
                  }

                  setDocusignContactMode('existing');
                  setDocusignSelectedContactId(selectedValue);
                }}
              />

              {docusignContactMode === 'existing' && docusignSelectedExistingContact && (
                <Segment color="blue" secondary>
                  <Header as="h4" style={{ marginBottom: '12px' }}>
                    <Icon name="address card" />
                    Dados do contato selecionado
                    <Label color="blue" size="small" style={{ marginLeft: '8px' }}>
                      {docusignSelectedExistingContact.label}
                    </Label>
                  </Header>
                  <p><strong>Nome:</strong> {docusignSelectedExistingContact.recipientName}</p>
                  <p><strong>Telefone/WhatsApp:</strong> {docusignSelectedExistingContact.recipientPhone}</p>
                  <p><strong>E-mail:</strong> {docusignSelectedExistingContact.recipientEmail || 'Não informado'}</p>
                </Segment>
              )}

              {docusignContactMode === 'existing' && !docusignSelectedExistingContact && (
                <Message warning>
                  <Message.Header>Nenhum contato encontrado</Message.Header>
                  <p>Selecione “Preencher novo contato” para informar manualmente o destinatário do DocuSign.</p>
                </Message>
              )}

              {docusignContactMode === 'custom' && (
                <>
                  <Form.Input
                    label="Nome do destinatário"
                    value={docusignContactForm.recipientName}
                    readOnly={docusignFieldsReadonly}
                    disabled={docusignFieldsReadonly}
                    style={{ opacity: docusignFieldsReadonly ? 0.75 : 1 }}
                    onChange={(_, data) => setDocusignContactForm(prev => ({ ...prev, recipientName: String(data.value || '') }))}
                  />
                  <Form.Group widths="equal">
                    <Form.Input
                      label="Telefone/WhatsApp"
                      value={docusignContactForm.recipientPhone}
                      readOnly={docusignFieldsReadonly}
                      disabled={docusignFieldsReadonly}
                      style={{ opacity: docusignFieldsReadonly ? 0.75 : 1 }}
                      onChange={(_, data) => setDocusignContactForm(prev => ({ ...prev, recipientPhone: String(data.value || '') }))}
                    />
                    <Form.Input
                      label="E-mail (opcional)"
                      value={docusignContactForm.recipientEmail}
                      readOnly={docusignFieldsReadonly}
                      disabled={docusignFieldsReadonly}
                      style={{ opacity: docusignFieldsReadonly ? 0.75 : 1 }}
                      onChange={(_, data) => setDocusignContactForm(prev => ({ ...prev, recipientEmail: String(data.value || '') }))}
                    />
                  </Form.Group>
                </>
              )}
              {docusignContactMode === 'existing' && docusignFieldsReadonly && (
                <Form.Group widths="equal">
                  <Form.Input
                    label="Nome do destinatário"
                    value={docusignActiveContact.recipientName}
                    readOnly
                    disabled
                    style={{ opacity: 0.75 }}
                  />
                  <Form.Input
                    label="Telefone/WhatsApp"
                    value={docusignActiveContact.recipientPhone}
                    readOnly
                    disabled
                    style={{ opacity: 0.75 }}
                  />
                </Form.Group>
              )}
            </Form>
          )}

          {(docusignSending || docusignFieldsReadonly) && docusignBackgroundInfoMessage}

          {showDocusignProgressSection && docusignModalProgress && (
            <Segment>
              <Header as="h4">
                <Icon name="tasks" />
                Progresso do DocuSign
              </Header>
              {isDocusignGroupSend && docusignSendQueue.length > 1 && !docusignUnifiedGroupSendRef.current ? (
                docusignSendQueue.map((contract) => {
                  const progress = docusignProgressByContractId[contract.id];
                  if (!progress) {
                    return null;
                  }
                  return (
                    <div key={contract.id} style={{ marginBottom: '16px' }}>
                      <Header as="h5" style={{ marginBottom: '8px' }}>
                        {getContractTypeLabel(contract)}
                      </Header>
                      {renderDocusignModalProgressBar(progress)}
                    </div>
                  );
                })
              ) : (
                renderDocusignModalProgressBar(docusignModalProgress)
              )}
              {renderDocusignModalRetryActions()}
            </Segment>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleCloseDocusignSendModal}>
            <Icon name="close" />
            Fechar
          </Button>
          <Button
            primary
            icon="send"
            content={docusignSending ? 'Enviando...' : 'Enviar pelo DocuSign'}
            loading={docusignSending}
            disabled={docusignLoadingContact || docusignSending || docusignFieldsReadonly}
            onClick={handleSendSelectedContractToDocusign}
          />
        </Modal.Actions>
      </Modal>

      <CancelSuspensionModal
        open={cancelSuspensionModalOpen}
        lines={cancelSuspensionLines}
        contractLabel={cancelSuspensionContractInfo?.contractNumber || cancelSuspensionContractInfo?.id}
        loading={cancelSuspensionLoading}
        onClose={() => {
          setCancelSuspensionModalOpen(false);
          setCancelSuspensionLines([]);
          setCancelSuspensionContractInfo(null);
        }}
        onConfirm={handleConfirmCancelSuspensions}
      />

      {/* Modal de aviso de edição de contrato antigo */}
      {contractToEdit && (
        <EditContractWarningModal
          open={showEditWarningModal}
          onCancel={handleCancelEditAfterWarning}
          onConfirm={handleConfirmEditAfterWarning}
          contractCreatedAt={new Date(contractToEdit.createdAt)}
        />
      )}

      {/* Modal de criação/edição de contrato */}
      {alertaColeta ? (
        <ColetaAprovacaoAlerta
          fluxo={alertaColeta.fluxo}
          onAguardar={() => {
            alertaColeta.resolver(false);
            setAlertaColeta(null);
          }}
          onEnviar={() => {
            alertaColeta.resolver(true);
            setAlertaColeta(null);
          }}
        />
      ) : null}

      <ContractFormModal
        isOpen={isCreateModalOpen}
        onClose={() => handleCloseAttempt()}
        title={
          contractModalMode === 'linesOnly'
            ? 'Editar linhas do contrato'
            : editingContractId
              ? isEditingGroup
                ? 'Editar grupo'
                : 'Editar Contrato'
              : 'Novo Contrato'
        }
        subtitle={
          contractModalMode === 'linesOnly'
            ? (() => {
                const razao =
                  selectedClient?.razaoSocial || selectedContractInfo?.clientRazaoSocial || '';
                const cnpj = selectedClient?.cnpj || selectedContractInfo?.clientCnpj || '';
                const base = [razao, cnpj].filter(Boolean).join(' — ');
                if (isEditingGroup && editingGroupContracts && editingGroupContracts.length > 1) {
                  const aditivo = editingGroupContracts.find(
                    (c) => c.contractType === 'Aditivo' || c.tipoContratacao === 'Aditivo'
                  );
                  const reneg = editingGroupContracts.find(
                    (c) =>
                      c.contractType === 'Renegociação' || c.tipoContratacao === 'Renegociação'
                  );
                  const tokenParts: string[] = [];
                  if (aditivo?.token) {
                    tokenParts.push(
                      `Aditivo: ${formatContractTokenDisplayLabel(aditivo.token, aditivo.fastchip ?? fastchip)}`
                    );
                  }
                  if (reneg?.token) {
                    tokenParts.push(
                      `Reneg: ${formatContractTokenDisplayLabel(reneg.token, reneg.fastchip ?? fastchip)}`
                    );
                  }
                  if (tokenParts.length > 0) {
                    return base ? `${base} | ${tokenParts.join(' · ')}` : tokenParts.join(' · ');
                  }
                }
                return base || 'Edite as linhas e salve na aba Linhas a Desativar';
              })()
            : editingContractId
              ? isEditingGroup
                ? 'Edite as informações do grupo. Todas as linhas são exibidas; ao salvar, cada contrato receberá suas respectivas linhas.'
                : 'Edite as informações do contrato'
              : 'Preencha os dados do contrato passo a passo'
        }
        activeTab={activeTab}
        onTabChange={handleTabChange}
        actions={modalActions}
        tabs={modalTabs}
        tabValidationError={tabValidationError || undefined}
        duplicatedFromContractId={duplicatedFromContractId}
      >
        {renderTabContent()}
      </ContractFormModal>

      {/* Modal de confirmação de fechamento */}
      <Modal
        open={showCloseConfirmation}
        onClose={handleCancelClose}
        size="small"
        closeOnDimmerClick={false}
      >
        <Modal.Header>
          <Icon name="warning sign" />
          Confirmar Fechamento
        </Modal.Header>
        <Modal.Content>
          <p>
            <strong>Atenção!</strong> Você tem dados preenchidos no formulário que serão perdidos se fechar agora.
          </p>
          <p>
            Deseja realmente fechar o modal e perder todas as informações preenchidas?
          </p>
          <Message warning>
            <Icon name="info circle" />
            Esta ação não pode ser desfeita.
          </Message>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleCancelClose}>
            <Icon name="cancel" />
            Continuar Preenchendo
          </Button>
          <Button
            color="red"
            onClick={handleConfirmClose}
          >
            <Icon name="trash" />
            Fechar e Perder Dados
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Modal de visualização detalhada */}
      {renderViewContractModal()}

      {/* Modal de sucesso após criação do contrato */}
      <Modal
        open={showSuccessModal}
        onClose={() => handleClosePostCreateSuccessModal({ resetForm: false })}
        size="small"
        closeOnDimmerClick
        closeOnEscape
      >
        <Modal.Header>
          <Icon name="check circle" color="green" />
          Contrato Criado com Sucesso!
        </Modal.Header>
        <Modal.Content>
          <Message success>
            <Message.Header>Parabéns!</Message.Header>
            <p>O contrato foi criado com sucesso e está pronto para uso.</p>
            <p><strong>ID do Contrato:</strong> {createdContractId}</p>
          </Message>
          
          {/* Seção "Contrato Assinado?" - Para Sellers e TeamLeaders */}
          {(hasRole('Seller') || hasRole('TeamLeader')) && createdContractId && (
            <Segment style={{ marginTop: '20px', marginBottom: '20px' }}>
              <Header as="h4">
                <Icon name="check circle outline" />
                Contrato Assinado?
              </Header>
              <p>O cliente já assinou o contrato agora ou você irá assinar depois?</p>
              <Button.Group fluid>
                <Button
                  positive
                  icon="check"
                  content="Sim"
                  loading={postCreateAssinadoLoading}
                  disabled={
                    postCreateAssinadoLoading ||
                    postCreateAssinadoConcluido ||
                    (postCreateDocusignQueue.length > 0
                      ? !postCreateDocusignQueue.some((c) => contratoPendenteAssinatura(c.status))
                      : false)
                  }
                  onClick={() => void handlePostCreateConfirmarAssinado()}
                  style={{ marginRight: '10px' }}
                />
                <Button
                  icon="clock"
                  content="Assinar Depois"
                  disabled={
                    postCreateAssinadoLoading ||
                    postCreateAssinadoConcluido ||
                    (postCreateDocusignQueue.length > 0
                      ? !postCreateDocusignQueue.some((c) => contratoPendenteAssinatura(c.status))
                      : false)
                  }
                  onClick={() => {
                    showSuccess('O contrato permanecerá com status "Criado" e você receberá notificações para atualizar o status.');
                  }}
                />
              </Button.Group>
              {postCreateAssinadoFeedback === 'success' && (
                <Message positive style={{ marginTop: '12px', marginBottom: 0 }}>
                  <Icon name="check circle" />
                  {postCreateDocusignQueue.length > 1
                    ? 'Contratos assinados com sucesso.'
                    : 'Contrato assinado com sucesso.'}
                </Message>
              )}
              {postCreateAssinadoFeedback === 'error' && (
                <Message negative style={{ marginTop: '12px', marginBottom: 0 }}>
                  <Icon name="warning circle" />
                  Erro ao assinar contrato.
                </Message>
              )}
            </Segment>
          )}
          
          <p>Você pode baixar todos os documentos do contrato (Aditivo, Proposta Comercial e Renegociação) em um arquivo ZIP.</p>

          {docusignUiEnabled && createdContractId && !rpaStarted && (
            <Segment>
              <Header as="h4">
                <Icon name="send" />
                Envio para DocuSign
              </Header>
              <p>Selecione quem receberá o contrato e envie para processamento no DocuSign via RPA.</p>
              {postCreateDocusignQueue.length > 1 && (
                <Message info style={{ marginBottom: '12px' }}>
                  <Message.Header>Envio em grupo</Message.Header>
                  <p>
                    Será enviado 1 envelope DocuSign com os PDFs de{' '}
                    {postCreateDocusignQueue.length} contratos (
                    {postCreateDocusignQueue.map((c) => getContractTypeLabel(c)).join(' + ')}) na ordem
                    Aditivo e depois Renegociação.
                  </p>
                </Message>
              )}

              {docusignLoadingContact ? (
                <Segment placeholder>
                  <Dimmer active inverted>
                    <Loader>Carregando contatos...</Loader>
                  </Dimmer>
                </Segment>
              ) : (
                <Form style={{ marginBottom: '16px' }}>
                  <Form.Select
                    label="Contato que receberá o contrato"
                    placeholder="Selecione o contato"
                    options={docusignContactSelectOptions}
                    value={docusignContactMode === 'custom' ? 'custom' : docusignSelectedContactId}
                    disabled={isSendingToDocusign}
                    onChange={(_, data) => {
                      const selectedValue = String(data.value || '');
                      if (selectedValue === 'custom') {
                        setDocusignContactMode('custom');
                        return;
                      }

                      setDocusignContactMode('existing');
                      setDocusignSelectedContactId(selectedValue);
                    }}
                  />

                  {docusignContactMode === 'existing' && docusignSelectedExistingContact && (
                    <Segment color="blue" secondary>
                      <Header as="h4" style={{ marginBottom: '12px' }}>
                        <Icon name="address card" />
                        Dados do contato selecionado
                        <Label color="blue" size="small" style={{ marginLeft: '8px' }}>
                          {docusignSelectedExistingContact.label}
                        </Label>
                      </Header>
                      <p><strong>Nome:</strong> {docusignSelectedExistingContact.recipientName}</p>
                      <p><strong>Telefone/WhatsApp:</strong> {docusignSelectedExistingContact.recipientPhone}</p>
                      <p><strong>E-mail:</strong> {docusignSelectedExistingContact.recipientEmail || 'Não informado'}</p>
                    </Segment>
                  )}

                  {docusignContactMode === 'existing' && !docusignSelectedExistingContact && (
                    <Message warning>
                      <Message.Header>Nenhum contato encontrado</Message.Header>
                      <p>Selecione “Preencher novo contato” para informar manualmente o destinatário do DocuSign.</p>
                    </Message>
                  )}

                  {docusignContactMode === 'custom' && (
                    <>
                      <Form.Input
                        label="Nome do destinatário"
                        value={docusignContactForm.recipientName}
                        disabled={isSendingToDocusign}
                        onChange={(_, data) => setDocusignContactForm(prev => ({ ...prev, recipientName: String(data.value || '') }))}
                      />
                      <Form.Group widths="equal">
                        <Form.Input
                          label="Telefone/WhatsApp"
                          value={docusignContactForm.recipientPhone}
                          disabled={isSendingToDocusign}
                          onChange={(_, data) => setDocusignContactForm(prev => ({ ...prev, recipientPhone: String(data.value || '') }))}
                        />
                        <Form.Input
                          label="E-mail (opcional)"
                          value={docusignContactForm.recipientEmail}
                          disabled={isSendingToDocusign}
                          onChange={(_, data) => setDocusignContactForm(prev => ({ ...prev, recipientEmail: String(data.value || '') }))}
                        />
                      </Form.Group>
                    </>
                  )}
                </Form>
              )}

              <Button
                primary
                icon="send"
                content={
                  isSendingToDocusign || docusignSending
                    ? 'Enviando...'
                    : postCreateDocusignQueue.length > 1
                      ? 'Enviar contratos via DocuSign'
                      : 'Enviar via DocuSign'
                }
                loading={isSendingToDocusign || docusignSending}
                disabled={isSendingToDocusign || docusignSending || docusignLoadingContact}
                onClick={handleSendToDocusign}
                color="blue"
              />
            </Segment>
          )}

          {/* Passo a Passo para Próximas Etapas */}
          {createdContractId && !rpaStarted && (
            <Segment style={{ 
              marginTop: '20px',
              padding: '24px',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid #e0e0e0'
            }}>
              <Header as="h3" style={{ 
                marginBottom: '20px', 
                color: '#2c3e50',
                fontSize: '1.4em',
                fontWeight: 600
              }}>
                <Icon name="list ol" style={{ color: '#3498db' }} />
                Próximas Etapas
              </Header>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Passo DocuSign — oculto quando módulo desativado */}
                {docusignUiEnabled && (
                <div style={{ 
                  padding: '16px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef',
                  display: 'flex',
                  gap: '16px'
                }}>
                  <Icon 
                    name='upload' 
                    size='large'
                    style={{ color: '#3498db', flexShrink: 0 }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      color: '#2c3e50',
                      fontSize: '1.1em',
                      fontWeight: 600,
                      marginBottom: '8px'
                    }}>
                      1. Envio para DocuSign
                    </div>
                    <div style={{ 
                      color: '#6c757d',
                      lineHeight: '1.6'
                    }}>
                      <div style={{ marginBottom: '8px' }}>
                        Use o botão acima para envio automático via RPA ou, se necessário, suba o arquivo manualmente no DocuSign e configure o envio:
                      </div>
                      <ul style={{ 
                        marginLeft: '20px',
                        marginTop: '8px',
                        listStyleType: 'disc'
                      }}>
                        <li>Selecione <strong>envio para WhatsApp</strong></li>
                        <li>Insira o <strong>número do cliente</strong></li>
                        <li>Configure os <strong>campos personalizados</strong></li>
                        <li>Clique na opção <strong style={{ color: '#e74c3c' }}>SIGN_REPRESENTANTE</strong></li>
                      </ul>
                    </div>
                  </div>
                </div>
                )}

                {/* CQV */}
                <div style={{ 
                  padding: '16px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef',
                  display: 'flex',
                  gap: '16px'
                }}>
                  <Icon 
                    name='check circle' 
                    size='large'
                    style={{ color: '#27ae60', flexShrink: 0 }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      color: '#2c3e50',
                      fontSize: '1.1em',
                      fontWeight: 600,
                      marginBottom: '8px'
                    }}>
                      {docusignUiEnabled ? '2. Confirmação CQV' : '1. Confirmação CQV'}
                    </div>
                    <div style={{ 
                      color: '#6c757d',
                      lineHeight: '1.6'
                    }}>
                      Após a assinatura do contrato durante a ligação, o <strong>Analista de CQV</strong> irá realizar a confirmação da CQV no sistema.
                    </div>
                  </div>
                </div>

                {/* Passo 3: VTME */}
                <div style={{ 
                  padding: '16px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef',
                  display: 'flex',
                  gap: '16px'
                }}>
                  <Icon 
                    name='settings' 
                    size='large'
                    style={{ color: '#f39c12', flexShrink: 0 }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      color: '#2c3e50',
                      fontSize: '1.1em',
                      fontWeight: 600,
                      marginBottom: '8px'
                    }}>
                      {docusignUiEnabled ? '3. Processamento VTME' : '2. Processamento VTME'}
                    </div>
                    <div style={{ 
                      color: '#6c757d',
                      lineHeight: '1.6'
                    }}>
                      Realize o <strong>VTME do contrato</strong> para finalizar o processamento e ativação das linhas do cliente.
                    </div>
                  </div>
                </div>
              </div>

              <Message info style={{ 
                marginTop: '16px',
                backgroundColor: '#e8f4fd',
                borderLeft: '4px solid #3498db'
              }}>
                <Icon name='info circle' />
                <strong>Dica:</strong> Siga cada etapa na ordem para garantir um processamento correto do contrato.
              </Message>
            </Segment>
          )}

          {/* Progresso do envio DocuSign (um contrato ou grupo) */}
          {docusignUiEnabled && createdContractId && rpaStarted && isPostCreateDocusignStillProcessing() && docusignBackgroundInfoMessage}

          {docusignUiEnabled && createdContractId && rpaStarted && docusignModalProgress && (
            <Segment>
              <Header as="h4">
                <Icon name="tasks" />
                Progresso do DocuSign
              </Header>
              {renderDocusignModalProgressBar(docusignModalProgress)}
              {renderDocusignModalRetryActions()}
            </Segment>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button
            primary
            loading={isDownloadingContracts}
            onClick={() => {
              if (createdContractGroupId) {
                handleDownloadGroupPdfs(createdContractGroupId);
              } else if (createdContractId) {
                handleDownloadAllContracts(createdContractId);
              }
            }}
            icon="download"
            content={createdContractGroupId ? "Baixar Todos os Contratos do Grupo" : "Baixar Contratos"}
          />
          <Button
            onClick={() => handleClosePostCreateSuccessModal({ resetForm: true })}
            content="Fechar"
          />
        </Modal.Actions>
      </Modal>

      {/* Modal de edição de linha */}
      <Modal
        open={showEditLinhaModal}
        onClose={cancelarEdicaoLinha}
        size="large"
        closeOnDimmerClick={false}
      >
        <Modal.Header>
          <Icon name="edit" />
          Editar Linha do Contrato
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Grid stackable>
              <Grid.Row>
                <Grid.Column width={4}>
                  <Form.Field required>
                    <label>Negociação</label>
                    <select
                      value={linhaEditando.negociacao}
                      onChange={(e) => setLinhaEditando(prev => ({
                        ...prev,
                        negociacao: e.target.value,
                        planId: '',
                        plano: '',
                        valorMensal: '',
                      }))}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    >
                      <option value="Aditivo">Aditivo</option>
                      <option value="Renegociação">Renegociação</option>
                    </select>
                  </Form.Field>
                </Grid.Column>

                {linhaEditando.negociacao === 'Aditivo' && (
                  <Grid.Column width={4}>
                    <Form.Field required>
                      <label>DDD</label>
                      <input
                        type="text"
                        placeholder="11"
                        maxLength={2}
                        value={linhaEditando.ddd}
                        onChange={(e) => setLinhaEditando(prev => ({ ...prev, ddd: e.target.value.replace(/\D/g, '') }))}
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      />
                    </Form.Field>
                  </Grid.Column>
                )}

                {linhaEditando.negociacao === 'Renegociação' && (
                  <>
                    <Grid.Column width={4}>
                      <Form.Field>
                        <label>DDD</label>
                        <Dropdown
                          placeholder="Selecione ou digite o DDD"
                          selection
                          search
                          allowAdditions
                          additionLabel="Usar DDD: "
                          value={linhaEditando.ddd || ''}
                          options={dddOptionsEdit}
                          onAddItem={(e, { value }) => {
                            const dddValue = String(value).replace(/\D/g, '').slice(0, 2);
                            if (dddValue.length === 2) {
                              // Adicionar o novo DDD às opções se ainda não existir
                              setDddOptionsEdit(prev => {
                                const exists = prev.some(opt => opt.value === dddValue);
                                if (!exists) {
                                  return [...prev, { key: dddValue, text: dddValue, value: dddValue }];
                                }
                                return prev;
                              });
                              // Atualizar o estado com o novo DDD
                              setLinhaEditando(prev => ({ 
                                ...prev, 
                                ddd: dddValue
                              }));
                            }
                          }}
                          onChange={(e, { value }) => {
                            const dddValue = String(value).replace(/\D/g, '').slice(0, 2);
                            if (dddValue.length === 2) {
                              setLinhaEditando(prev => ({ 
                                ...prev, 
                                ddd: dddValue
                              }));
                            }
                          }}
                          style={{
                            width: '100%'
                          }}
                        />
                        {selectedClient && (
                          <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                            DDDs sugeridos da região de {selectedClient.estado} {fastchipFlowEnabled ? '(Fastchip)' : ''} - ou digite qualquer DDD de 2 dígitos
                          </div>
                        )}
                      </Form.Field>
                    </Grid.Column>
                    <Grid.Column width={4}>
                      <Form.Field required>
                        <label>Número</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="999999999"
                          value={renegociacaoLocalDigitsFromFormField(linhaEditando.ddd || '', linhaEditando.numero || '')}
                          onChange={(e) => {
                            const update = parseRenegociacaoNumeroFieldInput(linhaEditando.ddd || '', e.target.value);
                            if (update.ddd) {
                              const extractedDdd = update.ddd;
                              setDddOptions((prev) => {
                                const exists = prev.some((opt) => opt.value === extractedDdd);
                                if (exists) return prev;
                                return [...prev, { key: extractedDdd, text: extractedDdd, value: extractedDdd }];
                              });
                            }
                            setLinhaEditando((prev) => ({
                              ...prev,
                              ...(update.ddd ? { ddd: update.ddd } : {}),
                              numero: update.numero,
                            }));
                          }}
                          style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '14px'
                          }}
                        />
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                          Digite apenas o número (até 9 dígitos, sem DDD) - DDD deve ser selecionado no campo acima
                        </div>
                      </Form.Field>
                    </Grid.Column>
                  </>
                )}

                {linhaEditando.negociacao === 'Aditivo' && (
                  <Grid.Column width={4}>
                    <Form.Field required={fastchipFlowEnabled}>
                      <label>CCID {fastchipFlowEnabled ? '*' : ''}</label>
                      {!fastchipFlowEnabled ? (
                        <div style={{ 
                          width: '100%', 
                          padding: '10px', 
                          border: '1px solid #ccc', 
                          borderRadius: '4px', 
                          fontSize: '14px',
                          backgroundColor: '#f5f5f5',
                          color: '#666',
                          cursor: 'not-allowed'
                        }}>
                          ICCID não é necessário para Aditivo sem Fastchip (chip vem da operadora)
                        </div>
                      ) : (
                        // Em Aditivo + Fastchip o CCID e atribuido automaticamente ao adicionar
                        // a linha (selectRandomCcid) ou pela conversao para Fastchip. Permitir
                        // edicao manual quebrava a regra (usuario digitava qualquer numero,
                        // inclusive duplicado). Exibimos somente leitura mantendo o valor real.
                        <div
                          title={linhaEditando.chipCcid || ''}
                          style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #28a745',
                            borderRadius: '4px',
                            fontSize: '14px',
                            color: '#155724',
                            backgroundColor: '#d4edda',
                            fontFamily: 'monospace',
                            wordBreak: 'break-all',
                            cursor: 'not-allowed'
                          }}
                        >
                          <Icon name="lock" />
                          {linhaEditando.chipCcid
                            ? linhaEditando.chipCcid
                            : 'CCID será atribuído automaticamente'}
                        </div>
                      )}
                      <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                        {backendFastchipAllocationMode
                          ? 'Gerenciado no backend. O frontend não exibe ICCIDs neste modo.'
                          : (fastchipFlowEnabled
                            ? 'Atribuído automaticamente ao salvar (não editável)'
                            : 'Não necessário para Aditivo sem Fastchip')}
                      </div>
                    </Form.Field>
                  </Grid.Column>
                )}

              </Grid.Row>

              <Grid.Row>
                <ContractLineHandsetFields
                  catalog={handsetCatalog}
                  value={{
                    aparelho: linhaEditando.aparelho || '',
                    tipoVenda: linhaEditando.tipoVenda || 'Sem aparelho',
                    handsetDeviceId: linhaEditando.handsetDeviceId,
                    aparelhoValorTotal: linhaEditando.aparelhoValorTotal,
                    aparelhoValorMensal: linhaEditando.aparelhoValorMensal,
                  }}
                  onChange={(patch) => {
                    const next = {
                      ...linhaEditando,
                      ...patch,
                      negociacao: isWttxLine({ aparelho: patch.aparelho ?? linhaEditando.aparelho })
                        ? 'Aditivo'
                        : linhaEditando.negociacao,
                    };
                    if (fastchipFlowEnabled && lineHasAnyHandset(next)) {
                      setFastchip(false);
                      showWarning(
                        'Ao selecionar aparelho (incluindo WTTX), o Fastchip foi desmarcado. Nesses casos o envio é por entrega da operadora.'
                      );
                    }
                    setLinhaEditando(next);
                  }}
                />
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={4}>
                  <Form.Field required>
                    <label>Plano</label>
                    <select
                      value={linhaEditando.planId}
                      onChange={(e) => {
                        const selectedPlan = plansFiltrados.find(p => p.id === e.target.value);
                        setLinhaEditando(prev => ({ 
                          ...prev, 
                          planId: e.target.value,
                          plano: selectedPlan?.name || '',
                          valorMensal:
                            selectedPlan != null
                              ? String(resolvePlanTotalMonthlyPrice(selectedPlan.totalMonthlyPrice))
                              : prev.valorMensal
                        }));
                      }}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                      disabled={plansLoading}
                    >
                      <option value="">Selecione um plano...</option>
                      {plansFiltrados.map(plan => (
                        <option key={plan.id} value={plan.id}>
                          {formatContractPlanOptionLabel(plan)}
                        </option>
                      ))}
                    </select>
                    {plansLoading && (
                      <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                        <Icon name="spinner" loading />
                        Carregando planos...
                      </div>
                    )}
                  </Form.Field>
                </Grid.Column>

                <Grid.Column width={4}>
                  <Form.Field required>
                    <label>Valor Chip (R$)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="7,00"
                      value={fastchipFlowEnabled ? "0.00" : linhaEditando.valorChip}
                      readOnly={fastchipFlowEnabled}
                      onChange={fastchipFlowEnabled ? undefined : (e) => setLinhaEditando(prev => ({ ...prev, valorChip: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontSize: '14px',
                        backgroundColor: fastchipFlowEnabled ? '#f5f5f5' : 'white',
                        cursor: fastchipFlowEnabled ? 'not-allowed' : 'text'
                      }}
                    />
                    {fastchipFlowEnabled && (
                      <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                        Valor fixo para Fastchip: R$ 0,00
                      </div>
                    )}
                  </Form.Field>
                </Grid.Column>

                <Grid.Column width={4}>
                  <Form.Field required>
                    <label>Valor Mensal (R$)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      placeholder="0,00"
                      value={linhaEditando.valorMensal}
                      readOnly
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontSize: '14px',
                        backgroundColor: '#f5f5f5',
                        cursor: 'not-allowed'
                      }}
                    />
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                      Valor preenchido automaticamente pelo plano
                    </div>
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>

              {linhaEditando.negociacao === 'Aditivo' && (
                <Grid.Row>
                  <Grid.Column width={16}>
                    <Form.Field>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                        <input
                          type="checkbox"
                          id="ehPortabilidadeEdit"
                          checked={linhaEditando.ehPortabilidade}
                          onChange={e => {
                            const checked = e.target.checked;
                            setLinhaEditando(prev => ({
                              ...prev,
                              ehPortabilidade: checked,
                              portabilidadeNomeCompleto: checked ? prev.portabilidadeNomeCompleto : '',
                              portabilidadeCpf: checked ? prev.portabilidadeCpf : '',
                              portabilidadeOperadora: checked ? prev.portabilidadeOperadora : '',
                              portabilidadeLinha: checked ? prev.portabilidadeLinha : '',
                            }));
                          }}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <label htmlFor="ehPortabilidadeEdit" style={{ fontWeight: 'bold', color: '#2c3e50', fontSize: '15px', cursor: 'pointer', marginBottom: 0 }}>
                          É portabilidade?
                        </label>
                      </div>
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>
              )}

              {linhaEditando.negociacao === 'Aditivo' &&
                linhaEditando.ehPortabilidade &&
                linhas.some(
                  (l: any) => isPortabilidadeAditivoLine(l) && l.id !== editingLinha?.id
                ) && (
                <Grid.Row>
                  <Grid.Column width={16}>
                    <Form.Field>
                      <label style={{ fontSize: '13px' }}>Usar dados de portabilidade de…</label>
                      <select
                        value=""
                        onChange={(e) => {
                          const sourceId = e.target.value;
                          if (!sourceId) return;
                          const source = linhas.find((l: any) => l.id === sourceId);
                          if (!source || !isPortabilidadeAditivoLine(source)) return;
                          setLinhaEditando((prev) => ({
                            ...prev,
                            ...copyPortabilidadeFieldsFromLine(source),
                          }));
                        }}
                        style={{
                          width: '100%',
                          maxWidth: '420px',
                          padding: '8px',
                          borderRadius: '4px',
                          border: '1px solid #ccc',
                        }}
                      >
                        <option value="">Selecione uma linha…</option>
                        {linhas.map((l: any, i) =>
                          isPortabilidadeAditivoLine(l) && l.id !== editingLinha?.id ? (
                            <option key={l.id ?? i} value={l.id}>
                              {[l.ddd, l.numero].filter(Boolean).join(' ') || `Linha ${i + 1}`} —{' '}
                              {l.portabilidadeNomeCompleto || l.portabilidadeRazaoSocialCedente || 'Portabilidade'}
                            </option>
                          ) : null
                        )}
                      </select>
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>
              )}

              {linhaEditando.negociacao === 'Aditivo' && linhaEditando.ehPortabilidade && (
                <Grid.Row>
                  <Grid.Column width={16}>
                    <PortabilidadeDadosForm
                      linha={linhaEditando}
                      outrasLinhas={linhas.filter((l: any) => l.id !== editingLinha?.id)}
                      onChange={(field, value) =>
                        setLinhaEditando((prev) => ({ ...prev, [field]: value }))
                      }
                      onUnificarPortanteEmTodas={(source) => {
                        setLinhas((prev) => applyPortanteUnification(prev, source));
                        setLinhaEditando((prev) => ({
                          ...prev,
                          portabilidadeNomeCompleto: source.portabilidadeNomeCompleto ?? '',
                          portabilidadeCpf: source.portabilidadeCpf ?? '',
                        }));
                        showSuccess('Titular PF→CNPJ aplicado em todas as linhas compatíveis.');
                      }}
                    />
                  </Grid.Column>
                </Grid.Row>
              )}
            </Grid>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={cancelarEdicaoLinha}>
            <Icon name="cancel" />
            Cancelar
          </Button>
          <Button
            primary
            onClick={salvarEdicaoLinha}
            disabled={(linhaEditando.negociacao === 'Aditivo' && !linhaEditando.ddd) || 
                     !linhaEditando.valorMensal || !linhaEditando.planId || 
                     (linhaEditando.negociacao === 'Renegociação' && (!linhaEditando.ddd || !linhaEditando.numero || linhaEditando.numero.length < 8)) ||
                     (linhaEditando.negociacao === 'Aditivo' && linhaEditando.ehPortabilidade && isPortabilidadeIncompleta(linhaEditando, teamContractRules.requirePortabilidadeCompleta))}
          >
            <Icon name="save" />
            Salvar Alterações
          </Button>
        </Modal.Actions>
      </Modal>

      <PortabilidadeConflictModal
        open={!!portabilidadeConflict?.conflict}
        conflict={portabilidadeConflict}
        onConfirm={handlePortabilidadeConflictConfirm}
        onCancel={() => {
          setPortabilidadeConflict(null);
          setPortabilidadeConflictAction(null);
        }}
      />

      <ClusterConfirmModal
        open={showClusterConfirmModal}
        clientRazaoSocial={selectedClient?.razaoSocial ?? ''}
        clientEstado={selectedClient?.estado}
        fastchip={fastchip}
        tokenDisplayLabel={formatContractTokenDisplayLabel(token, fastchip)}
        suggestionSource={clusterSuggestionSource}
        clientPhoneDisplay={clusterConfirmClientPhoneDisplay}
        onConfirm={() => {
          setShowClusterConfirmModal(false);
          if (pendingClusterConfirmActionRef.current) {
            pendingClusterConfirmActionRef.current();
            pendingClusterConfirmActionRef.current = null;
          }
        }}
        onCancel={() => {
          setShowClusterConfirmModal(false);
          pendingClusterConfirmActionRef.current = null;
        }}
      />

      <ConvertToEntregaModal
        open={showConvertToEntregaModal}
        affectedLines={pendingEntregaConversionLines}
        onConfirm={handleConvertToEntregaConfirm}
        onCancel={handleConvertToEntregaCancel}
      />

      <ContractClusterTokensEditModal
        open={showClusterTokensEditModal}
        onClose={() => setShowClusterTokensEditModal(false)}
        fastchip={!!selectedContractInfo?.fastchip}
        clientEstado={selectedContractInfo?.deliveryAddress?.estado}
        clientCidade={
          linesModalGroupContracts[0]?.clientCidade ??
          selectedContractInfo?.clientCidade ??
          selectedContractInfo?.deliveryAddress?.cidade
        }
        aditivo={linesModalTokenTargets.aditivo}
        renegociacao={linesModalTokenTargets.renegociacao}
        saving={clusterTokensSaving}
        onSave={handleSaveClusterTokens}
      />

      {/* Modal de confirmação — contrato sem linhas */}
      <ConfirmEmptyContractCancellationModal
        open={emptyContractConfirmOpen}
        contracts={emptyContractsOnSave}
        loading={isSavingContract}
        onConfirm={handleConfirmEmptyContractSave}
        onCancel={() => {
          pendingContractSaveRef.current = null;
          setEmptyContractConfirmOpen(false);
        }}
      />

      {/* Modal de Bypass de IE Inapta */}
      <IeBypassConfirmModal
        open={showIeBypassModal}
        client={selectedClient}
        onConfirm={(reason) => {
          ieBypassConfirmedRef.current = true;
          setIeBypassConfirmed(true);
          setIeBypassReason(reason);
          setShowIeBypassModal(false);
          // Executar a ação que estava pendente (ex.: avançar tab ou salvar)
          if (pendingIeBypassActionRef.current) {
            pendingIeBypassActionRef.current();
            pendingIeBypassActionRef.current = null;
          }
        }}
        onCancel={() => {
          setShowIeBypassModal(false);
          pendingIeBypassActionRef.current = null;
        }}
      />

      {/* Modal de rascunhos do cliente */}
      <ClientDraftsModal
        open={showClientDraftsModal}
        drafts={clientDraftsList}
        loading={clientDraftsLoading}
        allowCreateNewDraft={teamContractRules.allowContractDraft}
        onContinue={handleClientDraftContinue}
        onCreateNew={handleClientDraftCreateNew}
        onClose={handleClientDraftsModalClose}
        onChooseAnotherClient={handleChooseAnotherClient}
      />

      {/* Modal de Duplicidade */}
      <DuplicateContractModal
        open={showDuplicateModal}
        onCancel={handleDuplicateCancel}
        onContinue={handleDuplicateConfirm}
        duplicateContract={duplicateContract}
      />

      {/* Modal de Contrato Existente por CNPJ */}
      <Modal
        open={showExistingContractModal}
        onClose={handleExistingContractCancel}
        size="small"
        closeIcon
      >
        <Modal.Header>
          <Icon name="warning" />
          Contrato Existente Encontrado
        </Modal.Header>
        <Modal.Content>
          <Message warning>
            <Message.Header>Atenção</Message.Header>
            <p>
              Já existe um contrato não cancelado criado por você hoje para este cliente (CNPJ: {existingContractCnpj}).
            </p>
            <p>
              Se você continuar, o contrato anterior será cancelado automaticamente e você poderá criar um novo contrato.
            </p>
            <p>
              <strong>Deseja continuar?</strong>
            </p>
          </Message>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleExistingContractCancel}>
            <Icon name="cancel" />
            Cancelar
          </Button>
          <Button primary onClick={handleExistingContractConfirm}>
            <Icon name="check" />
            Sim, continuar
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Modal de Separação */}
      <SeparateContractModal
        open={showSeparateModal}
        onCancel={handleSeparateCancel}
        onSeparate={handleSeparateConfirm}
        onKeepTogether={handleKeepTogether}
        aditivoCount={linhas.filter(l => !l.numero || l.numero.trim() === '').length}
        renegociacaoCount={linhas.filter(l => l.numero && l.numero.trim() !== '').length}
      />

      {/* Modal de Escolha para Duplicação de Grupo */}
      <Modal
        open={showDuplicateGroupModal}
        onClose={() => {
          setShowDuplicateGroupModal(false);
          setSelectedGroupForDuplicate(null);
          setDuplicatingContractId(null);
        }}
        size="small"
        closeIcon
      >
        <Modal.Header>
          <Icon name="copy" />
          Duplicar Grupo de Contratos
        </Modal.Header>
        <Modal.Content>
          {selectedGroupForDuplicate && (selectedGroupForDuplicate as any).groupContracts && (
            <div>
              <Message info>
                <Message.Header>Este é um grupo de contratos agrupados</Message.Header>
                <p>
                  Este contrato faz parte de um grupo com {(selectedGroupForDuplicate as any).groupContracts.length} contrato(s).
                  Como deseja proceder?
                </p>
              </Message>
              
              <div style={{ marginTop: '20px' }}>
                <Button
                  fluid
                  primary
                  size="large"
                  icon="copy"
                  content={`Duplicar Grupo Completo (${(selectedGroupForDuplicate as any).groupContracts.length} contratos)`}
                  onClick={() => handleDuplicateGroupComplete((selectedGroupForDuplicate as any).groupContracts)}
                  loading={duplicatingGroupId !== null}
                  style={{ marginBottom: '10px' }}
                />
                
                <div style={{ textAlign: 'center', margin: '15px 0', color: '#666' }}>
                  <strong>OU</strong>
                </div>
                
                <div style={{ marginTop: '20px' }}>
                  <strong style={{ display: 'block', marginBottom: '10px' }}>
                    Duplicar apenas um contrato específico:
                  </strong>
                  {(selectedGroupForDuplicate as any).groupContracts.map((subContract: any) => (
                    <Button
                      key={subContract.id}
                      fluid
                      basic
                      size="medium"
                      icon="file"
                      content={`${subContract.tipoContratacao || subContract.contractType || 'Contrato'} - ${subContract.totalLinhas || 0} linha(s) - R$ ${(subContract.valorTotalMensal || 0).toFixed(2).replace('.', ',')}`}
                      onClick={() => handleDuplicateSpecificContract(subContract.id)}
                      loading={duplicatingContractId === subContract.id}
                      style={{ marginBottom: '8px' }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => {
              setShowDuplicateGroupModal(false);
              setSelectedGroupForDuplicate(null);
              setDuplicatingContractId(null);
            }}
            disabled={duplicatingGroupId !== null}
          >
            Cancelar
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Modal de Histórico de Edições */}
      {editHistoryContractId && (
        <EditHistoryModal
          isOpen={showEditHistoryModal}
          onClose={() => {
            setShowEditHistoryModal(false);
            setEditHistoryContractId(null);
          }}
          contractId={editHistoryContractId}
        />
      )}

      <ContractLineAuditHistoryModal
        isOpen={showLineAuditHistoryModal}
        onClose={() => {
          setShowLineAuditHistoryModal(false);
          setLineAuditHistoryContractId(null);
          setLineAuditHistoryGroupId(null);
        }}
        mode={lineAuditHistoryMode}
        contractId={lineAuditHistoryContractId ?? undefined}
        groupId={lineAuditHistoryGroupId ?? undefined}
      />

      {/* Modal de Seleção de Edição de Grupo */}
      <Modal
        open={showEditGroupSelectionModal}
        onClose={() => {
          setShowEditGroupSelectionModal(false);
          setSelectedGroupForEdit(null);
        }}
        size="small"
        closeIcon
      >
        <Modal.Header>
          <Icon name="edit" />
          Editar Contratos do Grupo
        </Modal.Header>
        <Modal.Content>
          {selectedGroupForEdit && selectedGroupForEdit.length > 0 && (
            <div>
              <Message info>
                <Message.Header>Este é um grupo de contratos agrupados</Message.Header>
                <p>
                  Este grupo possui {selectedGroupForEdit.length} contrato(s).
                  Selecione como deseja editar:
                </p>
              </Message>

              <div style={{ marginTop: '20px' }}>
                <Button
                  fluid
                  primary
                  size="large"
                  icon="edit"
                  content={`Editar Grupo Completo (${selectedGroupForEdit.length} contratos)`}
                  onClick={() => handleEditGroupComplete(selectedGroupForEdit)}
                  style={{ marginBottom: '10px' }}
                />

                <div style={{ textAlign: 'center', margin: '15px 0', color: '#666' }}>
                  <strong>OU</strong>
                </div>

                <div style={{ marginTop: '20px' }}>
                  <strong style={{ display: 'block', marginBottom: '10px' }}>
                    Editar apenas um contrato específico:
                  </strong>
                  {selectedGroupForEdit.map((subContract) => (
                    <Button
                      key={subContract.id}
                      fluid
                      basic
                      size="medium"
                      icon="file"
                      content={`${subContract.tipoContratacao || 'Contrato'} - ${subContract.linhas?.length || 0} linha(s) - R$ ${(subContract.valorTotalMensal || 0).toFixed(2).replace('.', ',')}`}
                      onClick={() => handleEditSingleFromGroup(subContract.id)}
                      style={{ marginBottom: '8px' }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => {
              setShowEditGroupSelectionModal(false);
              setSelectedGroupForEdit(null);
            }}
          >
            Cancelar
          </Button>
        </Modal.Actions>
      </Modal>

      <Modal
        open={showCqvOpenChoiceModal}
        onClose={() => {
          if (!cqvOpenChoiceLoading) {
            setShowCqvOpenChoiceModal(false);
            setCqvOpenChoiceContracts([]);
            cqvOpenChoiceIdsRef.current = [];
          }
        }}
        size="small"
        closeIcon={!cqvOpenChoiceLoading}
      >
        <Modal.Header>
          <Icon name="external alternate" />
          Ver dados CQV
        </Modal.Header>
        <Modal.Content>
          {cqvOpenChoiceLoading ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <Loader active inline="centered" content="A carregar contratos do grupo..." />
            </div>
          ) : (
            <>
              <Message info>
                <p>Qual contrato deseja abrir numa nova aba?</p>
              </Message>
              <Button.Group vertical fluid style={{ marginTop: '1rem' }}>
                {cqvOpenChoiceContracts.map((item) => (
                  <Button
                    key={`${item.id}-${item.label}`}
                    type="button"
                    basic
                    icon="file outline"
                    content={`Abrir ${item.label}`}
                    onClick={() => {
                      openCqvViewTabs([item.id]);
                      setShowCqvOpenChoiceModal(false);
                      setCqvOpenChoiceContracts([]);
                      cqvOpenChoiceIdsRef.current = [];
                    }}
                    style={{ marginBottom: 8 }}
                  />
                ))}
              </Button.Group>
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button
            disabled={cqvOpenChoiceLoading}
            onClick={() => {
              setShowCqvOpenChoiceModal(false);
              setCqvOpenChoiceContracts([]);
              cqvOpenChoiceIdsRef.current = [];
            }}
          >
            Fechar
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Modal de Confirmação Fastchip - Endereço de Entrega */}
      <FastchipAddressModal
        open={showFastchipAddressModal}
        onConfirm={handleFastchipAddressConfirm}
        deliveryAddress={{
          cep: cep,
          endereco: endereco,
          numero: normalizeDeliveryAddressStreetNumberForApi(numero),
          bairro: bairro,
          cidade: cidade,
          estado: estado,
          complemento: complemento,
          referencia: referencia
        }}
      />

      {/* S6: Modal de confirmacao para converter contrato em Fastchip ao tentar suspender */}
      <Modal
        open={showConvertToFastchipModal}
        onClose={clearPendingSuspensionConversion}
        size="small"
      >
        <Modal.Header>
          <Icon name="bolt" color="orange" /> Conversão para Fastchip
        </Modal.Header>
        <Modal.Content>
          <p>
            O contrato está <strong>sem Fastchip</strong>. Para marcar linhas para suspensão, é necessário converter
            para Fastchip (há estoque disponível na região).
          </p>
          <ul style={{ paddingLeft: '20px', marginTop: '8px' }}>
            <li><strong>Fastchip</strong> será marcado automaticamente.</li>
            <li>“Tem suspensão?” passará para <strong>SIM</strong>.</li>
            <li>
              Linhas <strong>Aditivo</strong> sem CCID receberão um CCID disponível (sem duplicar).
            </li>
            <li>
              <strong>Valor do chip</strong> dos Aditivos será zerado.
            </li>
            <li>DDDs inválidos para o estado do cliente serão limpos para você revisar.</li>
            <li>
              {pendingManualSuspensionLine
                ? '1 linha manual será adicionada e marcada para suspensão.'
                : pendingSuspensionLinhaIds.length === 1
                  ? '1 linha será marcada para suspensão.'
                  : `${pendingSuspensionLinhaIds.length} linhas serão marcadas para suspensão.`}
            </li>
          </ul>
          <Message warning size="tiny" style={{ marginTop: '12px' }}>
            <Icon name="info circle" /> Reveja as linhas Aditivo após a confirmação.
          </Message>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={clearPendingSuspensionConversion}>
            Cancelar
          </Button>
          <Button color="orange" onClick={confirmConvertToFastchipAndApply} disabled={!pendingSuspensionCanConvert}>
            <Icon name="bolt" /> Converter e suspender
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Modal de Troca de FastChip em Lote */}
      <FastchipSwapModal
        open={showFastchipSwapModal}
        onClose={() => setShowFastchipSwapModal(false)}
        onConfirm={handleFastchipSwapConfirm}
        lines={selectedContractLines
          .filter((l: any) => selectedLinesForSwap.has(l.id))
          .map((l: any) => ({
            id: l.id,
            contractId: l.contractId || selectedContractInfo?.id,
            ddd: l.ddd,
            planName: l.planName,
            chipCcid: l.chipCcid,
            negociacao: l.negociacao,
            isEsim: l.isEsim ?? false,
          }))}
        clientEstado={selectedContractInfo?.clientEstado || selectedContractInfo?.client?.estado || ''}
        currentIccids={selectedContractLines
          .map((l: any) => l.chipCcid)
          .filter(Boolean)}
      />

      {/* Modal de Detalhes das Linhas */}
      <Modal
        open={showLinesModal}
        onClose={handleCloseLinesModal}
        closeIcon
        style={{ marginTop: '3vh', width: 'min(96vw, 1440px)', maxWidth: '96vw' }}
      >
        <Modal.Header style={{
          background: 'linear-gradient(135deg, #E6E6FA 0%, #B0C4DE 100%)',
          borderBottom: '3px solid #4A90E2',
          padding: '20px 24px',
          boxShadow: '0 2px 8px rgba(74, 144, 226, 0.2)',
        }}>
          <Icon name="list" style={{ 
            fontSize: '1.3em',
            color: '#2c3e50',
            marginRight: '10px',
          }} />
          <span style={{
            fontSize: '1.2em',
            fontWeight: '700',
            color: '#2c3e50',
          }}>
            Detalhes das Linhas - {selectedContractInfo?.clientRazaoSocial || 'Contrato'}
          </span>
          {showEditLinesShortcut && (
            <Button
              primary
              size="small"
              style={{ marginLeft: '16px', verticalAlign: 'middle' }}
              loading={openingLinesOnlyEdit}
              disabled={openingLinesOnlyEdit || linesModalLoading}
              onClick={() => {
                void handleOpenLinesOnlyEdit();
              }}
            >
              <Icon name="edit" /> Editar linhas do contrato
            </Button>
          )}
          {showLinesModal && (
            <Button
              size="small"
              style={{ marginLeft: '8px', verticalAlign: 'middle' }}
              disabled={linesModalLoading}
              onClick={handleOpenLineAuditHistory}
            >
              <Icon name="history" /> Histórico de linhas
            </Button>
          )}
          {selectedContractInfo && (
            <>
              {(() => {
                const qualityCcvFlag = getQualityCcvFlag(linesModalHeaderStatus);
                return (
                  <>
                    {qualityCcvFlag ? (
                      <Label
                        size="large"
                        color={qualityCcvFlag.color}
                        style={{
                          marginLeft: '14px',
                          verticalAlign: 'middle',
                          fontWeight: 700,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                        }}
                      >
                        <Icon name="flag" />
                        {qualityCcvFlag.label}
                      </Label>
                    ) : (
                      <Label
                        size="large"
                        color={getContractStatusSemanticColor(linesModalHeaderStatus)}
                        style={{
                          marginLeft: '14px',
                          verticalAlign: 'middle',
                          fontWeight: 700,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                        }}
                      >
                        {getStatusText(linesModalHeaderStatus)}
                      </Label>
                    )}
                  </>
                );
              })()}
              {(linesModalTokenTargets.aditivo || linesModalTokenTargets.renegociacao) && (
                <Label
                  size="large"
                  color="brown"
                  style={{
                    marginLeft: '14px',
                    verticalAlign: 'middle',
                    fontWeight: 700,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                  }}
                >
                  {linesModalGroupContracts.length > 1 ? (
                    <>
                      {linesModalTokenTargets.aditivo?.token && (
                        <span style={{ marginRight: 10 }}>
                          Aditivo:{' '}
                          {formatContractTokenDisplayLabel(
                            linesModalTokenTargets.aditivo.token,
                            selectedContractInfo.fastchip
                          )}
                        </span>
                      )}
                      {linesModalTokenTargets.renegociacao?.token && (
                        <span>
                          Reneg:{' '}
                          {formatContractTokenDisplayLabel(
                            linesModalTokenTargets.renegociacao.token,
                            selectedContractInfo.fastchip
                          )}
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      Token:{' '}
                      {formatContractTokenDisplayLabel(
                        selectedContractInfo.token,
                        selectedContractInfo.fastchip
                      )}
                    </>
                  )}
                  {linesModalCanEditClusterToken && (
                    <Icon
                      name="edit"
                      style={{ marginLeft: 8, cursor: 'pointer' }}
                      title="Editar tokens do pedido"
                      onClick={() => setShowClusterTokensEditModal(true)}
                    />
                  )}
                </Label>
              )}
              <Label
                size="large"
                color={linesModalDeliveryBadge.color}
                style={{
                  marginLeft: '14px',
                  verticalAlign: 'middle',
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                }}
              >
                <Icon name={linesModalDeliveryBadge.icon} />
                {linesModalDeliveryBadge.label}
              </Label>
              {(() => {
                const classificationMeta = getClientClassificationMeta(
                  selectedContractInfo?.clientClassification ??
                    readClientClassification(selectedContractInfo?.client)
                );
                return (
                  <Label
                    size="large"
                    color={classificationMeta.color}
                    style={{
                      marginLeft: '8px',
                      verticalAlign: 'middle',
                      fontWeight: 700,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                    }}
                  >
                    <Icon name="tag" />
                    {classificationMeta.label}
                  </Label>
                );
              })()}
              {selectedContractLines.some((l: { handsetDeviceId?: string | null; tipoVenda?: string | null; aparelho?: string | null; aparelhoValorMensal?: number | null; aparelhoValorTotal?: number | null }) =>
                lineHasCatalogHandset(l)
              ) && (
                <Label
                  size="large"
                  style={{
                    marginLeft: '8px',
                    verticalAlign: 'middle',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
                    color: '#9d174d',
                    border: '1px solid #f9a8d4',
                    boxShadow: '0 1px 3px rgba(219, 39, 119, 0.2)',
                  }}
                >
                  <Icon name="mobile alternate" />
                  Com aparelho
                </Label>
              )}
              {selectedContractLines.some(
                (l: { ehPortabilidade?: boolean; excluirDoContrato?: boolean; requerSuspensao?: boolean }) =>
                  !!l.ehPortabilidade && !l.excluirDoContrato && !l.requerSuspensao
              ) && (
                <Label
                  size="large"
                  color="orange"
                  style={{
                    marginLeft: '8px',
                    verticalAlign: 'middle',
                    fontWeight: 700,
                    boxShadow: '0 2px 8px rgba(242, 113, 28, 0.3)',
                  }}
                >
                  <Icon name="exchange" />
                  Portabilidade
                </Label>
              )}
              {/* Badge BYPASS IE — contrato criado ignorando bloqueio de IE inapta */}
              {selectedContractInfo.ieBypassedAtCreation && (
                <Popup
                  content={
                    selectedContractInfo.ieBypassReason
                      ? `Bypass de IE inapta — Justificativa: ${selectedContractInfo.ieBypassReason}`
                      : 'Contrato criado com bypass de Inscrição Estadual inapta. Risco de recusa pela TIM.'
                  }
                  trigger={
                    <Label
                      size="large"
                      color="orange"
                      style={{
                        marginLeft: '8px',
                        verticalAlign: 'middle',
                        fontWeight: 700,
                        cursor: 'default',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                      }}
                    >
                      <Icon name="warning circle" />
                      BYPASS IE
                    </Label>
                  }
                />
              )}
              {/* Badge IE INAPTA — visibilidade geral do cadastro do cliente, independente de bypass */}
              {!selectedContractInfo.ieBypassedAtCreation &&
                !isMeiOuIndividual({
                  naturezaJuridica: selectedContractInfo.clientNaturezaJuridica,
                }) &&
                selectedContractInfo.clientHasActiveInscricaoEstadual === false && (
                  <Popup
                    content="Cliente sem Inscrição Estadual ativa no UF. Contratos de entrega com Aditivo podem ser recusados pela TIM."
                    trigger={
                      <Label
                        size="large"
                        color="red"
                        style={{
                          marginLeft: '8px',
                          verticalAlign: 'middle',
                          fontWeight: 700,
                          cursor: 'default',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                        }}
                      >
                        <Icon name="warning sign" />
                        IE INAPTA
                      </Label>
                    }
                  />
                )}
            </>
          )}
        </Modal.Header>
        <Modal.Content scrolling style={{
          padding: '24px',
          background: '#f5f7fa',
        }}>
          {/* Disclaimer pro time de CQV: usam este modal pra Confirmar/Recusar CQV, mas
              entram no sistema com permissão de Admin (não têm um papel "CQV" próprio ainda). */}
          {hasRole('Admin') && !linesModalLoading && (
            <Message info style={{ marginBottom: '20px' }}>
              <Icon name="info circle" />
              Esta tela é usada pelo time de CQV pra Confirmar/Recusar a checagem de qualidade da
              venda. O acesso é feito com permissão de Admin — ainda não existe um papel "CQV"
              dedicado no sistema.
            </Message>
          )}

          {/* Aviso para contratos cancelados */}
          {isContractCancelled(selectedContractInfo) && !linesModalLoading && (
            <Message warning style={{ marginBottom: '20px' }}>
              <Message.Header>
                <Icon name="warning sign" />
                Contrato Cancelado
              </Message.Header>
              <p>Este contrato está com status <strong>Cancelado</strong>. Não é possível realizar operações de CQV (Confirmação de Qualidade de Venda).</p>
            </Message>
          )}

          <Dimmer active={linesModalLoading} inverted>
            <Loader size="large">
              {linesModalSlowLoad
                ? 'Buscando informação na base de dados...'
                : 'Carregando detalhes das linhas...'}
            </Loader>
          </Dimmer>

          {!linesModalLoading && linesModalError && (
            <Message negative icon style={{ marginBottom: '1rem' }}>
              <Icon name="warning circle" />
              <Message.Content>
                <Message.Header>Erro ao carregar detalhes</Message.Header>
                <p>{linesModalError}</p>
                {selectedContractInfo?.id && (
                  <Button
                    size="small"
                    icon="refresh"
                    content="Tentar novamente"
                    style={{ marginTop: '0.5rem' }}
                    onClick={() => {
                      void handleShowLinesModal(selectedContractInfo.id, selectedContractInfo, {
                        softRefresh: true,
                      });
                    }}
                  />
                )}
              </Message.Content>
            </Message>
          )}

          {!linesModalLoading && !linesModalError && (selectedContractLines.length > 0 ? (
            <div>
              {(() => {
                // Determinar se deve mostrar o script CQV (não para Sellers)
                const shouldShowScript = (hasRole('Validator') || hasRole('Admin') || hasRole('TeamLeader')) && !hasRole('Seller');
                
                return (
                  <>
                    {/* Layout responsivo: 2 colunas com script, 1 coluna sem script */}
                    <Grid columns={shouldShowScript ? 2 : 1} stackable style={{ marginBottom: '20px' }}>
                <Grid.Column>
                  {/* Resumo de Auditoria */}
                  {(() => {
                // ✅ Calcular estatísticas para o resumo (excluindo linhas suspensas)
                const linhasAtivas = selectedContractLines.filter(l => 
                  !l.excluirDoContrato && !l.requerSuspensao
                );
                const linhasSuspensas = selectedContractLines.filter(l => 
                  l.excluirDoContrato || l.requerSuspensao
                );
                
                const renovacoes = linhasAtivas.filter(l => 
                  l.negociacao === 'Renegociação' || 
                  (selectedContractInfo?.isGroupParent && l.contractType === 'Renegociação')
                );
                const linhasNovas = linhasAtivas.filter(l => 
                  l.negociacao === 'Aditivo' || 
                  (selectedContractInfo?.isGroupParent && l.contractType === 'Aditivo')
                );
                const portabilidades = linhasNovas.filter(l => l.ehPortabilidade);
                const linhasNovasSemPortabilidade = linhasNovas.filter(l => !l.ehPortabilidade);

                // Agrupar planos por tipo (renovação, portabilidade ou linha nova)
                const planosRenovacao: { [key: string]: { quantidade: number; valor: number; planName: string } } = {};
                const planosPortabilidade: {
                  [key: string]: {
                    quantidade: number;
                    valor: number;
                    planName: string;
                    detalhes: Array<{ operadora?: string; linha?: string }>;
                  };
                } = {};
                const planosNovos: { [key: string]: { quantidade: number; valor: number; planName: string } } = {};

                renovacoes.forEach(linha => {
                  const planKey = linha.planName || linha.plano || 'N/A';
                  if (!planosRenovacao[planKey]) {
                    planosRenovacao[planKey] = {
                      quantidade: 0,
                      valor: 0,
                      planName: planKey
                    };
                  }
                  planosRenovacao[planKey].quantidade += linha.quantidade || 1;
                  planosRenovacao[planKey].valor += (linha.valorMensal || linha.valorTotalMensal || 0) * (linha.quantidade || 1);
                });

                portabilidades.forEach(linha => {
                  const planKey = linha.planName || linha.plano || 'N/A';
                  if (!planosPortabilidade[planKey]) {
                    planosPortabilidade[planKey] = {
                      quantidade: 0,
                      valor: 0,
                      planName: planKey,
                      detalhes: [],
                    };
                  }
                  planosPortabilidade[planKey].quantidade += linha.quantidade || 1;
                  planosPortabilidade[planKey].valor += (linha.valorMensal || linha.valorTotalMensal || 0) * (linha.quantidade || 1);
                  planosPortabilidade[planKey].detalhes.push({
                    operadora: linha.portabilidadeOperadora,
                    linha: linha.portabilidadeLinha,
                  });
                });

                linhasNovasSemPortabilidade.forEach(linha => {
                  const planKey = linha.planName || linha.plano || 'N/A';
                  if (!planosNovos[planKey]) {
                    planosNovos[planKey] = {
                      quantidade: 0,
                      valor: 0,
                      planName: planKey
                    };
                  }
                  planosNovos[planKey].quantidade += linha.quantidade || 1;
                  planosNovos[planKey].valor += (linha.valorMensal || linha.valorTotalMensal || 0) * (linha.quantidade || 1);
                });

                // Calcular valor total (apenas linhas ativas)
                const valorTotal = linhasAtivas.reduce((sum, linha) => {
                  return sum + ((linha.valorMensal || linha.valorTotalMensal || 0) * (linha.quantidade || 1));
                }, 0);

                const totalRenovacoes = renovacoes.reduce((sum, l) => sum + (l.quantidade || 1), 0);
                const totalPortabilidades = portabilidades.reduce((sum, l) => sum + (l.quantidade || 1), 0);
                const totalLinhasNovas = linhasNovasSemPortabilidade.reduce((sum, l) => sum + (l.quantidade || 1), 0);

                return (
                  <Segment color="blue" style={{ 
                    marginBottom: '20px', 
                    background: '#e8f4ff',
                    border: '2px solid #B0C4DE',
                    boxShadow: '0 4px 12px rgba(176, 196, 222, 0.3)',
                  }}>
                    <Header as="h3" style={{ color: '#2c3e50', marginBottom: '15px', fontSize: '1.15em', fontWeight: '700' }}>
                      <Icon name="clipboard list" style={{ fontSize: '1.1em' }} />
                      Resumo para Auditoria
                    </Header>
                    
                    {/* Consultor Responsável */}
                    <div
                      style={{
                        marginBottom: '15px',
                        padding: '12px 15px',
                        background: '#fff',
                        borderRadius: '8px',
                        border: '1px solid #B0C4DE',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '1.05em',
                          fontWeight: 600,
                          marginBottom: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <Icon name="user circle" style={{ color: '#4A90E2' }} />
                        Consultor Responsável:
                        {canEditConsultantInLinesModal && !editingConsultantInModal && (
                          <Icon
                            name="edit"
                            style={{ cursor: 'pointer', marginLeft: '4px', fontSize: '0.95em' }}
                            title="Alterar consultor responsável"
                            onClick={() => {
                              setResponsibleConsultantEditInModal(
                                selectedContractInfo?.user?.id ??
                                  selectedContractInfo?.userId ??
                                  ''
                              );
                              setEditingConsultantInModal(true);
                            }}
                          />
                        )}
                      </div>
                      {editingConsultantInModal ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <ResponsibleConsultantSelect
                            value={responsibleConsultantEditInModal}
                            onChange={setResponsibleConsultantEditInModal}
                            options={consultantDropdownOptions}
                            loading={loadingConsultantOptions}
                            disabled={savingInlineEdit}
                            onOpen={() => {
                              window.setTimeout(() => handleConsultantFieldInteraction(), 0);
                            }}
                          />
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <Button
                              size="small"
                              color="green"
                              loading={savingInlineEdit}
                              disabled={!responsibleConsultantEditInModal || savingInlineEdit}
                              onClick={() => void handleSaveConsultantInline()}
                            >
                              Salvar
                            </Button>
                            <Button
                              size="small"
                              disabled={savingInlineEdit}
                              onClick={() => {
                                setEditingConsultantInModal(false);
                                setResponsibleConsultantEditInModal('');
                              }}
                            >
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <span style={{ fontSize: '1.05em' }}>
                          {resolveConsultantDisplayLabel(
                            selectedContractInfo?.user,
                            selectedContractInfo?.userId,
                            consultantDropdownOptions,
                          )}
                        </span>
                      )}
                    </div>

                    <div
                      style={{
                        marginBottom: '15px',
                        padding: '12px 15px',
                        background: '#fff',
                        borderRadius: '8px',
                        border: '1px solid #B0C4DE',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '1.05em',
                          fontWeight: 600,
                          marginBottom: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <Icon name="mail" style={{ color: '#4A90E2' }} />
                        Atribuído VTME:
                      </div>
                      <span style={{ fontSize: '1.05em' }}>
                        {selectedContractInfo?.vtmeAssignedName
                          ? `${selectedContractInfo.vtmeAssignedName}${
                              selectedContractInfo.vtmeAssignedEmail
                                ? ` (${selectedContractInfo.vtmeAssignedEmail})`
                                : ''
                            }`
                          : 'Sem atribuição'}
                      </span>
                    </div>

                    {/* Informações do Cliente */}
                    <div style={{ 
                      marginBottom: '15px', 
                      padding: '15px', 
                      background: 'linear-gradient(135deg, #E6E6FA 0%, #ffffff 100%)',
                      borderRadius: '8px',
                      border: '2px solid #B0C4DE',
                      boxShadow: '0 2px 8px rgba(176, 196, 222, 0.2)',
                    }}>
                      <div style={{ fontSize: '1.15em', marginBottom: '8px', fontWeight: '600' }}>
                        <strong>Cliente:</strong> {selectedContractInfo?.clientRazaoSocial || 'N/A'}
                      </div>
                      <div style={{ fontSize: '1.05em', marginBottom: '10px', fontWeight: '600' }}>
                        <strong>CNPJ:</strong> {selectedContractInfo?.clientCnpj || selectedContractInfo?.client?.cnpj || 'N/A'}
                      </div>
                      <div style={{ fontSize: '1.05em', marginBottom: '10px', fontWeight: '600' }}>
                        <strong>Classificação:</strong>{' '}
                        {getClientClassificationMeta(
                          selectedContractInfo?.clientClassification ??
                            readClientClassification(selectedContractInfo?.client)
                        ).label}
                      </div>
                      {(() => {
                        const legacyNotes =
                          selectedContractInfo?.clientInternalNotes ??
                          readClientInternalNotes(selectedContractInfo?.client);
                        const hasStructured = lineDetailsClientObservations.length > 0;
                        if (!hasStructured && !lineDetailsObservationsLoading && !legacyNotes) return null;
                        return (
                          <Message info size="small" style={{ marginTop: 0 }}>
                            <Message.Header>Observações internas do cliente</Message.Header>
                            {hasStructured || lineDetailsObservationsLoading ? (
                              <div style={{ marginTop: 8 }}>
                                <ClientObservationsSummary
                                  observations={lineDetailsClientObservations}
                                  loading={lineDetailsObservationsLoading}
                                  maxRows={4}
                                  maxHeight={180}
                                  dense
                                />
                              </div>
                            ) : (
                              <p style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>{legacyNotes}</p>
                            )}
                          </Message>
                        );
                      })()}
                      {(() => {
                        const contractNotes =
                          selectedContractInfo?.internalNotes ??
                          readContractInternalNotes(selectedContractInfo);
                        if (!contractNotes) return null;
                        return (
                          <Message info size="small">
                            <Message.Header>Observações internas do contrato</Message.Header>
                            <p style={{ whiteSpace: 'pre-wrap' }}>{contractNotes}</p>
                          </Message>
                        );
                      })()}
                      
                      {/* Informações do Responsável pela Auditoria que assinou - Destacado */}
                      <div style={{ 
                        marginTop: '12px', 
                        padding: '15px', 
                        background: 'linear-gradient(135deg, #E6E6FA 0%, #f8f9fa 100%)',
                        borderRadius: '8px', 
                        borderLeft: '5px solid #4A90E2',
                        border: '2px solid #B0C4DE',
                        boxShadow: '0 2px 8px rgba(74, 144, 226, 0.2)',
                      }}>
                        <div style={{ 
                          fontSize: '1.05em', 
                          fontWeight: '700', 
                          marginBottom: '10px', 
                          color: '#4A90E2',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <Icon name="user" style={{ fontSize: '1.2em' }} />
                          Responsável pela Auditoria do contrato (CQV):
                          {linesModalCanInlineEdit && !editingAdminInModal && (
                            <Icon
                              name="edit"
                              style={{ cursor: 'pointer', marginLeft: '8px', fontSize: '0.95em' }}
                              title="Editar responsável"
                              onClick={() => {
                                setEditingAdminData({
                                  nomeCompleto: selectedContractInfo?.administrator?.nomeCompleto || '',
                                  cpf: selectedContractInfo?.administrator?.cpf || '',
                                  telefone1: selectedContractInfo?.administrator?.telefone1 || '',
                                });
                                setEditingAdminFieldErrors({});
                                setEditingAdminInModal(true);
                              }}
                            />
                          )}
                        </div>
                        {editingAdminInModal ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div>
                              <strong style={{ fontSize: '0.9em' }}>Nome:</strong>
                              <Input
                                fluid
                                size="small"
                                value={editingAdminData.nomeCompleto}
                                onChange={(_, d) => {
                                  const nomeCompleto = d.value;
                                  setEditingAdminData((prev) => ({ ...prev, nomeCompleto }));
                                  if (editingAdminFieldErrors.nomeCompleto && nomeCompleto.trim()) {
                                    setEditingAdminFieldErrors((prev) => ({ ...prev, nomeCompleto: undefined }));
                                  }
                                }}
                                onBlur={() => {
                                  const errs = validateAdminInlineEdit(editingAdminData);
                                  setEditingAdminFieldErrors((prev) => ({
                                    ...prev,
                                    nomeCompleto: errs.nomeCompleto,
                                  }));
                                }}
                                error={!!editingAdminFieldErrors.nomeCompleto}
                              />
                              {editingAdminFieldErrors.nomeCompleto && (
                                <div style={{ fontSize: '0.75em', color: '#e74c3c', marginTop: '2px', fontWeight: 600 }}>
                                  {editingAdminFieldErrors.nomeCompleto}
                                </div>
                              )}
                            </div>
                            <div>
                              <strong style={{ fontSize: '0.9em' }}>CPF:</strong>
                              <Input
                                fluid
                                size="small"
                                value={formatCpfProgressive(editingAdminData.cpf)}
                                onChange={(_, d) => {
                                  const cpf = cleanCpf(d.value);
                                  setEditingAdminData((prev) => ({ ...prev, cpf }));
                                  if (editingAdminFieldErrors.cpf && isValidCpfLength(cpf)) {
                                    setEditingAdminFieldErrors((prev) => ({ ...prev, cpf: undefined }));
                                  }
                                }}
                                onBlur={() => {
                                  const errs = validateAdminInlineEdit(editingAdminData);
                                  setEditingAdminFieldErrors((prev) => ({ ...prev, cpf: errs.cpf }));
                                }}
                                maxLength={14}
                                placeholder="000.000.000-00"
                                error={!!editingAdminFieldErrors.cpf}
                              />
                              {editingAdminFieldErrors.cpf && (
                                <div style={{ fontSize: '0.75em', color: '#e74c3c', marginTop: '2px', fontWeight: 600 }}>
                                  {editingAdminFieldErrors.cpf}
                                </div>
                              )}
                            </div>
                            <div>
                              <strong style={{ fontSize: '0.9em' }}>Telefone:</strong>
                              <Input
                                fluid
                                size="small"
                                inputMode="tel"
                                value={formatPhoneProgressive(editingAdminData.telefone1)}
                                onChange={(_, d) => {
                                  const telefone1 = toBrazilNationalPhoneDigits(d.value);
                                  setEditingAdminData((prev) => ({ ...prev, telefone1 }));
                                  if (editingAdminFieldErrors.telefone1 && isValidPhoneFormat(telefone1)) {
                                    setEditingAdminFieldErrors((prev) => ({ ...prev, telefone1: undefined }));
                                  }
                                }}
                                onBlur={() => {
                                  const errs = validateAdminInlineEdit(editingAdminData);
                                  setEditingAdminFieldErrors((prev) => ({
                                    ...prev,
                                    telefone1: errs.telefone1,
                                  }));
                                }}
                                placeholder="(41) 99999-9999"
                                error={!!editingAdminFieldErrors.telefone1}
                              />
                              {editingAdminFieldErrors.telefone1 && (
                                <div style={{ fontSize: '0.75em', color: '#e74c3c', marginTop: '2px', fontWeight: 600 }}>
                                  {editingAdminFieldErrors.telefone1}
                                </div>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                              <Button
                                size="small"
                                color="green"
                                loading={savingInlineEdit}
                                disabled={savingInlineEdit}
                                onClick={handleSaveAdminInline}
                              >
                                Salvar
                              </Button>
                              <Button
                                size="small"
                                disabled={savingInlineEdit}
                                onClick={() => {
                                  setEditingAdminInModal(false);
                                  setEditingAdminFieldErrors({});
                                }}
                              >
                                Cancelar
                              </Button>
                            </div>
                          </div>
                        ) : selectedContractInfo?.administrator?.nomeCompleto ? (
                          <>
                            <div style={{ fontSize: '1.05em', marginBottom: '6px', lineHeight: '1.6', fontWeight: '600' }}>
                              <strong>Nome:</strong> {selectedContractInfo.administrator.nomeCompleto}
                            </div>
                            {selectedContractInfo?.administrator?.cpf && (
                              <div style={{ fontSize: '1.05em', marginBottom: '6px', lineHeight: '1.6', fontWeight: '600' }}>
                                <strong>CPF:</strong> {selectedContractInfo.administrator.cpf}
                              </div>
                            )}
                            {selectedContractInfo?.administrator?.telefone1 && (
                              <div style={{ fontSize: '1.05em', marginTop: '6px', marginBottom: '6px', lineHeight: '1.6', fontWeight: '600' }}>
                                <strong>Telefone:</strong> {formatPhoneProgressive(selectedContractInfo.administrator.telefone1)}
                              </div>
                            )}
                            {selectedContractInfo?.administrator?.telefone2 && (
                              <div style={{ fontSize: '1.05em', marginBottom: '6px', lineHeight: '1.6', fontWeight: '600' }}>
                                <strong>Telefone 2:</strong> {formatPhoneProgressive(selectedContractInfo.administrator.telefone2)}
                              </div>
                            )}
                          </>
                        ) : (
                          <div style={{ 
                            fontSize: '0.9em', 
                            color: '#6c757d', 
                            fontStyle: 'italic',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <Icon name="warning sign" color="orange" />
                            Informações do responsável pela auditoria não disponíveis
                          </div>
                        )}
                      </div>

                      {/* Informações dos Representantes Legais (QSA) - Destacado */}
                      <div style={{ 
                        marginTop: '12px', 
                        padding: '15px', 
                        background: 'linear-gradient(135deg, #E8F5E9 0%, #f8f9fa 100%)',
                        borderRadius: '8px', 
                        borderLeft: '5px solid #27ae60',
                        border: '2px solid #81c784',
                        boxShadow: '0 2px 8px rgba(39, 174, 96, 0.2)',
                      }}>
                        <div style={{ 
                          fontSize: '1.05em', 
                          fontWeight: '700', 
                          marginBottom: '10px', 
                          color: '#27ae60',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <Icon name="users" style={{ fontSize: '1.2em' }} />
                          Representantes Legais (QSA):
                        </div>
                        {selectedContractInfo?.representatives && selectedContractInfo.representatives.length > 0 ? (
                          <div>
                            {selectedContractInfo.representatives.map((rep: any, idx: number) => {
                              const repEditKey = getLineModalRepresentativeEditKey(rep, idx);
                              return (
                              <div key={repEditKey} style={{ 
                                fontSize: '1.05em', 
                                marginBottom: idx < selectedContractInfo.representatives.length - 1 ? '10px' : '0',
                                paddingBottom: idx < selectedContractInfo.representatives.length - 1 ? '10px' : '0',
                                borderBottom: idx < selectedContractInfo.representatives.length - 1 ? '2px solid #c8e6c9' : 'none',
                                lineHeight: '1.6',
                                fontWeight: '600',
                              }}>
                                {editingRepInModal === repEditKey ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <div>
                                      <strong style={{ fontSize: '0.9em' }}>Nome:</strong>
                                      <Input
                                        fluid
                                        size="small"
                                        value={editingRepData.nome}
                                        onChange={(_, d) => {
                                          const nome = d.value;
                                          setEditingRepData((prev) => ({ ...prev, nome }));
                                          if (editingRepFieldErrors.nome && nome.trim()) {
                                            setEditingRepFieldErrors((prev) => ({ ...prev, nome: undefined }));
                                          }
                                        }}
                                        onBlur={() => {
                                          const errs = validateRepInlineEdit(editingRepData);
                                          setEditingRepFieldErrors((prev) => ({ ...prev, nome: errs.nome }));
                                        }}
                                        error={!!editingRepFieldErrors.nome}
                                      />
                                      {editingRepFieldErrors.nome && (
                                        <div style={{ fontSize: '0.75em', color: '#e74c3c', marginTop: '2px', fontWeight: 600 }}>
                                          {editingRepFieldErrors.nome}
                                        </div>
                                      )}
                                    </div>
                                    <div>
                                      <strong style={{ fontSize: '0.9em' }}>CPF:</strong>
                                      <Input
                                        fluid
                                        size="small"
                                        value={formatCpfProgressive(editingRepData.cpf)}
                                        onChange={(_, d) => {
                                          const cpf = cleanCpf(d.value);
                                          setEditingRepData((prev) => ({ ...prev, cpf }));
                                          if (editingRepFieldErrors.cpf && isValidCpfLength(cpf)) {
                                            setEditingRepFieldErrors((prev) => ({ ...prev, cpf: undefined }));
                                          }
                                        }}
                                        onBlur={() => {
                                          const errs = validateRepInlineEdit(editingRepData);
                                          setEditingRepFieldErrors((prev) => ({ ...prev, cpf: errs.cpf }));
                                        }}
                                        maxLength={14}
                                        placeholder="000.000.000-00"
                                        error={!!editingRepFieldErrors.cpf}
                                      />
                                      {editingRepFieldErrors.cpf && (
                                        <div style={{ fontSize: '0.75em', color: '#e74c3c', marginTop: '2px', fontWeight: 600 }}>
                                          {editingRepFieldErrors.cpf}
                                        </div>
                                      )}
                                    </div>
                                    <div>
                                      <strong style={{ fontSize: '0.9em' }}>E-mail (proposta comercial):</strong>
                                      <Input
                                        fluid
                                        size="small"
                                        type="email"
                                        value={editingRepData.email}
                                        onChange={(_, d) => {
                                          const email = d.value;
                                          setEditingRepData((prev) => ({ ...prev, email }));
                                          const emailTrim = email.trim();
                                          if (
                                            editingRepFieldErrors.email &&
                                            emailTrim &&
                                            /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)
                                          ) {
                                            setEditingRepFieldErrors((prev) => ({ ...prev, email: undefined }));
                                          }
                                        }}
                                        onBlur={() => {
                                          const errs = validateRepInlineEdit(editingRepData);
                                          setEditingRepFieldErrors((prev) => ({ ...prev, email: errs.email }));
                                        }}
                                        error={!!editingRepFieldErrors.email}
                                      />
                                      {editingRepFieldErrors.email && (
                                        <div style={{ fontSize: '0.75em', color: '#e74c3c', marginTop: '2px', fontWeight: 600 }}>
                                          {editingRepFieldErrors.email}
                                        </div>
                                      )}
                                    </div>
                                    <div>
                                      <strong style={{ fontSize: '0.9em' }}>Telefone:</strong>
                                      <Input
                                        fluid
                                        size="small"
                                        inputMode="tel"
                                        placeholder="41984028564"
                                        value={editingRepData.telefone}
                                        onChange={(_, d) => {
                                          const telefone = normalizeRepresentativePhoneInput(d.value);
                                          setEditingRepData((prev) => ({ ...prev, telefone }));
                                          if (
                                            editingRepFieldErrors.telefone &&
                                            (telefone.length === 10 || telefone.length === 11)
                                          ) {
                                            setEditingRepFieldErrors((prev) => ({ ...prev, telefone: undefined }));
                                          }
                                        }}
                                        onBlur={() => {
                                          const errs = validateRepInlineEdit(editingRepData);
                                          setEditingRepFieldErrors((prev) => ({
                                            ...prev,
                                            telefone: errs.telefone,
                                          }));
                                        }}
                                        error={!!editingRepFieldErrors.telefone}
                                      />
                                      {editingRepFieldErrors.telefone && (
                                        <div style={{ fontSize: '0.75em', color: '#e74c3c', marginTop: '2px', fontWeight: 600 }}>
                                          {editingRepFieldErrors.telefone}
                                        </div>
                                      )}
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                                      <Button
                                        size="small"
                                        color="green"
                                        loading={savingInlineEdit}
                                        disabled={savingInlineEdit}
                                        onClick={handleSaveRepInline}
                                      >
                                        Salvar
                                      </Button>
                                      <Button
                                        size="small"
                                        disabled={savingInlineEdit}
                                        onClick={() => {
                                          setEditingRepInModal(null);
                                          setEditingRepFieldErrors({});
                                        }}
                                      >
                                        Cancelar
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <div style={{ marginBottom: '6px', display: 'flex', alignItems: 'center' }}>
                                      <strong>Nome:</strong>&nbsp;{rep.nome || 'Não informado'}
                                      {linesModalCanInlineEdit && (
                                        <Icon
                                          name="edit"
                                          style={{ cursor: 'pointer', marginLeft: '8px', fontSize: '0.9em', color: '#27ae60' }}
                                          title="Editar representante"
                                          onClick={() => {
                                            setEditingRepData({
                                              nome: rep.nome || '',
                                              cpf: rep.cpf || '',
                                              telefone: normalizeRepresentativePhoneInput(rep.telefone || ''),
                                              email: rep.email || '',
                                            });
                                            setEditingRepFieldErrors({});
                                            setEditingRepInModal(repEditKey);
                                          }}
                                        />
                                      )}
                                    </div>
                                    {rep.cpf && (
                                      <div style={{ marginBottom: '6px' }}>
                                        <strong>CPF:</strong> {rep.cpf}
                                      </div>
                                    )}
                                    {rep.email && (
                                      <div style={{ marginBottom: '6px' }}>
                                        <strong>E-mail:</strong> {rep.email}
                                      </div>
                                    )}
                                    {rep.telefone && (
                                      <div style={{ marginBottom: '6px' }}>
                                        <strong>Telefone:</strong> {formatPhoneProgressive(rep.telefone)}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            );
                            })}
                          </div>
                        ) : (
                          <div style={{ 
                            fontSize: '0.9em', 
                            color: '#6c757d', 
                            fontStyle: 'italic',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <Icon name="info circle" color="grey" />
                            Nenhum representante legal cadastrado
                          </div>
                        )}
                      </div>

                      {/* Recebedores Autorizados - cadastrados na criação do contrato */}
                      <div style={{
                        marginTop: '12px',
                        padding: '15px',
                        background: 'linear-gradient(135deg, #E1F5FE 0%, #f8f9fa 100%)',
                        borderRadius: '8px',
                        borderLeft: '5px solid #0288D1',
                        border: '2px solid #81D4FA',
                        boxShadow: '0 2px 8px rgba(2, 136, 209, 0.2)',
                      }}>
                        <div style={{
                          fontSize: '1.05em',
                          fontWeight: '700',
                          marginBottom: '10px',
                          color: '#0288D1',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <Icon name="user plus" style={{ fontSize: '1.2em' }} />
                          Recebedores Autorizados:
                        </div>
                        {selectedContractInfo?.authorizedReceivers && selectedContractInfo.authorizedReceivers.length > 0 ? (
                          <div>
                            {selectedContractInfo.authorizedReceivers.map((rec: any, idx: number) => (
                              <div key={rec.id || idx} style={{
                                fontSize: '1.05em',
                                marginBottom: idx < selectedContractInfo.authorizedReceivers.length - 1 ? '10px' : '0',
                                paddingBottom: idx < selectedContractInfo.authorizedReceivers.length - 1 ? '10px' : '0',
                                borderBottom: idx < selectedContractInfo.authorizedReceivers.length - 1 ? '2px solid #b3e5fc' : 'none',
                                lineHeight: '1.6',
                                fontWeight: '600',
                              }}>
                                {editingReceiverInModal === rec.id ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <div>
                                      <strong style={{ fontSize: '0.9em' }}>Nome:</strong>
                                      <Input
                                        fluid
                                        size="small"
                                        value={editingReceiverData.nome}
                                        onChange={(_, d) => {
                                          const nome = d.value;
                                          setEditingReceiverData((prev) => ({ ...prev, nome }));
                                          if (editingReceiverFieldErrors.nome && nome.trim()) {
                                            setEditingReceiverFieldErrors((prev) => ({ ...prev, nome: undefined }));
                                          }
                                        }}
                                        onBlur={() => {
                                          const errs = validateReceiverInlineEdit(editingReceiverData);
                                          setEditingReceiverFieldErrors((prev) => ({ ...prev, nome: errs.nome }));
                                        }}
                                        error={!!editingReceiverFieldErrors.nome}
                                      />
                                      {editingReceiverFieldErrors.nome && (
                                        <div style={{ fontSize: '0.75em', color: '#e74c3c', marginTop: '2px', fontWeight: 600 }}>
                                          {editingReceiverFieldErrors.nome}
                                        </div>
                                      )}
                                    </div>
                                    <div>
                                      <strong style={{ fontSize: '0.9em' }}>CPF:</strong>
                                      <Input
                                        fluid
                                        size="small"
                                        value={formatCpfProgressive(editingReceiverData.cpf)}
                                        onChange={(_, d) => {
                                          const cpf = cleanCpf(d.value);
                                          setEditingReceiverData((prev) => ({ ...prev, cpf }));
                                          if (editingReceiverFieldErrors.cpf && isValidCpfLength(cpf)) {
                                            setEditingReceiverFieldErrors((prev) => ({ ...prev, cpf: undefined }));
                                          }
                                        }}
                                        onBlur={() => {
                                          const errs = validateReceiverInlineEdit(editingReceiverData);
                                          setEditingReceiverFieldErrors((prev) => ({ ...prev, cpf: errs.cpf }));
                                        }}
                                        maxLength={14}
                                        placeholder="000.000.000-00"
                                        error={!!editingReceiverFieldErrors.cpf}
                                      />
                                      {editingReceiverFieldErrors.cpf && (
                                        <div style={{ fontSize: '0.75em', color: '#e74c3c', marginTop: '2px', fontWeight: 600 }}>
                                          {editingReceiverFieldErrors.cpf}
                                        </div>
                                      )}
                                    </div>
                                    <div>
                                      <strong style={{ fontSize: '0.9em' }}>RG:</strong>
                                      <Input
                                        fluid
                                        size="small"
                                        value={editingReceiverData.rg}
                                        onChange={(_, d) => {
                                          const rg = d.value.replace(/\D/g, '').substring(0, 10);
                                          setEditingReceiverData((prev) => ({ ...prev, rg }));
                                        }}
                                        maxLength={10}
                                      />
                                    </div>
                                    <div>
                                      <strong style={{ fontSize: '0.9em' }}>Qualificação:</strong>
                                      <Input
                                        fluid
                                        size="small"
                                        value={editingReceiverData.qualificacao}
                                        onChange={(_, d) =>
                                          setEditingReceiverData((prev) => ({ ...prev, qualificacao: d.value }))
                                        }
                                      />
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                                      <Button
                                        size="small"
                                        color="green"
                                        loading={savingInlineEdit}
                                        disabled={savingInlineEdit}
                                        onClick={handleSaveReceiverInline}
                                      >
                                        Salvar
                                      </Button>
                                      <Button
                                        size="small"
                                        disabled={savingInlineEdit}
                                        onClick={() => {
                                          setEditingReceiverInModal(null);
                                          setEditingReceiverFieldErrors({});
                                        }}
                                      >
                                        Cancelar
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <div style={{ marginBottom: '6px', display: 'flex', alignItems: 'center' }}>
                                      <strong>Nome:</strong>&nbsp;{rec.nome || 'Não informado'}
                                      {linesModalCanInlineEdit && (
                                        <Icon
                                          name="edit"
                                          style={{ cursor: 'pointer', marginLeft: '8px', fontSize: '0.9em', color: '#0288D1' }}
                                          title="Editar recebedor"
                                          onClick={() => {
                                            setEditingReceiverData({
                                              nome: rec.nome || '',
                                              cpf: rec.cpf || '',
                                              qualificacao: rec.qualificacao || '',
                                              rg: rec.rg || '',
                                            });
                                            setEditingReceiverFieldErrors({});
                                            setEditingReceiverInModal(rec.id);
                                          }}
                                        />
                                      )}
                                    </div>
                                    {rec.qualificacao && (
                                      <div style={{ marginBottom: '6px' }}>
                                        <strong>Qualificação:</strong> {rec.qualificacao}
                                      </div>
                                    )}
                                    {rec.cpf && (
                                      <div style={{ marginBottom: '6px' }}>
                                        <strong>CPF:</strong> {rec.cpf}
                                      </div>
                                    )}
                                    {rec.rg && (
                                      <div style={{ marginBottom: '6px' }}>
                                        <strong>RG:</strong> {rec.rg}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{
                            fontSize: '0.9em',
                            color: '#6c757d',
                            fontStyle: 'italic',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <Icon name="info circle" color="grey" />
                            Nenhum recebedor autorizado cadastrado
                          </div>
                        )}
                      </div>

                      {/* Endereço de Entrega - bloco editável inline */}
                      <div style={{
                        marginTop: '12px',
                        padding: '15px',
                        background: selectedContractInfo?.fastchip
                          ? 'linear-gradient(135deg, #FFF3E0 0%, #f8f9fa 100%)'
                          : 'linear-gradient(135deg, #ECEFF1 0%, #f8f9fa 100%)',
                        borderRadius: '8px',
                        borderLeft: `5px solid ${selectedContractInfo?.fastchip ? '#FB8C00' : '#607D8B'}`,
                        border: `2px solid ${selectedContractInfo?.fastchip ? '#FFB74D' : '#B0BEC5'}`,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        boxSizing: 'border-box',
                        maxWidth: '100%',
                        minWidth: 0,
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          fontSize: '1.05em',
                          fontWeight: '700',
                          marginBottom: '10px',
                          color: selectedContractInfo?.fastchip
                            ? linesModalHasEsim
                              ? '#6f42c1'
                              : '#E65100'
                            : '#37474F',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          flexWrap: 'wrap',
                        }}>
                          <Icon name="map marker alternate" style={{ fontSize: '1.2em' }} />
                          Endereço de Entrega
                          <Label
                            size="small"
                            color={linesModalDeliveryBadge.color}
                            style={{ marginLeft: '6px' }}
                          >
                            <Icon name={linesModalDeliveryBadge.icon} />
                            {resolveFastchipDeliveryDetailLabel({
                              fastchip: !!selectedContractInfo?.fastchip,
                              hasEsim: linesModalHasEsim,
                            })}
                          </Label>
                          {linesModalCanInlineEdit && !editingDeliveryAddress && (
                            <Popup
                              content={
                                selectedContractInfo?.fastchip
                                  ? 'Editar endereço de entrega'
                                  : 'Para editar o endereço de entrega o contrato precisa ser Fastchip. O endereço fica vinculado ao CNPJ na Receita Federal.'
                              }
                              trigger={
                                <span>
                                  <Icon
                                    name="edit"
                                    style={{
                                      cursor: selectedContractInfo?.fastchip ? 'pointer' : 'not-allowed',
                                      marginLeft: '8px',
                                      fontSize: '0.95em',
                                      color: selectedContractInfo?.fastchip ? '#E65100' : '#9E9E9E',
                                      opacity: selectedContractInfo?.fastchip ? 1 : 0.6,
                                    }}
                                    onClick={() => {
                                      if (selectedContractInfo?.fastchip) handleStartEditDeliveryAddress();
                                    }}
                                  />
                                </span>
                              }
                            />
                          )}
                        </div>

                        {editingDeliveryAddress ? (
                          // Form responsivo: usa CSS Grid com auto-fit para nao estourar a
                          // largura do card. Form.Group widths="equal" do Semantic forcava
                          // grid de 16 colunas e quebrava o layout dentro do card estreito.
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '10px',
                              width: '100%',
                              boxSizing: 'border-box',
                              minWidth: 0,
                            }}
                          >
                            {(() => {
                              const fieldWrap: React.CSSProperties = { display: 'flex', flexDirection: 'column', minWidth: 0 };
                              const labelStyle: React.CSSProperties = {
                                fontSize: '0.78em',
                                fontWeight: 600,
                                color: '#5a5a5a',
                                marginBottom: '2px',
                              };
                              const inputStyle: React.CSSProperties = {
                                width: '100%',
                                padding: '6px 8px',
                                fontSize: '0.9em',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                boxSizing: 'border-box',
                                minWidth: 0,
                              };
                              const inputStyleWithError = (hasError: boolean): React.CSSProperties => ({
                                ...inputStyle,
                                border: hasError ? '2px solid #e74c3c' : '1px solid #ccc',
                                backgroundColor: hasError ? '#fff5f5' : 'white',
                              });
                              const errorHintStyle: React.CSSProperties = {
                                fontSize: '0.72em',
                                color: '#e74c3c',
                                marginTop: '2px',
                                fontWeight: 600,
                              };
                              const clearAddressFieldError = (
                                field: keyof DeliveryAddressInlineFieldErrors,
                                isValid: boolean
                              ) => {
                                if (editingDeliveryAddressFieldErrors[field] && isValid) {
                                  setEditingDeliveryAddressFieldErrors((prev) => ({
                                    ...prev,
                                    [field]: undefined,
                                  }));
                                }
                              };
                              const blurAddressField = (field: keyof DeliveryAddressInlineFieldErrors) => {
                                const errs = validateDeliveryAddressInlineEdit(editingDeliveryAddressData);
                                setEditingDeliveryAddressFieldErrors((prev) => ({
                                  ...prev,
                                  [field]: errs[field],
                                }));
                              };
                              return (
                                <>
                                  <div
                                    style={{
                                      display: 'grid',
                                      gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
                                      gap: '8px',
                                      width: '100%',
                                    }}
                                  >
                                    <div style={fieldWrap}>
                                      <label style={labelStyle}>
                                        CEP *
                                        {editingDeliveryAddressCepLoading && (
                                          <Icon
                                            name="circle notch"
                                            loading
                                            style={{ marginLeft: '6px', fontSize: '0.85em', color: '#1976d2' }}
                                          />
                                        )}
                                      </label>
                                      <input
                                        style={inputStyleWithError(!!editingDeliveryAddressFieldErrors.cep)}
                                        value={editingDeliveryAddressData.cep}
                                        onChange={(e) => {
                                          const raw = e.target.value;
                                          handleEditingDeliveryAddressCepChange(raw);
                                          const cepDigits = raw.replace(/\D/g, '');
                                          clearAddressFieldError('cep', cepDigits.length === 8);
                                        }}
                                        onBlur={() => blurAddressField('cep')}
                                        maxLength={9}
                                        placeholder="00000-000"
                                      />
                                      {editingDeliveryAddressFieldErrors.cep && (
                                        <span style={errorHintStyle}>{editingDeliveryAddressFieldErrors.cep}</span>
                                      )}
                                    </div>
                                    <div style={fieldWrap}>
                                      <label style={labelStyle}>UF *</label>
                                      <input
                                        style={inputStyleWithError(!!editingDeliveryAddressFieldErrors.estado)}
                                        value={editingDeliveryAddressData.estado}
                                        onChange={(e) => {
                                          const estado = (e.target.value || '').toUpperCase().slice(0, 2);
                                          setEditingDeliveryAddressData((prev) => ({ ...prev, estado }));
                                          clearAddressFieldError('estado', estado.length === 2);
                                        }}
                                        onBlur={() => blurAddressField('estado')}
                                        maxLength={2}
                                      />
                                      {editingDeliveryAddressFieldErrors.estado && (
                                        <span style={errorHintStyle}>{editingDeliveryAddressFieldErrors.estado}</span>
                                      )}
                                    </div>
                                    <div style={{ ...fieldWrap, gridColumn: 'span 2' }}>
                                      <label style={labelStyle}>Cidade *</label>
                                      <input
                                        style={inputStyleWithError(!!editingDeliveryAddressFieldErrors.cidade)}
                                        value={editingDeliveryAddressData.cidade}
                                        onChange={(e) => {
                                          const cidade = e.target.value;
                                          setEditingDeliveryAddressData((prev) => ({ ...prev, cidade }));
                                          clearAddressFieldError('cidade', !!cidade.trim());
                                        }}
                                        onBlur={() => blurAddressField('cidade')}
                                      />
                                      {editingDeliveryAddressFieldErrors.cidade && (
                                        <span style={errorHintStyle}>{editingDeliveryAddressFieldErrors.cidade}</span>
                                      )}
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      display: 'grid',
                                      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                                      gap: '8px',
                                      width: '100%',
                                    }}
                                  >
                                    <div style={{ ...fieldWrap, gridColumn: 'span 2' }}>
                                      <label style={labelStyle}>Endereço *</label>
                                      <input
                                        style={inputStyleWithError(!!editingDeliveryAddressFieldErrors.endereco)}
                                        value={editingDeliveryAddressData.endereco}
                                        onChange={(e) => {
                                          const endereco = normalizeDeliveryAddressStreetNameForInput(
                                            e.target.value
                                          );
                                          setEditingDeliveryAddressData((prev) => ({ ...prev, endereco }));
                                          clearAddressFieldError(
                                            'endereco',
                                            isValidDeliveryAddressStreetName(endereco)
                                          );
                                        }}
                                        onBlur={() => blurAddressField('endereco')}
                                      />
                                      {editingDeliveryAddressFieldErrors.endereco && (
                                        <span style={errorHintStyle}>{editingDeliveryAddressFieldErrors.endereco}</span>
                                      )}
                                    </div>
                                    <div style={fieldWrap}>
                                      <label style={labelStyle}>Número *</label>
                                      <input
                                        style={inputStyleWithError(!!editingDeliveryAddressFieldErrors.numero)}
                                        value={editingDeliveryAddressData.numero}
                                        onChange={(e) => {
                                          const numero = e.target.value;
                                          setEditingDeliveryAddressData((prev) => ({ ...prev, numero }));
                                          clearAddressFieldError(
                                            'numero',
                                            isFilledDeliveryAddressStreetNumber(numero)
                                          );
                                        }}
                                        onBlur={() => blurAddressField('numero')}
                                      />
                                      {editingDeliveryAddressFieldErrors.numero && (
                                        <span style={errorHintStyle}>{editingDeliveryAddressFieldErrors.numero}</span>
                                      )}
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      display: 'grid',
                                      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                                      gap: '8px',
                                      width: '100%',
                                    }}
                                  >
                                    <div style={fieldWrap}>
                                      <label style={labelStyle}>Bairro *</label>
                                      <input
                                        style={inputStyleWithError(!!editingDeliveryAddressFieldErrors.bairro)}
                                        value={editingDeliveryAddressData.bairro}
                                        onChange={(e) => {
                                          const bairro = e.target.value;
                                          setEditingDeliveryAddressData((prev) => ({ ...prev, bairro }));
                                          clearAddressFieldError('bairro', !!bairro.trim());
                                        }}
                                        onBlur={() => blurAddressField('bairro')}
                                      />
                                      {editingDeliveryAddressFieldErrors.bairro && (
                                        <span style={errorHintStyle}>{editingDeliveryAddressFieldErrors.bairro}</span>
                                      )}
                                    </div>
                                    <div style={fieldWrap}>
                                      <label style={labelStyle}>Complemento</label>
                                      <input
                                        style={inputStyle}
                                        value={editingDeliveryAddressData.complemento}
                                        onChange={(e) => setEditingDeliveryAddressData(prev => ({ ...prev, complemento: e.target.value }))}
                                      />
                                    </div>
                                  </div>
                                  <div style={{ width: '100%' }}>
                                    <div style={fieldWrap}>
                                      <label style={labelStyle}>Ponto de Referência</label>
                                      <input
                                        style={inputStyle}
                                        value={editingDeliveryAddressData.referencia}
                                        onChange={(e) => setEditingDeliveryAddressData(prev => ({ ...prev, referencia: e.target.value }))}
                                      />
                                    </div>
                                  </div>
                                </>
                              );
                            })()}
                            <div style={{ display: 'flex', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                              <Button
                                size="tiny"
                                color="green"
                                loading={savingInlineEdit}
                                disabled={savingInlineEdit}
                                onClick={handleSaveDeliveryAddressInline}
                              >
                                <Icon name="save" />
                                Salvar
                              </Button>
                              <Button
                                size="tiny"
                                disabled={savingInlineEdit}
                                onClick={handleCancelEditDeliveryAddress}
                              >
                                Cancelar
                              </Button>
                            </div>
                          </div>
                        ) : selectedContractInfo?.deliveryAddress ? (
                          <div style={{ fontSize: '1em', lineHeight: '1.6', fontWeight: '600' }}>
                            <div><strong>CEP:</strong> {selectedContractInfo.deliveryAddress.cep || 'Não informado'}</div>
                            <div>
                              <strong>Endereço:</strong> {selectedContractInfo.deliveryAddress.endereco || 'Não informado'}
                              {isFilledDeliveryAddressStreetNumber(selectedContractInfo.deliveryAddress.numero) &&
                                `, ${coerceDeliveryAddressStreetNumber(selectedContractInfo.deliveryAddress.numero)}`}
                              {selectedContractInfo.deliveryAddress.complemento && ` - ${selectedContractInfo.deliveryAddress.complemento}`}
                            </div>
                            <div><strong>Bairro:</strong> {selectedContractInfo.deliveryAddress.bairro || 'Não informado'}</div>
                            <div>
                              <strong>Cidade/UF:</strong>{' '}
                              {selectedContractInfo.deliveryAddress.cidade || 'Não informado'}/
                              {selectedContractInfo.deliveryAddress.estado || ''}
                            </div>
                            {selectedContractInfo.deliveryAddress.referencia && (
                              <div><strong>Referência:</strong> {selectedContractInfo.deliveryAddress.referencia}</div>
                            )}
                          </div>
                        ) : (
                          <div style={{
                            fontSize: '0.9em',
                            color: '#6c757d',
                            fontStyle: 'italic',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <Icon name="warning sign" color="orange" />
                            Endereço de entrega não disponível
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Resumo de Contratação */}
                    <div style={{ 
                      marginBottom: '15px', 
                      padding: '20px', 
                      background: 'linear-gradient(135deg, #E6E6FA 0%, #f8f9fa 100%)',
                      borderRadius: '8px', 
                      borderLeft: '5px solid #4A90E2',
                      border: '2px solid #4A90E2',
                      boxShadow: '0 3px 10px rgba(74, 144, 226, 0.2)',
                    }}>
                      <div style={{ fontSize: '1.2em', marginBottom: '15px', fontWeight: '700', color: '#2c3e50' }}>
                        A CONTRATAÇÃO FICOU:
                      </div>
                      
                      {/* Renovações */}
                      {Object.keys(planosRenovacao).length > 0 && (
                        <div style={{ 
                          marginBottom: '12px', 
                          padding: '12px', 
                          background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
                          borderRadius: '8px',
                          border: '2px solid #64B5F6',
                          boxShadow: '0 2px 6px rgba(100, 181, 246, 0.3)',
                        }}>
                          {Object.values(planosRenovacao).map((plano, idx) => {
                            const planDesc = getPlanDescription(plano.planName);
                            return (
                              <div key={idx} style={{ fontSize: '1.05em', marginBottom: '8px', fontWeight: '600', lineHeight: '1.6' }}>
                                <strong>{plano.quantidade} {plano.quantidade === 1 ? 'RENOVAÇÃO' : 'RENOVAÇÕES'}</strong> NO PLANO <strong>{plano.planName.toUpperCase()}</strong>
                                {planDesc && (
                                  <div style={{ fontSize: '0.9em', color: '#5a6c7d', fontWeight: '500', marginTop: '4px', marginLeft: '0' }}>
                                    {planDesc}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Portabilidades */}
                      {Object.keys(planosPortabilidade).length > 0 && (
                        <div style={{
                          marginBottom: '12px',
                          padding: '12px',
                          background: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)',
                          borderRadius: '8px',
                          border: '2px solid #F2711C',
                          boxShadow: '0 2px 6px rgba(242, 113, 28, 0.3)',
                        }}>
                          <div style={{ fontSize: '0.95em', fontWeight: '700', color: '#E65100', marginBottom: '8px' }}>
                            <Icon name="exchange" />
                            PORTABILIDADES
                          </div>
                          {Object.values(planosPortabilidade).map((plano, idx) => {
                            const planDesc = getPlanDescription(plano.planName);
                            return (
                              <div key={idx} style={{ fontSize: '1.05em', marginBottom: '8px', fontWeight: '600', lineHeight: '1.6' }}>
                                <strong>{plano.quantidade} {plano.quantidade === 1 ? 'PORTABILIDADE' : 'PORTABILIDADES'}</strong> NO PLANO <strong>{plano.planName.toUpperCase()}</strong>
                                {plano.detalhes.map((det, detIdx) => {
                                  const op = (det.operadora ?? '').trim();
                                  const num = (det.linha ?? '').trim();
                                  if (!op && !num) return null;
                                  return (
                                    <div key={detIdx} style={{ fontSize: '0.9em', color: '#BF360C', fontWeight: '500', marginTop: '4px' }}>
                                      {op && <>Op: <strong>{op.toUpperCase()}</strong></>}
                                      {op && num && ' | '}
                                      {num && <>Linha: <strong>{num}</strong></>}
                                    </div>
                                  );
                                })}
                                {planDesc && (
                                  <div style={{ fontSize: '0.9em', color: '#5a6c7d', fontWeight: '500', marginTop: '4px', marginLeft: '0' }}>
                                    {planDesc}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Linhas Novas (chip novo, sem portabilidade) */}
                      {Object.keys(planosNovos).length > 0 && (
                        <div style={{ 
                          marginBottom: '12px', 
                          padding: '12px', 
                          background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
                          borderRadius: '8px',
                          border: '2px solid #81C784',
                          boxShadow: '0 2px 6px rgba(129, 199, 132, 0.3)',
                        }}>
                          {Object.values(planosNovos).map((plano, idx) => {
                            const planDesc = getPlanDescription(plano.planName);
                            return (
                              <div key={idx} style={{ fontSize: '1.05em', marginBottom: '8px', fontWeight: '600', lineHeight: '1.6' }}>
                                <strong>{plano.quantidade} {plano.quantidade === 1 ? 'LINHA NOVA' : 'LINHAS NOVAS'}</strong> NO PLANO <strong>{plano.planName.toUpperCase()}</strong>
                                {planDesc && (
                                  <div style={{ fontSize: '0.9em', color: '#5a6c7d', fontWeight: '500', marginTop: '4px', marginLeft: '0' }}>
                                    {planDesc}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Valor Total */}
                      <div style={{ 
                        marginTop: '15px', 
                        padding: '15px', 
                        background: 'linear-gradient(135deg, #FFF9C4 0%, #FFF59D 100%)',
                        borderRadius: '8px', 
                        border: '3px solid #FBC02D',
                        boxShadow: '0 4px 12px rgba(251, 192, 45, 0.4)',
                      }}>
                        <div style={{ fontSize: '1.3em', fontWeight: '700', color: '#F57F17' }}>
                          <Icon name="money bill alternate" />
                          COM O VALOR TOTAL DE R$ {valorTotal.toFixed(2).replace('.', ',')}
                        </div>
                      </div>

                      {/* Linhas que serão canceladas (se houver) */}
                      {linhasSuspensas.length > 0 && (
                        <div style={{ 
                          marginTop: '15px', 
                          padding: '12px', 
                          background: 'linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)',
                          borderRadius: '8px', 
                          border: '3px solid #E53935',
                          boxShadow: '0 3px 10px rgba(229, 57, 53, 0.3)',
                        }}>
                          <div style={{ fontSize: '1.05em', fontWeight: '700', color: '#B71C1C', marginBottom: '10px' }}>
                            <Icon name="warning sign" />
                            ⚠️ LINHAS QUE SERÃO CANCELADAS:
                          </div>
                          {linhasSuspensas.map((linha, idx) => (
                            <div key={idx} style={{ fontSize: '1em', color: '#B71C1C', marginLeft: '20px', marginBottom: '5px', fontWeight: '600' }}>
                              • {linha.ddd}{linha.numero} ({linha.planName || 'Plano TIM'})
                            </div>
                          ))}
                          <div style={{ fontSize: '0.9em', color: '#B71C1C', marginTop: '10px', fontStyle: 'italic', fontWeight: '500' }}>
                            * Estas linhas NÃO estão incluídas no valor total acima e serão desativadas após 120 dias
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Estatísticas Resumidas */}
                    <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                      <Label color="blue" size="large" style={{
                        fontSize: '1.1em',
                        fontWeight: '600',
                        padding: '10px 16px',
                        boxShadow: '0 2px 8px rgba(33, 133, 208, 0.3)',
                      }}>
                        <Icon name="refresh" />
                        {totalRenovacoes} {totalRenovacoes === 1 ? 'Renovação' : 'Renovações'}
                      </Label>
                      <Label color="green" size="large" style={{
                        fontSize: '1.1em',
                        fontWeight: '600',
                        padding: '10px 16px',
                        boxShadow: '0 2px 8px rgba(33, 186, 69, 0.3)',
                      }}>
                        <Icon name="plus circle" />
                        {totalLinhasNovas} {totalLinhasNovas === 1 ? 'Linha Nova' : 'Linhas Novas'}
                      </Label>
                      <Label color="teal" size="large" style={{
                        fontSize: '1.1em',
                        fontWeight: '600',
                        padding: '10px 16px',
                        boxShadow: '0 2px 8px rgba(0, 181, 173, 0.3)',
                      }}>
                        <Icon name="list" />
                        {linhasAtivas.length} {linhasAtivas.length === 1 ? 'Linha Ativa' : 'Linhas Ativas'}
                      </Label>
                      {totalPortabilidades > 0 && (
                        <Label color="orange" size="large" style={{
                          fontSize: '1.1em',
                          fontWeight: '600',
                          padding: '10px 16px',
                          boxShadow: '0 2px 8px rgba(242, 113, 28, 0.3)',
                        }}>
                          <Icon name="exchange" />
                          {totalPortabilidades} {totalPortabilidades === 1 ? 'Portabilidade' : 'Portabilidades'}
                        </Label>
                      )}
                      {linhasSuspensas.length > 0 && (
                        <Label color="red" size="large" style={{
                          fontSize: '1.1em',
                          fontWeight: '600',
                          padding: '10px 16px',
                          boxShadow: '0 2px 8px rgba(219, 40, 40, 0.3)',
                        }}>
                          <Icon name="warning sign" />
                          {linhasSuspensas.length} {linhasSuspensas.length === 1 ? 'Linha a Cancelar' : 'Linhas a Cancelar'}
                        </Label>
                      )}
                    </div>
                  </Segment>
                );
              })()}
                </Grid.Column>
                
                {shouldShowScript && (
                  <Grid.Column>
                    {/* Script CQV: ligação + WhatsApp (abas) */}
                    <Segment color="teal" style={{ 
                      height: 'fit-content',
                      background: 'linear-gradient(135deg, #E0F2F1 0%, #B2DFDB 100%)',
                      border: '2px solid #26A69A',
                      boxShadow: '0 4px 12px rgba(38, 166, 154, 0.3)',
                      padding: '20px',
                    }}>
                      <Header as="h3" style={{ 
                        fontSize: '1.15em',
                        fontWeight: '700',
                        color: '#00695C',
                        marginBottom: '12px',
                      }}>
                        <Icon name="phone" style={{ fontSize: '1.1em' }} />
                        Script CQV
                        <Header.Subheader style={{ color: '#00897B', marginTop: '5px', fontSize: '0.9em', fontWeight: '500' }}>
                          Textos gerados com dados do contrato (inclui detalhes de entrega) — aba Ligação ou WhatsApp
                        </Header.Subheader>
                      </Header>

                      {linesModalCqvBlockedReason && (
                        <Message warning icon style={{ marginBottom: '12px' }}>
                          <Icon name="warning sign" />
                          <Message.Content>
                            <Message.Header>Script CQV bloqueado</Message.Header>
                            <p>{linesModalCqvBlockedReason}</p>
                          </Message.Content>
                        </Message>
                      )}

                      <Tab
                        menu={{ secondary: true, pointing: true }}
                        activeIndex={cqvScriptTabIndex}
                        onTabChange={(_, data) => setCqvScriptTabIndex(Number(data.activeIndex))}
                        panes={[
                          {
                            menuItem: 'Ligação CQV',
                            render: () => (
                              <Tab.Pane attached={false} style={{ border: 'none', boxShadow: 'none', padding: '12px 0 0 0', background: 'transparent' }}>
                                <Form>
                                  <Form.TextArea
                                    value={callScript}
                                    readOnly
                                    rows={20}
                                    style={{
                                      fontFamily: 'Consolas, Monaco, monospace',
                                      fontSize: '14px',
                                      lineHeight: '1.6',
                                      padding: '15px',
                                      resize: 'vertical',
                                      background: '#ffffff',
                                      cursor: 'default',
                                      border: '2px solid #26A69A',
                                      borderRadius: '8px',
                                      boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.06)',
                                      color: '#2c3e50',
                                      fontWeight: '500',
                                    }}
                                    placeholder="Script será gerado automaticamente com dados reais..."
                                  />
                                </Form>
                                <div style={{ marginTop: '10px', fontSize: '0.95em', color: '#00695C', fontStyle: 'italic', fontWeight: '500' }}>
                                  Atualiza automaticamente ao mudar linhas ou endereço do contrato.
                                </div>
                              </Tab.Pane>
                            ),
                          },
                          {
                            menuItem: 'WhatsApp',
                            render: () => (
                              <Tab.Pane attached={false} style={{ border: 'none', boxShadow: 'none', padding: '12px 0 0 0', background: 'transparent' }}>
                                <Message info size="small" style={{ marginBottom: '12px' }}>
                                  <Message.Header>Roteiro WhatsApp (padrão Gettel)</Message.Header>
                                  São <strong>três mensagens</strong>, na ordem 1 → 2 → 3. Envie cada bloco após o anterior; use o botão <strong>Copiar</strong> de cada uma.
                                </Message>
                                {[
                                  {
                                    key: 'm1',
                                    label: 'Mensagem 1 — até nome completo e CPF',
                                    value: whatsappCqvParts.part1,
                                    rows: 18,
                                    minHeight: 260,
                                    ok: 'Mensagem 1 copiada',
                                  },
                                  {
                                    key: 'm2',
                                    label: 'Mensagem 2 — assinatura física ou aceite digital',
                                    value: whatsappCqvParts.part2,
                                    rows: 3,
                                    minHeight: 72,
                                    ok: 'Mensagem 2 copiada',
                                  },
                                  {
                                    key: 'm3',
                                    label: 'Mensagem 3 — dúvidas e encerramento',
                                    value: whatsappCqvParts.part3,
                                    rows: 4,
                                    minHeight: 88,
                                    ok: 'Mensagem 3 copiada',
                                  },
                                ].map((block) => (
                                  <div key={block.key} style={{ marginBottom: '14px' }}>
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        marginBottom: '6px',
                                        flexWrap: 'wrap',
                                        gap: '8px',
                                      }}
                                    >
                                      <strong style={{ color: '#00695C' }}>{block.label}</strong>
                                      <Button
                                        type="button"
                                        size="small"
                                        basic
                                        color="teal"
                                        icon="copy"
                                        content="Copiar"
                                        disabled={!block.value}
                                        onClick={() => copyTextToClipboard(block.value, block.ok)}
                                      />
                                    </div>
                                    <Form.TextArea
                                      value={block.value}
                                      readOnly
                                      rows={block.rows}
                                      style={{
                                        fontFamily: 'system-ui, -apple-system, sans-serif',
                                        fontSize: '14px',
                                        lineHeight: '1.55',
                                        padding: '12px',
                                        background: '#fff',
                                        border: '1px solid #26A69A',
                                        borderRadius: '8px',
                                        color: '#2c3e50',
                                        minHeight: block.minHeight,
                                      }}
                                    />
                                  </div>
                                ))}
                              </Tab.Pane>
                            ),
                          },
                        ]}
                      />
                    </Segment>

                    {lineModalClientId && (
                      <Segment
                        style={{
                          marginTop: 12,
                          padding: '12px 14px',
                          background: '#faf5ff',
                          border: '1px solid #e1bee7',
                          boxShadow: 'none',
                        }}
                      >
                        <Header as="h5" style={{ margin: '0 0 8px', color: '#6a1b9a', fontSize: '0.95em' }}>
                          <Icon name="sticky note outline" />
                          Observações do cliente
                        </Header>
                        <ClientObservationsSummary
                          observations={lineDetailsClientObservations}
                          loading={lineDetailsObservationsLoading}
                          maxRows={4}
                          maxHeight={160}
                          dense
                        />
                        <div style={{ marginTop: 10 }}>
                          <ClientObservationsPanel
                            clientId={lineModalClientId}
                            observations={lineDetailsClientObservations}
                            canAdd
                            hideTable
                            contractId={selectedContractInfo?.id}
                            sourceType="contract_line_details"
                            onObservationsChanged={reloadLineDetailsClientObservations}
                            compact
                          />
                        </div>
                      </Segment>
                    )}
                  </Grid.Column>
                )}
                    </Grid>
                  </>
                );
              })()}

              {linesModalStatusNorm !== EContractStatus.Rascunho ? (
                <ContractColetaPainel
                  cnpj={selectedContractInfo?.clientCnpj || selectedContractInfo?.client?.cnpj || ''}
                  linhas={selectedContractLines}
                  telefoneAdministrador={selectedContractInfo?.administrator?.telefone1}
                  telefoneAdministrador2={selectedContractInfo?.administrator?.telefone2}
                  contractId={selectedContractInfo?.id}
                  groupId={selectedContractInfo?.groupId}
                  idAditivo={selectedContractInfo?.idPedidoContrato1}
                  idRenegociacao={selectedContractInfo?.idPedidoContrato2}
                  status={linesModalStatusNorm}
                  podeAcionarColeta={podeAcionarColeta}
                />
              ) : null}

              {selectedContractInfo?.id && (
                <ContractClientCancellationLinesPanel
                  contractId={selectedContractInfo.id}
                  clientId={
                    selectedContractInfo.clientId ??
                    selectedContractInfo.client?.id ??
                    lineModalClientId ??
                    undefined
                  }
                  lines={selectedContractLines.map((line: ContractLineDto) => ({
                    id: line.id,
                    ddd: line.ddd ?? '',
                    numero: line.numero ?? '',
                    label: `${line.negociacao ?? 'Linha'} — (${line.ddd ?? ''}) ${line.numero ?? ''}`,
                  }))}
                />
              )}

              {fluxoSuspensaoLinhasBloqueado && (
                <div style={{ marginBottom: 16 }}>
                  <SuspensionBlockBanner message={mensagemOrientacaoSuspensao1056} />
                </div>
              )}

              <Segment color="blue" style={{ 
                marginBottom: '20px',
                background: 'linear-gradient(135deg, #E6E6FA 0%, #B0C4DE 100%)',
                border: '2px solid #4A90E2',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 4px 12px rgba(74, 144, 226, 0.3)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong style={{ fontSize: '1.2em', fontWeight: '700', color: '#2c3e50' }}>
                      <Icon name="info circle" style={{ fontSize: '1.1em' }} />
                      Informações do Contrato
                    </strong>
                    <div style={{ marginTop: '10px', fontSize: '1em', color: '#2c3e50', lineHeight: '1.8', fontWeight: '600' }}>
                      <div><strong>Cliente:</strong> {selectedContractInfo?.clientRazaoSocial}</div>
                      <div>
                        <strong>Classificação:</strong>{' '}
                        {getClientClassificationMeta(
                          selectedContractInfo?.clientClassification ??
                            readClientClassification(selectedContractInfo?.client)
                        ).label}
                      </div>
                      <div><strong>Tipo:</strong> {selectedContractInfo?.contractType || selectedContractInfo?.tipoContratacao}</div>
                      <div><strong>Total de Linhas:</strong> {selectedContractLines.length}</div>
                      {(() => {
                        const legacyNotes =
                          selectedContractInfo?.clientInternalNotes ??
                          readClientInternalNotes(selectedContractInfo?.client);
                        const hasStructured = lineDetailsClientObservations.length > 0;
                        if (!hasStructured && !lineDetailsObservationsLoading && !legacyNotes) return null;
                        return (
                          <div>
                            <strong>Obs. internas do cliente:</strong>{' '}
                            {hasStructured ? (
                              <span>{lineDetailsClientObservations.length} registro(s) no histórico</span>
                            ) : (
                              legacyNotes
                            )}
                          </div>
                        );
                      })()}
                      {(() => {
                        const contractNotes =
                          selectedContractInfo?.internalNotes ??
                          readContractInternalNotes(selectedContractInfo);
                        if (!contractNotes) return null;
                        return (
                          <div>
                            <strong>Obs. internas do contrato:</strong> {contractNotes}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  <Label color="teal" size="big" style={{
                    fontSize: '1.3em',
                    fontWeight: '700',
                    padding: '12px 20px',
                    boxShadow: '0 3px 10px rgba(0, 150, 136, 0.4)',
                  }}>
                    <Icon name="money bill alternate" />
                    R$ {selectedContractInfo?.valorTotalMensal?.toFixed(2).replace('.', ',') || '0,00'}
                  </Label>
                </div>
              </Segment>

              {/* Campos para IDs dos pedidos - pós-CQV / VTME (status normalizado da API) */}
              {linesModalShowPedidoIds && (
                <Segment color="blue" style={{ marginBottom: '20px' }}>
                  <Header as="h4" color="blue">
                    <Icon name="file alternate" />
                    IDs dos Pedidos
                    {selectedContractInfo?.vtmeConfirmado && (
                      <Label color="green" size="small" style={{ marginLeft: '10px' }}>
                        <Icon name="check" />
                        VTME Confirmado
                      </Label>
                    )}
                  </Header>
                  <Form>
                    <Form.Group widths="equal">
                      <Form.Field>
                        <label>Id do Pedido Aditivo</label>
                        <Input
                          placeholder="Digite o ID do pedido Aditivo"
                          value={idPedido1}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Validar: apenas alfanuméricos, hífens e underscores
                            if (value === '' || /^[a-zA-Z0-9\-_]*$/.test(value)) {
                              setIdPedido1(value);
                            }
                          }}
                          maxLength={100}
                          disabled={linesModalPedidoIdsReadOnly}
                          style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: linesModalPedidoIdsReadOnly ? '#1a1a1a' : '#000000',
                            backgroundColor: linesModalPedidoIdsReadOnly ? '#f5f5f5' : '#ffffff'
                          }}
                        />
                      </Form.Field>
                      <Form.Field>
                        <label>Id do Pedido Renegociação</label>
                        <Input
                          placeholder="Digite o ID do pedido Renegociação (opcional)"
                          value={idPedido2}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Validar: apenas alfanuméricos, hífens e underscores
                            if (value === '' || /^[a-zA-Z0-9\-_]*$/.test(value)) {
                              setIdPedido2(value);
                            }
                          }}
                          maxLength={100}
                          disabled={linesModalPedidoIdsReadOnly}
                          style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: linesModalPedidoIdsReadOnly ? '#1a1a1a' : '#000000',
                            backgroundColor: linesModalPedidoIdsReadOnly ? '#f5f5f5' : '#ffffff'
                          }}
                        />
                      </Form.Field>
                    </Form.Group>
                    {selectedContractInfo?.vtmeConfirmado && hasRole('Admin') && (
                      <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Button
                          color="blue"
                          size="small"
                          loading={savingPedidoIds}
                          disabled={savingPedidoIds || !linesModalPedidoIdsDirty}
                          onClick={() => void handleSavePedidoIds()}
                        >
                          <Icon name="save" />
                          Salvar IDs
                        </Button>
                        {linesModalPedidoIdsDirty && (
                          <span style={{ fontSize: '12px', color: '#666' }}>
                            Alterações não salvas
                          </span>
                        )}
                      </div>
                    )}
                  </Form>
                </Segment>
              )}

              {selectedContractInfo?.fastchip && selectedLinesForSwap.size > 0 && (() => {
                const swapAllowed = isContractStatusSwapAllowedForContractsPage(selectedContractInfo?.status);
                return (
                  <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Button
                      color="blue"
                      size="small"
                      onClick={() => setShowFastchipSwapModal(true)}
                      disabled={!swapAllowed}
                      title={!swapAllowed ? 'Troca de FastChip permitida apenas em contratos Assinado ou Criado' : undefined}
                    >
                      <Icon name="exchange" />
                      Trocar FastChips ({selectedLinesForSwap.size} selecionada{selectedLinesForSwap.size > 1 ? 's' : ''})
                    </Button>
                    {!swapAllowed && (
                      <span style={{ fontSize: '12px', color: '#856404' }}>
                        <Icon name="lock" size="small" />
                        Apenas contratos Assinado ou Criado permitem troca de FastChip
                      </span>
                    )}
                    <Button
                      basic
                      size="small"
                      onClick={() => setSelectedLinesForSwap(new Set())}
                    >
                      Limpar seleção
                    </Button>
                  </div>
                );
              })()}

              <div style={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
              <Table celled striped style={{ minWidth: 1100 }}>
                <Table.Header>
                  <Table.Row style={{
                    background: 'linear-gradient(135deg, #E6E6FA 0%, #B0C4DE 100%)',
                    boxShadow: '0 2px 8px rgba(176, 196, 222, 0.3)',
                  }}>
                    {selectedContractInfo?.fastchip && (
                      <Table.HeaderCell width={1} textAlign="center" style={{
                        fontSize: '0.95em',
                        fontWeight: '700',
                        color: '#2c3e50',
                        padding: '14px 10px',
                        borderRight: '2px solid #B0C4DE',
                      }}>
                        <Checkbox
                          checked={selectedLinesForSwap.size > 0 && selectedContractLines
                            .filter((l: any) => (l.negociacao === 'Aditivo' || l.contractType === 'Aditivo') && !l.excluirDoContrato && !l.requerSuspensao)
                            .every((l: any) => selectedLinesForSwap.has(l.id))}
                          indeterminate={selectedLinesForSwap.size > 0 && !selectedContractLines
                            .filter((l: any) => (l.negociacao === 'Aditivo' || l.contractType === 'Aditivo') && !l.excluirDoContrato && !l.requerSuspensao)
                            .every((l: any) => selectedLinesForSwap.has(l.id))}
                          onChange={(_, data) => {
                            if (data.checked) {
                              const aditivoLines = selectedContractLines
                                .filter((l: any) => (l.negociacao === 'Aditivo' || l.contractType === 'Aditivo') && !l.excluirDoContrato && !l.requerSuspensao)
                                .map((l: any) => l.id);
                              setSelectedLinesForSwap(new Set(aditivoLines));
                            } else {
                              setSelectedLinesForSwap(new Set());
                            }
                          }}
                        />
                      </Table.HeaderCell>
                    )}
                    <Table.HeaderCell width={2} style={{
                      fontSize: '0.95em',
                      fontWeight: '700',
                      color: '#2c3e50',
                      padding: '14px 10px',
                      borderRight: '2px solid #B0C4DE',
                    }}>Status</Table.HeaderCell>
                    <Table.HeaderCell width={2} style={{
                      fontSize: '0.95em',
                      fontWeight: '700',
                      color: '#2c3e50',
                      padding: '14px 10px',
                      borderRight: '2px solid #B0C4DE',
                    }}>Negociação</Table.HeaderCell>
                    <Table.HeaderCell width={1} style={{
                      fontSize: '0.95em',
                      fontWeight: '700',
                      color: '#2c3e50',
                      padding: '14px 10px',
                      borderRight: '2px solid #B0C4DE',
                    }}>DDD</Table.HeaderCell>
                    <Table.HeaderCell width={2} style={{
                      fontSize: '0.95em',
                      fontWeight: '700',
                      color: '#2c3e50',
                      padding: '14px 10px',
                      borderRight: '2px solid #B0C4DE',
                    }}>Número</Table.HeaderCell>
                    <Table.HeaderCell width={2} style={{
                      fontSize: '0.95em',
                      fontWeight: '700',
                      color: '#2c3e50',
                      padding: '14px 10px',
                      borderRight: '2px solid #B0C4DE',
                    }}>Plano</Table.HeaderCell>
                    <Table.HeaderCell width={2} style={{
                      fontSize: '0.95em',
                      fontWeight: '700',
                      color: '#2c3e50',
                      padding: '14px 10px',
                      borderRight: '2px solid #B0C4DE',
                    }}>Aparelho</Table.HeaderCell>
                    <Table.HeaderCell width={2} style={{
                      fontSize: '0.95em',
                      fontWeight: '700',
                      color: '#2c3e50',
                      padding: '14px 10px',
                      borderRight: '2px solid #B0C4DE',
                    }}>Valor Mensal</Table.HeaderCell>
                    <Table.HeaderCell width={2} style={{
                      fontSize: '0.95em',
                      fontWeight: '700',
                      color: '#2c3e50',
                      padding: '14px 10px',
                      borderRight: '2px solid #B0C4DE',
                    }}>Tipo Venda</Table.HeaderCell>
                    <Table.HeaderCell width={2} style={{
                      fontSize: '0.95em',
                      fontWeight: '700',
                      color: '#2c3e50',
                      padding: '14px 10px',
                      borderRight: '2px solid #B0C4DE',
                    }}>ICCID</Table.HeaderCell>
                    <Table.HeaderCell width={2} style={{
                      fontSize: '0.95em',
                      fontWeight: '700',
                      color: '#2c3e50',
                      padding: '14px 10px',
                      borderRight: '2px solid #B0C4DE',
                    }}>Portabilidade</Table.HeaderCell>
                    {linesModalShowPedidoIds && (
                      <Table.HeaderCell width={2} style={{
                        fontSize: '0.95em',
                        fontWeight: '700',
                        color: '#2c3e50',
                        padding: '14px 10px',
                        borderRight: '2px solid #B0C4DE',
                      }}>Apto</Table.HeaderCell>
                    )}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {selectedContractLines.map((line: any, index: number) => {
                    // ✅ Verificar se a linha está marcada para suspensão
                    const isMarkedForSuspension = line.excluirDoContrato || line.requerSuspensao;
                    const isTrocaLinha = !!line.trocaLinha;

                    // Verificar se pode editar ICCID: apenas para Fastchip + Aditivo
                    const isFastchip = selectedContractInfo?.fastchip === true;
                    const isAditivo = line.negociacao === 'Aditivo' || line.contractType === 'Aditivo';
                    const canEditIccid = isFastchip && isAditivo;

                    const trocaLinhaRowBg = 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)';
                    const trocaLinhaRowBgHover = 'linear-gradient(135deg, #FFE0B2 0%, #FFCC80 100%)';

                    return (
                    <Table.Row 
                      key={line.id || index}
                      warning={isMarkedForSuspension}
                      style={
                        isMarkedForSuspension
                          ? {
                              background: 'linear-gradient(135deg, #FFF3CD 0%, #FFE082 100%)',
                              borderLeft: '4px solid #FF6F00',
                            }
                          : isTrocaLinha
                            ? {
                                background: trocaLinhaRowBg,
                                borderLeft: '4px solid #FF9800',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                              }
                            : {
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                              }
                      }
                      onMouseEnter={(e: React.MouseEvent<HTMLTableRowElement>) => {
                        if (isMarkedForSuspension) return;
                        if (isTrocaLinha) {
                          e.currentTarget.style.background = trocaLinhaRowBgHover;
                        } else {
                          e.currentTarget.style.background = '#f8f9fa';
                        }
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                      }}
                      onMouseLeave={(e: React.MouseEvent<HTMLTableRowElement>) => {
                        if (isMarkedForSuspension) return;
                        if (isTrocaLinha) {
                          e.currentTarget.style.background = trocaLinhaRowBg;
                        } else {
                          e.currentTarget.style.background = 'white';
                        }
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      {isFastchip && (
                        <Table.Cell textAlign="center" style={{
                          padding: '12px 10px',
                          borderRight: '1px solid #e0e0e0',
                        }}>
                          {isAditivo && !isMarkedForSuspension && (
                            <Checkbox
                              checked={selectedLinesForSwap.has(line.id)}
                              onChange={(_, data) => {
                                setSelectedLinesForSwap(prev => {
                                  const next = new Set(prev);
                                  if (data.checked) {
                                    next.add(line.id);
                                  } else {
                                    next.delete(line.id);
                                  }
                                  return next;
                                });
                              }}
                            />
                          )}
                        </Table.Cell>
                      )}
                      <Table.Cell textAlign="center" style={{
                        padding: '12px 10px',
                        fontSize: '0.95em',
                        borderRight: '1px solid #e0e0e0',
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                          {isMarkedForSuspension ? (
                            <Popup
                              content="Esta linha será cancelada/desativada em 120 dias e não está incluída no valor total do contrato"
                              trigger={
                                <Label color="red" size="medium" style={{
                                  fontSize: '0.9em',
                                  fontWeight: '600',
                                  padding: '6px 12px',
                                  boxShadow: '0 1px 4px rgba(219, 40, 40, 0.2)',
                                }}>
                                  <Icon name="times circle" />
                                  A Cancelar
                                </Label>
                              }
                            />
                          ) : (
                            <Label color="green" size="medium" style={{
                              fontSize: '0.9em',
                              fontWeight: '600',
                              padding: '6px 12px',
                              boxShadow: '0 1px 4px rgba(33, 186, 69, 0.2)',
                            }}>
                              <Icon name="check circle" />
                              Ativa
                            </Label>
                          )}
                          {isTrocaLinha && (
                            <Label color="orange" size="small" style={{ margin: 0 }}>
                              <Icon name="random" />
                              Troca de Linha
                            </Label>
                          )}
                        </div>
                      </Table.Cell>
                      <Table.Cell style={{
                        padding: '12px 10px',
                        fontSize: '0.95em',
                        borderRight: '1px solid #e0e0e0',
                      }}>
                        <Label color={line.negociacao === 'Aditivo' ? 'green' : 'blue'} size="medium" style={{
                          fontSize: '0.9em',
                          fontWeight: '600',
                          padding: '6px 12px',
                          boxShadow: line.negociacao === 'Aditivo' ? '0 1px 4px rgba(33, 186, 69, 0.2)' : '0 1px 4px rgba(33, 133, 208, 0.2)',
                        }}>
                          <Icon name={line.negociacao === 'Aditivo' ? 'plus circle' : 'refresh'} />
                          {line.negociacao}
                        </Label>
                      </Table.Cell>
                      <Table.Cell style={{
                        padding: '12px 10px',
                        fontSize: '0.95em',
                        borderRight: '1px solid #e0e0e0',
                      }}>
                        <Label color="grey" size="medium" style={{
                          fontSize: '0.9em',
                          fontWeight: '600',
                          padding: '6px 12px',
                          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
                        }}>
                          {line.ddd || '-'}
                        </Label>
                      </Table.Cell>
                      <Table.Cell style={{
                        padding: '12px 10px',
                        fontSize: '0.95em',
                        borderRight: '1px solid #e0e0e0',
                      }}>
                        <strong style={isMarkedForSuspension ? { textDecoration: 'line-through', color: '#856404', fontSize: '1em' } : { fontSize: '1em' }}>
                          {line.numero || '-'}
                        </strong>
                      </Table.Cell>
                      <Table.Cell style={{
                        padding: '12px 10px',
                        fontSize: '0.95em',
                        borderRight: '1px solid #e0e0e0',
                      }}>
                        <div>
                          <strong style={{ fontSize: '1em' }}>{line.planName || 'N/A'}</strong>
                        </div>
                      </Table.Cell>
                      <Table.Cell style={{
                        padding: '12px 10px',
                        fontSize: '0.9em',
                        borderRight: '1px solid #e0e0e0',
                        minWidth: 140,
                        maxWidth: 220,
                        wordBreak: 'break-word',
                      }}>
                        {(() => {
                          const ap = formatLineAparelhoLabel(line);
                          if (ap.title === '—') {
                            return <span style={{ color: '#999' }}>—</span>;
                          }
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <Label
                                size="small"
                                style={{
                                  alignSelf: 'flex-start',
                                  background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
                                  color: '#9d174d',
                                  border: '1px solid #f9a8d4',
                                  fontWeight: 600,
                                  whiteSpace: 'normal',
                                }}
                              >
                                <Icon name="mobile alternate" />
                                {ap.title}
                              </Label>
                              {ap.detail && (
                                <span style={{ fontSize: '0.85em', color: '#6b7280', lineHeight: 1.35 }}>
                                  {ap.detail}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </Table.Cell>
                      <Table.Cell style={{
                        padding: '12px 10px',
                        fontSize: '0.95em',
                        borderRight: '1px solid #e0e0e0',
                      }}>
                        <Label color={isMarkedForSuspension ? 'grey' : 'teal'} size="medium" style={{
                          fontSize: '0.9em',
                          fontWeight: '600',
                          padding: '6px 12px',
                          boxShadow: isMarkedForSuspension ? '0 1px 4px rgba(0, 0, 0, 0.1)' : '0 1px 4px rgba(0, 181, 173, 0.2)',
                        }}>
                          <Icon name="money bill alternate" />
                          {isMarkedForSuspension ? (
                            <span style={{ textDecoration: 'line-through' }}>
                              R$ {line.valorTotalMensal?.toFixed(2).replace('.', ',') || '0,00'}
                            </span>
                          ) : (
                            `R$ ${line.valorTotalMensal?.toFixed(2).replace('.', ',') || '0,00'}`
                          )}
                        </Label>
                      </Table.Cell>
                      <Table.Cell style={{
                        padding: '12px 10px',
                        fontSize: '0.95em',
                        borderRight: '1px solid #e0e0e0',
                      }}>
                        <Label color="purple" size="medium" style={{
                          fontSize: '0.9em',
                          fontWeight: '600',
                          padding: '6px 12px',
                          boxShadow: '0 1px 4px rgba(163, 51, 200, 0.2)',
                        }}>
                          {line.tipoVenda || '-'}
                        </Label>
                      </Table.Cell>
                      <Table.Cell style={{
                        padding: '12px 10px',
                        fontSize: '0.95em',
                        borderRight: '1px solid #e0e0e0',
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <code style={{ fontSize: '0.95em', fontFamily: 'monospace', fontWeight: '600' }}>
                            {backendFastchipAllocationMode && line.negociacao === 'Aditivo'
                              ? 'Alocado no backend'
                              : (line.chipCcid || '-')}
                          </code>
                          {line.isEsim && (
                            <Label color="purple" size="mini" style={{ alignSelf: 'flex-start' }}>
                              <Icon name="qrcode" />
                              FASTCHIP/E-SIM
                            </Label>
                          )}
                          {line.isEsim && line.chipCcid && (
                            <Button
                              size="mini"
                              color="purple"
                              basic
                              disabled
                              title="Envio de QR Code em desenvolvimento — disponível em breve"
                              style={{ marginTop: '2px', padding: '4px 8px', opacity: 0.5 }}
                            >
                              <Icon name="send" />
                              Reenviar QR Code
                            </Button>
                          )}
                        </div>
                      </Table.Cell>
                      <Table.Cell style={{
                        padding: '12px 10px',
                        fontSize: '0.95em',
                        borderRight: '1px solid #e0e0e0',
                      }}>
                        {line.ehPortabilidade ? (
                          <div style={{ fontSize: '0.85em', lineHeight: '1.5' }}>
                            <div><strong>{line.portabilidadeNomeCompleto}</strong></div>
                            <div>CPF: {line.portabilidadeCpf}</div>
                            <div>Op: {line.portabilidadeOperadora}</div>
                            <div>Linha: {line.portabilidadeLinha || '-'}</div>
                          </div>
                        ) : '-'}
                      </Table.Cell>
                      {linesModalShowPedidoIds && (
                        <Table.Cell textAlign="center" style={{
                          padding: '12px 10px',
                          fontSize: '0.95em',
                          borderRight: '1px solid #e0e0e0',
                        }}>
                          {line.negociacao === 'Renegociação' ? (
                            <Checkbox
                              toggle
                              checked={(() => {
                                // Priorizar valor do Map (estado local), depois valor da linha (salvo no backend)
                                const mapValue = linhasAptas.get(line.id);
                                if (mapValue !== undefined) return mapValue;
                                const lineValue = line.isApto;
                                return lineValue === true || lineValue === 1 || lineValue === 'true';
                              })()}
                              onChange={(e, data) => {
                                void handleLineAptoToggle(line, data.checked ?? false);
                              }}
                              disabled={
                                hasRole('Admin') ? false :
                                (selectedContractInfo?.vtmeConfirmado ||
                                savingLinhaAptoId === line.id)
                              }
                              loading={savingLinhaAptoId === line.id}
                              label={(() => {
                                const mapValue = linhasAptas.get(line.id);
                                if (mapValue !== undefined) return mapValue ? 'Apta' : 'Não Apta';
                                return convertIsAptoToBoolean(line.isApto) ? 'Apta' : 'Não Apta';
                              })()}
                              style={{
                                fontSize: '1em',
                                fontWeight: '600',
                              }}
                            />
                          ) : (
                            <Label color="grey" size="medium" style={{
                              fontSize: '0.9em',
                              fontWeight: '600',
                              padding: '6px 12px',
                              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)',
                            }}>
                              N/A
                            </Label>
                          )}
                        </Table.Cell>
                      )}
                    </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
              </div>

              {/* Ofertas das Linhas */}
              {selectedContractLines.some((line: any) => line.ofertas && line.ofertas.length > 0) && (
                <Segment color="green" style={{ marginTop: '20px' }}>
                  <Header as="h4" color="green">
                    <Icon name="gift" />
                    Ofertas Inclusas
                  </Header>
                  {selectedContractLines.map((line: any, lineIndex: number) => (
                    line.ofertas && line.ofertas.length > 0 && (
                      <div key={lineIndex} style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                        <strong>Linha {lineIndex + 1} - {line.negociacao}</strong>
                        <div style={{ marginTop: '8px' }}>
                          {line.ofertas.map((oferta: any, ofertaIndex: number) => (
                            <Label key={ofertaIndex} color="green" size="small" style={{ margin: '2px' }}>
                              <Icon name="gift" />
                              {oferta.nomeOferta} - R$ {oferta.valorMensal?.toFixed(2).replace('.', ',')}
                            </Label>
                          ))}
                        </div>
                      </div>
                    )
                  ))}
                </Segment>
              )}
            </div>
          ) : (
            <div>
              {linesModalStatusNorm !== EContractStatus.Rascunho ? (
                <ContractColetaPainel
                  cnpj={selectedContractInfo?.clientCnpj || selectedContractInfo?.client?.cnpj || ''}
                  linhas={selectedContractLines}
                  telefoneAdministrador={selectedContractInfo?.administrator?.telefone1}
                  telefoneAdministrador2={selectedContractInfo?.administrator?.telefone2}
                  contractId={selectedContractInfo?.id}
                  groupId={selectedContractInfo?.groupId}
                  idAditivo={selectedContractInfo?.idPedidoContrato1}
                  idRenegociacao={selectedContractInfo?.idPedidoContrato2}
                  status={linesModalStatusNorm}
                  podeAcionarColeta={podeAcionarColeta}
                />
              ) : null}
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Icon name="list" size="huge" color="grey" />
                <Header as="h3" color="grey">
                  Nenhuma linha encontrada
                </Header>
                <p>Este contrato não possui linhas cadastradas.</p>
              </div>
            </div>
          ))}
          {selectedContractInfo && (() => {
            const legacyNotes =
              selectedContractInfo?.clientInternalNotes ??
              readClientInternalNotes(selectedContractInfo?.client);
            const contractNotes =
              selectedContractInfo?.internalNotes ??
              readContractInternalNotes(selectedContractInfo);
            const qualityCcvObs = lineDetailsClientObservations.filter((o) =>
              /qualidade|ccv/i.test(`${o.occurrence ?? ''} ${o.observation ?? ''}`)
            );
            const otherObs = lineDetailsClientObservations.filter(
              (o) => !/qualidade|ccv/i.test(`${o.occurrence ?? ''} ${o.observation ?? ''}`)
            );
            const bottomObservations = [...qualityCcvObs, ...otherObs];
            const hasStructured = bottomObservations.length > 0;
            if (!hasStructured && !lineDetailsObservationsLoading && !legacyNotes && !contractNotes) {
              return null;
            }
            return (
              <Segment
                style={{
                  marginTop: 16,
                  textAlign: 'left',
                  background: '#faf5ff',
                  border: '1px solid #ce93d8',
                  boxShadow: 'none',
                }}
              >
                <Header as="h5" style={{ margin: '0 0 8px', color: '#6a1b9a', fontSize: '0.95em' }}>
                  <Icon name="sticky note outline" />
                  Observações internas (cliente e contrato)
                </Header>
                {hasStructured || lineDetailsObservationsLoading ? (
                  <ClientObservationsSummary
                    observations={bottomObservations}
                    loading={lineDetailsObservationsLoading}
                    maxRows={6}
                    maxHeight={180}
                    dense
                  />
                ) : legacyNotes ? (
                  <Message info size="small" style={{ marginTop: 0 }}>
                    <Message.Header>Observações internas do cliente</Message.Header>
                    <p style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>{legacyNotes}</p>
                  </Message>
                ) : null}
                {contractNotes ? (
                  <Message info size="small" style={{ marginTop: 8 }}>
                    <Message.Header>Observações internas do contrato</Message.Header>
                    <p style={{ whiteSpace: 'pre-wrap' }}>{contractNotes}</p>
                  </Message>
                ) : null}
              </Segment>
            );
          })()}
        </Modal.Content>
        <Modal.Actions style={{ opacity: linesModalLoading ? 0.6 : 1, pointerEvents: linesModalLoading ? 'none' : 'auto' }}>
          {/* Solicitar/Reenviar CQV — automação do pedido ao grupo de WhatsApp
              (specs/solicitacao-cqv-automatica-sla). Visível a qualquer papel
              (operador/supervisor), não só Admin — é quem vende que solicita.
              Só aparece com contrato Assinado (ainda sem decisão) ou Recusado CQV (reenvio) —
              mais restrito que o Confirmar/Recusar CQV manual (linesModalShowCqvButtons). */}
          {shouldShowSolicitarCqvButton(linesModalStatusNorm) && !isContractCancelled(selectedContractInfo) && (() => {
            const relevantLatest =
              latestCqvRequest && latestCqvRequest.contractId === selectedContractInfo?.id
                ? latestCqvRequest
                : null;

            // Só supervisor (TeamLeader/Admin) escolhe o modo — vendedor é sempre Ligação
            // (o backend também força isso, esse seletor é só conveniência de quem pode escolher).
            const isCqvSupervisor = hasRole('TeamLeader') || hasRole('Admin');
            const modeSelector = isCqvSupervisor && relevantLatest?.status !== ECqvRequestSlaStatus.Pendente && (
              <Dropdown
                selection
                compact
                value={cqvRequestMode}
                onChange={(_, { value }) => setCqvRequestMode(value as ECqvRequestMode)}
                options={[
                  { key: 'ligacao', value: ECqvRequestMode.Ligacao, text: 'Ligação' },
                  { key: 'whatsapp', value: ECqvRequestMode.WhatsApp, text: 'WhatsApp' },
                ]}
                style={{ marginRight: '8px' }}
                title="Como a CQV vai ser feita com o cliente — muda o SLA"
              />
            );

            if (relevantLatest?.status === ECqvRequestSlaStatus.Pendente && cqvRequestReachedGroup(relevantLatest)) {
              return (
                <Popup
                  trigger={
                    <Button color="grey" disabled style={{ marginRight: '10px' }}>
                      <Icon name="clock" />
                      CQV solicitada — aguardando
                    </Button>
                  }
                  content="Já existe uma solicitação de CQV em andamento pra este contrato (SLA correndo). Aguarde a resposta no grupo antes de solicitar de novo."
                />
              );
            }

            // Pendente mas a mensagem nunca chegou ao grupo (falha no envio) — não faz sentido
            // travar em "aguardando" pra sempre esperando uma resposta que ninguém vai ver.
            // Deixa tentar de novo (achado a pedido do Davi, 2026-09-02).
            if (relevantLatest?.status === ECqvRequestSlaStatus.Pendente && !cqvRequestReachedGroup(relevantLatest)) {
              return (
                <>
                  {modeSelector}
                  <Popup
                    trigger={
                      <Button
                        color="orange"
                        onClick={() => handleRequestCqv(relevantLatest.id)}
                        loading={requestingCqv}
                        disabled={requestingCqv}
                        style={{ marginRight: '10px' }}
                        title="O envio anterior falhou e não chegou no grupo — pode tentar de novo"
                      >
                        <Icon name="redo" />
                        Tentar reenviar CQV
                      </Button>
                    }
                    content="O pedido foi registrado, mas a mensagem não chegou ao grupo CQV. Clique pra tentar de novo."
                  />
                </>
              );
            }

            if (relevantLatest?.status === ECqvRequestSlaStatus.RecusadoManual) {
              return (
                <>
                  {modeSelector}
                  <Popup
                    trigger={
                      <Button
                        color="orange"
                        onClick={() => handleRequestCqv(relevantLatest.id)}
                        loading={requestingCqv}
                        disabled={requestingCqv}
                        style={{ marginRight: '10px' }}
                        title="A solicitação anterior foi recusada — reenviar cria um novo pedido citando o motivo"
                      >
                        <Icon name="redo" />
                        Reenviar CQV
                      </Button>
                    }
                    content={
                      relevantLatest.rejectionReason
                        ? `Recusado anteriormente: ${relevantLatest.rejectionReason}`
                        : 'Solicitação anterior foi recusada.'
                    }
                  />
                </>
              );
            }

            return (
              <>
                {modeSelector}
                <Button
                  color="teal"
                  onClick={() => handleRequestCqv()}
                  loading={requestingCqv}
                  disabled={requestingCqv}
                  style={{ marginRight: '10px' }}
                  title="Enviar solicitação de CQV automaticamente ao grupo de WhatsApp"
                >
                  <Icon name="whatsapp" />
                  Solicitar CQV
                </Button>
              </>
            );
          })()}
          {/* Botões CQV/VTME - Visíveis apenas para Admin */}
              {hasRole('Admin') && (
            <>
              {linesModalShowVtmeCriadoButton ? (
                <Button 
                  color="blue" 
                  onClick={handleConfirmarVtme}
                  loading={confirmingVtme}
                  disabled={
                    confirmingVtme ||
                    trocaDeCarteiraFromModalLoading ||
                    isContractCancelled(selectedContractInfo) ||
                    selectedContractInfo?.vtmeConfirmado
                  }
                  style={{ marginRight: '10px' }}
                  title={
                    isContractCancelled(selectedContractInfo)
                      ? "Não é possível registrar VTME de contratos cancelados"
                      : selectedContractInfo?.vtmeConfirmado
                        ? "VTME já foi criado para este contrato"
                        : "VTME Criado: atualizar status, notificar processamento e acionar integração VTME"
                  }
                >
                  <Icon name="check circle" />
                  VTME Criado
                </Button>
              ) : (
                linesModalShowCqvButtons && (
                  <>
                    <Button 
                      color="green" 
                      onClick={handleConfirmCQV}
                      loading={confirmingCQV}
                      disabled={
                        confirmingCQV ||
                        rejectingCQV ||
                        trocaDeCarteiraFromModalLoading ||
                        reconsultarCarteiraVtmeLoading ||
                        isContractCancelled(selectedContractInfo)
                      }
                      style={{ marginRight: '10px' }}
                      title={isContractCancelled(selectedContractInfo) ? "Não é possível fazer CQV de contratos cancelados" : "Confirmar CQV"}
                    >
                      <Icon name="check" />
                      Confirmar CQV
                    </Button>
                    <Button 
                      color="red" 
                      onClick={handleRejectCQV}
                      loading={rejectingCQV}
                      disabled={
                        confirmingCQV ||
                        rejectingCQV ||
                        trocaDeCarteiraFromModalLoading ||
                        reconsultarCarteiraVtmeLoading ||
                        isContractCancelled(selectedContractInfo)
                      }
                      style={{ marginRight: '10px' }}
                      title={isContractCancelled(selectedContractInfo) ? "Não é possível fazer CQV de contratos cancelados" : "Recusar CQV"}
                    >
                      <Icon name="times" />
                      Recusar CQV
                    </Button>
                  </>
                )
              )}
              {linesModalShowQualidadeButtons && (
                <>
                  <Button
                    color="green"
                    onClick={handleQualidadeOk}
                    loading={confirmingCQV}
                    disabled={
                      confirmingCQV ||
                      rejectingCQV ||
                      confirmingVtme ||
                      trocaDeCarteiraFromModalLoading ||
                      isContractCancelled(selectedContractInfo)
                    }
                    style={{ marginRight: '10px' }}
                    title={
                      isContractCancelled(selectedContractInfo)
                        ? 'Não é possível classificar qualidade de contratos cancelados'
                        : 'Qualidade OK: atualiza o status sem pedir texto'
                    }
                  >
                    <Icon name="check" />
                    Qualidade OK
                  </Button>
                  <Button
                    color="red"
                    onClick={handleRejectQualidadeFromLinesModal}
                    loading={rejectingCQV}
                    disabled={
                      confirmingCQV ||
                      rejectingCQV ||
                      confirmingVtme ||
                      trocaDeCarteiraFromModalLoading ||
                      isContractCancelled(selectedContractInfo)
                    }
                    style={{ marginRight: '10px' }}
                    title={
                      isContractCancelled(selectedContractInfo)
                        ? 'Não é possível recusar contratos cancelados'
                        : 'Recusar Qualidade: motivo obrigatório (Recusado Qualidade)'
                    }
                  >
                    <Icon name="times" />
                    Recusar Qualidade
                  </Button>
                </>
              )}
              {linesModalShowCcvButtons && (
                <>
                  <Button
                    color="green"
                    onClick={handleAprovadoCcv}
                    loading={confirmingCQV}
                    disabled={
                      confirmingCQV ||
                      rejectingCQV ||
                      confirmingVtme ||
                      trocaDeCarteiraFromModalLoading ||
                      isContractCancelled(selectedContractInfo)
                    }
                    style={{ marginRight: '10px' }}
                    title={
                      isContractCancelled(selectedContractInfo)
                        ? 'Não é possível classificar CCV de contratos cancelados'
                        : 'Aprovado CCV: atualiza o status sem pedir texto'
                    }
                  >
                    <Icon name="check" />
                    Aprovado CCV
                  </Button>
                  <Button
                    color="red"
                    onClick={handleRejectCcvFromLinesModal}
                    loading={rejectingCQV}
                    disabled={
                      confirmingCQV ||
                      rejectingCQV ||
                      confirmingVtme ||
                      trocaDeCarteiraFromModalLoading ||
                      isContractCancelled(selectedContractInfo)
                    }
                    style={{ marginRight: '10px' }}
                    title={
                      isContractCancelled(selectedContractInfo)
                        ? 'Não é possível recusar contratos cancelados'
                        : 'Recusado CCV: motivo obrigatório'
                    }
                  >
                    <Icon name="times" />
                    Recusado CCV
                  </Button>
                </>
              )}
              {linesModalShowCorrectionButtons && (
                <div style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                  {correctionCycleLoading ? (
                    <span style={{ marginRight: 10, color: '#888', fontSize: '0.85rem' }}>
                      <Icon name="spinner" loading style={{ margin: 0 }} />
                      Verificando correção…
                    </span>
                  ) : correctionCycle?.hasOpenCycle && !correctionCycle.startedByCurrentUser ? (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        marginRight: 10,
                        padding: '6px 10px',
                        borderRadius: 6,
                        background: '#f8f4ff',
                        border: '1px solid #ce93d8',
                        color: '#6a1b9a',
                        fontSize: '0.85rem',
                      }}
                    >
                      <Icon name="lock" style={{ margin: 0 }} />
                      Em correção por {correctionCycle.startedByUserName || 'outro usuário'} desde{' '}
                      {formatCorrectionCycleStartedAt(correctionCycle.startedAtUtc)}
                    </span>
                  ) : (
                    <Button
                      color="purple"
                      onClick={() => {
                        setCorrectionActionKind(correctionCycle?.hasOpenCycle ? 'end' : 'start');
                        setCorrectionActionReason('');
                      }}
                      disabled={correctionActionSubmitting}
                      style={{ marginRight: '10px' }}
                      title={
                        correctionCycle?.hasOpenCycle
                          ? 'Finalizar o ciclo de correção manual (SLA 2) deste contrato'
                          : 'Abrir ciclo de correção manual (SLA 2) para este contrato'
                      }
                    >
                      <Icon name={correctionCycle?.hasOpenCycle ? 'stop' : 'play'} />
                      {correctionCycle?.hasOpenCycle ? 'Terminei a correção' : 'Comecei a corrigir'}
                    </Button>
                  )}
                  {correctionActionKind && (
                    <div
                      style={{
                        display: 'block',
                        width: '100%',
                        marginTop: 10,
                        background: '#f8f4ff',
                        border: '1px dashed #ce93d8',
                        borderRadius: 10,
                        padding: '12px 14px',
                        textAlign: 'left',
                      }}
                    >
                      <div style={{ fontWeight: 600, color: '#6a1b9a', fontSize: '0.85rem', marginBottom: 6 }}>
                        {correctionActionKind === 'start' ? 'Iniciar correção CQV' : 'Finalizar correção CQV'} — motivo (opcional)
                      </div>
                      <Form>
                        <Form.TextArea
                          value={correctionActionReason}
                          onChange={(_, data) => setCorrectionActionReason(String(data.value ?? ''))}
                          rows={3}
                          placeholder="Detalhe o motivo (opcional)..."
                          style={{ width: '100%', resize: 'vertical' }}
                        />
                      </Form>
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <Button
                          size="small"
                          color="purple"
                          loading={correctionActionSubmitting}
                          disabled={correctionActionSubmitting}
                          style={{ borderRadius: 20 }}
                          onClick={() => void handleSubmitCorrectionAction()}
                        >
                          <Icon name="check" />
                          Confirmar
                        </Button>
                        <Button
                          size="small"
                          disabled={correctionActionSubmitting}
                          style={{ borderRadius: 20 }}
                          onClick={() => {
                            setCorrectionActionKind(null);
                            setCorrectionActionReason('');
                          }}
                        >
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {linesModalShowRejectQualidadeCvpButtons && (
                <>
                  <Button
                    color="red"
                    onClick={handleRejectQualidadeFromLinesModal}
                    loading={rejectingCQV}
                    disabled={
                      confirmingCQV ||
                      rejectingCQV ||
                      trocaDeCarteiraFromModalLoading ||
                      isContractCancelled(selectedContractInfo)
                    }
                    style={{ marginRight: '10px' }}
                    title={
                      isContractCancelled(selectedContractInfo)
                        ? 'Não é possível recusar contratos cancelados'
                        : 'Recusado Qualidade: motivo, notificação e e-mail'
                    }
                  >
                    <Icon name="times" />
                    Recusado Qualidade
                  </Button>
                  <Button
                    color="red"
                    onClick={handleRejectCvpFromLinesModal}
                    loading={rejectingCQV}
                    disabled={
                      confirmingCQV ||
                      rejectingCQV ||
                      trocaDeCarteiraFromModalLoading ||
                      isContractCancelled(selectedContractInfo)
                    }
                    style={{ marginRight: '10px' }}
                    title={
                      isContractCancelled(selectedContractInfo)
                        ? 'Não é possível recusar contratos cancelados'
                        : 'Recusado CVP: motivo, notificação e e-mail'
                    }
                  >
                    <Icon name="times" />
                    Recusado CVP
                  </Button>
                </>
              )}
            </>
          )}
          {hasRole('Admin') &&
            (linesModalStatusNorm === EContractStatus.AprovadoCQV ||
              isTrocaDeCarteiraFlowStatus(linesModalStatusNorm)) &&
            selectedContractInfo?.id && (
              <Button
                color="purple"
                style={{ marginRight: '10px' }}
                onClick={handleOpenCqvViewPages}
                disabled={trocaDeCarteiraFromModalLoading}
                title="Em grupo: escolhe Aditivo, Renegociação ou ambos. Contrato único: abre logo numa nova aba."
              >
                <Icon name="external alternate" />
                Ver dados CQV
              </Button>
            )}
          {hasRole('Admin') &&
            linesModalStatusNorm === EContractStatus.AprovadoCQV &&
            (highlightedContractId || selectedContractInfo?.id) &&
            !isContractCancelled(selectedContractInfo) && (
              <Button
                color="yellow"
                style={{ marginRight: '10px' }}
                loading={trocaDeCarteiraFromModalLoading}
                disabled={
                  trocaDeCarteiraFromModalLoading ||
                  confirmingVtme ||
                  confirmingCQV ||
                  rejectingCQV ||
                  linesModalLoading
                }
                onClick={handleTrocaCarteiraFromLinesModal}
                title="Altera o status para Troca de Carteira em todo o grupo, se houver groupId (mesma regra da tabela)."
              >
                <Icon name="exchange" />
                Troca de Carteira
              </Button>
            )}
          {hasRole('Admin') &&
            isTrocaDeCarteiraFlowStatus(linesModalStatusNorm) &&
            (highlightedContractId || selectedContractInfo?.id) &&
            !isContractCancelled(selectedContractInfo) && (
              <Button
                color="red"
                style={{ marginRight: '10px' }}
                loading={rejectingCQV}
                disabled={
                  trocaDeCarteiraFromModalLoading ||
                  reconsultarCarteiraVtmeLoading ||
                  confirmingVtme ||
                  confirmingCQV ||
                  rejectingCQV ||
                  linesModalLoading
                }
                onClick={handleRejectTrocaCarteiraFromLinesModal}
                title="Registra Troca de Carteira Não Autorizada, com motivo e e-mail ao supervisor (mesmo fluxo da recusa CQV)."
              >
                <Icon name="ban" />
                Recusado Troca de Carteira
              </Button>
            )}
          {hasRole('Admin') &&
            linesModalStatusNorm === EContractStatus.TrocaDeCarteiraSolicitada &&
            (highlightedContractId || selectedContractInfo?.id) &&
            !isContractCancelled(selectedContractInfo) && (
              <Button
                color="teal"
                style={{ marginRight: '10px' }}
                loading={reconsultarCarteiraVtmeLoading}
                disabled={
                  reconsultarCarteiraVtmeLoading ||
                  trocaDeCarteiraFromModalLoading ||
                  confirmingVtme ||
                  confirmingCQV ||
                  rejectingCQV ||
                  linesModalLoading
                }
                onClick={handleReconsultarCarteiraVtmeFromLinesModal}
                title="Consulta o VTME sem alterar o status. Se o cliente já estiver na nossa carteira, encerra a solicitação WhatsApp e restaura o status anterior."
              >
                <Icon name="search" />
                Verificar carteira
              </Button>
            )}
          {(hasRole('TeamLeader') || hasRole('Admin')) && (
            <Button
              color="green"
              style={{ marginRight: '10px' }}
              onClick={() => {
                setShowWelcomeModal(true);
                setWelcomeModalTabIndex(0);
                setWelcomeSelectedRepIdx(0);
              }}
            >
              <Icon name="whatsapp" /> Enviar Boas-Vindas
            </Button>
          )}
          <Button onClick={handleCloseLinesModal}>
            <Icon name="close" />
            Fechar
          </Button>
        </Modal.Actions>
      </Modal>

      <Modal
        size="small"
        open={showTrocaCarteiraConfirmModal}
        onClose={() => !trocaDeCarteiraFromModalLoading && setShowTrocaCarteiraConfirmModal(false)}
        closeOnDimmerClick={!trocaDeCarteiraFromModalLoading}
      >
        <Modal.Header>Confirmar Troca de Carteira</Modal.Header>
        <Modal.Content>
          <p style={{ color: '#2c3e50', lineHeight: 1.5 }}>
            {selectedContractInfo?.groupId ? (
              <>
                Todos os contratos deste grupo terão o status alterado para <strong>Troca de Carteira</strong>. Deseja
                continuar?
              </>
            ) : (
              <>
                Deseja alterar o status deste contrato para <strong>Troca de Carteira</strong>?
              </>
            )}
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => setShowTrocaCarteiraConfirmModal(false)}
            disabled={trocaDeCarteiraFromModalLoading}
          >
            Cancelar
          </Button>
          <Button
            color="yellow"
            loading={trocaDeCarteiraFromModalLoading}
            disabled={trocaDeCarteiraFromModalLoading}
            onClick={handleConfirmTrocaCarteiraFromLinesModal}
          >
            <Icon name="check" />
            Confirmar
          </Button>
        </Modal.Actions>
      </Modal>

      <Modal
        open={UNITY_TROCA_CARTEIRA_MESSAGE_MODAL_ENABLED && showUnityTrocaCarteiraMessageModal}
        onClose={() => setShowUnityTrocaCarteiraMessageModal(false)}
        size="small"
        closeOnDimmerClick
      >
        <Modal.Header>
          <Icon name="whatsapp" color="green" />
          Mensagem para a Unity (Troca de Carteira)
        </Modal.Header>
        <Modal.Content>
          <p style={{ marginBottom: '12px', color: '#2c3e50' }}>
            Copie o texto abaixo e envie no WhatsApp ao time da Unity para solicitar a troca de carteira.
          </p>
          {!((selectedContractInfo?.user?.usuarioVtme || '').trim()) && (
            <Message warning size="small">
              <p>
                <strong>Usuário VTME</strong> não está cadastrado para este consultor — a linha CONSULTOR virá só com o
                nome. Complemente no cadastro de usuários se precisar do login VTME entre parênteses.
              </p>
            </Message>
          )}
          <TextArea
            readOnly
            rows={6}
            value={unityTrocaCarteiraMessageBody}
            style={{
              fontFamily: 'Consolas, Monaco, monospace',
              fontSize: '14px',
              width: '100%',
              marginTop: '8px',
            }}
          />
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => {
              setShowUnityTrocaCarteiraMessageModal(false);
              setUnityTrocaCarteiraMessageBody('');
            }}
          >
            Fechar
          </Button>
          <Button color="green" onClick={handleCopyUnityTrocaCarteiraMessage}>
            <Icon name="copy" />
            Copiar mensagem
          </Button>
        </Modal.Actions>
      </Modal>
      {/* Modal de Rastreamento */}
      <TrackingModal
        open={showTrackingModal}
        contractId={trackingModalContractId}
        trackingCode={trackingModalPersistedCode}
        canEditCode={hasRole('Admin')}
        trackingInfo={trackingInfo}
        loading={trackingLoading}
        fetchError={trackingFetchError}
        saveLoading={trackingSaveLoading}
        saveError={trackingSaveError}
        onSaveCode={handleTrackingSaveCode}
        onClose={handleCloseTrackingModal}
      />

      <SendContractEmailModal
        open={showSendEmailModal}
        onClose={() => {
          setShowSendEmailModal(false);
          setSendingEmailContractIds(null);
        }}
        onSend={handleSendEmail}
        defaultEmail={getUserInfo()?.email || ''}
        contractId={sendingEmailContractIds?.[0] || undefined}
      />

      <Modal
        open={showConfirmCQVModal}
        onClose={() => setShowConfirmCQVModal(false)}
        size="small"
      >
        <Modal.Header>Confirmar Aprovação CQV</Modal.Header>
        <Modal.Content>
          <p>Deseja aprovar este contrato? Você receberá um email de confirmação.</p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowConfirmCQVModal(false)}>
            Cancelar
          </Button>
          <Button primary onClick={handleConfirmCQVConfirm} loading={confirmingCQV}>
            Confirmar
          </Button>
        </Modal.Actions>
      </Modal>

      <RejectContractModal
        open={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setPendingRejectionContractId(null);
          setPendingRejectionKind('cqv');
        }}
        onConfirm={handleRejectCQVConfirm}
        rejectionKind={pendingRejectionKind}
      />

      <Modal
        open={showVtmeWarningModal}
        onClose={() => setShowVtmeWarningModal(false)}
        size="small"
      >
        <Modal.Header>
          <Icon name="warning sign" color="yellow" />
          IDs de Pedido não preenchidos
        </Modal.Header>
        <Modal.Content>
          <Message warning>
            <Message.Header>
              {vtmeMissingFields.length === 1
                ? 'O seguinte ID de pedido está vazio:'
                : 'Os seguintes IDs de pedido estão vazios:'}
            </Message.Header>
            <Message.List>
              {vtmeMissingFields.includes('Id do Pedido Aditivo') && (
                <Message.Item>
                  <strong>Id do Pedido Aditivo</strong> — necessário para contratos do tipo Aditivo
                </Message.Item>
              )}
              {vtmeMissingFields.includes('Id do Pedido Renegociação') && (
                <Message.Item>
                  <strong>Id do Pedido Renegociação</strong> — necessário para contratos do tipo Renegociação
                </Message.Item>
              )}
            </Message.List>
          </Message>
          <p>
            {vtmeMissingFields.length === 1
              ? 'Deseja confirmar o VTME mesmo sem preencher este campo?'
              : 'Deseja confirmar o VTME mesmo sem preencher estes campos?'}
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowVtmeWarningModal(false)}>
            <Icon name="arrow left" />
            Voltar e preencher
          </Button>
          <Button color="blue" loading={confirmingVtme} onClick={() => { setShowVtmeWarningModal(false); doConfirmarVtme(); }}>
            <Icon name="check circle" />
            Confirmar mesmo assim
          </Button>
        </Modal.Actions>
      </Modal>
      {/* Modal Boas-Vindas */}
      {showWelcomeModal && selectedContractInfo && (hasRole('TeamLeader') || hasRole('Admin')) && (() => {
        // Admin usa o próprio nome; TeamLeader usa o managerName do time do vendedor do contrato
        let leaderName = user?.nome?.trim() || user?.name?.trim() || 'GERENTE NÃO IDENTIFICADO';
        if (hasRole('TeamLeader') && !hasRole('Admin')) {
          const sellerManagerName = selectedContractInfo?.user?.managerName?.trim();
          if (sellerManagerName) {
            leaderName = sellerManagerName;
          }
        }
        const sellerName = (
          selectedContractInfo?.user?.nome?.trim() ||
          selectedContractInfo?.user?.username?.trim() ||
          'CONSULTOR NÃO IDENTIFICADO'
        );
        const monthYear = selectedContractInfo.createdAt
          ? formatMonthYear(String(selectedContractInfo.createdAt))
          : '';
        // "contratar/contratação" apenas quando não há groupId E é puramente Aditivo
        const hasGroupId = !!selectedContractInfo?.groupId;
        const hasAditivoLine = selectedContractLines.some(
          (l: any) => l.negociacao === 'Aditivo' || l.contractType === 'Aditivo'
        );
        const hasRenegociacaoLine = selectedContractLines.some(
          (l: any) => l.negociacao === 'Renegociação' || l.contractType === 'Renegociação'
        );
        const isContratoAditivoOnly = !hasGroupId && hasAditivoLine && !hasRenegociacaoLine;
        const contractAction = isContratoAditivoOnly ? 'contratar' : 'renovar';
        const actionNoun = isContratoAditivoOnly ? 'contratação' : 'renovação';
        const script = buildWelcomeScript(
          leaderName,
          sellerName,
          monthYear,
          selectedContractInfo.clientRazaoSocial ?? '',
          selectedContractInfo.clientCnpj ?? '',
          contractAction,
          actionNoun
        );

        const adminPhone = selectedContractInfo.administrator?.telefone1 ?? '';
        const reps: any[] = selectedContractInfo.representatives ?? [];
        const selectedRep = reps[welcomeSelectedRepIdx];
        const repPhone = selectedRep?.telefone ?? '';

        const repOptions = reps.map((r: any, idx: number) => ({
          key: idx,
          value: idx,
          text: r.nome || `Representante ${idx + 1}`,
        }));

        const renderWhatsAppButton = (phone: string) => (
          <Popup
            disabled={!!phone}
            content="Telefone não cadastrado"
            trigger={
              <span>
                <Button
                  color="green"
                  disabled={!phone}
                  onClick={() => window.open(buildWhatsAppUrl(phone, script), '_blank')}
                >
                  <Icon name="whatsapp" /> WhatsApp
                </Button>
              </span>
            }
          />
        );

        const welcomePanes = [
          {
            menuItem: 'Administrador do Contrato',
            render: () => (
              <Tab.Pane>
                <Form>
                  <Form.TextArea
                    readOnly
                    value={script}
                    rows={14}
                  />
                </Form>
                <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                  <Button
                    icon="copy"
                    content="Copiar Script"
                    onClick={() => copyTextToClipboard(script, 'Script copiado!')}
                  />
                  {renderWhatsAppButton(adminPhone)}
                </div>
              </Tab.Pane>
            ),
          },
          {
            menuItem: 'Representante Legal',
            render: () => (
              <Tab.Pane>
                {reps.length > 1 && (
                  <Dropdown
                    selection
                    options={repOptions}
                    value={welcomeSelectedRepIdx}
                    onChange={(_: any, data: any) => setWelcomeSelectedRepIdx(data.value as number)}
                    style={{ marginBottom: '1rem' }}
                  />
                )}
                {reps.length === 0 && (
                  <Message info content="Nenhum representante legal cadastrado neste contrato." />
                )}
                {reps.length > 0 && (
                  <>
                    <Form>
                      <Form.TextArea
                        readOnly
                        value={script}
                        rows={14}
                      />
                    </Form>
                    <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                      <Button
                        icon="copy"
                        content="Copiar Script"
                        onClick={() => copyTextToClipboard(script, 'Script copiado!')}
                      />
                      {renderWhatsAppButton(repPhone)}
                    </div>
                  </>
                )}
              </Tab.Pane>
            ),
          },
        ];

        return (
          <Modal
            open={showWelcomeModal}
            onClose={() => setShowWelcomeModal(false)}
            size="large"
            closeIcon
          >
            <Modal.Header>
              <Icon name="whatsapp" color="green" /> Boas-Vindas — {selectedContractInfo.clientRazaoSocial}
            </Modal.Header>
            <Modal.Content>
              <Tab
                panes={welcomePanes}
                activeIndex={welcomeModalTabIndex}
                onTabChange={(_: any, data: any) => setWelcomeModalTabIndex(data.activeIndex as number)}
              />
            </Modal.Content>
            <Modal.Actions>
              <Button onClick={() => setShowWelcomeModal(false)}>Fechar</Button>
            </Modal.Actions>
          </Modal>
        );
      })()}

    </GlassPageWrapper>
  );
};

export default ContractsPage;

